import * as React from 'react';
import { useTranslations, type AppConfig } from 'use-intl';
import { FunctionalIconWrapper } from './FunctionalIconWrapper';
import { type FunctionalIconT, type Size } from './FunctionalIcon.types';

const shouldUseSmallIcon = (
  size: Size | number | undefined | string,
): boolean => {
  return (typeof size === 'number' && size <= 3) || size === undefined;
};
const createFunctionalIcon =
  (
    ariaLabelMessageKey: keyof AppConfig['Messages']['ragnar']['functional_icons'],
    Small: React.ComponentType,
    Normal: React.ComponentType,
  ) =>
  ({ size, ...rest }: FunctionalIconT) => {
    const isSmall = shouldUseSmallIcon(size);
    const viewBox = isSmall ? '0 0 16 16' : '0 0 24 24';
    const Element = isSmall ? Small : Normal;
    const t = useTranslations('ragnar.functional_icons');

    return (
      <FunctionalIconWrapper
        aria-label={t(ariaLabelMessageKey)}
        size={size}
        viewBox={viewBox}
        {...rest}
      >
        <Element />
      </FunctionalIconWrapper>
    );
  };

/**
 * AccessibilityIcon
 */

const AccessibilityIcon16x16 = () => (
  <>
    <path d="M7.99951 5.24976C8.7589 5.24976 9.37451 4.63421 9.37451 3.87488C9.37451 3.11556 8.7589 2.5 7.99951 2.5C7.24012 2.5 6.62451 3.11556 6.62451 3.87488C6.62451 4.63421 7.24012 5.24976 7.99951 5.24976Z" />
    <path d="M12.1166 5.70703L7.99975 6.25999L3.88295 5.70703L3.71655 6.94591L7.37475 7.43726V8.97208L5.25146 12.5575L6.327 13.1944L7.99975 10.3698L9.67251 13.1944L10.7481 12.5575L8.62475 8.97208V7.43726L12.283 6.94591L12.1166 5.70703Z" />
    <path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM1.25 8C1.25 4.27208 4.27208 1.25 8 1.25C11.7279 1.25 14.75 4.27208 14.75 8C14.75 11.7279 11.7279 14.75 8 14.75C4.27208 14.75 1.25 11.7279 1.25 8Z" />
  </>
);

const AccessibilityIcon24x24 = () => (
  <>
    <path d="M13.5 7.25C13.5 8.07843 12.8284 8.75 12 8.75C11.1716 8.75 10.5 8.07843 10.5 7.25C10.5 6.42157 11.1716 5.75 12 5.75C12.8284 5.75 13.5 6.42157 13.5 7.25Z" />
    <path d="M16.6275 9.38362L12 10.0379L7.3725 9.38362L7.1275 11.1164L11.125 11.6816V13.0767L8.73739 17.321L10.2626 18.179L12 15.0906L13.7374 18.179L15.2626 17.321L12.875 13.0767V11.6816L16.8725 11.1164L16.6275 9.38362Z" />
    <path d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75Z" />
  </>
);

export const AccessibilityIcon = createFunctionalIcon(
  'accessibility_icon_label_a11y',
  AccessibilityIcon16x16,
  AccessibilityIcon24x24,
);

/**
 * AdminIcon
 */

const AdminIcon16x16 = () => (
  <path d="M8 2.75L11.5004 7.0403L15 2.75743V13H1.00002L1 2.75743L4.49961 7.0403L8 2.75ZM8 4.72731L4.50034 9.01671L2.25001 6.26272L2.25001 11.75H13.75V6.26273L11.4997 9.01671L8 4.72731Z" />
);

const AdminIcon24x24 = () => (
  <path d="M3 4.53027L7.5 10.2991L12 4.53027L16.5 10.2991L21 4.53027V18.9997H3V4.53027ZM12 6.96906L7.5 12.7379L4.5 8.892V17.4997H19.5V8.892L16.5 12.7379L12 6.96906Z" />
);

export const AdminIcon = createFunctionalIcon(
  'admin_icon_label_a11y',
  AdminIcon16x16,
  AdminIcon24x24,
);

/**
 * AdminSolidIcon
 */

const AdminSolidIcon16x16 = () => (
  <path d="M8 2.75L11.5004 7.0403L15 2.75742V13H1.00002L1 2.75741L4.4996 7.0403L8 2.75Z" />
);

const AdminSolidIcon24x24 = () => (
  <path d="M12 4.56543L16.5409 10.3784L21 4.67017V18.9998H3V4.67017L7.45909 10.3784L12 4.56543Z" />
);

export const AdminSolidIcon = createFunctionalIcon(
  'admin_icon_label_a11y',
  AdminSolidIcon16x16,
  AdminSolidIcon24x24,
);

/**
 * AlertIcon
 */

const AlertIcon16x16 = () => (
  <path d="M8 2.25C4.82436 2.25 2.25 4.82436 2.25 8C2.25 11.1756 4.82436 13.75 8 13.75C11.1756 13.75 13.75 11.1756 13.75 8C13.75 4.82436 11.1756 2.25 8 2.25ZM1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8ZM7.375 9V4.25H8.625V9H7.375ZM7.375 11.3333V10H8.625V11.3333H7.375Z" />
);

const AlertIcon24x24 = () => (
  <path d="M12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11.125 13V7.5H12.875V13H11.125ZM11.125 16.5V14.5H12.875V16.5H11.125Z" />
);

export const AlertIcon = createFunctionalIcon(
  'warning_icon_label_a11y',
  AlertIcon16x16,
  AlertIcon24x24,
);

/**
 * AlignBottomIcon
 */

const AlignBottomIcon16x16 = () => (
  <path d="M7.375 9.15786L7.375 1H8.625L8.625 9.1577L10.7246 7.05806L11.6085 7.94194L7.99992 11.5506L4.39131 7.94194L5.27519 7.05806L7.375 9.15786ZM15 15L1 15V13.75L15 13.75V15Z" />
);

const AlignBottomIcon24x24 = () => (
  <path d="M11.125 13.6376L11.125 3H12.875L12.875 13.6376L16.3813 10.1313L17.6187 11.3687L12 16.9874L6.38128 11.3687L7.61872 10.1313L11.125 13.6376ZM22 19.25V21L2 21V19.25L22 19.25Z" />
);

export const AlignBottomIcon = createFunctionalIcon(
  'align_items_to_bottom_icon_label_a11y',
  AlignBottomIcon16x16,
  AlignBottomIcon24x24,
);

/**
 * AlignCenterIcon
 */

const AlignCenterIcon16x16 = () => (
  <path d="M1 3L15 3V4.25L1 4.25L1 3ZM3 7.375H13V8.625H3V7.375ZM14 11.75V13L2 13L2 11.75L14 11.75Z" />
);

const AlignCenterIcon24x24 = () => (
  <path d="M22 6.75L2 6.75L2 5L22 5V6.75ZM4.875 11.125H19.125V12.875H4.875V11.125ZM4 17.25L20 17.25V19L4 19L4 17.25Z" />
);

export const AlignCenterIcon = createFunctionalIcon(
  'align_items_to_center_icon_label_a11y',
  AlignCenterIcon16x16,
  AlignCenterIcon24x24,
);

/**
 * AlignLeftIcon
 */

const AlignLeftIcon16x16 = () => (
  <path d="M0.875 3L14.875 3V4.25L0.875 4.25L0.875 3ZM0.875 7.375H10.875V8.625H0.875V7.375ZM12.875 11.75V13L0.875 13L0.875 11.75L12.875 11.75Z" />
);

const AlignLeftIcon24x24 = () => (
  <path d="M2 5L22 5.00001V6.75001L2 6.75V5ZM2 11.125H15V12.875H2V11.125ZM2 17.25L17 17.25V19L2 19V17.25Z" />
);

export const AlignLeftIcon = createFunctionalIcon(
  'align_items_to_left_icon_label_a11y',
  AlignLeftIcon16x16,
  AlignLeftIcon24x24,
);

/**
 * AlignMiddleIcon
 */

const AlignMiddleIcon16x16 = () => (
  <path d="M7.375 3.99087V0H8.625V3.99071L10.2246 2.39107L11.1085 3.27495L7.99992 6.38356L4.89131 3.27495L5.77519 2.39107L7.375 3.99087ZM15 8.625L1 8.625V7.375L15 7.375V8.625ZM7.99992 9.61612L11.1085 12.7247L10.2246 13.6086L8.625 12.009V16.0003H7.375V12.0088L5.77519 13.6086L4.89131 12.7247L7.99992 9.61612Z" />
);

const AlignMiddleIcon24x24 = () => (
  <path d="M11.125 6.13756V0H12.875V6.13756L15.3813 3.63128L16.6187 4.86872L12 9.48744L7.38128 4.86872L8.61872 3.63128L11.125 6.13756ZM22 11.125V12.875L2 12.875V11.125L22 11.125ZM12 14.5126L16.6187 19.1313L15.3813 20.3687L12.875 17.8624V24H11.125V17.8624L8.61872 20.3687L7.38128 19.1313L12 14.5126Z" />
);

export const AlignMiddleIcon = createFunctionalIcon(
  'align_items_to_middle_icon_label_a11y',
  AlignMiddleIcon16x16,
  AlignMiddleIcon24x24,
);

/**
 * AlignRightIcon
 */

const AlignRightIcon16x16 = () => (
  <path d="M1 3L15 3V4.25L1 4.25L1 3ZM5 7.375H15V8.625H5V7.375ZM15 11.75V13L3 13V11.75L15 11.75Z" />
);

const AlignRightIcon24x24 = () => (
  <path d="M2 5L22 5V6.75L2 6.75V5ZM8 11.125H22V12.875H8V11.125ZM6 17.25L22 17.25V19L6 19V17.25Z" />
);

export const AlignRightIcon = createFunctionalIcon(
  'align_items_to_right_icon_label_a11y',
  AlignRightIcon16x16,
  AlignRightIcon24x24,
);

/**
 * AlignTopIcon
 */

const AlignTopIcon16x16 = () => (
  <path d="M15 2.25L1 2.25V1L15 1V2.25ZM7.99992 4.49144L11.6085 8.10005L10.7246 8.98393L8.625 6.88429L8.625 15H7.375L7.375 6.88413L5.27519 8.98393L4.39131 8.10005L7.99992 4.49144Z" />
);

const AlignTopIcon24x24 = () => (
  <path d="M22 3.75H2V2H22V3.75ZM12 6.01256L18.1187 12.1313L16.8813 13.3687L12.875 9.36244V20H11.125V9.36244L7.11872 13.3687L5.88128 12.1313L12 6.01256Z" />
);

export const AlignTopIcon = createFunctionalIcon(
  'align_items_to_top_icon_label_a11y',
  AlignTopIcon16x16,
  AlignTopIcon24x24,
);

/**
 * ArrowDownIcon
 */

const ArrowDownIcon16x16 = () => (
  <path d="M8.625 11.4907L12.0581 8.05762L12.942 8.9415L8.00005 13.8834L3.05811 8.9415L3.94199 8.05762L7.375 11.4906V3H8.625V11.4907Z" />
);

const ArrowDownIcon24x24 = () => (
  <path d="M12.875 15.8871L16.8812 11.8809L18.1187 13.1183L11.9999 19.237L5.88123 13.1183L7.11866 11.8809L11.125 15.8872V5H12.875V15.8871Z" />
);

export const ArrowDownIcon = createFunctionalIcon(
  'arrow_down_icon_label_a11y',
  ArrowDownIcon16x16,
  ArrowDownIcon24x24,
);

/**
 * ArrowLeftIcon
 */

const ArrowLeftIcon16x16 = () => (
  <path d="M4.51244 7.375L7.94594 3.9415L7.06206 3.05762L2.12012 7.99956L7.06206 12.9415L7.94594 12.0576L4.51333 8.625H13.0039V7.375H4.51244Z" />
);

const ArrowLeftIcon24x24 = () => (
  <path d="M12.1187 7.6183L10.8813 6.38086L4.76257 12.4996L10.8813 18.6183L12.1187 17.3809L8.11287 13.375H19V11.625H8.11203L12.1187 7.6183Z" />
);

export const ArrowLeftIcon = createFunctionalIcon(
  'arrow_left_icon_label_a11y',
  ArrowLeftIcon16x16,
  ArrowLeftIcon24x24,
);

/**
 * ArrowRightIcon
 */

const ArrowRightIcon16x16 = () => (
  <path d="M13.8839 7.99956L8.94199 3.05762L8.05811 3.9415L11.4916 7.375H3V8.625H11.4907L8.05811 12.0576L8.94199 12.9415L13.8839 7.99956Z" />
);

const ArrowRightIcon24x24 = () => (
  <path d="M11.8813 7.6183L13.1187 6.38086L19.2374 12.4996L13.1187 18.6183L11.8813 17.3809L15.8871 13.375H5V11.625H15.888L11.8813 7.6183Z" />
);

export const ArrowRightIcon = createFunctionalIcon(
  'arrow_right_icon_label_a11y',
  ArrowRightIcon16x16,
  ArrowRightIcon24x24,
);

/**
 * ArrowUpIcon
 */

const ArrowUpIcon16x16 = () => (
  <path d="M12.942 7.06206L8.00005 2.12012L3.05811 7.06206L3.94199 7.94594L7.375 4.51293V13.0039H8.625V4.51284L12.0581 7.94594L12.942 7.06206Z" />
);

const ArrowUpIcon24x24 = () => (
  <path d="M18.1188 10.8814L12.0001 4.7627L5.88135 10.8814L7.11878 12.1189L11.125 8.11264V19H12.875V8.1125L16.8813 12.1189L18.1188 10.8814Z" />
);

export const ArrowUpIcon = createFunctionalIcon(
  'arrow_up_icon_label_a11y',
  ArrowUpIcon16x16,
  ArrowUpIcon24x24,
);

/**
 * AssessIcon
 */

const AssessIcon16x16 = () => {
  return (
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.28571 1.00003C8 0.999939 7.60551 0.999974 8 0.999974C8.39449 0.999974 8 0.999979 8.71429 0.999939V3.28569H15C15 3.99997 15 3.60548 15 3.99997C15 4.39446 15 3.99997 15 4.71426H13.3542L15.9457 10.9786C15.9816 11.0652 16 11.1579 16 11.2516C16 12.2432 15.6106 13.1957 14.915 13.8993C14.2191 14.6031 13.2734 15 12.2857 15C11.298 15 10.3524 14.6031 9.65647 13.8993C8.96081 13.1957 8.57143 12.2432 8.57143 11.2516C8.57143 11.1579 8.58986 11.0652 8.62568 10.9786L11.2172 4.71426H4.78278L7.37432 10.9786C7.41014 11.0652 7.42857 11.1579 7.42857 11.2516C7.42857 12.2432 7.03919 13.1957 6.34353 13.8993C5.6476 14.6031 4.702 15 3.71429 15C2.72657 15 1.78097 14.6031 1.08504 13.8993C0.389387 13.1957 0 12.2432 0 11.2516C0 11.1579 0.0184337 11.0652 0.0542515 10.9786L2.64579 4.71426H1C1 3.99997 1 4.39446 1 3.99997C1 3.60548 1 4.08342 1 3.28569H7.28571V1.00003ZM10.0587 11.2516L12.2857 5.86848L14.5127 11.2516H10.0587ZM1.48729 11.2516H5.94128L3.71429 5.86848L1.48729 11.2516Z"
      fill="#161617"
    />
  );
};

const AssessIcon24x24 = () => {
  return (
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.1964 4.0001C12 4 11.5562 4.00004 12 4.00004C12.4438 4.00004 12 4.00005 12.8036 4V6.61228H19.875C19.875 7.4286 19.875 6.97776 19.875 7.4286C19.875 7.87945 19.875 7.4286 19.875 8.24493H18.0234L20.9389 15.4041C20.9793 15.5031 21 15.6091 21 15.7162C21 16.8494 20.562 17.938 19.7793 18.7421C18.9965 19.5464 17.9325 20 16.8214 20C15.7102 20 14.6465 19.5464 13.8635 18.7421C13.0809 17.938 12.6429 16.8494 12.6429 15.7162C12.6429 15.6091 12.6636 15.5031 12.7039 15.4041L15.6194 8.24493H8.38062L11.2961 15.4041C11.3364 15.5031 11.3571 15.6091 11.3571 15.7162C11.3571 16.8494 10.9191 17.938 10.1365 18.7421C9.35355 19.5464 8.28975 20 7.17857 20C6.06739 20 5.00359 19.5464 4.22067 18.7421C3.43806 17.938 3 16.8494 3 15.7162C3 15.6091 3.02074 15.5031 3.06103 15.4041L5.97652 8.24493H4.125C4.125 7.4286 4.125 7.87945 4.125 7.4286C4.125 6.97776 4.125 7.52397 4.125 6.61228H11.1964V4.0001ZM14.3161 15.7162L16.8214 9.56403L19.3268 15.7162H14.3161ZM4.6732 15.7162H9.68394L7.17857 9.56403L4.6732 15.7162Z"
      fill="#161617"
    />
  );
};

export const AssessIcon = createFunctionalIcon(
  'assess_icon_label_a11y',
  AssessIcon16x16,
  AssessIcon24x24,
);

/**
 * AtSignIcon
 */

const AtSignIcon16x16 = () => (
  <path d="M7.54238 1.02084C9.23566 0.90986 10.9115 1.41823 12.2578 2.45125C13.6041 3.48427 14.5289 4.97147 14.8599 6.63578C15.017 7.42572 14.9783 8.50582 14.8743 9.36744C14.7348 10.5232 13.7336 11.2146 12.6934 11.2418C11.7589 11.2662 10.9312 10.7849 10.4687 10.0494C9.88023 10.7577 8.9927 11.2087 7.99984 11.2087C6.22792 11.2087 4.7915 9.77224 4.7915 8.00033C4.7915 6.22841 6.22792 4.79199 7.99984 4.79199C8.79333 4.79199 9.51954 5.08005 10.0796 5.5573V4.72952H11.3296V8.69549C11.3296 9.42518 11.9313 10.0113 12.6607 9.99222C13.2087 9.97789 13.5824 9.63922 13.6333 9.21763C13.7318 8.40208 13.7526 7.4765 13.6339 6.87964C13.362 5.51276 12.6025 4.29134 11.4968 3.44294C10.3912 2.59454 9.0148 2.17702 7.62414 2.26817C6.23347 2.35932 4.92336 2.95292 3.93789 3.93838C2.95243 4.92384 2.35883 6.23395 2.26768 7.62462C2.17653 9.01529 2.59405 10.3917 3.44245 11.4973C4.29086 12.603 5.51228 13.3625 6.87915 13.6344C8.24603 13.9063 9.66513 13.672 10.8721 12.9752L11.4971 14.0577C10.0275 14.9062 8.2996 15.1914 6.63529 14.8604C4.97098 14.5293 3.48378 13.6045 2.45076 12.2583C1.41775 10.912 0.909372 9.23615 1.02036 7.54287C1.13134 5.84959 1.85411 4.2544 3.05401 3.0545C4.25391 1.8546 5.8491 1.13183 7.54238 1.02084ZM7.99984 6.04199C6.91828 6.04199 6.0415 6.91877 6.0415 8.00033C6.0415 9.08188 6.91828 9.95866 7.99984 9.95866C9.0814 9.95866 9.95817 9.08188 9.95817 8.00033C9.95817 6.91877 9.0814 6.04199 7.99984 6.04199Z" />
);

const AtSignIcon24x24 = () => (
  <path d="M11.346 2.02142C13.7669 1.86275 16.1629 2.58957 18.0876 4.06648C20.0123 5.54338 21.3345 7.66963 21.8078 10.0491C22.0343 11.1877 21.9761 12.8692 21.8241 14.1272C21.632 15.7154 20.2734 16.5828 18.8887 16.6191C17.5654 16.6537 16.3861 16.0066 15.6822 15.0007C14.8112 16.0683 13.4851 16.75 11.9999 16.75C9.37653 16.75 7.24988 14.6234 7.24988 12C7.24988 9.37665 9.37653 7.25 11.9999 7.25C13.1376 7.25 14.182 7.65002 14.9999 8.31708V7.31418H16.7499V12.8307C16.7499 13.9781 17.6959 14.8997 18.8429 14.8697C19.5833 14.8503 20.0236 14.4393 20.0867 13.9171C20.2313 12.7213 20.2639 11.2573 20.0915 10.3905C19.701 8.42745 18.6102 6.67329 17.0223 5.45484C15.4344 4.2364 13.4577 3.63677 11.4604 3.76768C9.46319 3.89858 7.58166 4.75109 6.16638 6.16638C4.75109 7.58166 3.89858 9.46319 3.76768 11.4604C3.63677 13.4577 4.2364 15.4344 5.45484 17.0223C6.67329 18.6102 8.42745 19.701 10.3905 20.0915C12.3536 20.4819 14.3916 20.1455 16.125 19.1447L17 20.6602C14.8989 21.8733 12.4286 22.2811 10.0491 21.8078C7.66963 21.3345 5.54338 20.0123 4.06648 18.0876C2.58957 16.1629 1.86275 13.7669 2.02142 11.346C2.1801 8.92508 3.21344 6.64444 4.92894 4.92894C6.64444 3.21344 8.92508 2.1801 11.346 2.02142ZM14.9999 12C14.9999 10.3431 13.6567 9 11.9999 9C10.343 9 8.99988 10.3431 8.99988 12C8.99988 13.6569 10.343 15 11.9999 15C13.6567 15 14.9999 13.6569 14.9999 12Z" />
);

export const AtSignIcon = createFunctionalIcon(
  'at_sign_icon_label_a11y',
  AtSignIcon16x16,
  AtSignIcon24x24,
);

/**
 * BarsIcon
 */

const BarsIcon16x16 = () => (
  <path d="M13 2V14H11.75V2H13ZM8.625 6.5L8.625 14H7.375L7.375 6.5H8.625ZM4.25 10.25V14H3V10.25H4.25Z" />
);

const BarsIcon24x24 = () => (
  <path d="M20 3L20 20H18.25L18.25 3H20ZM12.875 8V20H11.125L11.125 8H12.875ZM5.75 14V20H4V14H5.75Z" />
);

export const BarsIcon = createFunctionalIcon(
  'bar_charts_icon_label_a11y',
  BarsIcon16x16,
  BarsIcon24x24,
);

/**
 * BellIcon
 */

const BellIcon16x16 = () => (
  <path d="M8 2.375C5.85297 2.375 4.10991 4.12007 4.10991 6.27586V8.63756L4.05118 8.81424L3.10742 11.75H12.8926L11.9488 8.81423L11.8901 8.63756V6.27586C11.8901 4.12007 10.147 2.375 8 2.375ZM8.93542 13H7.06458C7.10523 13.0608 7.152 13.118 7.2045 13.1705C7.41548 13.3815 7.70163 13.5 8 13.5C8.29837 13.5 8.58452 13.3815 8.79549 13.1705C8.84799 13.118 8.89477 13.0608 8.93542 13ZM5.7087 13C5.81654 13.3953 6.02583 13.7596 6.32062 14.0544C6.76602 14.4998 7.37011 14.75 8 14.75C8.62989 14.75 9.23398 14.4998 9.67938 14.0544C9.97416 13.7596 10.1835 13.3953 10.2913 13H14.6074L13.1401 8.43557V6.27586C13.1401 3.43253 10.8402 1.125 8 1.125C5.15981 1.125 2.85991 3.43253 2.85991 6.27586V8.43557L1.39258 13H5.7087Z" />
);

const BellIcon24x24 = () => (
  <path d="M12 4.125C9.03149 4.125 6.62502 6.53147 6.62502 9.5V12.7636L6.54655 12.9991L5.29481 17.25H18.7052L17.4535 12.9991L17.375 12.7636V9.5C17.375 6.53147 14.9685 4.125 12 4.125ZM4.87502 9.5C4.87502 5.56498 8.065 2.375 12 2.375C15.935 2.375 19.125 5.56498 19.125 9.5V12.4804L21.0448 19H15.255C15.1059 19.5333 14.8216 20.0246 14.4231 20.4231C13.8136 21.0326 12.987 21.375 12.125 21.375C11.2631 21.375 10.4364 21.0326 9.82691 20.4231C9.42843 20.0246 9.14411 19.5333 8.995 19H2.9552L4.87502 12.4804V9.5ZM10.9066 19C10.9538 19.0656 11.0065 19.1278 11.0643 19.1857C11.3457 19.467 11.7272 19.625 12.125 19.625C12.5228 19.625 12.9044 19.467 13.1857 19.1857C13.2436 19.1278 13.2962 19.0656 13.3434 19H10.9066Z" />
);

export const BellIcon = createFunctionalIcon(
  'bell_icon_label_a11y',
  BellIcon16x16,
  BellIcon24x24,
);

/**
 * BoldIcon
 */

const BoldIcon16x16 = () => (
  <path d="M3 1.5H9.5C10.4946 1.5 11.4484 1.89509 12.1516 2.59835C12.8549 3.30161 13.25 4.25544 13.25 5.25C13.25 6.17927 12.9051 7.07297 12.2862 7.75996C12.4232 7.86532 12.5535 7.98081 12.676 8.10588C13.366 8.8107 13.75 9.7622 13.75 10.75C13.75 11.7378 13.366 12.6893 12.676 13.3941C11.9853 14.0996 11.0439 14.5 10.0577 14.5H3V1.5ZM9.5 7C9.96413 7 10.4092 6.81563 10.7374 6.48744C11.0656 6.15925 11.25 5.71413 11.25 5.25C11.25 4.78587 11.0656 4.34075 10.7374 4.01256C10.4092 3.68437 9.96413 3.5 9.5 3.5H5V7H9.5ZM5 9H10.0577C10.4996 9 10.9278 9.17911 11.2469 9.50503C11.5667 9.83167 11.75 10.2791 11.75 10.75C11.75 11.2209 11.5667 11.6683 11.2469 11.995C10.9278 12.3209 10.4996 12.5 10.0577 12.5H5V9Z" />
);

const BoldIcon24x24 = () => (
  <path d="M5.5 3.25H13.75C15.0387 3.25 16.2556 3.79929 17.1387 4.74545C18.0191 5.6887 18.5 6.95068 18.5 8.25C18.5 9.50136 18.0539 10.7181 17.2346 11.6488C17.4742 11.8261 17.6987 12.0274 17.9046 12.2505C18.776 13.1944 19.25 14.4548 19.25 15.75C19.25 17.0452 18.776 18.3056 17.9046 19.2495C17.0302 20.1968 15.8218 20.75 14.5385 20.75H5.5V3.25ZM13.75 10.75C14.3178 10.75 14.8814 10.5091 15.3111 10.0488C15.7434 9.58548 16 8.9398 16 8.25C16 7.5602 15.7434 6.91452 15.3111 6.45125C14.8814 5.99089 14.3178 5.75 13.75 5.75H8V10.75H13.75ZM8 13.25H14.5385C15.0912 13.25 15.6437 13.487 16.0676 13.9462C16.4946 14.4088 16.75 15.0561 16.75 15.75C16.75 16.4439 16.4946 17.0912 16.0676 17.5538C15.6437 18.013 15.0912 18.25 14.5385 18.25H8V13.25Z" />
);

export const BoldIcon = createFunctionalIcon(
  'bold_icon_label_a11y',
  BoldIcon16x16,
  BoldIcon24x24,
);

/**
 * BrainstormIcon
 */

const BrainstormIcon16x16 = () => {
  return (
    <>
      <g clipPath="url(#clip0_2659_17413)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.1303 0.74196C11.3394 -0.243378 12.7006 -0.249278 12.9177 0.73606L12.9291 0.783261L12.9486 0.874125C13.2 1.97629 14.0629 2.81884 15.1451 3.01237C16.1417 3.19173 16.1417 4.66915 15.1451 4.84852C14.6135 4.94406 14.1211 5.19967 13.7296 5.58324C13.3382 5.96682 13.0653 6.46125 12.9451 7.00446L12.9177 7.126C12.7006 8.11016 11.3406 8.10544 11.1303 7.11774L11.1074 7.0139C10.9919 6.46893 10.7219 5.97183 10.3317 5.58597C9.94149 5.2001 9.44885 4.94297 8.91657 4.84734C7.92229 4.66797 7.92229 3.19291 8.91657 3.01473C9.44744 2.91954 9.93897 2.6637 10.3288 2.27966C10.7187 1.89561 10.9893 1.40067 11.1063 0.857604L11.1223 0.782081L11.1303 0.74314V0.74196ZM9.89486 7.28885C9.83274 6.99409 9.68746 6.72494 9.47735 6.51535C9.26725 6.30575 9.00172 6.16511 8.71429 6.11116V14.6252C9.09187 15.1295 9.59442 15.5192 10.1688 15.7529C10.7432 15.9866 11.3681 16.0558 11.9774 15.953C12.5867 15.8502 13.1578 15.5794 13.6303 15.1691C14.1028 14.7588 14.4592 14.2243 14.6617 13.6221C14.3574 13.5912 14.0584 13.5186 13.7726 13.4062C13.0926 13.1419 12.3531 12.6215 11.7463 11.7117C11.6388 11.5505 11.5978 11.3518 11.6322 11.1594C11.6666 10.967 11.7736 10.7966 11.9297 10.6856C12.0858 10.5747 12.2782 10.5323 12.4646 10.5678C12.6509 10.6033 12.816 10.7138 12.9234 10.875C13.36 11.5299 13.8606 11.8651 14.2754 12.0255C14.4857 12.1082 14.6731 12.1436 14.8194 12.1554L14.8457 12.1577C15.5543 11.3624 16 9.82358 16 8.53852C16 7.67236 15.7977 6.81801 15.448 6.09346L15.3577 6.11116C14.7634 6.21737 14.2903 6.6823 14.1566 7.28885L14.1291 7.40921C13.6171 9.73036 10.4091 9.7162 9.91657 7.39033L9.89371 7.28767L9.89486 7.28885ZM7.28571 5.18483C7.05215 4.81069 6.92794 4.37498 6.92794 3.92985C6.92794 3.48472 7.05215 3.04902 7.28571 2.67487V2.06125C6.88887 1.57961 6.35843 1.23532 5.76604 1.07487C5.17365 0.914412 4.54785 0.945525 3.97313 1.164C3.39841 1.38248 2.90246 1.7778 2.55227 2.29658C2.20207 2.81536 2.01449 3.43261 2.01486 4.06497V4.06733C2.05943 4.52754 2.24114 4.86976 2.45143 5.11757C2.71314 5.42556 2.98857 5.55182 3.05371 5.56598C3.14526 5.58745 3.23182 5.62732 3.30845 5.68332C3.38508 5.73932 3.45027 5.81036 3.50031 5.89238C3.55035 5.9744 3.58425 6.06579 3.60008 6.16134C3.61591 6.25689 3.61336 6.35472 3.59257 6.44925C3.57179 6.54378 3.53317 6.63315 3.47893 6.71227C3.42469 6.79139 3.35589 6.85871 3.27646 6.91037C3.19702 6.96204 3.10851 6.99704 3.01598 7.01339C2.92344 7.02973 2.82869 7.0271 2.73714 7.00564C2.32571 6.90769 1.8 6.58672 1.37714 6.0911C1.2058 5.88913 1.05882 5.66644 0.939429 5.42792C0.356571 6.25985 0 7.38915 0 8.53852C0 9.40585 0.203429 10.3888 0.552 11.1795C0.643429 11.0733 0.742857 10.9765 0.849143 10.8915C0.922127 10.8264 1.00712 10.7773 1.09901 10.747C1.19089 10.7167 1.28776 10.706 1.38378 10.7154C1.47979 10.7249 1.57296 10.7543 1.65766 10.8019C1.74237 10.8495 1.81685 10.9144 1.87663 10.9925C1.93641 11.0707 1.98024 11.1605 2.00548 11.2566C2.03072 11.3527 2.03685 11.4531 2.02349 11.5518C2.01014 11.6504 1.97758 11.7452 1.92778 11.8305C1.87797 11.9158 1.81196 11.9897 1.73371 12.048C1.53029 12.2144 1.31771 12.5719 1.18743 12.9861C1.28666 13.6718 1.57888 14.3122 2.02775 14.8278C2.47662 15.3433 3.06235 15.7113 3.71212 15.8859C4.36189 16.0606 5.04704 16.0342 5.6824 15.81C6.31776 15.5859 6.8753 15.1739 7.28571 14.6252V10.2944C6.82599 10.6793 6.26844 10.919 5.68 10.9847C5.58672 10.9955 5.49231 10.9872 5.40214 10.9603C5.31197 10.9334 5.22782 10.8884 5.15449 10.8279C5.00639 10.7058 4.91135 10.5279 4.89029 10.3334C4.87985 10.2371 4.8879 10.1396 4.91396 10.0465C4.94002 9.95336 4.98358 9.86647 5.04216 9.79075C5.16047 9.63783 5.33276 9.5397 5.52114 9.51795C6.10286 9.45187 6.52571 9.10612 6.82743 8.6518C7.13371 8.19158 7.26629 7.68652 7.28571 7.44461V5.18483Z"
          fill="#161617"
        />
      </g>
      <defs>
        <clipPath id="clip0_2659_17413">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </>
  );
};

const BrainstormIcon24x24 = () => {
  return (
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.5216 3.8347C15.7569 2.7262 17.2881 2.71956 17.5324 3.82807L17.5453 3.88117L17.5671 3.98339C17.85 5.22332 18.8207 6.17119 20.0383 6.38891C21.1594 6.5907 21.1594 8.25279 20.0383 8.45458C19.4402 8.56207 18.8862 8.84963 18.4458 9.28115C18.0055 9.71267 17.6985 10.2689 17.5633 10.88L17.5324 11.0168C17.2881 12.1239 15.7581 12.1186 15.5216 11.0075L15.4959 10.8906C15.3659 10.2776 15.0621 9.71831 14.6231 9.28421C14.1842 8.85012 13.63 8.56084 13.0311 8.45325C11.9126 8.25147 11.9126 6.59203 13.0311 6.39157C13.6284 6.28448 14.1813 5.99667 14.6199 5.56462C15.0585 5.13257 15.3629 4.57575 15.4946 3.9648L15.5126 3.87984L15.5216 3.83603V3.8347ZM14.1317 11.2C14.0618 10.8684 13.8984 10.5656 13.662 10.3298C13.4257 10.094 13.1269 9.93575 12.8036 9.87506V19.4533C13.2284 20.0207 13.7937 20.4591 14.4399 20.722C15.0861 20.985 15.7891 21.0627 16.4746 20.9471C17.16 20.8315 17.8025 20.5268 18.3341 20.0653C18.8657 19.6037 19.2666 19.0024 19.4944 18.3249C19.152 18.2901 18.8157 18.2084 18.4941 18.082C17.7291 17.7846 16.8973 17.1992 16.2146 16.1756C16.0937 15.9943 16.0475 15.7708 16.0862 15.5543C16.1249 15.3379 16.2453 15.1461 16.4209 15.0213C16.5965 14.8965 16.813 14.8488 17.0226 14.8888C17.2323 14.9287 17.418 15.0531 17.5389 15.2344C18.03 15.9712 18.5931 16.3482 19.0599 16.5287C19.2964 16.6217 19.5073 16.6615 19.6719 16.6748L19.7014 16.6774C20.4986 15.7827 21 14.0515 21 12.6058C21 11.6314 20.7724 10.6703 20.379 9.85515L20.2774 9.87506C19.6089 9.99454 19.0766 10.5176 18.9261 11.2L18.8953 11.3354C18.3193 13.9467 14.7103 13.9307 14.1561 11.3141L14.1304 11.1986L14.1317 11.2ZM11.1964 8.83293C10.9337 8.41202 10.7939 7.92185 10.7939 7.42108C10.7939 6.92031 10.9337 6.43014 11.1964 6.00923V5.31891C10.75 4.77706 10.1532 4.38974 9.4868 4.20922C8.82036 4.02871 8.11633 4.06372 7.46977 4.3095C6.82321 4.55529 6.26527 5.00003 5.8713 5.58365C5.47733 6.16728 5.2663 6.86168 5.26671 7.57309V7.57574C5.31686 8.09349 5.52129 8.47848 5.75786 8.75726C6.05229 9.10375 6.36214 9.2458 6.43543 9.26173C6.53842 9.28588 6.6358 9.33073 6.72201 9.39374C6.80821 9.45674 6.88155 9.53666 6.93785 9.62893C6.99414 9.7212 7.03228 9.82402 7.05009 9.93151C7.0679 10.039 7.06503 10.1491 7.04164 10.2554C7.01826 10.3617 6.97482 10.4623 6.9138 10.5513C6.85278 10.6403 6.77538 10.716 6.68602 10.7742C6.59665 10.8323 6.49708 10.8717 6.39297 10.8901C6.28887 10.9085 6.18228 10.9055 6.07929 10.8813C5.61643 10.7712 5.025 10.4101 4.54929 9.85249C4.35652 9.62527 4.19117 9.37475 4.05686 9.10641C3.40114 10.0423 3 11.3128 3 12.6058C3 13.5816 3.22886 14.6874 3.621 15.5769C3.72386 15.4574 3.83571 15.3485 3.95529 15.253C4.03739 15.1797 4.13302 15.1244 4.23639 15.0904C4.33976 15.0563 4.44873 15.0442 4.55675 15.0549C4.66476 15.0655 4.76958 15.0986 4.86487 15.1521C4.96016 15.2057 5.04396 15.2787 5.11121 15.3666C5.17846 15.4545 5.22777 15.5556 5.25617 15.6637C5.28456 15.7718 5.29145 15.8848 5.27643 15.9957C5.26141 16.1067 5.22478 16.2133 5.16875 16.3093C5.11272 16.4052 5.03845 16.4885 4.95043 16.554C4.72157 16.7411 4.48243 17.1434 4.33586 17.6094C4.4475 18.3807 4.77624 19.1012 5.28122 19.6812C5.7862 20.2612 6.44514 20.6752 7.17613 20.8717C7.90712 21.0682 8.67792 21.0385 9.3927 20.7863C10.1075 20.5341 10.7347 20.0706 11.1964 19.4533V14.5812C10.6792 15.0142 10.052 15.2839 9.39 15.3578C9.28506 15.37 9.17884 15.3606 9.07741 15.3303C8.97597 15.3001 8.8813 15.2495 8.7988 15.1814C8.63219 15.044 8.52527 14.8439 8.50157 14.625C8.48984 14.5167 8.49889 14.407 8.5282 14.3023C8.55752 14.1975 8.60653 14.0998 8.67243 14.0146C8.80553 13.8426 8.99936 13.7322 9.21129 13.7077C9.86571 13.6334 10.3414 13.2444 10.6809 12.7333C11.0254 12.2155 11.1746 11.6473 11.1964 11.3752V8.83293Z"
      fill="#161617"
    />
  );
};

export const BrainstormIcon = createFunctionalIcon(
  'brainstorm_icon_label_a11y',
  BrainstormIcon16x16,
  BrainstormIcon24x24,
);

/**
 * CalendarIcon
 */

const CalendarIcon16x16 = () => (
  <path d="M4.25 1V2H11.75V1H13V2H15V15H1V2H3V1H4.25ZM2.25 3.25V5.75H13.75V3.25H2.25ZM13.75 7H2.25V13.75H13.75V7Z" />
);

const CalendarIcon24x24 = () => (
  <path d="M7.75 2V4H16.25V2H18V4H21V21H3V4H6V2H7.75ZM4.75 5.75V9H19.25V5.75H4.75ZM19.25 10.75H4.75V19.25H19.25V10.75Z" />
);

export const CalendarIcon = createFunctionalIcon(
  'calendar_icon_label_a11y',
  CalendarIcon16x16,
  CalendarIcon24x24,
);

/**
 * CalendarSolidIcon
 */

const CalendarSolidIcon16x16 = () => (
  <>
    <path d="M1 7H15V15H1V7Z" />
    <path d="M4.25 1V2H3V1H4.25Z" />
    <path d="M13 1V2H11.75V1H13Z" />
    <path d="M1 2H15V5.75H1V2Z" />
  </>
);

const CalendarSolidIcon24x24 = () => (
  <>
    <path d="M7.75 4V2H6V4H3V9H21V4H18V2H16.25V4H7.75Z" />
    <path d="M21 10.75H3V21H21V10.75Z" />
  </>
);

export const CalendarSolidIcon = createFunctionalIcon(
  'calendar_icon_label_a11y',
  CalendarSolidIcon16x16,
  CalendarSolidIcon24x24,
);

/**
 * CancelIcon
 */

const CancelIcon16x16 = () => (
  <path d="M8 2.25C4.82436 2.25 2.25 4.82436 2.25 8C2.25 11.1756 4.82436 13.75 8 13.75C11.1756 13.75 13.75 11.1756 13.75 8C13.75 4.82436 11.1756 2.25 8 2.25ZM1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8ZM7.11612 8L5.05806 5.94194L5.94194 5.05806L8 7.11612L10.0581 5.05806L10.9419 5.94194L8.88388 8L10.9419 10.0581L10.0581 10.9419L8 8.88388L5.94194 10.9419L5.05806 10.0581L7.11612 8Z" />
);

const CancelIcon24x24 = () => (
  <path d="M12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM10.7626 12L7.88128 9.11872L9.11872 7.88128L12 10.7626L14.8813 7.88128L16.1187 9.11872L13.2374 12L16.1187 14.8813L14.8813 16.1187L12 13.2374L9.11872 16.1187L7.88128 14.8813L10.7626 12Z" />
);

export const CancelIcon = createFunctionalIcon(
  'cancel_icon_label_a11y',
  CancelIcon16x16,
  CancelIcon24x24,
);

/**
 * CastIcon
 */

const CastIcon16x16 = () => (
  <path d="M0.958252 2H15.0001V14H10.0001V12.75H13.7501V3.25H2.20825V5H0.958252V2ZM1.00006 7.625C1.83724 7.625 2.66622 7.78989 3.43967 8.11027C4.21312 8.43064 4.91589 8.90022 5.50787 9.49219C6.09984 10.0842 6.56942 10.7869 6.88979 11.5604C7.21017 12.3338 7.37506 13.1628 7.37506 14H6.12506C6.12506 13.327 5.9925 12.6605 5.73494 12.0387C5.47739 11.417 5.09988 10.852 4.62398 10.3761C4.14808 9.90018 3.58311 9.52267 2.96131 9.26512C2.33952 9.00756 1.67309 8.875 1.00006 8.875V7.625ZM1.00006 10.125C1.50893 10.125 2.01282 10.2252 2.48296 10.42C2.9531 10.6147 3.38027 10.9001 3.7401 11.26C4.09993 11.6198 4.38536 12.047 4.58009 12.5171C4.77483 12.9872 4.87506 13.4911 4.87506 14H3.62506C3.62506 13.6553 3.55716 13.3139 3.42524 12.9955C3.29333 12.677 3.09997 12.3876 2.85622 12.1438C2.61246 11.9001 2.32308 11.7067 2.00461 11.5748C1.68613 11.4429 1.34478 11.375 1.00006 11.375V10.125ZM1.00006 12.75C1.16421 12.75 1.32676 12.7823 1.47842 12.8451C1.63008 12.908 1.76787 13 1.88394 13.1161C2.00002 13.2322 2.09209 13.37 2.15491 13.5216C2.21773 13.6733 2.25006 13.8358 2.25006 14H1.00006V12.75Z" />
);

const CastIcon24x24 = () => (
  <path d="M2 4H22V20H14V18.25H20.25V5.75H3.75V8H2V4ZM2 11.125C3.16548 11.125 4.31955 11.3546 5.39632 11.8006C6.47308 12.2466 7.45145 12.9003 8.27557 13.7244C9.09969 14.5485 9.75342 15.5269 10.1994 16.6037C10.6454 17.6804 10.875 18.8345 10.875 20H9.125C9.125 19.0643 8.94071 18.1378 8.58264 17.2734C8.22458 16.4089 7.69975 15.6235 7.03814 14.9619C6.37652 14.3002 5.59106 13.7754 4.72662 13.4174C3.86217 13.0593 2.93567 12.875 2 12.875V11.125ZM2 14.375C2.73869 14.375 3.47014 14.5205 4.15259 14.8032C4.83505 15.0859 5.45515 15.5002 5.97748 16.0225C6.4998 16.5449 6.91414 17.1649 7.19682 17.8474C7.47951 18.5299 7.625 19.2613 7.625 20H5.875C5.875 19.4911 5.77477 18.9872 5.58003 18.5171C5.3853 18.047 5.09987 17.6198 4.74004 17.26C4.38021 16.9001 3.95303 16.6147 3.4829 16.42C3.01276 16.2252 2.50887 16.125 2 16.125V14.375ZM2 18.125C2.24623 18.125 2.49005 18.1735 2.71753 18.2677C2.94502 18.362 3.15172 18.5001 3.32583 18.6742C3.49993 18.8483 3.63805 19.055 3.73227 19.2825C3.8265 19.51 3.875 19.7538 3.875 20H2.125C2.125 19.9836 2.12177 19.9673 2.11548 19.9522C2.1092 19.937 2.1 19.9232 2.08839 19.9116C2.07678 19.9 2.063 19.8908 2.04784 19.8845C2.03267 19.8782 2.01642 19.875 2 19.875V18.125Z" />
);

export const CastIcon = createFunctionalIcon(
  'cast_icon_label_a11y',
  CastIcon16x16,
  CastIcon24x24,
);

/**
 * CheckBoxIcon
 *
 */

const CheckBoxIcon16x16 = () => (
  <path d="M1 1H15V15H1V1ZM2.25 2.25V13.75H13.75V2.25H2.25ZM11.9454 5.93846L7.07009 10.8909L4.0546 7.82769L4.9454 6.95078L7.07009 9.1091L11.0546 5.06154L11.9454 5.93846Z" />
);

const CheckBoxIcon24x24 = () => (
  <path d="M2.875 2.875H21.125V21.125H2.875V2.875ZM4.625 4.625V19.375H19.375V4.625H4.625ZM16.8796 9.35759L10.7014 15.7601L7.12035 12.0491L8.37965 10.8339L10.7014 13.2399L15.6204 8.14241L16.8796 9.35759Z" />
);

export const CheckBoxIcon = createFunctionalIcon(
  'checkbox_icon_label_a11y',
  CheckBoxIcon16x16,
  CheckBoxIcon24x24,
);

/**
 * CheckIcon
 */

const CheckIcon16x16 = () => (
  <path d="M14.4532 4.92992L6.40579 13.4074L1.54663 8.28856L2.45321 7.42798L6.40579 11.5918L13.5466 4.06934L14.4532 4.92992Z" />
);

const CheckIcon24x24 = () => (
  <path d="M20.8835 7.59876L9.47478 19.0075L3.1167 12.6494L4.35414 11.412L9.47478 16.5326L19.6461 6.36133L20.8835 7.59876Z" />
);

export const CheckIcon = createFunctionalIcon(
  'checkmark_icon_label_a11y',
  CheckIcon16x16,
  CheckIcon24x24,
);

/**
 * ChevronDownIcon
 */

const ChevronDownIcon16x16 = () => (
  <path d="M8.00005 9.36602L3.94199 5.30796L3.05811 6.19185L8.00005 11.1338L12.942 6.19185L12.0581 5.30796L8.00005 9.36602Z" />
);

const ChevronDownIcon24x24 = () => (
  <path d="M11.9999 14.2624L6.11866 8.38115L4.88123 9.61859L11.9999 16.7373L19.1187 9.61859L17.8812 8.38115L11.9999 14.2624Z" />
);

export const ChevronDownIcon = createFunctionalIcon(
  'expand_icon_label_a11y',
  ChevronDownIcon16x16,
  ChevronDownIcon24x24,
);

/**
 * ChevronLeftIcon
 */

const ChevronLeftIcon16x16 = () => (
  <path d="M6.38398 7.99956L10.442 3.9415L9.55815 3.05762L4.61621 7.99956L9.55815 12.9415L10.442 12.0576L6.38398 7.99956Z" />
);

const ChevronLeftIcon24x24 = () => (
  <path d="M9.23745 11.9996L15.1187 6.1183L13.8813 4.88086L6.76257 11.9996L13.8813 19.1183L15.1187 17.8809L9.23745 11.9996Z" />
);

export const ChevronLeftIcon = createFunctionalIcon(
  'back_icon_label_a11y',
  ChevronLeftIcon16x16,
  ChevronLeftIcon24x24,
);

/**
 * ChevronRightIcon
 */

const ChevronRightIcon16x16 = () => (
  <path d="M6.44199 3.05762L11.3839 7.99956L6.44199 12.9415L5.55811 12.0576L9.61616 7.99956L5.55811 3.9415L6.44199 3.05762Z" />
);

const ChevronRightIcon24x24 = () => (
  <path d="M10.1187 4.88086L17.2374 11.9996L10.1187 19.1183L8.88123 17.8809L14.7625 11.9996L8.88123 6.1183L10.1187 4.88086Z" />
);

export const ChevronRightIcon = createFunctionalIcon(
  'forward_icon_label_a11y',
  ChevronRightIcon16x16,
  ChevronRightIcon24x24,
);

/**
 * ChevronUpIcon
 */

const ChevronUpIcon16x16 = () => (
  <path d="M8.00005 4.86621L12.942 9.80815L12.0581 10.692L8.00005 6.63398L3.94199 10.692L3.05811 9.80815L8.00005 4.86621Z" />
);

const ChevronUpIcon24x24 = () => (
  <path d="M11.9999 7.2627L19.1187 14.3814L17.8812 15.6189L11.9999 9.73757L6.11866 15.6189L4.88123 14.3814L11.9999 7.2627Z" />
);

export const ChevronUpIcon = createFunctionalIcon(
  'collapse_icon_label_a11y',
  ChevronUpIcon16x16,
  ChevronUpIcon24x24,
);

/**
 * ChevronsDownIcon
 */

const ChevronsDownIcon16x16 = () => (
  <path d="M7.99995 13.1396L3.55801 8.69771L4.44189 7.81382L7.99995 11.3719L11.558 7.81382L12.4419 8.69771L7.99995 13.1396ZM7.99995 9.13965L3.55801 4.69771L4.4419 3.81382L7.99995 7.37188L11.558 3.81382L12.4419 4.69771L7.99995 9.13965Z" />
);

const ChevronsDownIcon24x24 = () => (
  <path d="M12.0001 19.2402L5.38134 12.6215L6.61878 11.3841L12.0001 16.7654L17.3813 11.3841L18.6188 12.6215L12.0001 19.2402ZM12.0001 13.7402L5.38134 7.12151L6.61878 5.88408L12.0001 11.2654L17.3813 5.88408L18.6188 7.12152L12.0001 13.7402Z" />
);

export const ChevronsDownIcon = createFunctionalIcon(
  'arrows_down_icon_label_a11y',
  ChevronsDownIcon16x16,
  ChevronsDownIcon24x24,
);

/**
 * ChevronsLeftIcon
 */

const ChevronsLeftIcon16x16 = () => (
  <path d="M2.86011 7.99849L7.30205 3.55655L8.18593 4.44043L4.62787 7.99849L8.18593 11.5565L7.30205 12.4404L2.86011 7.99849ZM6.86011 7.99849L11.302 3.55655L12.1859 4.44043L8.62787 7.99849L12.1859 11.5565L11.302 12.4404L6.86011 7.99849Z" />
);

const ChevronsLeftIcon24x24 = () => (
  <path d="M4.76001 11.6186L11.3787 4.99987L12.6162 6.2373L7.23488 11.6186L12.6162 16.9999L11.3787 18.2373L4.76001 11.6186ZM10.26 11.6186L16.8787 4.99987L18.1162 6.2373L12.7349 11.6186L18.1162 16.9999L16.8787 18.2373L10.26 11.6186Z" />
);

export const ChevronsLeftIcon = createFunctionalIcon(
  'arrows_left_icon_label_a11y',
  ChevronsLeftIcon16x16,
  ChevronsLeftIcon24x24,
);

/**
 * ChevronsRightIcon
 */

const ChevronsRightIcon16x16 = () => (
  <path d="M13.1399 8.00151L8.69795 12.4435L7.81407 11.5596L11.3721 8.00151L7.81407 4.44345L8.69795 3.55957L13.1399 8.00151ZM9.13989 8.00151L4.69795 12.4435L3.81407 11.5596L7.37213 8.00151L3.81407 4.44345L4.69795 3.55957L9.13989 8.00151Z" />
);

const ChevronsRightIcon24x24 = () => (
  <path d="M19.24 11.6187L12.6213 18.2374L11.3838 17L16.7651 11.6187L11.3838 6.23744L12.6213 5L19.24 11.6187ZM13.74 11.6187L7.12127 18.2374L5.88383 17L11.2651 11.6187L5.88383 6.23744L7.12127 5L13.74 11.6187Z" />
);

export const ChevronsRightIcon = createFunctionalIcon(
  'arrows_right_icon_label_a11y',
  ChevronsRightIcon16x16,
  ChevronsRightIcon24x24,
);

/**
 * ChevronsUpIcon
 */

const ChevronsUpIcon16x16 = () => (
  <path d="M8.00005 2.86621L12.442 7.30815L11.5581 8.19204L8.00005 4.63398L4.44199 8.19204L3.55811 7.30815L8.00005 2.86621ZM8.00005 6.86621L12.442 11.3082L11.5581 12.192L8.00005 8.63398L4.44199 12.192L3.55811 11.3082L8.00005 6.86621Z" />
);

const ChevronsUpIcon24x24 = () => (
  <path d="M11.9999 4.7627L18.6187 11.3814L17.3812 12.6189L11.9999 7.23757L6.61866 12.6189L5.38123 11.3814L11.9999 4.7627ZM11.9999 10.2627L18.6187 16.8814L17.3812 18.1189L11.9999 12.7376L6.61866 18.1189L5.38123 16.8814L11.9999 10.2627Z" />
);

export const ChevronsUpIcon = createFunctionalIcon(
  'arrows_up_icon_label_a11y',
  ChevronsUpIcon16x16,
  ChevronsUpIcon24x24,
);

/**
 * ClipboardIcon
 */

const ClipboardIcon16x16 = () => (
  <path d="M5 0H11V1H14V15H2V1H5V0ZM5 2.25H3.25V13.75H12.75V2.25H11V3.5H5V2.25ZM6.25 1.25V2.25H9.75V1.25H6.25Z" />
);

const ClipboardIcon24x24 = () => (
  <path d="M6.875 1H17.125V3H20V21H4V3H6.875V1ZM6.875 4.75H5.75V19.25H18.25V4.75H17.125V6H6.875V4.75ZM15.375 4.25V2.75H8.625V4.25H15.375Z" />
);

export const ClipboardIcon = createFunctionalIcon(
  'copy_to_clipboard_icon_label_a11y',
  ClipboardIcon16x16,
  ClipboardIcon24x24,
);

/**
 * ClipboardSolidIcon
 */

const ClipboardSolidIcon16x16 = () => (
  <>
    <path d="M3.75 1H2V15H14V1H12.25V3.25H3.75V1Z" />
    <path d="M5 0H11V2H5V0Z" />
  </>
);

const ClipboardSolidIcon24x24 = () => (
  <>
    <path d="M4 3H6.375V5.625H17.625V3H20V21H4V3Z" />
    <path d="M8 1H16V4H8V1Z" />
  </>
);

export const ClipboardSolidIcon = createFunctionalIcon(
  'copy_to_clipboard_icon_label_a11y',
  ClipboardSolidIcon16x16,
  ClipboardSolidIcon24x24,
);

/**
 * ClockIcon
 */

const ClockIcon16x16 = () => (
  <path d="M8 2.25C4.82436 2.25 2.25 4.82436 2.25 8C2.25 11.1756 4.82436 13.75 8 13.75C11.1756 13.75 13.75 11.1756 13.75 8C13.75 4.82436 11.1756 2.25 8 2.25ZM1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8ZM7.625 4H8.875V7.68173L11.3676 9.49454L10.6324 10.5055L7.625 8.31827V4Z" />
);

const ClockIcon24x24 = () => (
  <path d="M12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11.125 6H12.875V11.5317L16.2354 13.772L15.2646 15.228L11.125 12.4683V6Z" />
);

export const ClockIcon = createFunctionalIcon(
  'clock_icon_label_a11y',
  ClockIcon16x16,
  ClockIcon24x24,
);

/**
 * ClockOffIcon
 */

const ClockOffIcon16x16 = () => (
  <path d="M14.5581 15.4415L0.558105 1.4415L1.44199 0.557617L3.5117 2.62732C6.26189 0.324257 10.3651 0.465086 12.9498 3.04981C15.5345 5.63454 15.6753 9.73771 13.3723 12.4879L15.442 14.5576L14.5581 15.4415ZM12.4841 11.5997C14.3015 9.34184 14.1621 6.02988 12.0659 3.9337C9.96972 1.83751 6.65775 1.69811 4.39986 3.51549L7.62505 6.74067V3.99956H8.87505V7.99067L12.4841 11.5997ZM2.92453 5.2938C1.76379 7.46905 2.1014 10.2327 3.93417 12.0654C5.82491 13.9562 8.70622 14.2555 10.9099 12.9607L11.5432 14.0384C8.8607 15.6145 5.35341 15.2524 3.05029 12.9493C0.81763 10.7166 0.409175 7.35245 1.82171 4.70532L2.92453 5.2938Z" />
);

const ClockOffIcon24x24 = () => (
  <path d="M21.6312 22.8683L1.38123 2.6183L2.61866 1.38086L5.57425 4.33645C9.50316 1.0344 15.3742 1.23175 19.0709 4.92851C22.7677 8.62527 22.965 14.4963 19.663 18.4252L22.8687 21.6309L21.6312 22.8683ZM18.4198 17.182C21.0418 13.9421 20.8463 9.17877 17.8335 6.16595C14.8207 3.15313 10.0574 2.95768 6.81742 5.57961L11.1249 9.88714V5.99958H12.8749V11.6371L18.4198 17.182ZM4.37844 8.83479C3.13696 11.8281 3.73462 15.4016 6.16623 17.8332C8.56729 20.2343 12.0816 20.8472 15.0517 19.6669L15.698 21.2931C12.1019 22.7223 7.8413 21.9832 4.92879 19.0706C1.97923 16.1211 1.25867 11.789 2.76195 8.16437L4.37844 8.83479Z" />
);

export const ClockOffIcon = createFunctionalIcon(
  'time_is_up_icon_label_a11y',
  ClockOffIcon16x16,
  ClockOffIcon24x24,
);

/**
 * ClockReverseIcon
 */

const ClockReverseIcon16x16 = () => (
  <path d="M3.7387 2.44653C4.96121 1.50846 6.45909 1 8.00003 1C9.54097 1 11.0388 1.50846 12.2614 2.44653C13.4839 3.38459 14.3627 4.69983 14.7615 6.18827C15.1603 7.6767 15.0569 9.25514 14.4672 10.6788C13.8775 12.1024 12.8345 13.2917 11.5 14.0622C10.1655 14.8326 8.6141 15.1412 7.08634 14.9401C5.55859 14.739 4.13989 14.0394 3.05028 12.9497L3.93416 12.0659C4.8292 12.9609 5.99456 13.5356 7.2495 13.7008C8.50444 13.866 9.77884 13.6125 10.875 12.9796C11.9712 12.3468 12.8279 11.3699 13.3123 10.2004C13.7967 9.03101 13.8817 7.73443 13.5541 6.51179C13.2265 5.28915 12.5046 4.20877 11.5004 3.43822C10.4962 2.66767 9.2658 2.25 8.00003 2.25C6.73426 2.25 5.50385 2.66767 4.49965 3.43822C3.93409 3.87218 3.45808 4.40442 3.09162 5.005H5V6.255H1V2H2.25V4.00786C2.65986 3.41752 3.16089 2.88989 3.7387 2.44653ZM7.625 4H8.875V7.68173L11.3676 9.49454L10.6324 10.5055L7.625 8.31827V4Z" />
);

const ClockReverseIcon24x24 = () => (
  <path d="M11.0198 2.04817C13.3222 1.8214 15.632 2.39998 17.5557 3.68532C19.4793 4.97066 20.8978 6.88323 21.5694 9.09716C22.241 11.3111 22.1241 13.6894 21.2388 15.8268C20.3534 17.9643 18.7543 19.7286 16.7139 20.8192C14.6736 21.9098 12.3182 22.2592 10.0491 21.8078C7.77996 21.3565 5.73756 20.1323 4.26986 18.3439L5.62262 17.2337C6.83348 18.7092 8.51846 19.7191 10.3905 20.0915C12.2625 20.4638 14.2057 20.1756 15.889 19.2758C17.5723 18.3761 18.8915 16.9205 19.622 15.1571C20.3524 13.3937 20.4488 11.4317 19.8947 9.60516C19.3407 7.77867 18.1704 6.2008 16.5834 5.14039C14.9964 4.07999 13.0908 3.60266 11.1913 3.78974C9.29413 3.9766 7.52028 4.81471 6.17124 6.16148L5.11762 7.25H8V9H2V3H3.75V6.14672L4.92385 4.93398L4.92889 4.92894C6.56482 3.29302 8.71738 2.27494 11.0198 2.04817ZM12.875 11.5317V6H11.125V12.4683L15.2646 15.228L16.2354 13.772L12.875 11.5317Z" />
);

export const ClockReverseIcon = createFunctionalIcon(
  'clock_reverse_icon_label_a11y',
  ClockReverseIcon16x16,
  ClockReverseIcon24x24,
);

/**
 * CloudIcon
 */

const CloudIcon16x16 = () => (
  <path d="M6 4.25C3.92893 4.25 2.25 5.92893 2.25 8V11.75H11.5C12.7426 11.75 13.75 10.7426 13.75 9.5C13.75 8.25735 12.7426 7.25 11.5 7.25C11.305 7.25 11.1176 7.27452 10.9398 7.31998L9.88501 7.58978L9.47301 6.58203C9.47301 6.58203 9.47301 6.58203 9.47301 6.58203C8.91284 5.21195 7.56739 4.25 6 4.25ZM1 8C1 5.23857 3.23857 3 6 3C8.09205 3 9.88426 4.28491 10.63 6.10897C10.9082 6.03784 11.1997 6 11.5 6C13.433 6 15 7.567 15 9.5C15 11.433 13.433 13 11.5 13H1V8Z" />
);

const CloudIcon24x24 = () => (
  <path d="M9.5 5.75C6.32436 5.75 3.75 8.32436 3.75 11.5V17.25H17C18.795 17.25 20.25 15.795 20.25 14C20.25 12.205 18.795 10.75 17 10.75C16.9069 10.75 16.8151 10.7539 16.7246 10.7614C16.7246 10.7614 16.7247 10.7614 16.7246 10.7614L15.3758 10.8738L14.928 9.59652C14.928 9.5965 14.928 9.59653 14.928 9.59652C14.1417 7.35421 12.0061 5.75 9.5 5.75ZM2 11.5C2 7.35786 5.35786 4 9.5 4C12.7718 4 15.5547 6.0951 16.5794 9.01744C16.7181 9.00589 16.8583 9 17 9C19.7614 9 22 11.2386 22 14C22 16.7614 19.7614 19 17 19H2V11.5Z" />
);

export const CloudIcon = createFunctionalIcon(
  'cloud_service_icon_label_a11y',
  CloudIcon16x16,
  CloudIcon24x24,
);

/**
 * CodeIcon
 */

const CodeIcon16x16 = () => (
  <path d="M10.192 4.55762L13.634 7.99956L10.192 11.4415L9.30815 10.5576L11.8662 7.99956L9.30815 5.4415L10.192 4.55762ZM4.13398 7.99956L6.69204 5.4415L5.80815 4.55762L2.36621 7.99956L5.80815 11.4415L6.69204 10.5576L4.13398 7.99956Z" />
);

const CodeIcon24x24 = () => (
  <path d="M6.48745 11.9996L10.1187 8.3683L8.88129 7.13086L4.01257 11.9996L8.88129 16.8683L10.1187 15.6309L6.48745 11.9996ZM15.1187 7.13086L19.9874 11.9996L15.1187 16.8683L13.8813 15.6309L17.5126 11.9996L13.8813 8.3683L15.1187 7.13086Z" />
);

export const CodeIcon = createFunctionalIcon(
  'code_block_icon_label_a11y',
  CodeIcon16x16,
  CodeIcon24x24,
);

/**
 * ColorBucketIcon
 */

const ColorBucketIcon16x16 = () => (
  <>
    <path d="M13.5 7.98648L6.6081 1.5L0.75 9.28378L5.22972 13.5L13.5 7.98648ZM2.42201 9.14087L6.76765 3.36673L11.498 7.81882L5.36298 11.9089L2.42201 9.14087Z" />
    <path d="M13.625 10.75L14.5973 11.7751C15.1342 12.3413 15.1342 13.2592 14.5973 13.8254C14.0603 14.3915 13.1897 14.3915 12.6527 13.8254C12.1158 13.2592 12.1158 12.3413 12.6527 11.7751L13.625 10.75Z" />
  </>
);

const ColorBucketIcon24x24 = () => (
  <>
    <path d="M19.7505 11.1892L10.0208 2L1.75049 13.027L8.07483 19L19.7505 11.1892ZM4.08776 12.8273L10.2445 4.61839L16.9536 10.9548L8.26177 16.7694L4.08776 12.8273Z" />
    <path d="M19.75 15L21.1642 16.4645C21.9453 17.2732 21.9453 18.5846 21.1642 19.3934C20.3831 20.2022 19.1169 20.2022 18.3358 19.3934C17.5547 18.5846 17.5547 17.2732 18.3358 16.4645L19.75 15Z" />
  </>
);

export const ColorBucketIcon = createFunctionalIcon(
  'color_bucket_icon_label_a11y',
  ColorBucketIcon16x16,
  ColorBucketIcon24x24,
);

/**
 * ColorBucketSolidIcon
 */

const ColorBucketSolidIcon16x16 = () => (
  <>
    <path d="M13.5 7.98648L6.6081 1.5L0.75 9.28378L5.22972 13.5L13.5 7.98648Z" />
    <path d="M13.625 10.75L14.5973 11.7751C15.1342 12.3413 15.1342 13.2592 14.5973 13.8254C14.0603 14.3915 13.1897 14.3915 12.6527 13.8254C12.1158 13.2592 12.1158 12.3413 12.6527 11.7751L13.625 10.75Z" />
  </>
);

const ColorBucketSolidIcon24x24 = () => (
  <>
    <path d="M1.75 13.027L8.07432 19L19.75 11.1892L10.0203 2L1.75 13.027Z" />
    <path d="M19.75 15L21.1642 16.4645C21.9453 17.2732 21.9453 18.5846 21.1642 19.3934C20.3831 20.2022 19.1169 20.2022 18.3358 19.3934C17.5547 18.5846 17.5547 17.2732 18.3358 16.4645L19.75 15Z" />
  </>
);

export const ColorBucketSolidIcon = createFunctionalIcon(
  'color_bucket_icon_label_a11y',
  ColorBucketSolidIcon16x16,
  ColorBucketSolidIcon24x24,
);

/**
 * ColumnIcon
 */

const ColumnIcon16x16 = () => (
  <path d="M1 1H15V15H1V1ZM2.25 2.25V13.75H5.125V2.25H2.25ZM6.375 13.75H9.625V2.25H6.375V13.75ZM10.875 13.75H13.75V2.25H10.875V13.75Z" />
);

const ColumnIcon24x24 = () => (
  <path d="M3 3H21V21H3V3ZM4.75 4.75V19.25H8.25V4.75H4.75ZM10 4.75V19.25H14V4.75H10ZM15.75 4.75V19.25H19.25V4.75H15.75Z" />
);

export const ColumnIcon = createFunctionalIcon(
  'columns_icon_label_a11y',
  ColumnIcon16x16,
  ColumnIcon24x24,
);

/**
 * CommandIcon
 */

const CommandIcon16x16 = () => (
  <path d="M1.80546 1.80546C2.32118 1.28973 3.02065 1 3.75 1C4.47935 1 5.17882 1.28973 5.69454 1.80546C6.21027 2.32118 6.5 3.02065 6.5 3.75V5.25H9.5V3.75C9.5 3.02066 9.78973 2.32118 10.3055 1.80546C10.8212 1.28973 11.5207 1 12.25 1C12.9793 1 13.6788 1.28973 14.1945 1.80546C14.7103 2.32118 15 3.02065 15 3.75C15 4.47935 14.7103 5.17882 14.1945 5.69454C13.6788 6.21027 12.9793 6.5 12.25 6.5H10.75V9.5H12.25C12.9793 9.5 13.6788 9.78973 14.1945 10.3055C14.7103 10.8212 15 11.5207 15 12.25C15 12.9793 14.7103 13.6788 14.1945 14.1945C13.6788 14.7103 12.9793 15 12.25 15C11.5207 15 10.8212 14.7103 10.3055 14.1945C9.78973 13.6788 9.5 12.9793 9.5 12.25V10.75H6.5V12.25C6.5 12.9793 6.21027 13.6788 5.69454 14.1945C5.17882 14.7103 4.47935 15 3.75 15C3.02065 15 2.32118 14.7103 1.80546 14.1945C1.28973 13.6788 1 12.9793 1 12.25C1 11.5207 1.28973 10.8212 1.80546 10.3055C2.32118 9.78973 3.02066 9.5 3.75 9.5H5.25V6.5H3.75C3.02065 6.5 2.32118 6.21027 1.80546 5.69454C1.28973 5.17882 1 4.47935 1 3.75C1 3.02065 1.28973 2.32118 1.80546 1.80546ZM5.25 5.25V3.75C5.25 3.35218 5.09196 2.97064 4.81066 2.68934C4.52936 2.40804 4.14782 2.25 3.75 2.25C3.35218 2.25 2.97064 2.40804 2.68934 2.68934C2.40804 2.97064 2.25 3.35218 2.25 3.75C2.25 4.14782 2.40804 4.52936 2.68934 4.81066C2.97064 5.09196 3.35218 5.25 3.75 5.25H5.25ZM6.5 6.5V9.5H9.5V6.5H6.5ZM5.25 10.75H3.75C3.35217 10.75 2.97064 10.908 2.68934 11.1893C2.40804 11.4706 2.25 11.8522 2.25 12.25C2.25 12.6478 2.40804 13.0294 2.68934 13.3107C2.97064 13.592 3.35218 13.75 3.75 13.75C4.14782 13.75 4.52936 13.592 4.81066 13.3107C5.09196 13.0294 5.25 12.6478 5.25 12.25V10.75ZM10.75 10.75V12.25C10.75 12.6478 10.908 13.0294 11.1893 13.3107C11.4706 13.592 11.8522 13.75 12.25 13.75C12.6478 13.75 13.0294 13.592 13.3107 13.3107C13.592 13.0294 13.75 12.6478 13.75 12.25C13.75 11.8522 13.592 11.4706 13.3107 11.1893C13.0294 10.908 12.6478 10.75 12.25 10.75H10.75ZM10.75 5.25H12.25C12.6478 5.25 13.0294 5.09196 13.3107 4.81066C13.592 4.52936 13.75 4.14782 13.75 3.75C13.75 3.35218 13.592 2.97064 13.3107 2.68934C13.0294 2.40804 12.6478 2.25 12.25 2.25C11.8522 2.25 11.4706 2.40804 11.1893 2.68934C10.908 2.97064 10.75 3.35217 10.75 3.75V5.25Z" />
);

const CommandIcon24x24 = () => (
  <path d="M4.16233 4.16233C4.82652 3.49814 5.72736 3.125 6.66667 3.125C7.60598 3.125 8.50681 3.49814 9.171 4.16233C9.8352 4.82652 10.2083 5.72736 10.2083 6.66667V8.45833H13.7917V6.66667C13.7917 5.72736 14.1648 4.82652 14.829 4.16233C15.4932 3.49814 16.394 3.125 17.3333 3.125C18.2726 3.125 19.1735 3.49814 19.8377 4.16233C20.5019 4.82652 20.875 5.72736 20.875 6.66667C20.875 7.60598 20.5019 8.50681 19.8377 9.171C19.1735 9.8352 18.2726 10.2083 17.3333 10.2083H15.5417V13.7917H17.3333C18.2726 13.7917 19.1735 14.1648 19.8377 14.829C20.5019 15.4932 20.875 16.394 20.875 17.3333C20.875 18.2726 20.5019 19.1735 19.8377 19.8377C19.1735 20.5019 18.2726 20.875 17.3333 20.875C16.394 20.875 15.4932 20.5019 14.829 19.8377C14.1648 19.1735 13.7917 18.2726 13.7917 17.3333V15.5417H10.2083V17.3333C10.2083 18.2726 9.8352 19.1735 9.171 19.8377C8.50681 20.5019 7.60598 20.875 6.66667 20.875C5.72736 20.875 4.82652 20.5019 4.16233 19.8377C3.49814 19.1735 3.125 18.2726 3.125 17.3333C3.125 16.394 3.49814 15.4932 4.16233 14.829C4.82652 14.1648 5.72736 13.7917 6.66667 13.7917H8.45833V10.2083H6.66667C5.72736 10.2083 4.82652 9.8352 4.16233 9.171C3.49814 8.50681 3.125 7.60598 3.125 6.66667C3.125 5.72736 3.49814 4.82652 4.16233 4.16233ZM8.45833 8.45833V6.66667C8.45833 6.19149 8.26957 5.73577 7.93357 5.39977C7.59756 5.06376 7.14185 4.875 6.66667 4.875C6.19149 4.875 5.73577 5.06376 5.39977 5.39977C5.06376 5.73577 4.875 6.19149 4.875 6.66667C4.875 7.14185 5.06376 7.59756 5.39977 7.93357C5.73577 8.26957 6.19149 8.45833 6.66667 8.45833H8.45833ZM10.2083 10.2083V13.7917H13.7917V10.2083H10.2083ZM8.45833 15.5417H6.66667C6.19149 15.5417 5.73577 15.7304 5.39977 16.0664C5.06376 16.4024 4.875 16.8582 4.875 17.3333C4.875 17.8085 5.06376 18.2642 5.39977 18.6002C5.73577 18.9362 6.19149 19.125 6.66667 19.125C7.14185 19.125 7.59756 18.9362 7.93357 18.6002C8.26957 18.2642 8.45833 17.8085 8.45833 17.3333V15.5417ZM15.5417 15.5417V17.3333C15.5417 17.8085 15.7304 18.2642 16.0664 18.6002C16.4024 18.9362 16.8582 19.125 17.3333 19.125C17.8085 19.125 18.2642 18.9362 18.6002 18.6002C18.9362 18.2642 19.125 17.8085 19.125 17.3333C19.125 16.8582 18.9362 16.4024 18.6002 16.0664C18.2642 15.7304 17.8085 15.5417 17.3333 15.5417H15.5417ZM15.5417 8.45833H17.3333C17.8085 8.45833 18.2642 8.26957 18.6002 7.93357C18.9362 7.59756 19.125 7.14185 19.125 6.66667C19.125 6.19149 18.9362 5.73577 18.6002 5.39977C18.2642 5.06376 17.8085 4.875 17.3333 4.875C16.8582 4.875 16.4024 5.06376 16.0664 5.39977C15.7304 5.73577 15.5417 6.19149 15.5417 6.66667V8.45833Z" />
);

export const CommandIcon = createFunctionalIcon(
  'command_icon_label_a11y',
  CommandIcon16x16,
  CommandIcon24x24,
);

/**
 * CommentIcon
 */

const CommentIcon16x16 = () => (
  <>
    <path d="M4 6H12V7.25H4V6Z" />
    <path d="M9 8.75H4V10H9V8.75Z" />
    <path d="M1 5.5C1 4.56936 1 4.10404 1.12236 3.72746C1.36965 2.96636 1.96636 2.36965 2.72746 2.12236C3.10404 2 3.56936 2 4.5 2H11.5C12.4306 2 12.896 2 13.2725 2.12236C14.0336 2.36965 14.6303 2.96636 14.8776 3.72746C15 4.10404 15 4.56936 15 5.5V10.5C15 11.4306 15 11.896 14.8776 12.2725C14.6303 13.0336 14.0336 13.6303 13.2725 13.8776C12.896 14 12.4306 14 11.5 14H10V16L7 14H4.5C3.56936 14 3.10404 14 2.72746 13.8776C1.96636 13.6303 1.36965 13.0336 1.12236 12.2725C1 11.896 1 11.4306 1 10.5V5.5ZM8.75 12.75V13.6644L7.37847 12.75H4.5C3.47207 12.75 3.25456 12.7346 3.11373 12.6888C2.73318 12.5652 2.43483 12.2668 2.31118 11.8863C2.26542 11.7454 2.25 11.5279 2.25 10.5V5.5C2.25 4.47207 2.26542 4.25456 2.31118 4.11373C2.43483 3.73318 2.73318 3.43483 3.11373 3.31118C3.25456 3.26542 3.47207 3.25 4.5 3.25H11.5C12.5279 3.25 12.7454 3.26542 12.8863 3.31118C13.2668 3.43483 13.5652 3.73318 13.6888 4.11373C13.7346 4.25455 13.75 4.47207 13.75 5.5V10.5C13.75 11.5279 13.7346 11.7454 13.6888 11.8863C13.5652 12.2668 13.2668 12.5652 12.8863 12.6888C12.7454 12.7346 12.5279 12.75 11.5 12.75H8.75Z" />
  </>
);

const CommentIcon24x24 = () => (
  <>
    <path d="M7 9H17V10.75H7V9Z" />
    <path d="M12 12H7V13.75H12V12Z" />
    <path d="M15 22L10.5 19H7.2C6.08323 19 5.52485 19 5.07295 18.8532C4.15964 18.5564 3.44358 17.8404 3.14683 16.9271C3 16.4752 3 15.9168 3 14.8V8.2C3 7.08323 3 6.52485 3.14683 6.07295C3.44358 5.15964 4.15964 4.44358 5.07295 4.14683C5.52485 4 6.08323 4 7.2 4H16.8C17.9168 4 18.4752 4 18.9271 4.14683C19.8404 4.44358 20.5564 5.15964 20.8532 6.07295C21 6.52485 21 7.08323 21 8.2V14.8C21 15.9168 21 16.4752 20.8532 16.9271C20.5564 17.8404 19.8404 18.5564 18.9271 18.8532C18.4752 19 17.9168 19 16.8 19H15V22ZM11.0299 17.25L13.25 18.7301V17.25H16.8C18.053 17.25 18.2644 17.2284 18.3863 17.1888C18.7668 17.0652 19.0652 16.7668 19.1888 16.3863C19.2284 16.2644 19.25 16.053 19.25 14.8V8.2C19.25 6.94702 19.2284 6.73557 19.1888 6.61373C19.0652 6.23318 18.7668 5.93483 18.3863 5.81118C18.2644 5.77159 18.053 5.75 16.8 5.75H7.2C5.94702 5.75 5.73557 5.77159 5.61373 5.81118C5.23318 5.93483 4.93483 6.23318 4.81118 6.61373C4.77159 6.73557 4.75 6.94702 4.75 8.2V14.8C4.75 16.053 4.77159 16.2644 4.81118 16.3863C4.93483 16.7668 5.23318 17.0652 5.61373 17.1888C5.73557 17.2284 5.94702 17.25 7.2 17.25H11.0299Z" />
  </>
);

export const CommentIcon = createFunctionalIcon(
  'comment_icon_label_a11y',
  CommentIcon16x16,
  CommentIcon24x24,
);

/**
 * CommentSolidIcon
 */

const CommentSolidIcon16x16 = () => (
  <path d="M1.12236 3.72746C1 4.10404 1 4.56936 1 5.5V10.5C1 11.4306 1 11.896 1.12236 12.2725C1.36965 13.0336 1.96636 13.6303 2.72746 13.8776C3.10404 14 3.56936 14 4.5 14H7L10 16V14H11.5C12.4306 14 12.896 14 13.2725 13.8776C14.0336 13.6303 14.6303 13.0336 14.8776 12.2725C15 11.896 15 11.4306 15 10.5V5.5C15 4.56936 15 4.10404 14.8776 3.72746C14.6303 2.96636 14.0336 2.36965 13.2725 2.12236C12.896 2 12.4306 2 11.5 2H4.5C3.56936 2 3.10404 2 2.72746 2.12236C1.96636 2.36965 1.36965 2.96636 1.12236 3.72746ZM12 6H4V7.25H12V6ZM4 8.75H9V10H4V8.75Z" />
);

const CommentSolidIcon24x24 = () => (
  <path d="M3.14683 6.07295C3 6.52485 3 7.08323 3 8.2V14.8C3 15.9168 3 16.4752 3.14683 16.9271C3.44358 17.8404 4.15964 18.5564 5.07295 18.8532C5.52485 19 6.08323 19 7.2 19H10.5L15 22V19H16.8C17.9168 19 18.4752 19 18.9271 18.8532C19.8404 18.5564 20.5564 17.8404 20.8532 16.9271C21 16.4752 21 15.9168 21 14.8V8.2C21 7.08323 21 6.52485 20.8532 6.07295C20.5564 5.15964 19.8404 4.44358 18.9271 4.14683C18.4752 4 17.9168 4 16.8 4H7.2C6.08323 4 5.52485 4 5.07295 4.14683C4.15964 4.44358 3.44358 5.15964 3.14683 6.07295ZM7 9H17V10.75H7V9ZM12 12H7V13.75H12V12Z" />
);

export const CommentSolidIcon = createFunctionalIcon(
  'comment_icon_label_a11y',
  CommentSolidIcon16x16,
  CommentSolidIcon24x24,
);

/**
 * CommentOffIcon
 */

const CommentOffIcon16x16 = () => (
  <>
    <path d="M0.557617 1.44248L14.5576 15.4425L15.4415 14.5586L14.2085 13.3256C14.5127 13.0408 14.7452 12.6805 14.8776 12.2731C14.9443 12.0679 14.9712 11.8551 14.9848 11.5927C14.9982 11.3333 15 10.9859 15 10.5005V5.50049C15 5.0151 14.9982 4.66768 14.9848 4.40824C14.9712 4.14586 14.9443 3.9331 14.8776 3.72786C14.6302 2.96683 14.0336 2.37016 13.2726 2.12286C13.0674 2.05617 12.8546 2.02927 12.5922 2.01568C12.3328 2.00225 11.9854 2.00049 11.5 2.00049H4.5C4.01461 2.00049 3.66719 2.00225 3.40776 2.01568C3.23781 2.02448 3.08869 2.03887 2.94894 2.06604L1.4415 0.558594L0.557617 1.44248ZM4.13401 3.2511L6.8834 6.00049H12V7.25049H8.1334L13.3236 12.4407C13.4908 12.2943 13.6182 12.1039 13.6888 11.8867C13.7062 11.8333 13.7252 11.7459 13.7365 11.5281C13.7479 11.3073 13.75 10.9944 13.75 10.5005V5.50049C13.75 5.0066 13.7479 4.69368 13.7365 4.47291C13.7252 4.25508 13.7062 4.16778 13.6888 4.11431C13.5651 3.73372 13.2668 3.43531 12.8862 3.31166C12.8328 3.29428 12.7454 3.27529 12.5276 3.26401C12.3068 3.25258 11.9939 3.25049 11.5 3.25049H4.5C4.36511 3.25049 4.24372 3.25064 4.13401 3.2511Z" />
    <path d="M2.25307 4.83775C2.25059 5.01671 2.25 5.23292 2.25 5.50049V10.5005C2.25 10.9944 2.25209 11.3073 2.26353 11.5281C2.27481 11.7459 2.29379 11.8332 2.31118 11.8867C2.43482 12.2673 2.73316 12.5656 3.11376 12.6893C3.16722 12.7067 3.2546 12.7257 3.47242 12.737C3.69319 12.7484 4.00611 12.7505 4.5 12.7505H7.37847L8.75 13.6649V12.7505H10V16.0005L6.99999 14.0005H4.5C4.01462 14.0005 3.66719 13.9987 3.40775 13.9853C3.14537 13.9717 2.93261 13.9448 2.72737 13.8781C1.96635 13.6307 1.36966 13.0341 1.12236 12.273C1.05569 12.0678 1.02879 11.8551 1.0152 11.5927C1.00176 11.3333 1 10.9859 1 10.5005V5.50049C1 5.2327 1.00057 5.00885 1.00319 4.8204L2.25307 4.83775Z" />
  </>
);

const CommentOffIcon24x24 = () => (
  <>
    <path d="M1.63086 2.8683L20.6309 21.8683L21.8683 20.6309L19.7052 18.4678C20.2401 18.0982 20.6474 17.5598 20.8532 16.9265C20.9346 16.6761 20.9659 16.4197 20.9818 16.1094C20.9975 15.8026 21 15.3867 21 14.7995V8.19951C21 7.61236 20.9975 7.19645 20.9818 6.88967C20.9659 6.57936 20.9346 6.32293 20.8532 6.07245C20.5564 5.15913 19.8404 4.44309 18.927 4.14633C18.6765 4.06495 18.4201 4.0336 18.1098 4.01771C17.8031 4.002 17.3871 3.99951 16.8 3.99951H7.2C6.61285 3.99951 6.19694 4.002 5.89016 4.01771C5.68017 4.02846 5.49486 4.0463 5.31936 4.08193L2.8683 1.63086L1.63086 2.8683ZM6.9871 5.74966L10.25 9.01256V8.99951H17V10.7495H11.9869L18.4158 17.1783C18.7822 17.0491 19.0684 16.7564 19.1888 16.3858C19.2006 16.3494 19.2213 16.2699 19.2341 16.0198C19.2471 15.7662 19.25 15.3972 19.25 14.7995V8.19951C19.25 7.60179 19.2471 7.2328 19.2341 6.97918C19.2213 6.72916 19.2006 6.64964 19.1888 6.61323C19.0652 6.23269 18.7668 5.93434 18.3863 5.81069C18.3499 5.79887 18.2704 5.77822 18.0203 5.76542C17.7667 5.75243 17.3977 5.74951 16.8 5.74951H7.2C7.12556 5.74951 7.05466 5.74956 6.9871 5.74966Z" />
    <path d="M4.75 8.19951V14.7995C4.75 15.3972 4.75292 15.7662 4.7659 16.0198C4.77871 16.2699 4.79936 16.3494 4.81118 16.3858C4.93482 16.7663 5.23317 17.0647 5.61373 17.1883C5.6501 17.2002 5.72958 17.2208 5.97967 17.2336C6.23328 17.2466 6.60227 17.2495 7.2 17.2495H11.0298L13.25 18.7296V17.2495H15V21.9995L10.5001 18.9995L7.2 18.9995C6.61285 18.9995 6.19694 18.997 5.89016 18.9813C5.57986 18.9654 5.32345 18.9341 5.07297 18.8527C4.15963 18.5559 3.44358 17.8399 3.14682 16.9265C3.06544 16.6761 3.03409 16.4197 3.0182 16.1094C3.00248 15.8026 3 15.3867 3 14.7995V8.19951H4.75Z" />
  </>
);

export const CommentOffIcon = createFunctionalIcon(
  'comments_are_disabled_icon_label_a11y',
  CommentOffIcon16x16,
  CommentOffIcon24x24,
);

/**
 * CommentOffSolidIcon
 */

const CommentOffSolidIcon16x16 = () => (
  <>
    <path d="M0.557617 1.44248L14.5576 15.4425L15.4415 14.5586L14.2085 13.3256C14.5128 13.0408 14.7453 12.6805 14.8776 12.273C15 11.8965 15 11.4311 15 10.5005V5.50049C15 4.56985 15 4.10453 14.8776 3.72795C14.6303 2.96685 14.0336 2.37014 13.2725 2.12285C12.896 2.00049 12.4306 2.00049 11.5 2.00049H4.5C3.74256 2.00049 3.29336 2.00049 2.94936 2.06646L1.4415 0.558594L0.557617 1.44248ZM6.8834 6.00049L8.1334 7.25049H12V6.00049H6.8834Z" />
    <path d="M1.00083 5.01909C1 5.16456 1 5.32417 1 5.50049V10.5005C1 11.4311 1 11.8965 1.12236 12.273C1.36965 13.0341 1.96636 13.6308 2.72746 13.8781C3.10404 14.0005 3.56936 14.0005 4.5 14.0005H7L10 16.0005V14.0005H9.98223L1.00083 5.01909Z" />
  </>
);

const CommentOffSolidIcon24x24 = () => (
  <>
    <path d="M1.63086 2.8683L20.6309 21.8683L21.8683 20.6309L19.7052 18.4678C20.2401 18.0982 20.6474 17.5598 20.8532 16.9266C21 16.4747 21 15.9163 21 14.7995V8.19951C21 7.08274 21 6.52436 20.8532 6.07246C20.5564 5.15915 19.8404 4.4431 18.9271 4.14634C18.4752 3.99951 17.9168 3.99951 16.8 3.99951H7.2C6.27681 3.99951 5.7352 3.99951 5.3199 4.08246L2.8683 1.63086L1.63086 2.8683ZM10.25 9.01256L11.9869 10.7495H17V8.99951H10.25V9.01256Z" />
    <path d="M3.01347 6.98785C3 7.30581 3 7.69588 3 8.19951V14.7995C3 15.9163 3 16.4747 3.14683 16.9266C3.44358 17.8399 4.15964 18.5559 5.07295 18.8527C5.52485 18.9995 6.08323 18.9995 7.2 18.9995H10.5L15 21.9995V18.9995H15.0251L3.01347 6.98785Z" />
  </>
);

export const CommentOffSolidIcon = createFunctionalIcon(
  'comments_are_disabled_icon_label_a11y',
  CommentOffSolidIcon16x16,
  CommentOffSolidIcon24x24,
);

/**
 * CopyIcon
 */

const CopyIcon16x16 = () => (
  <path d="M1 1H12V4H15V15H4V12H1V1ZM4 10.75V4H10.75V2.25H2.25V10.75H4ZM5.25 5.25V13.75H13.75V5.25H5.25ZM8.875 8.875V7H10.125V8.875H12V10.125H10.125V12H8.875V10.125H7V8.875H8.875Z" />
);

const CopyIcon24x24 = () => (
  <path d="M3 3H17V7H21V21H7V17H3V3ZM7 15.25V7H15.25V4.75H4.75V15.25H7ZM8.75 8.75V19.25H19.25V8.75H8.75ZM13.125 13.125V11H14.875V13.125H17V14.875H14.875V17H13.125V14.875H11V13.125H13.125Z" />
);

export const CopyIcon = createFunctionalIcon(
  'copy_icon_label_a11y',
  CopyIcon16x16,
  CopyIcon24x24,
);

/**
 * CornerDownRightIcon
 */
const CornerDownRightIcon16x16 = () => (
  <path d="M1 1v6.114c0 .95.39 1.86 1.086 2.531a3.778 3.778 0 0 0 2.624 1.05h7.996l-3.183 3.067.9.868 4.27-4.115a.598.598 0 0 0 0-.868l-4.27-4.115-.9.867 3.183 3.068H4.71a2.48 2.48 0 0 1-1.723-.689 2.314 2.314 0 0 1-.714-1.664V1H1Z" />
);

const CornerDownRightIcon24x24 = () => (
  <path d="M4.75 11V3H3v8a4.875 4.875 0 0 0 4.876 4.876H17.89l-3.507 3.507 1.238 1.237L21.24 15l-5.62-5.618-1.237 1.237 3.507 3.507H7.876A3.126 3.126 0 0 1 4.75 11Z" />
);

export const CornerDownRightIcon = createFunctionalIcon(
  'right_lower_corner_icon_label_a11y',
  CornerDownRightIcon16x16,
  CornerDownRightIcon24x24,
);

/**
 * CornerRightDownIcon
 */

const CornerRightDownIcon16x16 = () => (
  <path d="M1 1H7.22778C8.19469 1 9.12225 1.38335 9.80633 2.06611C10.4904 2.74891 10.875 3.67525 10.875 4.64139V12.4911L14 9.36612L14.8839 10.25L10.6919 14.4419C10.4479 14.686 10.0521 14.686 9.80806 14.4419L5.61612 10.25L6.5 9.36612L9.625 12.4911V4.64139C9.625 4.00755 9.37273 3.39942 8.9233 2.95085C8.47382 2.50224 7.86395 2.25 7.22778 2.25H1V1Z" />
);

const CornerRightDownIcon24x24 = () => (
  <path d="M11 4.75H3V3H11C12.2929 3 13.5329 3.51361 14.4471 4.42785C15.3614 5.34209 15.875 6.58207 15.875 7.875V17.8876L19.3813 14.3813L20.6187 15.6187L15 21.2374L9.38128 15.6187L10.6187 14.3813L14.125 17.8876V7.875C14.125 7.0462 13.7958 6.25134 13.2097 5.66529C12.6237 5.07924 11.8288 4.75 11 4.75Z" />
);

export const CornerRightDownIcon = createFunctionalIcon(
  'lower_right_corner_icon_label_a11y',
  CornerRightDownIcon16x16,
  CornerRightDownIcon24x24,
);

/**
 * CountdownIcon
 */

const CountdownIcon16x16 = () => (
  <path d="M7.375 1.25H5V0H11V1.25H8.625V2.02752C12.1982 2.34368 15 5.34464 15 9C15 12.866 11.866 16 8 16C4.13401 16 1 12.866 1 9C1 5.34464 3.8018 2.34368 7.375 2.02752V1.25ZM8 3.25C4.82436 3.25 2.25 5.82436 2.25 9C2.25 12.1756 4.82436 14.75 8 14.75C11.1756 14.75 13.75 12.1756 13.75 9C13.75 5.82436 11.1756 3.25 8 3.25ZM7.55802 8.22473L10.2247 5.55806L11.1086 6.44194L8.4419 9.10861L7.55802 8.22473Z" />
);

const CountdownIcon24x24 = () => (
  <path d="M11.125 1.875H9V0.125H15V1.875H12.875V3.66529C17.6424 4.10655 21.375 8.11738 21.375 13C21.375 18.1777 17.1777 22.375 12 22.375C6.82233 22.375 2.625 18.1777 2.625 13C2.625 8.11738 6.35759 4.10655 11.125 3.66529V1.875ZM12 5.375C7.78883 5.375 4.375 8.78883 4.375 13C4.375 17.2112 7.78883 20.625 12 20.625C16.2112 20.625 19.625 17.2112 19.625 13C19.625 8.78883 16.2112 5.375 12 5.375ZM11.3813 12.3813L15.3813 8.38128L16.6187 9.61872L12.6187 13.6187L11.3813 12.3813Z" />
);

export const CountdownIcon = createFunctionalIcon(
  'countdown_icon_label_a11y',
  CountdownIcon16x16,
  CountdownIcon24x24,
);

/**
 * CountdownOffIcon
 */

const CountdownOffIcon16x16 = () => (
  <path d="M7.37505 1.25H5.00005V0H11V1.25H8.62505V2.02779C10.2029 2.16839 11.7421 2.84254 12.9498 4.05025C15.4486 6.5491 15.6633 10.4672 13.5938 13.2098L15.442 15.0581L14.5581 15.9419L0.558105 1.94194L1.44199 1.05806L3.79022 3.40629C4.85804 2.60055 6.10402 2.14105 7.37505 2.02779V1.25ZM4.68477 4.30083L12.6992 12.3153C14.2866 10.0709 14.0755 6.94377 12.0659 4.93414C10.0563 2.9245 6.92917 2.7134 4.68477 4.30083ZM2.92454 6.29424C1.7638 8.46949 2.10142 11.2331 3.93418 13.0659C5.82492 14.9566 8.70624 15.2559 10.9099 13.9611L11.5432 15.0389C8.86071 16.615 5.35342 16.2529 3.0503 13.9497C0.817644 11.7171 0.409188 8.35289 1.82173 5.70576L2.92454 6.29424Z" />
);

const CountdownOffIcon24x24 = () => (
  <path d="M11.1251 1.875H9.00007V0.125H15.0001V1.875H12.8751V3.665C14.9749 3.86068 17.0214 4.76242 18.6292 6.3702C22.0818 9.82285 22.2785 15.2985 19.2191 18.9816L22.6188 22.3813L21.3813 23.6187L1.13135 3.36872L2.36878 2.13128L6.01779 5.78028C7.51045 4.5404 9.29754 3.83531 11.1251 3.665V1.875ZM7.26184 7.02433L17.9751 17.7376C20.3548 14.7443 20.1604 10.3763 17.3918 7.60764C14.6231 4.83902 10.2551 4.64458 7.26184 7.02433ZM4.78331 10.5319C3.87386 13.1991 4.48387 16.2665 6.60838 18.391C8.70321 20.4858 11.7146 21.1082 14.3556 20.2533L14.8946 21.9182C11.6527 22.9677 7.94853 22.206 5.37094 19.6285C2.7568 17.0143 2.01046 13.2416 3.12695 9.96712L4.78331 10.5319Z" />
);

export const CountdownOffIcon = createFunctionalIcon(
  'countdown_off_icon_label_a11y',
  CountdownOffIcon16x16,
  CountdownOffIcon24x24,
);

/**
 * CreditCardIcon
 */

const CreditCardIcon16x16 = () => (
  <>
    <path d="M4.99992 11H3V9.75H4.99992V11Z" />
    <path d="M6.625 11C6.97018 11 7.25 10.7202 7.25 10.375C7.25 10.0298 6.97018 9.75 6.625 9.75C6.27982 9.75 6 10.0298 6 10.375C6 10.7202 6.27982 11 6.625 11Z" />
    <path d="M1 3H15V14H1V3ZM2.25 6H13.75V4.25H2.25V6ZM2.25 12.75V8H13.75V12.75H2.25Z" />
  </>
);

const CreditCardIcon24x24 = () => (
  <>
    <path d="M9 15H5V13.25H9V15Z" />
    <path d="M10.875 15C11.3582 15 11.75 14.6082 11.75 14.125C11.75 13.6418 11.3582 13.25 10.875 13.25C10.3918 13.25 10 13.6418 10 14.125C10 14.6082 10.3918 15 10.875 15Z" />
    <path d="M2 4H22V20H2V4ZM3.75 5.75V8H20.25V5.75H3.75ZM3.75 18.25V11H20.25V18.25H3.75Z" />
  </>
);

export const CreditCardIcon = createFunctionalIcon(
  'credit_card_icon_label_a11y',
  CreditCardIcon16x16,
  CreditCardIcon24x24,
);

/**
 * CropIcon
 */

const CropIcon16x16 = () => (
  <path d="M3 3V1H4.25V3H13V11.75H15V13H13V15H11.75V13H3V4.25H1V3H3ZM4.25 4.25V11.75H11.75V4.25H4.25Z" />
);

const CropIcon24x24 = () => (
  <path d="M5 5V2H6.75V5H19V17.25H22V19H19V22H17.25V19H5V6.75H2V5H5ZM6.75 6.75V17.25H17.25V6.75H6.75Z" />
);

export const CropIcon = createFunctionalIcon(
  'crop_icon_label_a11y',
  CropIcon16x16,
  CropIcon24x24,
);

/**
 * CrossIcon
 */

const CrossIcon16x16 = () => (
  <path d="M7.11641 8.00054L3.55835 4.44248L4.44223 3.55859L8.00029 7.11665L11.5575 3.55945L12.4414 4.44333L8.88418 8.00054L12.4414 11.5577L11.5575 12.4416L8.00029 8.88442L4.44223 12.4425L3.55835 11.5586L7.11641 8.00054Z" />
);

const CrossIcon24x24 = () => (
  <path d="M11.9999 13.237L6.61866 18.6183L5.38123 17.3809L10.7625 11.9996L5.38123 6.6183L6.61866 5.38086L11.9999 10.7621L17.3812 5.38086L18.6187 6.6183L13.2374 11.9996L18.6187 17.3809L17.3812 18.6183L11.9999 13.237Z" />
);

export const CrossIcon = createFunctionalIcon(
  'close_icon_label_a11y',
  CrossIcon16x16,
  CrossIcon24x24,
);

/**
 * DesignIcon
 */

const DesignIcon16x16 = () => (
  <path d="M13.25 3.11312L2.75 0.998047V12H6V14C6 15.1046 6.89542 16 7.99998 16C9.10454 16 9.99995 15.1046 9.99995 14V12H13.25V3.11312ZM4 7.99912V2.52495L12 4.13644V7.99912H4ZM4 10.75V9.25H12V10.75H8.74995V14C8.74995 14.4142 8.41416 14.75 7.99998 14.75C7.58578 14.75 7.25 14.4142 7.25 14V10.75H4Z" />
);

const DesignIcon24x24 = () => (
  <path d="M19.25 4.88438L4.75 2.0625V17.124H9V18.999C9 20.7249 10.2742 21.999 12 21.999C13.7258 21.999 15 20.7249 15 18.999V17.124H19.25V4.88438ZM17.5 10.999V6.32664L6.5 4.1859V10.999H17.5ZM6.5 12.7492H17.5V15.374H13.25V18.999C13.25 19.7584 12.7594 20.249 12 20.249C11.2406 20.249 10.75 19.7584 10.75 18.999V15.374H6.5V12.7492Z" />
);

export const DesignIcon = createFunctionalIcon(
  'design_options_for_slide_icon_label_a11y',
  DesignIcon16x16,
  DesignIcon24x24,
);

/**
 * DesignSolidIcon
 */

const DesignSolidIcon16x16 = () => (
  <>
    <path d="M13 9.25H3V12H6V14C6 15.1046 6.89542 16 7.99998 16C9.10453 16 9.99995 15.1046 9.99995 14V12H13V9.25Z" />
    <path d="M3 1L13 3V8H3V1Z" />
  </>
);

const DesignSolidIcon24x24 = () => (
  <>
    <path d="M5 12.75H19V17.125H15V19C15 20.7258 13.7258 22 12 22C10.2742 22 9 20.7258 9 19V17.125H5V12.75Z" />
    <path d="M5 2.05469L19 4.89046V10.9995H5V2.05469Z" />
  </>
);

export const DesignSolidIcon = createFunctionalIcon(
  'design_options_for_slide_icon_label_a11y',
  DesignSolidIcon16x16,
  DesignSolidIcon24x24,
);

/**
 * DocumentIcon
 */

const DocumentIcon16x16 = () => (
  <path d="M2 1H9.88388L14 5.11612V15H2V1ZM3.25 2.25V13.75H12.75V6H9V2.25H3.25ZM10.25 3.13388L11.8661 4.75H10.25V3.13388Z" />
);

const DocumentIcon24x24 = () => (
  <path d="M4 3H14.3624L20 8.63756V21H4V3ZM5.75 4.75V19.25H18.25V9.875H13.125V4.75H5.75ZM14.875 5.98744L17.0126 8.125H14.875V5.98744Z" />
);

export const DocumentIcon = createFunctionalIcon(
  'document_icon_label_a11y',
  DocumentIcon16x16,
  DocumentIcon24x24,
);

/**
 * DocumnentsIcon
 */

const DocumnentsIcon16x16 = () => (
  <path d="M4 0H10.8839L15 4.11612V13H12V16H1V3H4V0ZM4 4.25H2.25V14.75H10.75V13H4V4.25ZM5.25 1.25V11.75H13.75V5H10V1.25H5.25ZM11.25 2.13388L12.8661 3.75H11.25V2.13388Z" />
);

const DocumnentsIcon24x24 = () => (
  <path d="M3 4H11.2135L12.7135 7H21V20H3V4ZM8 16.0219C8 14.7164 9.05824 13.6582 10.3636 13.6582C11.669 13.6582 12.7273 14.7164 12.7273 16.0218V17.0187H8V16.0219ZM10.3636 10.2129C9.61571 10.2129 9.0094 10.8192 9.0094 11.5671C9.0094 12.315 9.61571 12.9214 10.3636 12.9214C11.1116 12.9214 11.7179 12.315 11.7179 11.5671C11.7179 10.8192 11.1116 10.2129 10.3636 10.2129ZM12.2821 11.5671C12.2821 10.8192 12.8884 10.2129 13.6363 10.2129C14.3843 10.2129 14.9906 10.8192 14.9906 11.5671C14.9906 12.315 14.3843 12.9214 13.6363 12.9214C12.8884 12.9214 12.2821 12.315 12.2821 11.5671ZM13.3636 16.0218V17.0187H16V16.0218C16 14.7164 14.9417 13.6582 13.6364 13.6582C13.233 13.6582 12.8533 13.7592 12.5211 13.9373C13.0427 14.4771 13.3636 15.212 13.3636 16.0218Z" />
);

export const DocumnentsIcon = createFunctionalIcon(
  'documents_icon_label_a11y',
  DocumnentsIcon16x16,
  DocumnentsIcon24x24,
);

/**
 * DownloadCloudIcon
 */

const DownloadCloudIcon16x16 = () => (
  <path d="M6 2.25C3.92893 2.25 2.25 3.92893 2.25 6V11H1V6C1 3.23857 3.23857 1 6 1C8.09205 1 9.88426 2.28491 10.63 4.10897C10.9082 4.03784 11.1997 4 11.5 4C13.433 4 15 5.567 15 7.5C15 9.433 13.433 11 11.5 11V9.75C12.7426 9.75 13.75 8.74265 13.75 7.5C13.75 6.25735 12.7426 5.25 11.5 5.25C11.305 5.25 11.1176 5.27452 10.9398 5.31998L9.88501 5.58978L9.47301 4.58203C9.47301 4.58203 9.47301 4.58203 9.47301 4.58203C8.91284 3.21195 7.56739 2.25 6 2.25ZM6.125 12.3231V8H7.375V12.3231L8.75 10.9481L9.63388 11.832L6.75 14.7159L3.86612 11.832L4.75 10.9481L6.125 12.3231Z" />
);

const DownloadCloudIcon24x24 = () => (
  <path d="M9.5 3.75C6.32437 3.75 3.75 6.32437 3.75 9.5V17H2V9.5C2 5.35788 5.35788 2 9.5 2C12.7718 2 15.5546 4.09511 16.5794 7.01743C16.5794 7.01743 16.5794 7.01743 16.5794 7.01743C16.7181 7.00587 16.8584 7 17 7C19.7614 7 22 9.23858 22 12C22 14.7614 19.7614 17 17 17V15.25C18.7949 15.25 20.25 13.7949 20.25 12C20.25 10.2051 18.7949 8.75 17 8.75C16.9068 8.75 16.815 8.75387 16.7247 8.7614L15.3758 8.87376L14.928 7.59652C14.928 7.59651 14.928 7.59653 14.928 7.59652C14.1417 5.3542 12.0061 3.75 9.5 3.75ZM9.625 18.6376L9.625 12H11.375L11.375 18.6376L13.5 16.5126L14.7374 17.75L10.5 21.9874L6.26256 17.75L7.5 16.5126L9.625 18.6376Z" />
);

export const DownloadCloudIcon = createFunctionalIcon(
  'download_icon_label_a11y',
  DownloadCloudIcon16x16,
  DownloadCloudIcon24x24,
);

/**
 * DownloadIcon
 */

const DownloadIcon16x16 = () => (
  <path d="M7.375 9.24112V1H8.625V9.24112L11.0581 6.80806L11.9419 7.69194L8 11.6339L4.05806 7.69194L4.94194 6.80806L7.375 9.24112ZM14.9964 9.99414L15 10.3725C15.011 12.9346 12.9371 15.0174 10.375 15.0174L5.625 15.0174C3.07068 15.0174 1 12.9467 1 10.3924V10H2.25V10.3924C2.25 12.2564 3.76104 13.7674 5.625 13.7674L10.375 13.7674C12.2441 13.7674 13.7572 12.2483 13.75 10.3793C13.75 10.3788 13.75 10.3783 13.75 10.3779L13.7465 10.0059L14.9964 9.99414Z" />
);

const DownloadIcon24x24 = () => (
  <path d="M11.125 13.8876L11.125 3H12.875L12.875 13.8876L15.8813 10.8813L17.1187 12.1187L12 17.2374L6.88128 12.1187L8.11872 10.8813L11.125 13.8876ZM21.9947 13.9962L22 15.2204C22.0164 19.0289 18.9336 22.125 15.1251 22.125L8.87499 22.125C5.07804 22.125 2 19.0469 2 15.25V14H3.75V15.25C3.75 18.0804 6.04454 20.375 8.875 20.375L15.1251 20.375C17.9641 20.375 20.2622 18.067 20.25 15.2279L20.2447 14.0038L21.9947 13.9962Z" />
);

export const DownloadIcon = createFunctionalIcon(
  'download_icon_label_a11y',
  DownloadIcon16x16,
  DownloadIcon24x24,
);

/**
 * DuplicateIcon
 */

const DuplicateIcon16x16 = () => (
  <path d="M1 1H12V4H15V15H4V12H1V1ZM4 10.75V4H10.75V2.25H2.25V10.75H4ZM5.25 5.25V13.75H13.75V5.25H5.25Z" />
);

const DuplicateIcon24x24 = () => (
  <path d="M3 3H17V7H21V21H7V17H3V3ZM7 15.25V7H15.25V4.75H4.75V15.25H7ZM8.75 8.75V19.25H19.25V8.75H8.75Z" />
);

export const DuplicateIcon = createFunctionalIcon(
  'duplicate_icon_label_a11y',
  DuplicateIcon16x16,
  DuplicateIcon24x24,
);

/**
 * EnergizeIcon
 */

const EnergizeIcon16x16 = () => {
  return (
    <path
      d="M14 5H16V11H14V14H0V2H14V5ZM5.07617 7.73535L4.59766 8.5H7.59863L5.57617 11.7354L6.42383 12.2646L8.92383 8.26465L9.40234 7.5H6.40137L8.42383 4.26465L7.57617 3.73535L5.07617 7.73535Z"
      fill="#161617"
    />
  );
};

const EnergizeIcon24x24 = () => {
  return (
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.75 8.5H21V15.5H18.75V19H2.99902V5H18.75V8.5ZM8.75781 11.7412L8.30176 12.5H11.6768L9.32129 16.4082L10.1777 16.9248L13.4473 11.5H10.0723L12.4277 7.59082L11.999 7.33301L11.5713 7.0752L8.75781 11.7412Z"
      fill="#161617"
    />
  );
};

export const EnergizeIcon = createFunctionalIcon(
  'energize_icon_label_a11y',
  EnergizeIcon16x16,
  EnergizeIcon24x24,
);

/**
 * ExternalLinkIcon
 */

const ExternalLinkIcon16x16 = () => (
  <path d="M1 1H6V2.25H2.25V13.75H13.75V9H15V15H1V1ZM12.8661 2.25H10V1H15V6H13.75V3.13388L8.44194 8.44194L7.55806 7.55806L12.8661 2.25Z" />
);

const ExternalLinkIcon24x24 = () => (
  <path d="M4 4H9V5.75H5.75V18.25H18.25V14H20V20H4V4ZM17.0126 5.75H12.875V4H20L20 11.125H18.25L18.25 6.98744L11.6187 13.6187L10.3813 12.3813L17.0126 5.75Z" />
);

export const ExternalLinkIcon = createFunctionalIcon(
  'external_link_icon_label_a11y',
  ExternalLinkIcon16x16,
  ExternalLinkIcon24x24,
);

/**
 * EyeIcon
 */

const EyeIcon16x16 = () => (
  <>
    <path d="M8 5.75C6.75735 5.75 5.75 6.75735 5.75 8C5.75 9.24265 6.75735 10.25 8 10.25C9.24265 10.25 10.25 9.24265 10.25 8C10.25 6.75735 9.24265 5.75 8 5.75ZM7 8C7 7.4477 7.4477 7 8 7C8.5523 7 9 7.4477 9 8C9 8.5523 8.5523 9 8 9C7.4477 9 7 8.5523 7 8Z" />
    <path d="M8 2C13.0909 2 16 8 16 8C16 8 13.0909 14 8 14C2.90909 14 0 8 0 8C0 8 2.90909 2 8 2ZM1.59349 8.27404C1.5319 8.1757 1.4762 8.08394 1.42648 8C1.4762 7.91606 1.5319 7.8243 1.59349 7.72596C1.91766 7.20834 2.39661 6.52199 3.01583 5.84084C4.27999 4.45027 5.9692 3.25 8 3.25C10.0308 3.25 11.72 4.45027 12.9842 5.84084C13.6034 6.52199 14.0823 7.20834 14.4065 7.72596C14.4681 7.8243 14.5238 7.91606 14.5735 8C14.5238 8.08394 14.4681 8.1757 14.4065 8.27404C14.0823 8.79166 13.6034 9.47801 12.9842 10.1592C11.72 11.5497 10.0308 12.75 8 12.75C5.9692 12.75 4.27999 11.5497 3.01583 10.1592C2.39661 9.47801 1.91766 8.79166 1.59349 8.27404Z" />
  </>
);

const EyeIcon24x24 = () => (
  <>
    <path d="M12 9C10.3432 9 9 10.3432 9 12C9 13.6568 10.3432 15 12 15C13.6568 15 15 13.6568 15 12C15 10.3432 13.6568 9 12 9ZM10.75 12C10.75 11.3097 11.3097 10.75 12 10.75C12.6903 10.75 13.25 11.3097 13.25 12C13.25 12.6903 12.6903 13.25 12 13.25C11.3097 13.25 10.75 12.6903 10.75 12Z" />
    <path d="M12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12C1 12 5 4 12 4ZM3.20444 12.3006C3.13579 12.1943 3.07249 12.0938 3.01463 12C3.07249 11.9062 3.13579 11.8057 3.20444 11.6994C3.64795 11.0127 4.30367 10.1015 5.15169 9.1969C6.87966 7.35373 9.19914 5.75 12 5.75C14.8009 5.75 17.1203 7.35373 18.8483 9.1969C19.6963 10.1015 20.3521 11.0127 20.7956 11.6994C20.8642 11.8057 20.9275 11.9062 20.9854 12C20.9275 12.0938 20.8642 12.1943 20.7956 12.3006C20.3521 12.9873 19.6963 13.8985 18.8483 14.8031C17.1203 16.6463 14.8009 18.25 12 18.25C9.19914 18.25 6.87966 16.6463 5.15169 14.8031C4.30367 13.8985 3.64795 12.9873 3.20444 12.3006Z" />
  </>
);

export const EyeIcon = createFunctionalIcon(
  'visible_icon_label_a11y',
  EyeIcon16x16,
  EyeIcon24x24,
);

/**
 * EyeSolidIcon
 */

const EyeSolidIcon16x16 = () => (
  <>
    <path d="M9.75 8C9.75 8.9665 8.9665 9.75 8 9.75C7.0335 9.75 6.25 8.9665 6.25 8C6.25 7.0335 7.0335 6.25 8 6.25C8.9665 6.25 9.75 7.0335 9.75 8Z" />
    <path d="M0 8C0 8 2.90909 2 8 2C13.0909 2 16 8 16 8C16 8 13.0909 14 8 14C2.90909 14 0 8 0 8ZM8 11C9.65685 11 11 9.65685 11 8C11 6.34315 9.65685 5 8 5C6.34315 5 5 6.34315 5 8C5 9.65685 6.34315 11 8 11Z" />
  </>
);

const EyeSolidIcon24x24 = () => (
  <>
    <path d="M14.5 12C14.5 13.3807 13.3807 14.5 12 14.5C10.6193 14.5 9.5 13.3807 9.5 12C9.5 10.6193 10.6193 9.5 12 9.5C13.3807 9.5 14.5 10.6193 14.5 12Z" />
    <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12ZM12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" />
  </>
);

export const EyeSolidIcon = createFunctionalIcon(
  'visible_icon_label_a11y',
  EyeSolidIcon16x16,
  EyeSolidIcon24x24,
);

/**
 * EyeOffIcon
 */

const EyeOffIcon16x16 = () => (
  <>
    <path d="M14.4697 15.5294L0.469727 1.52941L1.53039 0.46875L4.22569 3.16405C5.29891 2.47742 6.56247 1.99902 8 1.99902C13.0909 1.99902 16 7.99902 16 7.99902C16 7.99902 14.94 10.1853 12.98 11.9184L15.5304 14.4688L14.4697 15.5294ZM12.0945 11.0328L10.2171 9.15549C10.709 8.21304 10.5592 7.02278 9.76771 6.23125C8.97618 5.43972 7.78593 5.28992 6.84347 5.78183L5.13434 4.0727C5.99046 3.57018 6.94953 3.24902 8 3.24902C10.0308 3.24902 11.72 4.44929 12.9842 5.83986C13.6034 6.52101 14.0823 7.20736 14.4065 7.72498C14.4681 7.82332 14.5238 7.91509 14.5735 7.99902C14.5238 8.08296 14.4681 8.17473 14.4065 8.27306C14.0823 8.79069 13.6034 9.47703 12.9842 10.1582C12.7076 10.4624 12.4108 10.7574 12.0945 11.0328ZM8.88383 7.11514C9.17299 7.40429 9.29086 7.79988 9.23745 8.17581L7.82315 6.76151C8.19908 6.7081 8.59467 6.82598 8.88383 7.11514Z" />
    <path d="M8 13.999C2.90909 13.999 0 7.99902 0 7.99902C0 7.99902 0.646495 6.66563 1.86329 5.256L2.83634 6.04183C2.30344 6.65505 1.88561 7.25852 1.59349 7.72498C1.5319 7.82332 1.4762 7.91509 1.42648 7.99902C1.4762 8.08296 1.5319 8.17473 1.59349 8.27306C1.91766 8.79069 2.39661 9.47703 3.01583 10.1582C4.27999 11.5488 5.9692 12.749 8 12.749C8.58966 12.749 9.15052 12.6478 9.68054 12.4702L9.94932 13.6978C9.34089 13.8886 8.69052 13.999 8 13.999Z" />
  </>
);

const EyeOffIcon24x24 = () => (
  <>
    <path d="M21.3812 22.6183L1.38123 2.6183L2.61866 1.38086L6.79829 5.56048C8.27656 4.64098 10.018 3.99963 12 3.99963C19 3.99963 23 11.9996 23 11.9996C23 11.9996 21.4672 15.0652 18.6388 17.401L22.6187 21.3809L21.3812 22.6183ZM12 5.74963C10.5592 5.74963 9.24584 6.174 8.07482 6.83702L10.5893 9.35153C11.7235 8.74712 13.1643 8.92156 14.1213 9.87851C15.0781 10.8353 15.2528 12.276 14.6482 13.4104L17.3953 16.1575C17.9177 15.7375 18.4027 15.2781 18.8483 14.8027C19.6963 13.8982 20.3521 12.9869 20.7956 12.3002C20.8642 12.1939 20.9275 12.0934 20.9854 11.9996C20.9275 11.9059 20.8642 11.8054 20.7956 11.6991C20.3521 11.0123 19.6963 10.1011 18.8483 9.19653C17.1203 7.35336 14.8009 5.74963 12 5.74963ZM13.25 12.0121C13.253 11.688 13.1307 11.3628 12.8838 11.1159C12.637 10.8691 12.3118 10.7468 11.9876 10.7498L13.25 12.0121Z" />
    <path d="M12 19.9996C5 19.9996 1 11.9996 1 11.9996C1 11.9996 2 9.99963 3.875 7.99963L5.15169 9.19653C4.30367 10.1011 3.64795 11.0123 3.20444 11.6991C3.13579 11.8054 3.07249 11.9059 3.01463 11.9996C3.07249 12.0934 3.13579 12.1939 3.20444 12.3002C3.64795 12.9869 4.30367 13.8982 5.15169 14.8027C6.87966 16.6459 9.19914 18.2496 12 18.2496C12.9497 18.2496 13.8441 18.0652 14.6789 17.7496L15.3313 19.3746C14.3124 19.7644 13.2003 19.9996 12 19.9996Z" />
  </>
);

export const EyeOffIcon = createFunctionalIcon(
  'invisible_icon_label_a11y',
  EyeOffIcon16x16,
  EyeOffIcon24x24,
);

/**
 * EyeOffSolidIcon
 */

const EyeOffSolidIcon16x16 = () => (
  <>
    <path d="M0.469666 1.52941L14.4697 15.5294L15.5303 14.4688L12.98 11.9184C14.94 10.1853 16 7.99902 16 7.99902C16 7.99902 13.0909 1.99902 8 1.99902C6.56246 1.99902 5.29888 2.47743 4.22565 3.16408L1.53033 0.46875L0.469666 1.52941ZM6.47599 5.41441C6.92263 5.15048 7.44363 4.99902 8 4.99902C9.65685 4.99902 11 6.34217 11 7.99902C11 8.5554 10.8485 9.07639 10.5846 9.52304L9.64871 8.58714C9.86815 7.97048 9.73107 7.25534 9.23745 6.76172C8.74383 6.2681 8.02869 6.13101 7.41203 6.35045L6.47599 5.41441Z" />
    <path d="M5.01799 7.66863C5.0061 7.77712 5 7.88736 5 7.99902C5 9.65588 6.34315 10.999 8 10.999C8.11166 10.999 8.2219 10.9929 8.33039 10.981L10.7439 13.3946C9.91728 13.7671 9.0008 13.999 8 13.999C2.90909 13.999 0 7.99902 0 7.99902C0 7.99902 0.771231 6.40836 2.21442 4.86506L5.01799 7.66863Z" />
  </>
);

const EyeOffSolidIcon24x24 = () => (
  <>
    <path d="M1.38129 2.6183L21.3813 22.6183L22.6187 21.3809L18.6388 17.4009C21.4672 15.0651 23 11.9995 23 11.9995C23 11.9995 19 3.99951 12 3.99951C10.018 3.99951 8.27653 4.64087 6.79825 5.56038L2.61872 1.38086L1.38129 2.6183ZM9.85839 8.62052L10.9623 9.7244C11.2784 9.57999 11.6298 9.49951 12 9.49951C13.3807 9.49951 14.5 10.6188 14.5 11.9995C14.5 12.3697 14.4195 12.7212 14.2751 13.0372L15.379 14.1411C15.7722 13.5219 16 12.7873 16 11.9995C16 9.79037 14.2091 7.99951 12 7.99951C11.2122 7.99951 10.4776 8.22727 9.85839 8.62052Z" />
    <path d="M8.00147 11.8898C8.00049 11.9263 8 11.9628 8 11.9995C8 14.2087 9.79086 15.9995 12 15.9995C12.0367 15.9995 12.0732 15.999 12.1097 15.998L15.4427 19.3311C14.3937 19.7468 13.2442 19.9995 12 19.9995C5 19.9995 1 11.9995 1 11.9995C1 11.9995 2.04086 9.9178 3.98983 7.87819L8.00147 11.8898Z" />
  </>
);

export const EyeOffSolidIcon = createFunctionalIcon(
  'invisible_icon_label_a11y',
  EyeOffSolidIcon16x16,
  EyeOffSolidIcon24x24,
);

/**
 * FlagSolidIcon

 */
const FlagSolidIcon16x16 = () => (
  <path d="M2.375 1H13.625V15.223L8 11.157L2.375 15.223V1Z" />
);

const FlagSolidIcon24x24 = () => (
  <path d="M5 3H19V21.0892L12 16.0492L5 21.0892V3Z" />
);

export const FlagSolidIcon = createFunctionalIcon(
  'added_icon_label_a11y',
  FlagSolidIcon16x16,
  FlagSolidIcon24x24,
);

/**
 * FlagIcon
 */

const FlagIcon16x16 = () => (
  <path d="M2.375 1H13.625V15.223L8 11.157L2.375 15.223V1ZM3.625 2.25V12.777L8 9.61464L12.375 12.777V2.25H3.625Z" />
);

const FlagIcon24x24 = () => (
  <path d="M5 3H19V21.0892L12 16.0492L5 21.0892V3ZM6.75 4.75V17.6728L10.9775 14.629L12 13.8928L13.0225 14.629L17.25 17.6728V4.75H6.75Z" />
);

export const FlagIcon = createFunctionalIcon(
  'add_icon_label_a11y',
  FlagIcon16x16,
  FlagIcon24x24,
);

/**
 * FlashIcon
 */

const FlashIcon16x16 = () => (
  <path d="M4.04737 1H11.5319L9.798 5H15.2173L4.82743 15.8149L6.33981 9.25H1.34351L4.04737 1ZM4.95312 2.25L3.0686 8H7.91051L7.06157 11.6851L12.2831 6.25H7.89378L9.62766 2.25H4.95312Z" />
);

const FlashIcon24x24 = () => (
  <path d="M7.38152 3H16.9459L14.0002 9H20.8988L8.0002 22.125L10.0027 14H3.57397L7.38152 3ZM8.62765 4.75L6.03159 12.25H12.2363L11.2272 16.3447L16.7254 10.75H11.1915L14.1372 4.75H8.62765Z" />
);

export const FlashIcon = createFunctionalIcon(
  'flash_icon_label_a11y',
  FlashIcon16x16,
  FlashIcon24x24,
);

/**
 * FlashSolidIcon
 */

const FlashSolidIcon16x16 = () => (
  <path d="M4.04713 1H11.5316L9.79776 5H15.2171L4.82718 15.8149L6.33956 9.25H1.34326L4.04713 1Z" />
);

const FlashSolidIcon24x24 = () => (
  <path d="M3.57373 14L7.38128 3H16.9457L14 9H20.8986L7.99997 22.125L10.0024 14H3.57373Z" />
);

export const FlashSolidIcon = createFunctionalIcon(
  'flash_icon_label_a11y',
  FlashSolidIcon16x16,
  FlashSolidIcon24x24,
);

/**
 * FolderIcon
 */

const FolderIcon16x16 = () => (
  <path d="M1 2H7.41676L8.55312 4H15V14H1V2ZM2.25 3.25V12.75H13.75V5.25H7.82567L6.6893 3.25H2.25Z" />
);

const FolderIcon24x24 = () => (
  <path d="M3 4H11.2135L12.7135 7H21V20H3V4ZM4.75 5.75V18.25H19.25V8.75H11.632L10.132 5.75H4.75Z" />
);

export const FolderIcon = createFunctionalIcon(
  'folder_icon_label_a11y',
  FolderIcon16x16,
  FolderIcon24x24,
);

/**
 * FolderSolidIcon
 */

const FolderSolidIcon16x16 = () => (
  <path d="M1 2H7.41676L8.55312 4H15V14H1V2Z" />
);

const FolderSolidIcon24x24 = () => (
  <path d="M3 4H11.2135L12.7135 7H21V20H3V4Z" />
);

export const FolderSolidIcon = createFunctionalIcon(
  'folder_icon_label_a11y',
  FolderSolidIcon16x16,
  FolderSolidIcon24x24,
);

/**
 * FolderArrowIcon
 */

const FolderArrowIcon16x16 = () => (
  <path d="M1 2H7.41676L8.55312 4H15V14H1V2ZM2.25 3.25V12.75H13.75V5.25H7.82567L6.6893 3.25H2.25ZM8.44194 5.85014L11.4668 8.875L8.44194 11.8999L7.55806 11.016L9.07208 9.50196H4.75V8.25196H9.07599L7.55806 6.73402L8.44194 5.85014Z" />
);

const FolderArrowIcon24x24 = () => (
  <>
    <path d="M12.7499 9.51172L16.4873 13.2492L12.7499 16.9866L11.5125 15.7492L13.1366 14.125L7.875 14.125V12.375L13.1383 12.375L11.5125 10.7492L12.7499 9.51172Z" />
    <path d="M3 4H11.2135L12.7135 6.99999L21 7V20H3V4ZM4.75 5.75V18.25H19.25V8.75H11.632L10.132 5.75H4.75Z" />
  </>
);

export const FolderArrowIcon = createFunctionalIcon(
  'move_folder_icon_label_a11y',
  FolderArrowIcon16x16,
  FolderArrowIcon24x24,
);

/**
 * FolderArrowSolidIcon
 */

const FolderArrowSolidIcon16x16 = () => (
  <path d="M1 2H7.41676L8.55312 4H15V14H1V2ZM11.4668 8.87496L8.44194 5.8501L7.55806 6.73398L9.07599 8.25192H4.75V9.50192H9.07208L7.55806 11.0159L8.44194 11.8998L11.4668 8.87496Z" />
);

const FolderArrowSolidIcon24x24 = () => (
  <path d="M11.2135 4H3V20H21V7H12.7135L11.2135 4ZM12.75 9.51257L16.4874 13.25L12.75 16.9874L11.5126 15.75L13.1376 14.125H7.875V12.375H13.1376L11.5126 10.75L12.75 9.51257Z" />
);

export const FolderArrowSolidIcon = createFunctionalIcon(
  'move_folder_icon_label_a11y',
  FolderArrowSolidIcon16x16,
  FolderArrowSolidIcon24x24,
);

/**
 * FolderUserSolidIcon
 */

const FolderUserSolidIcon16x16 = () => (
  <path d="M1 2H7.41676L8.55312 4H15V14H1V2ZM5.97894 11.1129C5.97894 9.99666 6.88381 9.0918 8.00002 9.0918C9.11622 9.0918 10.0211 9.99665 10.0211 11.1129V11.9963H5.97894V11.1129ZM8.00005 5.9375C7.31792 5.9375 6.76495 6.49047 6.76495 7.1726C6.76495 7.85472 7.31792 8.40769 8.00005 8.40769C8.68217 8.40769 9.23515 7.85472 9.23515 7.1726C9.23515 6.49047 8.68217 5.9375 8.00005 5.9375Z" />
);

const FolderUserSolidIcon24x24 = () => (
  <path d="M3 4H11.2135L12.7135 7H21V20H3V4ZM9.59473 16.0013C9.59473 14.6932 10.6716 13.6328 11.9999 13.6328C13.3282 13.6328 14.4051 14.6932 14.4051 16.0013V17.0002H9.59473V16.0013ZM12 10.0254C11.203 10.0254 10.5569 10.6715 10.5569 11.4686C10.5569 12.2656 11.203 12.9117 12 12.9117C12.797 12.9117 13.4431 12.2656 13.4431 11.4686C13.4431 10.6715 12.797 10.0254 12 10.0254Z" />
);

export const FolderUserSolidIcon = createFunctionalIcon(
  'folder_user_icon_label_a11y',
  FolderUserSolidIcon16x16,
  FolderUserSolidIcon24x24,
);

/**
 * FolderUsersSolidIcon
 */

const FolderUsersSolidIcon16x16 = () => (
  <path d="M1 2H7.41676L8.55312 4H15V14H1V2ZM4.5 11.1129C4.5 9.99666 5.40487 9.0918 6.52108 9.0918C7.63728 9.0918 8.54213 9.99665 8.54213 11.1129V11.9963H4.5V11.1129ZM6.52108 5.9375C5.83895 5.9375 5.28598 6.49047 5.28598 7.1726C5.28598 7.85472 5.83895 8.40769 6.52108 8.40769C7.2032 8.40769 7.75617 7.85472 7.75617 7.1726C7.75617 6.49047 7.2032 5.9375 6.52108 5.9375ZM8.42987 7.1726C8.42987 6.49047 8.98284 5.9375 9.66497 5.9375C10.3471 5.9375 10.9001 6.49047 10.9001 7.1726C10.9001 7.85472 10.3471 8.40769 9.66497 8.40769C8.98284 8.40769 8.42987 7.85472 8.42987 7.1726ZM9.10356 11.1129V11.9963H11.686V11.1129C11.686 9.99665 10.7812 9.0918 9.66498 9.0918C9.23278 9.0918 8.83243 9.22746 8.50403 9.45834C8.87832 9.90644 9.10356 10.4833 9.10356 11.1129Z" />
);

const FolderUsersSolidIcon24x24 = () => (
  <path d="M3 4H11.2135L12.7135 7H21V20H3V4ZM8 16.0219C8 14.7164 9.05824 13.6582 10.3636 13.6582C11.669 13.6582 12.7273 14.7164 12.7273 16.0218V17.0187H8V16.0219ZM10.3636 10.2129C9.61571 10.2129 9.0094 10.8192 9.0094 11.5671C9.0094 12.315 9.61571 12.9214 10.3636 12.9214C11.1116 12.9214 11.7179 12.315 11.7179 11.5671C11.7179 10.8192 11.1116 10.2129 10.3636 10.2129ZM12.2821 11.5671C12.2821 10.8192 12.8884 10.2129 13.6363 10.2129C14.3843 10.2129 14.9906 10.8192 14.9906 11.5671C14.9906 12.315 14.3843 12.9214 13.6363 12.9214C12.8884 12.9214 12.2821 12.315 12.2821 11.5671ZM13.3636 16.0218V17.0187H16V16.0218C16 14.7164 14.9417 13.6582 13.6364 13.6582C13.233 13.6582 12.8533 13.7592 12.5211 13.9373C13.0427 14.4771 13.3636 15.212 13.3636 16.0218Z" />
);

export const FolderUsersSolidIcon = createFunctionalIcon(
  'folder_users_icon_label_a11y',
  FolderUsersSolidIcon16x16,
  FolderUsersSolidIcon24x24,
);

/**
 * ForbiddenIcon
 */

const ForbiddenIcon16x16 = () => (
  <path d="M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1ZM4.49955 3.43791C5.46905 2.6929 6.6828 2.25 8 2.25C11.1756 2.25 13.75 4.82436 13.75 8C13.75 9.3172 13.3071 10.5309 12.5621 11.5005L4.49955 3.43791ZM3.43876 4.49844C2.69324 5.46813 2.25 6.6823 2.25 8C2.25 11.1756 4.82436 13.75 8 13.75C9.3177 13.75 10.5319 13.3068 11.5016 12.5612L3.43876 4.49844Z" />
);

const ForbiddenIcon24x24 = () => (
  <path d="M5.58036 6.81779C4.43558 8.23415 3.75 10.037 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C13.963 20.25 15.7658 19.5644 17.1822 18.4196L5.58036 6.81779ZM6.81779 5.58036L18.4196 17.1822C19.5644 15.7658 20.25 13.963 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75C10.037 3.75 8.23415 4.43558 6.81779 5.58036ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z" />
);

export const ForbiddenIcon = createFunctionalIcon(
  'prohibited_icon_label_a11y',
  ForbiddenIcon16x16,
  ForbiddenIcon24x24,
);

/**
 * FunnelIcon
 */

const FunnelIcon16x16 = () => (
  <path d="M14 4.25H2V3H14V4.25ZM12 8.25H4V7H12V8.25ZM10 12.25H6V11H10V12.25Z" />
);

const FunnelIcon24x24 = () => (
  <path d="M20 7.75H4V6H20V7.75ZM17 12.75H7V11H17V12.75ZM14 17.75H10V16H14V17.75Z" />
);

export const FunnelIcon = createFunctionalIcon(
  'filter_icon_label_a11y',
  FunnelIcon16x16,
  FunnelIcon24x24,
);

/**
 * GetToKnowIcon
 */

const GetToKnowIcon16x16 = () => {
  return (
    <>
      <g clipPath="url(#clip0_2659_17407)">
        <path
          d="M7 0.665039C8.6554 0.665062 10.2606 1.14946 11.3477 2.05664V2.05762C12.5165 3.03421 13.1975 4.67473 13.3164 6.44727C13.4352 8.2184 12.9837 10.0021 12.04 11.2158C11.8345 11.4804 11.858 11.8567 12.0947 12.0938L14.999 14.999L12.0908 12.0918C11.8839 11.885 11.5707 11.8407 11.3184 11.9697L11.2148 12.0361C9.98261 12.9873 8.20675 13.4335 6.45117 13.3164C4.69383 13.1991 3.06949 12.5255 2.0918 11.3779H2.09082C1.08188 10.1925 0.581384 8.38438 0.676758 6.56836C0.766244 4.8646 1.37317 3.26912 2.43457 2.25293L2.65234 2.05762C3.73875 1.14999 5.34403 0.665039 7 0.665039ZM5.46973 3.58887C5.24246 3.36202 4.89174 3.33356 4.63379 3.50391L4.5293 3.58984L1.58887 6.53027C1.46426 6.65499 1.39451 6.82467 1.39453 7.00098C1.39463 7.17723 1.46519 7.34608 1.58984 7.4707L4.53027 10.4111C4.65492 10.5356 4.82387 10.6054 5 10.6055C5.17632 10.6054 5.34602 10.5358 5.4707 10.4111L6.41113 9.46973C6.67041 9.20999 6.67066 8.78885 6.41113 8.5293L4.88086 7L6.41113 5.46973C6.53556 5.34507 6.60545 5.17614 6.60547 5C6.60545 4.82367 6.53581 4.65399 6.41113 4.5293L5.46973 3.58887ZM9.4707 3.58984C9.24362 3.3627 8.89287 3.33381 8.63477 3.50391L8.53027 3.58887L7.58984 4.5293C7.46512 4.65399 7.39455 4.82363 7.39453 5C7.39455 5.17613 7.46445 5.34508 7.58887 5.46973L9.11816 7L7.58984 8.5293C7.33025 8.78884 7.32959 9.20997 7.58887 9.46973L8.5293 10.4111C8.65399 10.5358 8.82367 10.6054 9 10.6055C9.17614 10.6054 9.34507 10.5356 9.46973 10.4111L12.4111 7.4707C12.5356 7.34611 12.6054 7.17708 12.6055 7.00098C12.6055 6.82466 12.5358 6.65499 12.4111 6.53027L9.4707 3.58984Z"
          fill="#161617"
          stroke="#161617"
          strokeWidth="1.33"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_2659_17407">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </>
  );
};

const GetToKnowIcon24x24 = () => {
  return (
    <path
      d="M10.959 3.66504C12.7388 3.66502 14.4746 4.1536 15.7197 5.0791L15.9619 5.27051C17.3259 6.4103 18.1077 8.32119 18.2344 10.3701C18.3609 12.418 17.8245 14.4803 16.709 15.8721C16.4969 16.1367 16.5171 16.519 16.7568 16.7588L16.7539 16.7559C16.5448 16.5468 16.2279 16.5034 15.9746 16.6367L15.8701 16.7051C14.4566 17.8291 12.4047 18.3602 10.375 18.2354C8.34337 18.1103 6.44944 17.3363 5.30859 15.9971H5.30762C4.1418 14.6271 3.56842 12.5459 3.67773 10.4639C3.78714 8.38065 4.57292 6.42784 5.95801 5.27051L5.95703 5.26953C7.21265 4.22056 9.06051 3.66514 10.959 3.66504ZM9.15527 7.27539C8.92803 7.04814 8.57746 7.019 8.31934 7.18945L8.21484 7.27539L5.46973 10.0205L5 10.4912C4.773 10.7187 4.74532 11.0692 4.91602 11.3271L5.00098 11.4316L5.4707 11.9004V11.9014L8.21484 14.6455C8.4744 14.905 8.89555 14.9048 9.15527 14.6455L10.0957 13.7051C10.2204 13.5804 10.291 13.4108 10.291 13.2344C10.291 13.102 10.2512 12.9737 10.1787 12.8652L10.0957 12.7637L8.29102 10.959L9.625 9.62598L10.0957 9.15625C10.2206 9.03159 10.2909 8.86201 10.291 8.68555C10.291 8.50921 10.2204 8.34053 10.0957 8.21582L9.15527 7.27539ZM13.2334 7.08008C13.1012 7.08007 12.9727 7.11914 12.8643 7.19141L12.7627 7.27539L11.8223 8.21582C11.5632 8.4754 11.5632 8.89567 11.8223 9.15527L13.626 10.96L11.8223 12.7646C11.563 13.0244 11.5627 13.4455 11.8223 13.7051L12.7627 14.6455C12.8874 14.7702 13.057 14.8399 13.2334 14.8398C13.4099 14.8398 13.5794 14.7695 13.7041 14.6445L14.1729 14.1748L16.918 11.4316C17.0427 11.307 17.1132 11.1373 17.1133 10.9609C17.1133 10.7846 17.0426 10.6159 16.918 10.4912L13.7031 7.27539C13.5784 7.15068 13.4098 7.0801 13.2334 7.08008Z"
      fill="#161617"
      stroke="#161617"
      strokeWidth="1.33"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
};

export const GetToKnowIcon = createFunctionalIcon(
  'get_to_know_icon_label_a11y',
  GetToKnowIcon16x16,
  GetToKnowIcon24x24,
);

/**
 * GhostIcon
 */

const GhostIcon16x16 = () => (
  <>
    <path d="M6 6.25C5.30965 6.25 4.75 6.80965 4.75 7.5C4.75 8.19035 5.30965 8.75 6 8.75C6.69035 8.75 7.25 8.19035 7.25 7.5C7.25 6.80965 6.69035 6.25 6 6.25Z" />
    <path d="M10 6.25C9.30965 6.25 8.75 6.80965 8.75 7.5C8.75 8.19035 9.30965 8.75 10 8.75C10.6904 8.75 11.25 8.19035 11.25 7.5C11.25 6.80965 10.6904 6.25 10 6.25Z" />
    <path d="M8 1C4.6863 1 2 3.6863 2 7V15H14V7C14 3.6863 11.3137 1 8 1ZM3.25 7C3.25 4.37665 5.37665 2.25 8 2.25C10.6233 2.25 12.75 4.37665 12.75 7V13.75H3.25V7Z" />
  </>
);

const GhostIcon24x24 = () => (
  <>
    <path d="M11 11C11 11.6904 10.4404 12.25 9.75 12.25C9.05964 12.25 8.5 11.6904 8.5 11C8.5 10.3096 9.05964 9.75 9.75 9.75C10.4404 9.75 11 10.3096 11 11Z" />
    <path d="M15.5 11C15.5 11.6904 14.9404 12.25 14.25 12.25C13.5596 12.25 13 11.6904 13 11C13 10.3096 13.5596 9.75 14.25 9.75C14.9404 9.75 15.5 10.3096 15.5 11Z" />
    <path d="M12 1.5C7.44365 1.5 3.75 5.19365 3.75 9.75V22H20.25V9.75C20.25 5.19365 16.5563 1.5 12 1.5ZM5.25 9.75C5.25 6.02208 8.27208 3 12 3C15.7279 3 18.75 6.02208 18.75 9.75V20.5H5.25V9.75Z" />
  </>
);

export const GhostIcon = createFunctionalIcon(
  'ghost_icon_label_a11y',
  GhostIcon16x16,
  GhostIcon24x24,
);

/**
 * GhostSolidIcon
 */

const GhostSolidIcon16x16 = () => (
  <path d="M2 7C2 3.68629 4.68629 1 8 1C11.3137 1 14 3.68629 14 7V15H2V7ZM4.75 7.5C4.75 6.80964 5.30964 6.25 6 6.25C6.69036 6.25 7.25 6.80964 7.25 7.5C7.25 8.19036 6.69036 8.75 6 8.75C5.30964 8.75 4.75 8.19036 4.75 7.5ZM10 6.25C9.30964 6.25 8.75 6.80964 8.75 7.5C8.75 8.19036 9.30964 8.75 10 8.75C10.6904 8.75 11.25 8.19036 11.25 7.5C11.25 6.80964 10.6904 6.25 10 6.25Z" />
);

const GhostSolidIcon24x24 = () => (
  <path d="M3.75 9.75C3.75 5.19365 7.44365 1.5 12 1.5C16.5563 1.5 20.25 5.19365 20.25 9.75V22.2341H3.75V9.75ZM11 11C11 11.6904 10.4404 12.25 9.75 12.25C9.05964 12.25 8.5 11.6904 8.5 11C8.5 10.3096 9.05964 9.75 9.75 9.75C10.4404 9.75 11 10.3096 11 11ZM14.25 12.25C14.9404 12.25 15.5 11.6904 15.5 11C15.5 10.3096 14.9404 9.75 14.25 9.75C13.5596 9.75 13 10.3096 13 11C13 11.6904 13.5596 12.25 14.25 12.25Z" />
);

export const GhostSolidIcon = createFunctionalIcon(
  'ghost_icon_label_a11y',
  GhostSolidIcon16x16,
  GhostSolidIcon24x24,
);

/**
 * GlobeIcon
 */

const GlobeIcon16x16 = () => (
  <path d="M4.43863 3.43392C3.88859 3.87079 3.40991 4.4165 3.03739 5.06172C2.34533 6.2604 2.13391 7.6047 2.34335 8.87142L4.46591 8.30269C4.11999 6.75418 4.06766 5.26886 4.2896 4.06338C4.32891 3.84988 4.37833 3.63888 4.43863 3.43392ZM9.12753 2.29337C9.24878 2.4142 9.36582 2.54137 9.47808 2.67313C10.273 3.60613 10.9704 4.91863 11.4451 6.43263L13.398 5.90935C12.946 4.70762 12.0907 3.64913 10.892 2.95707C10.3283 2.63161 9.73243 2.41246 9.12753 2.29337ZM13.709 7.1201L11.7655 7.64087C12.0974 9.16393 12.1448 10.6225 11.9262 11.8097C11.8994 11.9551 11.868 12.0994 11.8316 12.2413C12.2829 11.8412 12.6785 11.3628 12.9967 10.8117C13.6673 9.65021 13.8866 8.35199 13.709 7.1201ZM7.12829 13.6192C6.99273 13.4867 6.8623 13.3461 6.73774 13.1999C5.95486 12.2811 5.26665 10.9942 4.79254 9.50926L2.67935 10.0755C3.1415 11.2311 3.98053 12.2458 5.14204 12.9164C5.77451 13.2815 6.44752 13.5129 7.12829 13.6192ZM6.00141 9.18534C6.43363 10.5175 7.04206 11.6297 7.68921 12.3893C8.47022 13.3059 9.14666 13.5426 9.579 13.4267C10.0113 13.3109 10.4788 12.7677 10.6969 11.5834C10.8776 10.602 10.8484 9.33461 10.5566 7.96479L6.00141 9.18534ZM10.2365 6.75646C9.80304 5.39379 9.18486 4.25637 8.52661 3.48381C7.74559 2.56716 7.06916 2.33051 6.63682 2.44635C6.20448 2.5622 5.73699 3.10536 5.51894 4.28972C5.33516 5.28789 5.36851 6.58202 5.67447 7.97885L10.2365 6.75646ZM1.95486 4.43672C3.88786 1.08867 8.16899 -0.0584587 11.517 1.87454C14.8651 3.80753 16.0122 8.08867 14.0792 11.4367C12.1462 14.7848 7.86509 15.9319 4.51704 13.9989C1.16899 12.0659 0.0218635 7.78476 1.95486 4.43672Z" />
);

const GlobeIcon24x24 = () => (
  <path d="M6.77284 5.61695C4.92746 7.12994 3.75 9.42746 3.75 12C3.75 12.4133 3.7804 12.8196 3.83908 13.2166L6.8475 12.4105C6.36761 10.2191 6.29158 8.12293 6.5937 6.41966C6.64186 6.14815 6.7013 5.87938 6.77284 5.61695ZM13.3932 3.86714C13.5795 4.05306 13.759 4.24966 13.9306 4.45375C15.0439 5.77776 16.0261 7.63112 16.7062 9.76886L19.6769 8.97285C18.6342 6.33059 16.2668 4.35586 13.3932 3.86714ZM20.1417 10.66L17.165 11.4577C17.6723 13.699 17.758 15.8477 17.4495 17.5867C17.4047 17.8396 17.3501 18.09 17.285 18.3352C19.0971 16.8218 20.25 14.5456 20.25 12C20.25 11.5438 20.213 11.0962 20.1417 10.66ZM10.6511 20.1403C10.4644 19.954 10.2846 19.7571 10.1127 19.5526C8.976 18.2008 7.97593 16.2972 7.29459 14.1024L4.27813 14.9107C5.30175 17.6249 7.7154 19.6575 10.6511 20.1403ZM8.98636 13.6491C9.61139 15.6366 10.5034 17.2981 11.4521 18.4264C12.5713 19.7575 13.5368 20.1019 14.1478 19.9382C14.7588 19.7744 15.4227 18.9934 15.7264 17.2811C15.9839 15.8296 15.9257 13.9447 15.4732 11.911L8.98636 13.6491ZM15.0139 10.2223C14.3913 8.29457 13.5183 6.68262 12.5911 5.58C11.4719 4.24893 10.5065 3.90449 9.89545 4.06821C9.28441 4.23194 8.62054 5.01296 8.3168 6.72531C8.0652 8.14376 8.11511 9.97627 8.53982 11.957L15.0139 10.2223ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z" />
);

export const GlobeIcon = createFunctionalIcon(
  'globe_icon_label_a11y',
  GlobeIcon16x16,
  GlobeIcon24x24,
);

/**
 * GridIcon
 */

const GridIcon16x16 = () => (
  <path d="M1 1H15V15H1V1ZM2.25 2.25V7.375H7.375V2.25H2.25ZM8.625 2.25V7.375H13.75V2.25H8.625ZM13.75 8.625H8.625V13.75H13.75V8.625ZM7.375 13.75V8.625H2.25V13.75H7.375Z" />
);

const GridIcon24x24 = () => (
  <path d="M3 3H21V21H3V3ZM4.75 4.75V11.125L11.125 11.125V4.75H4.75ZM12.875 4.75V11.125L19.25 11.125V4.75H12.875ZM19.25 12.875L12.875 12.875V19.25H19.25V12.875ZM11.125 19.25L11.125 12.875L4.75 12.875V19.25H11.125Z" />
);

export const GridIcon = createFunctionalIcon(
  'grid_layout_icon_label_a11y',
  GridIcon16x16,
  GridIcon24x24,
);

/**
 * GridSolidIcon
 */

const GridSolidIcon16x16 = () => (
  <>
    <path d="M7.375 1H1V7.375H7.375V1Z" />
    <path d="M8.625 1V7.375H15V1H8.625Z" />
    <path d="M15 8.625H8.625V15H15V8.625Z" />
    <path d="M7.375 15V8.625H1V15H7.375Z" />
  </>
);

const GridSolidIcon24x24 = () => (
  <>
    <path d="M11.125 21V12.875H3V21H11.125Z" />
    <path d="M12.875 21H21V12.875H12.875V21Z" />
    <path d="M21 11.125V2.875H12.875V11.125H21Z" />
    <path d="M11.125 3H3V11.125H11.125V3Z" />
  </>
);

export const GridSolidIcon = createFunctionalIcon(
  'grid_layout_icon_label_a11y',
  GridSolidIcon16x16,
  GridSolidIcon24x24,
);

/**
 * HeartIcon
 */

const HeartIcon16x16 = () => (
  <path d="M9.48477 4.43281C9.48476 4.43281 9.48477 4.43281 9.48477 4.43281L7.989 5.91537L6.51523 4.45464C6.51523 4.45463 6.51523 4.45464 6.51523 4.45464C5.59667 3.54422 4.10282 3.54423 3.1843 4.45463C2.27191 5.35894 2.2719 6.82045 3.18429 7.72475L7.99188 12.4898L12.8154 7.70322C13.7278 6.79891 13.7281 5.33712 12.8157 4.43281C11.8972 3.52241 10.4033 3.5224 9.48477 4.43281ZM8.60483 3.545C10.0106 2.15167 12.2899 2.15166 13.6956 3.54501C15.1014 4.93835 15.1014 7.19742 13.6956 8.59076L7.99241 14.2503L2.30435 8.61256C0.898548 7.21921 0.898551 4.96017 2.30435 3.56683C3.71012 2.17348 5.98938 2.17349 7.39517 3.56683L7.98899 4.1554L8.60483 3.545Z" />
);

const HeartIcon24x24 = () => (
  <path d="M14.1111 6.33308C14.1111 6.33308 14.1112 6.33308 14.1111 6.33308L11.9834 8.44323L9.88884 6.36598C8.43268 4.92186 6.06875 4.92184 4.61256 6.36598C3.1604 7.80612 3.1604 10.138 4.61255 11.5781L11.9878 18.8923L19.3871 11.5456C20.8392 10.1055 20.8396 7.77321 19.3875 6.33308C17.9313 4.88897 15.5673 4.88898 14.1111 6.33308ZM20.6197 5.0905C22.762 7.21509 22.7622 10.6627 20.6201 12.7874L11.9885 21.3577L3.38028 12.8207C1.23782 10.696 1.23783 7.24812 3.38027 5.12341C5.51868 3.00271 8.98274 3.0027 11.1211 5.12342L11.9834 5.97857L12.8789 5.09051C15.0173 2.96984 18.4813 2.96983 20.6197 5.0905Z" />
);

export const HeartIcon = createFunctionalIcon(
  'heart_icon_label_a11y',
  HeartIcon16x16,
  HeartIcon24x24,
);

/**
 * HeartSolidIcon
 */

const HeartSolidIcon16x16 = () => (
  <path d="M13.6957 3.54501C12.2899 2.15166 10.0106 2.15167 8.60484 3.54501L7.989 4.15539L7.39516 3.56682C5.98937 2.17347 3.71013 2.17348 2.30434 3.56682C0.898553 4.96016 0.898551 7.21921 2.30434 8.61256L7.99215 14.25L13.083 9.20426L13.0798 9.20113L13.6957 8.59075C15.1014 7.1974 15.1014 4.93835 13.6957 3.54501Z" />
);

const HeartSolidIcon24x24 = () => (
  <path d="M20.6197 5.09051C22.762 7.2151 22.7622 10.6627 20.6201 12.7874L11.9885 21.3577L3.38028 12.8207C1.23782 10.696 1.23783 7.24812 3.38027 5.12342C5.51868 3.00271 8.98274 3.0027 11.1211 5.12342L11.9834 5.97855L12.8789 5.09051C15.0173 2.96983 18.4813 2.96983 20.6197 5.09051Z" />
);

export const HeartSolidIcon = createFunctionalIcon(
  'heart_icon_label_a11y',
  HeartSolidIcon16x16,
  HeartSolidIcon24x24,
);

/**
 * HomeIcon
 */

const HomeIcon16x16 = () => (
  <path d="M8 0.625L15 7V15H1V7L8 0.625ZM2.25 7.5523V13.75H13.75V7.5523L8 2.31569L2.25 7.5523Z" />
);

const HomeIcon24x24 = () => (
  <path d="M12 1.98828L21 10.1951V21.0008H3V10.1951L12 1.98828ZM4.75 10.9676V19.2508H19.25V10.9676L12 4.35661L4.75 10.9676Z" />
);

export const HomeIcon = createFunctionalIcon(
  'home_icon_label_a11y',
  HomeIcon16x16,
  HomeIcon24x24,
);

/**
 * HomeSolidIcon
 */

const HomeSolidIcon16x16 = () => <path d="M8 0.625L15 7V15H1V7L8 0.625Z" />;

const HomeSolidIcon24x24 = () => (
  <path d="M12 1.98828L21 10.1951V21.0008H3V10.1951L12 1.98828Z" />
);

export const HomeSolidIcon = createFunctionalIcon(
  'home_icon_label_a11y',
  HomeSolidIcon16x16,
  HomeSolidIcon24x24,
);

/**
 * HotkeyIcon
 */

const HotkeyIcon16x16 = () => (
  <path d="M2.99988 2.24586C2.58291 2.24586 2.24488 2.58388 2.24488 3.00086V13.0009C2.24488 13.4178 2.58291 13.7559 2.99988 13.7559H12.9999C13.4169 13.7559 13.7549 13.4178 13.7549 13.0009V3.00086C13.7549 2.58388 13.4169 2.24586 12.9999 2.24586H2.99988ZM1.00488 3.00086C1.00488 1.89905 1.89807 1.00586 2.99988 1.00586H12.9999C14.1017 1.00586 14.9949 1.89905 14.9949 3.00086V13.0009C14.9949 14.1027 14.1017 14.9959 12.9999 14.9959H2.99988C1.89807 14.9959 1.00488 14.1027 1.00488 13.0009V3.00086ZM5.37988 11.0009V5.00086H6.61988V7.38086H9.37991V5.00086H10.6199V11.0009H9.37991V8.62086H6.61988V11.0009H5.37988Z" />
);

const HotkeyIcon24x24 = () => (
  <path d="M7 3.75C5.20507 3.75 3.75 5.20507 3.75 7V17C3.75 18.7949 5.20507 20.25 7 20.25H17C18.7949 20.25 20.25 18.7949 20.25 17V7C20.25 5.20507 18.7949 3.75 17 3.75H7ZM2 7C2 4.23858 4.23858 2 7 2H17C19.7614 2 22 4.23858 22 7V17C22 19.7614 19.7614 22 17 22H7C4.23858 22 2 19.7614 2 17V7ZM8.625 16V8H10.375V11.125H13.625V8H15.375V16H13.625V12.875H10.375V16H8.625Z" />
);

export const HotkeyIcon = createFunctionalIcon(
  'keyboard_shortcut_instructions_icon_label_a11y',
  HotkeyIcon16x16,
  HotkeyIcon24x24,
);

/**
 * HotkeySolidIcon
 */

const HotkeySolidIcon16x16 = () => (
  <path d="M3 1C1.89543 1 1 1.89543 1 3V13C1 14.1046 1.89543 15 3 15H13C14.1046 15 15 14.1046 15 13V3C15 1.89543 14.1046 1 13 1H3ZM5.38 5V11H6.62V8.62L9.38 8.62V11H10.62V5H9.38V7.38H6.62V5H5.38Z" />
);

const HotkeySolidIcon24x24 = () => (
  <path d="M7 2C4.23858 2 2 4.23858 2 7V17C2 19.7614 4.23858 22 7 22H17C19.7614 22 22 19.7614 22 17V7C22 4.23858 19.7614 2 17 2H7ZM8.625 8V16H10.375V12.875H13.625V16H15.375V8H13.625V11.125H10.375V8H8.625Z" />
);

export const HotkeySolidIcon = createFunctionalIcon(
  'keyboard_shortcut_instructions_icon_label_a11y',
  HotkeySolidIcon16x16,
  HotkeySolidIcon24x24,
);

/**
 * ImageIcon
 */

const ImageIcon16x16 = () => (
  <path d="M9.26509 12.0345L6.18182 8.04433L2.54562 12.75H13.3312L11 9.95256L9.26509 12.0345ZM11 8L16 14H0L6.18182 6L9.30189 10.0377L11 8ZM10 4.75C10.4142 4.75 10.75 4.41421 10.75 4C10.75 3.58579 10.4142 3.25 10 3.25C9.58579 3.25 9.25 3.58579 9.25 4C9.25 4.41421 9.58579 4.75 10 4.75ZM10 6C11.1046 6 12 5.10457 12 4C12 2.89543 11.1046 2 10 2C8.89543 2 8 2.89543 8 4C8 5.10457 8.89543 6 10 6Z" />
);

const ImageIcon24x24 = () => (
  <path d="M15 4.75C14.3096 4.75 13.75 5.30965 13.75 6C13.75 6.69035 14.3096 7.25 15 7.25C15.6903 7.25 16.25 6.69035 16.25 6C16.25 5.30965 15.6903 4.75 15 4.75ZM12 6C12 4.34315 13.3432 3 15 3C16.6568 3 18 4.34315 18 6C18 7.65685 16.6568 9 15 9C13.3432 9 12 7.65685 12 6ZM9 8.99999L13.9127 15.5502L17 11.25L24 21H0L9 8.99999ZM3.5 19.25H20.5893L17 14.2507L13.9435 18.508L9 11.9167L3.5 19.25Z" />
);

export const ImageIcon = createFunctionalIcon(
  'image_icon_label_a11y',
  ImageIcon16x16,
  ImageIcon24x24,
);

/**
 * ImageSolidIcon
 */

const ImageSolidIcon16x16 = () => (
  <>
    <path d="M9.30189 10.0377L6.18182 6L0 14H16L11 8L9.30189 10.0377Z" />
    <path d="M12 4C12 5.10457 11.1046 6 10 6C8.89543 6 8 5.10457 8 4C8 2.89543 8.89543 2 10 2C11.1046 2 12 2.89543 12 4Z" />
  </>
);

const ImageSolidIcon24x24 = () => (
  <>
    <path d="M13.9127 15.5502L9 9L0 21H24L17 11.25L13.9127 15.5502Z" />
    <path d="M18 6C18 7.65685 16.6569 9 15 9C13.3431 9 12 7.65685 12 6C12 4.34315 13.3431 3 15 3C16.6569 3 18 4.34315 18 6Z" />
  </>
);

export const ImageSolidIcon = createFunctionalIcon(
  'image_icon_label_a11y',
  ImageSolidIcon16x16,
  ImageSolidIcon24x24,
);

/**
 * InboxIcon
 */

const InboxIcon16x16 = () => (
  <path d="M12.3312 3H3.67333L1 6.84575V13H1.62249L15 13.0874V6.93294L12.3312 3ZM13.5349 7L11.6688 4.25H4.32675L2.41512 7H6C6 8.10457 6.89543 9 8 9C9.10457 9 10 8.10457 10 7L13.5349 7ZM2.25 8.25V11.75L13.75 11.75V8.25L11.0009 8.25C10.5111 9.42448 9.35197 10.25 8 10.25C6.64803 10.25 5.48886 9.42448 4.99908 8.25H2.25Z" />
);

const InboxIcon24x24 = () => (
  <path d="M18.4974 5H5.50255L2 10.8843L2 19L22 19V10.8843L18.4974 5ZM20.0323 11L17.5026 6.75H6.49745L3.96768 11H9C9 12.6569 10.3431 14 12 14C13.6569 14 15 12.6569 15 11L20.0323 11ZM3.75 12.75L3.75 17.25L20.25 17.25V12.75L16.4172 12.75C15.7205 14.5073 14.0054 15.75 12 15.75C9.99465 15.75 8.27955 14.5073 7.58276 12.75H3.75Z" />
);

export const InboxIcon = createFunctionalIcon(
  'inbox_icon_label_a11y',
  InboxIcon16x16,
  InboxIcon24x24,
);

/**
 * InboxSolidIcon
 */

const InboxSolidIcon16x16 = () => (
  <path d="M12.3312 3H3.67333L1 6.84575V13H1.62249L15 13.0874V6.93294L12.3312 3ZM13.5349 7L11.6688 4.25H4.32675L2.41512 7H6C6 8.10457 6.89543 9 8 9C9.10457 9 10 8.10457 10 7L13.5349 7Z" />
);

const InboxSolidIcon24x24 = () => (
  <path d="M18.4974 5H5.50255L2 10.8843L2 19L22 19V10.8843L18.4974 5ZM20.0323 11L17.5026 6.75H6.49745L3.96768 11H9C9 12.6569 10.3431 14 12 14C13.6569 14 15 12.6569 15 11L20.0323 11Z" />
);

export const InboxSolidIcon = createFunctionalIcon(
  'inbox_icon_label_a11y',
  InboxSolidIcon16x16,
  InboxSolidIcon24x24,
);

/**
 * InkDotIcon
 */

const InkDotIcon16x16 = () => (
  <path d="M8.89067 1.73485C8.68834 1.56103 8.51089 1.41408 8.36742 1.29778L8.00008 1L7.63274 1.29778C7.48927 1.41408 7.31182 1.56103 7.10949 1.73485C6.55969 2.20718 5.8267 2.87752 5.09382 3.66962C4.36086 4.4618 3.62867 5.37505 3.07988 6.33317C2.53095 7.29156 2.16675 8.2927 2.16675 9.26128C2.16675 12.4804 4.78161 15.0843 8.00008 15.0843C11.2186 15.0843 13.8334 12.4804 13.8334 9.26128C13.8334 8.2927 13.4692 7.29155 12.9203 6.33317C12.3715 5.37505 11.6393 4.4618 10.9063 3.66962C10.1735 2.87753 9.44048 2.20719 8.89067 1.73485ZM8.89067 1.73485L8.88981 1.73586L8.89067 1.73485ZM8.89067 1.73485L8.88981 1.73586L8.89067 1.73485ZM8.07611 2.683C8.05032 2.66085 8.02498 2.63917 8.00008 2.61796C7.97519 2.63917 7.94984 2.66085 7.92406 2.683C7.39904 3.13404 6.703 3.77099 6.01133 4.51854C5.31585 5.27022 4.65024 6.10649 4.16455 6.95444C3.67276 7.81307 3.41675 8.59462 3.41675 9.26128C3.41675 11.7871 5.46897 13.8343 8.00008 13.8343C10.5312 13.8343 12.5834 11.7871 12.5834 9.26128C12.5834 8.59462 12.3274 7.81307 11.8356 6.95443C11.3499 6.10648 10.6843 5.27022 9.98883 4.51854C9.29717 3.77099 8.60113 3.13404 8.07611 2.683Z" />
);

const InkDotIcon24x24 = () => (
  <path d="M6.55966 10.4309C5.86579 11.6489 5.5 12.7652 5.5 13.7241C5.5 17.3335 8.41531 20.25 12 20.25C15.5847 20.25 18.5 17.3335 18.5 13.7241C18.5 12.7652 18.1342 11.6489 17.4403 10.4309C16.7547 9.22718 15.8161 8.04189 14.8372 6.97809C13.8635 5.9199 12.8839 5.01852 12.1451 4.38036C12.0956 4.33758 12.0472 4.29602 12 4.25574C11.9528 4.29602 11.9044 4.33758 11.8549 4.38036C11.1161 5.01852 10.1365 5.9199 9.16276 6.97809C8.18386 8.04189 7.24532 9.22718 6.55966 10.4309ZM12 2C12 2 3.75 8.20691 3.75 13.7241C3.75 18.2948 7.44365 22 12 22C16.5564 22 20.25 18.2948 20.25 13.7241C20.25 8.20691 12 2 12 2Z" />
);

export const InkDotIcon = createFunctionalIcon(
  'theme_icon_label_a11y',
  InkDotIcon16x16,
  InkDotIcon24x24,
);

/**
 * InkDotSolidIcon
 */

const InkDotSolidIcon16x16 = () => (
  <path d="M8.00008 1L8.36742 1.29778C8.51089 1.41408 8.68834 1.56103 8.89067 1.73485C9.44048 2.20719 10.1735 2.87753 10.9063 3.66962C11.6393 4.4618 12.3715 5.37505 12.9203 6.33317C13.4692 7.29155 13.8334 8.2927 13.8334 9.26128C13.8334 12.4804 11.2186 15.0843 8.00008 15.0843C4.78161 15.0843 2.16675 12.4804 2.16675 9.26128C2.16675 8.2927 2.53095 7.29156 3.07988 6.33317C3.62867 5.37505 4.36086 4.4618 5.09382 3.66962C5.8267 2.87752 6.55969 2.20718 7.10949 1.73485C7.31182 1.56103 7.48927 1.41408 7.63274 1.29778L8.00008 1Z" />
);

const InkDotSolidIcon24x24 = () => (
  <path d="M3.75 13.7241C3.75 8.20691 12 2 12 2C12 2 20.25 8.20691 20.25 13.7241C20.25 18.2948 16.5564 22 12 22C7.44365 22 3.75 18.2948 3.75 13.7241Z" />
);

export const InkDotSolidIcon = createFunctionalIcon(
  'theme_icon_label_a11y',
  InkDotSolidIcon16x16,
  InkDotSolidIcon24x24,
);

/**
 * InkDotsIcon
 */

const InkDotsIcon16x16 = () => (
  <path d="M5.54585 1.50407L5.16675 2.00097L4.78765 1.50407L5.16675 1.21484L5.54585 1.50407ZM7.46515 3.26754C7.03701 2.81024 6.61986 2.41873 6.28639 2.12304C6.0696 1.93082 5.88668 1.77778 5.75704 1.67208C5.69218 1.61921 5.64055 1.5781 5.60457 1.54978L5.56261 1.51698L5.55096 1.50799L5.54758 1.50539L5.54585 1.50407C5.54578 1.50402 5.54585 1.50407 5.16675 2.00097C4.78765 1.50407 4.78778 1.50397 4.78765 1.50407L4.78608 1.50527L4.78306 1.50758L4.77277 1.51553L4.73574 1.54441C4.70401 1.56934 4.65846 1.6055 4.60108 1.65206C4.48637 1.74513 4.32404 1.88001 4.12993 2.05005C3.74259 2.38935 3.22427 2.87246 2.7039 3.44591C2.18532 4.0174 1.65267 4.69172 1.24684 5.41416C0.843805 6.13163 0.541748 6.93914 0.541748 7.76534C0.541748 10.3476 2.6024 12.4593 5.16675 12.4593C5.20492 12.4593 5.24298 12.4588 5.28095 12.4579C6.3028 13.8721 7.95994 14.7926 9.83337 14.7926C12.944 14.7926 15.4584 12.2548 15.4584 9.13315C15.4584 8.14451 15.0928 7.17018 14.5957 6.29432C14.0957 5.41336 13.4377 4.58821 12.7941 3.88609C12.1486 3.18198 11.5052 2.58833 11.0241 2.17118C10.7831 1.96221 10.5816 1.79656 10.4396 1.68247C10.3685 1.6254 10.3122 1.58117 10.2732 1.55079L10.2278 1.51574L10.2153 1.50624L10.2118 1.50353L10.2107 1.5027C10.2105 1.50259 10.21 1.50221 9.83337 2.00097L10.2107 1.5027L9.83337 1.21778L9.45673 1.50221L9.83337 2.00097C9.45673 1.50221 9.45687 1.5021 9.45673 1.50221L9.45498 1.50353L9.45142 1.50624L9.43897 1.51574L9.39358 1.55079C9.35452 1.58117 9.29823 1.6254 9.22717 1.68247C9.0851 1.79656 8.8837 1.96221 8.64269 2.17118C8.31009 2.45956 7.89994 2.83228 7.46515 3.26754ZM6.60794 4.18122C6.19429 3.73445 5.78477 3.34888 5.45709 3.05833C5.35028 2.96362 5.25257 2.87937 5.16675 2.80676C5.10205 2.8615 5.03059 2.92285 4.95357 2.99032C4.59091 3.308 4.10922 3.75736 3.62959 4.28591C3.14818 4.81644 2.68083 5.41368 2.33666 6.02636C1.98969 6.64402 1.79175 7.23522 1.79175 7.76534C1.79175 9.47088 3.00184 10.8738 4.57888 11.1571C4.33949 10.5284 4.20837 9.846 4.20837 9.13315C4.20837 8.14451 4.57399 7.17018 5.07106 6.29432C5.50257 5.53397 6.0518 4.8152 6.60794 4.18122ZM9.83337 2.80086C9.72698 2.88868 9.60155 2.99423 9.46156 3.11561C9.00518 3.51131 8.39858 4.0714 7.79408 4.73079C7.18774 5.39218 6.59573 6.14031 6.15819 6.91129C5.71775 7.68736 5.45837 8.44364 5.45837 9.13315C5.45837 11.5725 7.42117 13.5426 9.83337 13.5426C12.2456 13.5426 14.2084 11.5725 14.2084 9.13315C14.2084 8.44364 13.949 7.68736 13.5086 6.91129C13.071 6.14031 12.479 5.39218 11.8727 4.73079C11.2682 4.0714 10.6616 3.51131 10.2052 3.11561C10.0652 2.99423 9.93976 2.88868 9.83337 2.80086Z" />
);

const InkDotsIcon24x24 = () => (
  <path d="M8.33893 2.43031L7.80819 3.12597L7.27745 2.43031L7.80819 2.02539L8.33893 2.43031ZM11.2074 5.07133C10.5843 4.40288 9.97516 3.82791 9.48247 3.38885C9.14859 3.09131 8.86588 2.85412 8.66533 2.6903C8.565 2.60835 8.48509 2.54464 8.42943 2.50079C8.4016 2.47886 8.37983 2.46189 8.36459 2.45008L8.34668 2.43625L8.34151 2.43229L8.3399 2.43105C8.3399 2.43105 8.33893 2.43031 7.80819 3.12597C7.27745 2.43031 7.27745 2.43031 7.27745 2.43031L7.2752 2.43203L7.2708 2.4354L7.2557 2.44705L7.20121 2.48956C7.15446 2.52629 7.08728 2.57962 7.0026 2.64833C6.83332 2.78568 6.59364 2.98483 6.30698 3.23593C5.73491 3.73705 4.96955 4.45045 4.2013 5.29706C3.43556 6.14092 2.65012 7.13548 2.05216 8.19994C1.45812 9.25743 1.01538 10.4435 1.01538 11.6541C1.01538 15.4479 4.04259 18.5489 7.80819 18.5489C7.87305 18.5489 7.93771 18.548 8.00217 18.5461C9.50371 20.638 11.948 22.001 14.7123 22.001C19.2867 22.001 22.9846 18.2689 22.9846 13.6777C22.9846 12.2269 22.4479 10.7941 21.7148 9.50229C20.9776 8.20332 20.0067 6.9856 19.0559 5.94847C18.1026 4.90856 17.1522 4.03161 16.4414 3.4153C16.0853 3.1066 15.7878 2.86194 15.5781 2.69348C15.4732 2.60922 15.3901 2.54394 15.3325 2.49916C15.3037 2.47676 15.2813 2.45948 15.2656 2.44751L15.2474 2.43356L15.2422 2.42961L15.2406 2.4284C15.2406 2.4284 15.2396 2.42771 14.7123 3.12597L15.2396 2.42771L14.7123 2.0295L14.185 2.42771L14.7123 3.12597C14.185 2.42771 14.185 2.42771 14.185 2.42771L14.1841 2.4284L14.1825 2.42961L14.1773 2.43356L14.159 2.44751C14.1434 2.45948 14.121 2.47676 14.0921 2.49916C14.0346 2.54394 13.9515 2.60922 13.8466 2.69348C13.6368 2.86194 13.3393 3.1066 12.9833 3.4153C12.4826 3.84939 11.8631 4.41277 11.2074 5.07133ZM10.0072 6.35076C9.40369 5.69611 8.80414 5.12842 8.31818 4.69535C8.12758 4.52549 7.9553 4.3771 7.80819 4.25311C7.70399 4.34094 7.58714 4.44102 7.46008 4.55232C6.92256 5.02316 6.20848 5.6893 5.49727 6.47306C4.78356 7.25958 4.08955 8.14622 3.57791 9.05702C3.06236 9.97479 2.76538 10.858 2.76538 11.6541C2.76538 14.2323 4.61725 16.3488 7.01643 16.7357C6.64441 15.7887 6.44006 14.7569 6.44006 13.6777C6.44006 12.2269 6.97676 10.7941 7.70989 9.50229C8.35377 8.36773 9.17591 7.29516 10.0072 6.35076ZM14.7123 4.24502C14.5481 4.38018 14.3512 4.54548 14.1297 4.7375C13.4535 5.32378 12.5546 6.15373 11.6587 7.13105C10.7602 8.11117 9.88168 9.22104 9.23188 10.366C8.57802 11.5182 8.19006 12.6457 8.19006 13.6777C8.19006 17.3137 11.1158 20.251 14.7123 20.251C18.3088 20.251 21.2346 17.3137 21.2346 13.6777C21.2346 12.6457 20.8466 11.5182 20.1928 10.366C19.543 9.22104 18.6645 8.11117 17.766 7.13105C16.87 6.15373 15.9711 5.32378 15.2949 4.7375C15.0735 4.54548 14.8766 4.38018 14.7123 4.24502Z" />
);

export const InkDotsIcon = createFunctionalIcon(
  'themes_icon_label_a11y',
  InkDotsIcon16x16,
  InkDotsIcon24x24,
);

/**
 * InkDotsSolidIcon
 */

const InkDotsSolidIcon16x16 = () => (
  <>
    <path d="M10.2732 1.54982C10.3122 1.58019 10.3685 1.62442 10.4396 1.68149C10.5816 1.79559 10.7831 1.96123 11.0241 2.1702C11.5052 2.58735 12.1486 3.18101 12.7941 3.88511C13.4377 4.58723 14.0957 5.41238 14.5957 6.29334C15.0928 7.16921 15.4584 8.14353 15.4584 9.13218C15.4584 12.2538 12.944 14.7917 9.83337 14.7917C6.72273 14.7917 4.20837 12.2538 4.20837 9.13218C4.20837 8.14353 4.57399 7.16921 5.07106 6.29334C5.57102 5.41238 6.22901 4.58723 6.87267 3.88511C7.51817 3.18101 8.16157 2.58735 8.64269 2.1702C8.8837 1.96123 9.0851 1.79559 9.22717 1.68149C9.29823 1.62442 9.35452 1.58019 9.39358 1.54982L9.83337 1.2168L10.2732 1.54982Z" />
    <path d="M6.57821 2.38739C6.37312 2.59277 6.16255 2.8116 5.95139 3.04194C5.27037 3.78479 4.54641 4.68698 3.98405 5.6779C3.43037 6.65351 2.9585 7.84589 2.9585 9.1337C2.9585 10.2378 3.21681 11.2831 3.67663 12.2107C1.84892 11.5802 0.541748 9.82204 0.541748 7.76534C0.541748 6.93914 0.843805 6.13163 1.24684 5.41416C1.65267 4.69172 2.18532 4.0174 2.7039 3.44591C3.22427 2.87246 3.74259 2.38935 4.12993 2.05005C4.32404 1.88001 4.48637 1.74513 4.60108 1.65206L5.16675 1.21484L5.73241 1.65206C5.84713 1.74513 6.00945 1.88001 6.20357 2.05005C6.31811 2.15038 6.4441 2.26329 6.57821 2.38739Z" />
  </>
);

const InkDotsSolidIcon24x24 = () => (
  <>
    <path d="M15.2655 2.44536C15.2811 2.45733 15.3036 2.47461 15.3324 2.497C15.39 2.54179 15.473 2.60707 15.578 2.69133C15.7877 2.85978 16.0852 3.10445 16.4412 3.41315C17.152 4.02945 18.1025 4.9064 19.0558 5.94632C20.0066 6.98345 20.9774 8.20117 21.7146 9.50013C22.4478 10.792 22.9845 12.2248 22.9845 13.6755C22.9845 18.2667 19.2865 21.9988 14.7122 21.9988C10.1379 21.9988 6.43994 18.2667 6.43994 13.6755C6.43994 12.2248 6.97664 10.792 7.70977 9.50013C8.44695 8.20117 9.4178 6.98345 10.3686 5.94632C11.3219 4.9064 12.2724 4.02945 12.9832 3.41315C13.3392 3.10445 13.6367 2.85978 13.8464 2.69133C13.9514 2.60707 14.0344 2.54179 14.092 2.497C14.1208 2.47461 14.1433 2.45733 14.1589 2.44536L14.7122 2.02734L15.2655 2.44536Z" />
    <path d="M9.96967 3.83171C9.67774 4.12486 9.37857 4.43632 9.07863 4.7635C8.07554 5.85767 7.01234 7.18322 6.18779 8.63614C5.3754 10.0676 4.68994 11.8057 4.68994 13.6753C4.68994 15.3133 5.07987 16.8625 5.77245 18.2324C3.00873 17.353 1.01538 14.7307 1.01538 11.6521C1.01538 10.4416 1.45812 9.25548 2.05216 8.19799C2.65012 7.13352 3.43556 6.13897 4.2013 5.29511C4.96955 4.4485 5.73491 3.73509 6.30698 3.23398C6.59364 2.98288 6.83332 2.78373 7.0026 2.64638C7.08728 2.57767 7.15446 2.52434 7.20121 2.48761L7.2557 2.4451L7.80819 2.02344L8.36068 2.4451L8.41517 2.48761C8.46192 2.52434 8.5291 2.57767 8.61378 2.64638C8.78306 2.78373 9.02274 2.98288 9.30939 3.23398C9.50871 3.40857 9.73148 3.60893 9.96967 3.83171Z" />
  </>
);

export const InkDotsSolidIcon = createFunctionalIcon(
  'themes_icon_label_a11y',
  InkDotsSolidIcon16x16,
  InkDotsSolidIcon24x24,
);

/**
 * InteractionIcon
 */

const InteractionIcon16x16 = () => (
  <>
    <path d="M7.99954 0.125C7.18276 0.125 6.37276 0.270587 5.61531 0.554635C4.85781 0.838692 4.16578 1.25642 3.58008 1.78667L4.41901 2.71333C4.88389 2.29246 5.43905 1.95573 6.05421 1.72505C6.6694 1.49435 7.33059 1.375 7.99954 1.375C8.6685 1.375 9.32968 1.49435 9.94489 1.72505C10.56 1.95573 11.1152 2.29246 11.5801 2.71333L12.419 1.78667C11.8333 1.25642 11.1413 0.838694 10.3838 0.554636C9.62633 0.270589 8.81633 0.125 7.99954 0.125Z" />
    <path d="M7.73263 3.25C6.64098 3.25 5.75601 4.13496 5.75601 5.22662V10H3.41283C3.19607 10 3.01382 10.0663 2.87108 10.1727C2.72769 10.2797 2.62275 10.4286 2.56177 10.595C2.43889 10.9307 2.49729 11.326 2.75532 11.6002L5.46482 14.5832C5.48558 14.6061 5.4971 14.6357 5.49736 14.6663L5.50014 15.0051L6.75009 14.9949L6.74731 14.6561C6.74454 14.3181 6.61729 13.993 6.39018 13.7429L4.1259 11.25H7.00601V5.22662C7.00601 4.82532 7.33133 4.5 7.73263 4.5C8.13395 4.5 8.45926 4.82532 8.45926 5.22662V8.75706L11.1528 9.77459C11.8197 10.0265 13 10.7776 13 12.2555V15H14.25V12.2555C14.25 9.98477 12.4294 8.9206 11.5945 8.60523L9.70926 7.89305V5.22662C9.70926 4.13496 8.8243 3.25 7.73263 3.25Z" />
  </>
);

const InteractionIcon24x24 = () => (
  <>
    <path d="M12.0002 0.125C10.8347 0.125 9.68063 0.354559 8.60387 0.80057C7.5271 1.24658 6.54873 1.90031 5.72461 2.72443L6.96205 3.96187C7.62366 3.30025 8.40912 2.77542 9.27356 2.41736C10.138 2.05929 11.0645 1.875 12.0002 1.875C12.9359 1.875 13.8624 2.05929 14.7268 2.41736C15.5912 2.77542 16.3767 3.30025 17.0383 3.96186L18.2758 2.72443C17.4516 1.90031 16.4733 1.24658 15.3965 0.800569C14.3197 0.354558 13.1657 0.125 12.0002 0.125Z" />
    <path d="M11.7272 3.875C10.0402 3.875 8.67267 5.24257 8.67267 6.92956V14H5.20588C3.92707 14 3.44698 15.5311 4.24571 16.3452L8.6369 20.8211C8.65983 20.8445 8.67267 20.8759 8.67267 20.9086V21H10.4227V20.9086C10.4227 20.4176 10.23 19.9461 9.8861 19.5955L6.11334 15.75H10.4227V6.92956C10.4227 6.20907 11.0067 5.625 11.7272 5.625C12.4477 5.625 13.0318 6.20907 13.0318 6.92956V11.858L16.9608 13.3201C17.9706 13.6958 19.75 14.8132 19.75 17V21H21.5V17C21.5 13.6868 18.8007 12.1375 17.5711 11.6799L14.7818 10.642V6.92956C14.7818 5.24257 13.4142 3.875 11.7272 3.875Z" />
  </>
);

export const InteractionIcon = createFunctionalIcon(
  'interactivity_icon_label_a11y',
  InteractionIcon16x16,
  InteractionIcon24x24,
);

/**
 * InteractionSolidIcon
 */

const InteractionSolidIcon16x16 = () => (
  <>
    <path d="M8.00003 0.125C7.18325 0.125 6.37325 0.270587 5.6158 0.554632C4.85831 0.83869 4.16626 1.25642 3.58057 1.78667L4.4195 2.71333C4.88438 2.29246 5.43955 1.95573 6.0547 1.72504C6.66989 1.49435 7.33108 1.375 8.00003 1.375C8.66899 1.375 9.33018 1.49435 9.94536 1.72504C10.5605 1.95572 11.1157 2.29246 11.5806 2.71333L12.4195 1.78667C11.8338 1.25642 11.1418 0.838689 10.3843 0.554631C9.62681 0.270586 8.81681 0.125 8.00003 0.125Z" />
    <path d="M6.00593 14.9373V15H14V12.2555C14 9.98221 12.1755 8.91916 11.3444 8.60521L9.45916 7.89304V4.85559L9.45728 4.8317C9.43581 4.55912 9.32707 4.17845 9.06165 3.85327C8.77754 3.50519 8.33534 3.25 7.73255 3.25C7.12975 3.25 6.68755 3.50519 6.40345 3.85327C6.13802 4.17845 6.02929 4.55912 6.00781 4.8317L6.00593 4.85559V10H3.66274C2.79839 10 2.4739 11.0505 3.01376 11.6091L5.98175 14.8772C5.99725 14.8933 6.00593 14.9148 6.00593 14.9373Z" />
  </>
);

const InteractionSolidIcon24x24 = () => (
  <>
    <path d="M11.9999 0.125C10.8345 0.125 9.68039 0.354559 8.60362 0.80057C7.52686 1.24658 6.54849 1.90031 5.72437 2.72443L6.9618 3.96187C7.62342 3.30025 8.40887 2.77542 9.27332 2.41736C10.1378 2.05929 11.0643 1.875 11.9999 1.875C12.9356 1.875 13.8621 2.05929 14.7266 2.41736C15.591 2.77542 16.3765 3.30025 17.0381 3.96186L18.2755 2.72443C17.4514 1.90031 16.473 1.24658 15.3963 0.800569C14.3195 0.354558 13.1654 0.125 11.9999 0.125Z" />
    <path d="M9.17267 20.9086V21H21V17C21 13.6868 18.3007 12.1375 17.0711 11.6799L14.2818 10.642V6.21506L14.279 6.18024C14.2472 5.78297 14.0864 5.22817 13.6937 4.75423C13.2733 4.24693 12.6191 3.875 11.7272 3.875C10.8354 3.875 10.1811 4.24693 9.76081 4.75423C9.36811 5.22817 9.20723 5.78297 9.17546 6.18024L9.17267 6.21507V14H5.70588C4.42707 14 3.94698 15.5311 4.74571 16.3452L9.1369 20.8211C9.15983 20.8445 9.17267 20.8759 9.17267 20.9086Z" />
  </>
);

export const InteractionSolidIcon = createFunctionalIcon(
  'interactivity_icon_label_a11y',
  InteractionSolidIcon16x16,
  InteractionSolidIcon24x24,
);

/**
 * IntersectIcon
 */

const IntersectIcon16x16 = () => (
  <path d="M5 1H15V11H11V15H1V5H5V1ZM5 6.25H2.25V13.75H9.75V11H5V6.25ZM11 9.75V5H6.25V2.25H13.75V9.75H11Z" />
);

const IntersectIcon24x24 = () => (
  <path d="M9 3H21V15H15V21H3V9H9V3ZM9 10.75H4.75V19.25H13.25V15H9V10.75ZM15 13.25V9H10.75V4.75H19.25V13.25H15Z" />
);

export const IntersectIcon = createFunctionalIcon(
  'intergrations_icon_label_a11y',
  IntersectIcon16x16,
  IntersectIcon24x24,
);

/**
 * InvoiceIcon
 */

const InvoiceIcon16x16 = () => (
  <path d="M2 1H14V15.2744L10.6875 14.0419L8 15.0419L5.3125 14.0419L2 15.2744V1ZM3.25 2.25V13.4756L5.3125 12.7081L8 13.7081L10.6875 12.7081L12.75 13.4756V2.25H3.25ZM11 5.375H5V4.125H11V5.375ZM9 7.625H5V6.375H9V7.625ZM10 9.875H5V8.625H10V9.875Z" />
);

const InvoiceIcon24x24 = () => (
  <path d="M4.125 2.125H19.875V22.1145L15.5625 20.904L12 21.904L8.4375 20.904L4.125 22.1145V2.125ZM5.625 3.625V20.1355L8.4375 19.346L12 20.346L15.5625 19.346L18.375 20.1355V3.625H5.625ZM16 9H8V7.5H16V9ZM13 12H8V10.5H13V12ZM14 15H8V13.5H14V15Z" />
);

export const InvoiceIcon = createFunctionalIcon(
  'invoice_icon_label_a11y',
  InvoiceIcon16x16,
  InvoiceIcon24x24,
);

/**
 * ItalicIcon
 */

const ItalicIcon16x16 = () => (
  <path d="M5.625 2L12 2V3.25L9.4206 3.25L7.72417 12.75H10V14H4V12.75H6.4544L8.15083 3.25L5.625 3.25V2Z" />
);

const ItalicIcon24x24 = () => (
  <path d="M12.6908 5.375L9.65217 5.375V3.625L18 3.625V5.375L14.5005 5.375L11.0103 18.625H14.3478V20.375L6 20.375V18.625H9.20063L12.6908 5.375Z" />
);

export const ItalicIcon = createFunctionalIcon(
  'italic_icon_label_a11y',
  ItalicIcon16x16,
  ItalicIcon24x24,
);

/**
 * KeyIcon
 */

const KeyIcon16x16 = () => (
  <>
    <path d="M4.59473 12.3223C5.21605 12.3223 5.71973 11.8186 5.71973 11.1973C5.71973 10.5759 5.21605 10.0723 4.59473 10.0723C3.97341 10.0723 3.46973 10.5759 3.46973 11.1973C3.46973 11.8186 3.97341 12.3223 4.59473 12.3223Z" />
    <path d="M15.5719 3.01862L13.5376 0.984375L7.87393 6.66107C7.15045 6.17689 6.28048 5.89453 5.34456 5.89453C2.82971 5.89453 0.791016 7.93322 0.791016 10.4481C0.791016 12.9629 2.82971 15.0016 5.34456 15.0016C7.85941 15.0016 9.89811 12.9629 9.89811 10.4481C9.89811 9.32046 9.48824 8.28859 8.80936 7.49329L11.4225 4.87421L12.573 6.02479L13.4569 5.1409L12.3053 3.98931L13.5387 2.75315L14.688 3.9025L15.5719 3.01862ZM2.04102 10.4481C2.04102 8.62358 3.52006 7.14453 5.34456 7.14453C7.16906 7.14453 8.64811 8.62358 8.64811 10.4481C8.64811 12.2726 7.16906 13.7516 5.34456 13.7516C3.52006 13.7516 2.04102 12.2726 2.04102 10.4481Z" />
  </>
);

const KeyIcon24x24 = () => (
  <>
    <path d="M8.75 16.375C8.75 17.1344 8.13439 17.75 7.375 17.75C6.61561 17.75 6 17.1344 6 16.375C6 15.6156 6.61561 15 7.375 15C8.13439 15 8.75 15.6156 8.75 16.375Z" />
    <path d="M13.184 11.3031C14.0831 12.3691 14.625 13.7463 14.625 15.25C14.625 18.6327 11.8827 21.375 8.5 21.375C5.11726 21.375 2.375 18.6327 2.375 15.25C2.375 11.8673 5.11726 9.125 8.5 9.125C9.74698 9.125 10.9069 9.49764 11.8746 10.1376L19.5063 2.50586L20.7438 3.7433L20.7375 3.74958L22.2438 5.25586L21.0063 6.4933L19.5001 4.98701L17.8625 6.62458L19.3688 8.13086L18.1313 9.3683L16.6251 7.86201L13.184 11.3031ZM8.5 10.875C6.08375 10.875 4.125 12.8338 4.125 15.25C4.125 17.6662 6.08375 19.625 8.5 19.625C10.9162 19.625 12.875 17.6662 12.875 15.25C12.875 12.8338 10.9162 10.875 8.5 10.875Z" />
  </>
);

export const KeyIcon = createFunctionalIcon(
  'key_icon_label_a11y',
  KeyIcon16x16,
  KeyIcon24x24,
);

/**
 * KeySolidIcon
 */

const KeySolidIcon16x16 = () => (
  <path d="M13.5372 0.984375L15.5714 3.01862L14.6875 3.9025L13.5382 2.75315L12.3041 3.99002L13.4562 5.14206L12.5723 6.02595L11.4213 4.87491L8.80925 7.49292C9.48819 8.28823 9.89811 9.32016 9.89811 10.4478C9.89811 12.9627 7.85941 15.0014 5.34456 15.0014C2.82971 15.0014 0.791016 12.9627 0.791016 10.4478C0.791016 7.93297 2.82971 5.89429 5.34456 5.89429C6.28042 5.89429 7.15033 6.17661 7.87379 6.66073L13.5372 0.984375ZM5.71948 11.1978C5.71948 11.8191 5.2158 12.3228 4.59448 12.3228C3.97316 12.3228 3.46948 11.8191 3.46948 11.1978C3.46948 10.5765 3.97316 10.0728 4.59448 10.0728C5.2158 10.0728 5.71948 10.5765 5.71948 11.1978Z" />
);

const KeySolidIcon24x24 = () => (
  <path d="M11.8748 10.1374L19.5063 2.50586L20.7438 3.7433L20.7375 3.74958L22.2438 5.25586L21.0063 6.4933L19.5001 4.98701L17.8625 6.62458L19.3688 8.13086L18.1313 9.3683L16.6251 7.86201L13.1842 11.3029C14.0832 12.3689 14.625 13.7459 14.625 15.2496C14.625 18.6323 11.8827 21.3746 8.5 21.3746C5.11726 21.3746 2.375 18.6323 2.375 15.2496C2.375 11.8668 5.11726 9.12457 8.5 9.12457C9.7471 9.12457 10.9071 9.49728 11.8748 10.1374ZM5.5 16.8746C5.5 16.1152 6.11561 15.4996 6.875 15.4996C7.63439 15.4996 8.25 16.1152 8.25 16.8746C8.25 17.634 7.63439 18.2496 6.875 18.2496C6.11561 18.2496 5.5 17.634 5.5 16.8746Z" />
);

export const KeySolidIcon = createFunctionalIcon(
  'key_icon_label_a11y',
  KeySolidIcon16x16,
  KeySolidIcon24x24,
);

/**
 * LayersIcon
 */

const LayersIcon16x16 = () => (
  <path d="M4 9H1V2H9V5H12V8H15V15H7V12H4V9ZM4 4.84375V7.75H2.25V3.25H7.75V4.84375H4ZM5.25 6.25V10.75H7V7.84375H10.75V6.25H5.25ZM8.25 9.25H13.75V13.75H8.25V9.25Z" />
);

const LayersIcon24x24 = () => (
  <path d="M2 3H14V7H18V11H22V21H10V17H6V13H2V3ZM3.5 4.5V11.5H6V7H12.5V4.5H3.5ZM7.5 8.5V15.5H10V11H16.5V8.5H7.5ZM11.5 12.5V19.5H20.5V12.5H11.5Z" />
);

export const LayersIcon = createFunctionalIcon(
  'layers_icon_label_a11y', // Todo: Should this be "Stack", as the StackIcon in Icon.tsx?
  LayersIcon16x16,
  LayersIcon24x24,
);

/**
 * LanguageIcon
 */

const LanguageIcon16x16 = () => (
  <path d="M6 1.875H4V0.625H6V1.875ZM6.48633 4.25H1V3H9.99992V4.25H7.87082L7.10136 5.86586L5.71777 7.24944L7.93353 9.38313L7.06647 10.2835L4.83373 8.13349L2.69194 10.2753L1.80806 9.39139L3.93317 7.26628L2.56647 5.9502L3.43353 5.0498L4.81721 6.38224L6.06531 5.13414L6.48633 4.25ZM10.525 7H12.225L15.0843 14.5281L13.9157 14.9719L13.0243 12.625H9.72567L8.83428 14.9719L7.66572 14.5281L10.525 7ZM10.2004 11.375H12.5496L11.375 8.28255L10.2004 11.375Z" />
);

const LanguageIcon24x24 = () => (
  <path d="M9 2.75H6V1H9V2.75ZM9.92372 5.75H2V4H15V5.75H11.862L10.7252 8.1372L8.61243 10.25L11.9819 13.4947L10.7681 14.7553L7.37477 11.4877L4.11872 14.7437L2.88128 13.5063L6.11399 10.2736L4.01806 8.25528L5.23194 6.99472L7.35164 9.03592L9.27476 7.1128L9.92372 5.75ZM15.8254 11H18.1746L22.0686 21.316L20.4314 21.934L19.1823 18.625H14.8177L13.5686 21.934L11.9314 21.316L15.8254 11ZM15.4783 16.875H18.5217L17 12.8437L15.4783 16.875Z" />
);

export const LanguageIcon = createFunctionalIcon(
  'language_icon_label_a11y',
  LanguageIcon16x16,
  LanguageIcon24x24,
);

/**
 * LightbulbIcon
 */

const LightbulbIcon16x16 = () => (
  <path d="M7.99996 2.25C5.63507 2.25 3.74658 4.11243 3.74658 6.375C3.74658 7.91488 4.61775 9.26602 5.92354 9.97599L6.25 10.1535V11.7495H9.75V10.1535L10.0764 9.97596C11.3822 9.26597 12.2533 7.91486 12.2533 6.375C12.2533 4.11243 10.3649 2.25 7.99996 2.25ZM2.49658 6.375C2.49658 3.39086 4.97635 1 7.99996 1C11.0236 1 13.5033 3.39086 13.5033 6.375C13.5033 8.26867 12.5016 9.92636 11 10.8818V12.9995H5V10.8819C3.49832 9.92642 2.49658 8.2687 2.49658 6.375ZM10.25 15L5.75 15L5.75 13.75L10.25 13.75V15Z" />
);

const LightbulbIcon24x24 = () => (
  <path d="M12 3.625C8.61726 3.625 5.875 6.36726 5.875 9.75C5.875 12.1488 7.25382 14.2271 9.26615 15.2327L9.75 15.4745V17.375H14.25V15.4745L14.7339 15.2327C16.7462 14.2271 18.125 12.1488 18.125 9.75C18.125 6.36726 15.3827 3.625 12 3.625ZM4.125 9.75C4.125 5.40076 7.65076 1.875 12 1.875C16.3492 1.875 19.875 5.40076 19.875 9.75C19.875 12.6402 18.3179 15.1654 16 16.5347V19.125H8V16.5347C5.68215 15.1654 4.125 12.6402 4.125 9.75ZM15 20.25V22H9V20.25H15Z" />
);

export const LightbulbIcon = createFunctionalIcon(
  'examples_icon_label_a11y',
  LightbulbIcon16x16,
  LightbulbIcon24x24,
);

/**
 * LinkIcon
 */

const LinkIcon16x16 = () => (
  <path d="M7.82252 2.33501C9.43587 0.721661 12.0516 0.721663 13.665 2.33502L13.223 2.77696L13.665 2.33502C15.2783 3.94837 15.2783 6.56413 13.665 8.17748L12.0094 9.8331L11.1255 8.94921L12.7811 7.2936C13.9063 6.1684 13.9063 4.3441 12.7811 3.2189C11.6559 2.0937 9.8316 2.0937 8.7064 3.2189L7.00875 4.91655L6.12487 4.03267L7.82252 2.33501ZM10.6919 6.19194L6.19194 10.6919L5.30806 9.80806L9.80806 5.30806L10.6919 6.19194ZM4.86641 7.05889L3.2189 8.7064C2.0937 9.8316 2.0937 11.6559 3.2189 12.7811C4.3441 13.9063 6.1684 13.9063 7.2936 12.7811L8.99184 11.0829L9.87572 11.9667L8.17748 13.665C6.56413 15.2783 3.94837 15.2783 2.33501 13.665C0.721661 12.0516 0.721663 9.43587 2.33502 7.82252L3.98253 6.17501L4.86641 7.05889Z" />
);

const LinkIcon24x24 = () => (
  <path d="M11.8059 4.34322C13.9927 2.15643 17.5382 2.15643 19.725 4.34322C21.9118 6.53002 21.9118 10.0755 19.725 12.2623L17.4941 14.4932L16.2567 13.2558L18.4876 11.0249C19.991 9.52149 19.991 7.08404 18.4876 5.58066C16.9842 4.07728 14.5468 4.07728 13.0434 5.58066L10.7558 7.86823L9.51837 6.63079L11.8059 4.34322ZM15.6187 9.61872L9.61872 15.6187L8.38128 14.3813L14.3813 8.38128L15.6187 9.61872ZM7.86902 10.755L5.64902 12.975C4.14564 14.4784 4.14564 16.9159 5.64902 18.4192C7.15239 19.9226 9.58985 19.9226 11.0932 18.4192L13.3816 16.1309L14.619 17.3683L12.3307 19.6567C10.1439 21.8435 6.59837 21.8435 4.41158 19.6567C2.22478 17.4699 2.22479 13.9244 4.41158 11.7376L6.63159 9.51758L7.86902 10.755Z" />
);

export const LinkIcon = createFunctionalIcon(
  'link_icon_label_a11y',
  LinkIcon16x16,
  LinkIcon24x24,
);

/**
 * ListIcon
 */

const ListIcon16x16 = () => (
  <path d="M15 2.625H1V1.375H15V2.625ZM15 5.625H1V4.375H15V5.625ZM15 8.625H1V7.375H15V8.625ZM15 11.625H1V10.375H15V11.625ZM15 14.625H1V13.375H15V14.625Z" />
);

const ListIcon24x24 = () => (
  <path d="M21 4.75H3V3H21V4.75ZM21 8.75H3V7H21V8.75ZM21 12.875H3V11.125H21V12.875ZM21 17H3V15.25H21V17ZM21 21H3V19.25H21V21Z" />
);

export const ListIcon = createFunctionalIcon(
  'list_icon_label_a11y',
  ListIcon16x16,
  ListIcon24x24,
);

/**
 * LoaderIcon
 */

const LoaderIcon16x16 = () => (
  <>
    <path d="M15 8C15 8.82843 14.3284 9.5 13.5 9.5C12.6716 9.5 12 8.82843 12 8C12 7.17157 12.6716 6.5 13.5 6.5C14.3284 6.5 15 7.17157 15 8Z" />
    <path d="M4 8C4 8.82843 3.32843 9.5 2.5 9.5C1.67157 9.5 1 8.82843 1 8C1 7.17157 1.67157 6.5 2.5 6.5C3.32843 6.5 4 7.17157 4 8Z" />
    <path d="M9.5 13.5C9.5 14.3284 8.82843 15 8 15C7.17157 15 6.5 14.3284 6.5 13.5C6.5 12.6716 7.17157 12 8 12C8.82843 12 9.5 12.6716 9.5 13.5Z" />
    <path d="M9.5 2.5C9.5 3.32843 8.82843 4 8 4C7.17157 4 6.5 3.32843 6.5 2.5C6.5 1.67157 7.17157 1 8 1C8.82843 1 9.5 1.67157 9.5 2.5Z" />
  </>
);

const LoaderIcon24x24 = () => (
  <>
    <path d="M21 12C21 13.1046 20.1046 14 19 14C17.8954 14 17 13.1046 17 12C17 10.8954 17.8954 10 19 10C20.1046 10 21 10.8954 21 12Z" />
    <path d="M14 19C14 20.1046 13.1046 21 12 21C10.8954 21 10 20.1046 10 19C10 17.8954 10.8954 17 12 17C13.1046 17 14 17.8954 14 19Z" />
    <path d="M7 12C7 13.1046 6.10457 14 5 14C3.89543 14 3 13.1046 3 12C3 10.8954 3.89543 10 5 10C6.10457 10 7 10.8954 7 12Z" />
    <path d="M14 5C14 6.10457 13.1046 7 12 7C10.8954 7 10 6.10457 10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5Z" />
  </>
);

const LoaderAnimation = ({
  children,
  radius,
}: {
  children: React.ReactNode;
  radius: number;
}) => (
  <g data-testid="loading-svg">
    <animateTransform
      attributeName="transform"
      attributeType="XML"
      type="rotate"
      from={`0 ${radius} ${radius}`}
      to={`360 ${radius} ${radius}`}
      dur="800ms"
      repeatCount="indefinite"
    />
    {children}
  </g>
);

const createAnimatedLoaderIcon =
  (IconToAnimate: React.ComponentType, radius: number) => () => (
    <LoaderAnimation radius={radius}>
      <IconToAnimate />
    </LoaderAnimation>
  );

export const LoaderIcon = createFunctionalIcon(
  'loading_icon_label_a11y',
  createAnimatedLoaderIcon(LoaderIcon16x16, 8),
  createAnimatedLoaderIcon(LoaderIcon24x24, 12),
);

/**
 * LockIcon
 */

const LockIcon16x16 = () => (
  <path d="M8 2.375C6.52086 2.375 5.34375 3.54646 5.34375 4.96469V7H10.6562V4.96469C10.6562 3.54646 9.47914 2.375 8 2.375ZM4.09375 4.96469C4.09375 2.83208 5.85477 1.125 8 1.125C10.1452 1.125 11.9062 2.83208 11.9062 4.96469V7H14V15H2V7H4.09375V4.96469ZM3.25 8.25V13.75H12.75V8.25H3.25Z" />
);

const LockIcon24x24 = () => (
  <path d="M12 3.625C10.1191 3.625 8.625 5.11427 8.625 6.9138V10H15.375V6.9138C15.375 5.11427 13.8809 3.625 12 3.625ZM6.875 6.9138C6.875 4.11413 9.18654 1.875 12 1.875C14.8135 1.875 17.125 4.11413 17.125 6.9138V10H21V22H3V10H6.875V6.9138ZM4.75 11.75V20.25H19.25V11.75H4.75Z" />
);

export const LockIcon = createFunctionalIcon(
  'locked_icon_label_a11y',
  LockIcon16x16,
  LockIcon24x24,
);

/**
 * LockSolidIcon
 */

const LockSolidIcon16x16 = () => (
  <>
    <path d="M5.44261 4.96389C5.44261 3.60212 6.57356 2.473 8.00005 2.473C9.42654 2.473 10.5575 3.60212 10.5575 4.96389V6.9992H12.0051L12.0051 4.96389C12.0051 2.77481 10.1979 1.02539 8.00005 1.02539C5.80217 1.02539 3.995 2.77481 3.995 4.96389V6.9992H5.44261V4.96389Z" />
    <path d="M14 7H2V15H14V7Z" />
  </>
);

const LockSolidIcon24x24 = () => (
  <path d="M7 10H3V22H21V10H17V6.91379C17 4.18557 14.7469 2 12 2C9.25314 2 7 4.18557 7 6.91379V10ZM8.5 6.91379C8.5 5.04282 10.0524 3.5 12 3.5C13.9476 3.5 15.5 5.04282 15.5 6.91379V10H8.5V6.91379Z" />
);

export const LockSolidIcon = createFunctionalIcon(
  'locked_icon_label_a11y',
  LockSolidIcon16x16,
  LockSolidIcon24x24,
);

/**
 * LogOutIcon
 */

const LogOutIcon16x16 = () => (
  <path d="M5.625 2.125C3.76105 2.125 2.25001 3.63604 2.25001 5.5L2.25 10.25C2.25 12.1191 3.76912 13.6322 5.63808 13.625C5.63857 13.625 5.63906 13.625 5.63955 13.625L6.01154 13.6215L6.02326 14.8714L5.6481 14.8749L5.64493 14.8749C3.08285 14.886 0.999998 12.8121 1 10.25L1 5.5C1.00001 2.94568 3.07069 0.875 5.625 0.875H6.0174V2.125H5.625ZM10.692 3.55806L15.1339 8L10.692 12.4419L9.80812 11.5581L12.7412 8.625H4.00006V7.375H12.7412L9.80812 4.44194L10.692 3.55806Z" />
);

const LogOutIcon24x24 = () => (
  <path d="M8.875 3.75C6.04454 3.75 3.75 6.04454 3.75 8.87499V15.1251C3.75 17.9641 6.05802 20.2622 8.89709 20.25L8.90086 20.25L9.99998 20.25V22H8.90281C5.09514 22.0154 2 18.933 2 15.1251V8.87499C2 5.07804 5.07805 2 8.875 2H9.99999V3.75H8.875ZM16 5.76256L22.2375 12L16 18.2374L14.7626 17L18.8876 12.875H6.00003V11.125H18.8876L14.7626 7L16 5.76256Z" />
);

export const LogOutIcon = createFunctionalIcon(
  'log_out_icon_label_a11y',
  LogOutIcon16x16,
  LogOutIcon24x24,
);

/**
 * MailIcon
 */

const MailIcon16x16 = () => (
  <path d="M1 2H15V14H1V2ZM3.06175 3.25L8 8.54916L12.9383 3.25H3.06175ZM13.75 4.21243L8 10.3827L2.25 4.21243V12.75H13.75V4.21243Z" />
);

const MailIcon24x24 = () => (
  <path d="M2 4H22V20H2V4ZM3.75 6.98744V18.25H20.25V6.98744L12 15.2374L3.75 6.98744ZM19.0126 5.75H4.98744L12 12.7626L19.0126 5.75Z" />
);

export const MailIcon = createFunctionalIcon(
  'email_icon_label_a11y',
  MailIcon16x16,
  MailIcon24x24,
);

/**
 * MapIcon
 */

const MapIcon16x16 = () => (
  <path d="M8 2.375C6.13865 2.375 4.625 3.89135 4.625 5.76767C4.625 6.12528 4.80937 6.82161 5.23625 7.81477L5.23626 7.81478C5.64045 8.75521 6.19023 9.80242 6.75685 10.7938C7.19009 11.5518 7.62568 12.2642 8 12.8533C8.37432 12.2642 8.80991 11.5518 9.24315 10.7938C9.80977 9.80242 10.3595 8.75521 10.7637 7.81478L10.7637 7.81477C11.1906 6.82161 11.375 6.12528 11.375 5.76767C11.375 3.89135 9.86135 2.375 8 2.375ZM3.375 5.76767C3.375 3.2062 5.4431 1.125 8 1.125C10.5569 1.125 12.625 3.2062 12.625 5.76767C12.625 6.40322 12.3431 7.30574 11.9122 8.30838L11.3379 8.06157L11.9122 8.30838C11.482 9.3091 10.9071 10.4016 10.3284 11.4141C9.74984 12.4264 9.16816 13.3577 8.72513 14.0358L8.7251 14.0359C8.65275 14.1466 8.5842 14.2505 8.51999 14.3468L8 15.1265L7.48001 14.3468C7.4158 14.2505 7.34725 14.1466 7.2749 14.0359L7.27487 14.0358C6.83184 13.3577 6.25016 12.4264 5.6716 11.4141C5.09292 10.4016 4.51795 9.30909 4.08785 8.30838C3.65688 7.30574 3.375 6.40322 3.375 5.76767ZM6.75 5.75C6.75 5.05965 7.30965 4.5 8 4.5C8.69035 4.5 9.25 5.05965 9.25 5.75C9.25 6.44035 8.69035 7 8 7C7.30965 7 6.75 6.44035 6.75 5.75ZM4.66241 8.06142L4.66246 8.0614L4.66241 8.06142Z" />
);

const MapIcon24x24 = () => (
  <>
    <path d="M13.8002 8.5002C13.8002 9.49431 12.9943 10.3002 12.0002 10.3002C11.0061 10.3002 10.2002 9.49431 10.2002 8.5002C10.2002 7.50608 11.0061 6.7002 12.0002 6.7002C12.9943 6.7002 13.8002 7.50608 13.8002 8.5002Z" />
    <path d="M11 19.1L12 20.6L13 19.1C13.4 18.5 14.2 17.3 15.1 15.8C15.9 14.3 16.6 12.9 17.1 11.8C17.7 10.4 18 9.3 18 8.5C18 5.2 15.3 2.5 12 2.5C8.7 2.5 6 5.2 6 8.5C6 9.3 6.3 10.4 6.9 11.8C7.4 13 8 14.3 8.9 15.8C9.79998 17.3 10.6 18.5 11 19.1ZM7.8 8.4C7.8 6.1 9.7 4.2 12 4.2C14.3 4.2 16.2 6.2 16.2 8.4C16.2 8.7 16.1 9.4 15.4 11C15 12.1 14.3 13.4 13.5 14.8C13 15.8 12.4 16.7 12 17.3C11.7417 16.9125 11.4417 16.4 11.1 15.8162C10.9126 15.496 10.7126 15.1543 10.5 14.8C9.7 13.4 9.1 12.1 8.6 11C7.9 9.4 7.8 8.6 7.8 8.4Z" />
  </>
);

export const MapIcon = createFunctionalIcon(
  'map_icon_label_a11y',
  MapIcon16x16,
  MapIcon24x24,
);

/**
 * MaximizeIcon
 */

const MaximizeIcon16x16 = () => (
  <path d="M11.8661 3.25H9V2H14V7H12.75V4.13388L9.44194 7.44194L8.55806 6.55806L11.8661 3.25ZM3.25 11.8661L6.55808 8.55806L7.44196 9.44194L4.13389 12.75H7V14H2L2 9H3.25V11.8661Z" />
);

const MaximizeIcon24x24 = () => (
  <path d="M18.0126 4.75H13.875V3H21L21 10.125L19.25 10.125L19.25 5.98744L14.1187 11.1187L12.8813 9.88128L18.0126 4.75ZM4.75 18.0126L9.88128 12.8813L11.1187 14.1187L5.98744 19.25H10.125V21H3L3 13.875H4.75L4.75 18.0126Z" />
);

export const MaximizeIcon = createFunctionalIcon(
  'expand_icon_label_a11y',
  MaximizeIcon16x16,
  MaximizeIcon24x24,
);

/**
 * MentiIcon
 */

const MentiIcon16x16 = () => (
  <>
    <path d="M11.697 2V4.39943H9.29755V6.79903H7.13796V8.8598C8.41864 10.1121 9.18452 11.7982 9.28504 13.5866V13.7628H9.05164C8.92726 10.0343 5.85729 7.03976 2.09909 7.03976V10.3996C4.00616 10.3996 5.56616 11.8829 5.68948 13.7594H5.44875C5.32525 12.0188 3.87051 10.6402 2.09909 10.6402V14H14.0964V2H11.697Z" />
    <path d="M6.89795 6.79903L4.49852 2H2.09909V6.79903C3.8706 6.79693 5.58013 7.4509 6.89795 8.63479V6.79903Z" />
    <path d="M15.2962 13.7461H0.899414V13.9975H15.2962V13.7461Z" />
  </>
);

const MentiIcon24x24 = () => (
  <>
    <path d="M16.799 3.91797V7.11721H13.5998V10.3167H10.7203V13.0644C12.4279 14.7341 13.4491 16.9823 13.5831 19.3668V19.6018H13.2719C13.1061 14.6304 9.01276 10.6377 4.00183 10.6377V15.1174C6.54459 15.1174 8.62459 17.0952 8.78901 19.5972H8.46804C8.30338 17.2763 6.36373 15.4382 4.00183 15.4382V19.918H19.9983V3.91797H16.799Z" />
    <path d="M10.4003 10.3167L7.20107 3.91797H4.00183V10.3167C6.36384 10.3139 8.64321 11.1858 10.4003 12.7643V10.3167Z" />
    <path d="M21.598 19.5781H2.40234V19.9134H21.598V19.5781Z" />
  </>
);

export const MentiIcon = createFunctionalIcon(
  'menti_icon_label_a11y',
  MentiIcon16x16,
  MentiIcon24x24,
);

/**
 * MenuIcon
 */

const MenuIcon16x16 = () => (
  <path d="M15 3.625H1V2.375H15V3.625ZM15 8.625L1 8.625V7.375L15 7.375V8.625ZM15 13.625H1V12.375H15V13.625Z" />
);

const MenuIcon24x24 = () => (
  <path d="M21 6.875H3V5.125H21V6.875ZM21 12.875L3 12.875V11.125L21 11.125V12.875ZM21 18.875H3V17.125H21V18.875Z" />
);

export const MenuIcon = createFunctionalIcon(
  'menu_icon_label_a11y',
  MenuIcon16x16,
  MenuIcon24x24,
);

/**
 * MessageCircleIcon
 */

const MessageCircleIcon16x16 = () => (
  <path d="M8 2.25C4.82438 2.25 2.25 4.82437 2.25 8V13.75H8C11.1756 13.75 13.75 11.1756 13.75 8C13.75 4.82437 11.1756 2.25 8 2.25ZM1 8C1 4.13401 4.13402 1 8 1C11.866 1 15 4.13401 15 8C15 11.866 11.866 15 8 15H1V8Z" />
);

const MessageCircleIcon24x24 = () => (
  <path d="M12 4.75C7.99595 4.75 4.75 7.99595 4.75 12V19.25H12C16.0041 19.25 19.25 16.0041 19.25 12C19.25 7.99595 16.0041 4.75 12 4.75ZM3 12C3 7.02945 7.02945 3 12 3C16.9705 3 21 7.02945 21 12C21 16.9705 16.9705 21 12 21H3V12Z" />
);

export const MessageCircleIcon = createFunctionalIcon(
  'chat_bubble_icon_label_a11y',
  MessageCircleIcon16x16,
  MessageCircleIcon24x24,
);

/**
 * MessageCircleSolidIcon
 */
const MessageCircleSolidIcon16x16 = () => (
  <path d="M15 8C15 11.866 11.866 15 8 15C6.43993 15 1 15 1 15C1 15 1 10.3059 1 8C1 4.13401 4.13402 1 8 1C11.866 1 15 4.13401 15 8Z" />
);

const MessageCircleSolidIcon24x24 = () => (
  <path d="M21 12C21 16.9706 16.9705 21 12 21C9.99419 21 3 21 3 21C3 21 3 14.9647 3 12C3 7.02944 7.02946 3 12 3C16.9705 3 21 7.02944 21 12Z" />
);

export const MessageCircleSolidIcon = createFunctionalIcon(
  'chat_bubble_icon_label_a11y',
  MessageCircleSolidIcon16x16,
  MessageCircleSolidIcon24x24,
);

/**
 * MessageCircleOffIcon
 */
const MessageCircleOffIcon16x16 = () => (
  <path d="M14.4697 15.5294L0.469727 1.52941L1.53039 0.46875L3.52011 2.45847C4.74364 1.46813 6.303 0.87408 8.00006 0.87408C11.9351 0.87408 15.1251 4.06406 15.1251 7.99908C15.1251 9.69612 14.531 11.2555 13.5407 12.479L15.5304 14.4688L14.4697 15.5294ZM12.4726 11.411L4.58817 3.52654C5.5348 2.80309 6.71685 2.37408 8.00006 2.37408C11.1066 2.37408 13.6251 4.89248 13.6251 7.99908C13.6251 9.2823 13.196 10.4643 12.4726 11.411ZM2.82374 5.79344C2.53516 6.46973 2.37506 7.21474 2.37506 7.99908V13.6241H8.00006C8.87251 13.6241 9.69659 13.426 10.4316 13.073L11.081 14.4252C10.1476 14.8734 9.10207 15.1241 8.00006 15.1241H0.875057V7.99908C0.875057 7.00872 1.07755 6.06371 1.44411 5.20472L2.82374 5.79344Z" />
);

const MessageCircleOffIcon24x24 = () => (
  <path d="M21.3813 22.6183L1.38135 2.6183L2.61878 1.38086L6.28483 5.0469C7.83871 3.76832 9.83025 2.99958 12.0001 2.99958C16.9706 2.99958 21.0001 7.02903 21.0001 11.9996C21.0001 14.1694 20.2313 16.1609 18.9527 17.7148L22.6188 21.3809L21.3813 22.6183ZM17.7081 16.4702C18.6746 15.2381 19.2501 13.6866 19.2501 11.9996C19.2501 7.99553 16.0041 4.74958 12.0001 4.74958C10.313 4.74958 8.76152 5.325 7.52947 6.29154L17.7081 16.4702ZM5.35295 9.0999C4.96546 9.98676 4.75007 10.9669 4.75007 11.9996V19.2496H12.0001C12.9917 19.2496 13.9347 19.051 14.7932 18.6922L15.4679 20.3069C14.3995 20.7534 13.2274 20.9996 12.0001 20.9996H3.00007V11.9996C3.00007 10.7212 3.26713 9.5029 3.74933 8.39925L5.35295 9.0999Z" />
);

export const MessageCircleOffIcon = createFunctionalIcon(
  'crossed_over_chat_bubble_icon_label_a11y',
  MessageCircleOffIcon16x16,
  MessageCircleOffIcon24x24,
);

/**
 * MessageCircleOffSolidIcon
 */
const MessageCircleOffSolidIcon16x16 = () => (
  <>
    <path d="M0.469727 1.52941L14.4697 15.5294L15.5304 14.4688L13.4518 12.3902C14.4203 11.1893 15.0001 9.66187 15.0001 7.99902C15.0001 4.13303 11.866 0.999023 8.00006 0.999023C6.33722 0.999023 4.8098 1.57883 3.60893 2.5473L1.53039 0.46875L0.469727 1.52941Z" />
    <path d="M1.00006 7.99902C1.00006 6.93746 1.23636 5.93109 1.65924 5.02964L10.9694 14.3398C10.068 14.7627 9.06162 14.999 8.00006 14.999H1.00006V7.99902Z" />
  </>
);

const MessageCircleOffSolidIcon24x24 = () => (
  <>
    <path d="M1.38135 2.6183L21.3813 22.6183L22.6188 21.3809L18.9527 17.7148C20.2319 16.1605 21.0001 14.1697 21.0001 11.9995C21.0001 7.02895 16.9706 2.99951 12.0001 2.99951C9.82988 2.99951 7.8391 3.76763 6.28475 5.04683L2.61878 1.38086L1.38135 2.6183Z" />
    <path d="M3.77114 8.34914L15.6504 20.2284C14.5348 20.7241 13.2996 20.9995 12.0001 20.9995H3.00006V11.9995C3.00006 10.7 3.27548 9.46481 3.77114 8.34914Z" />
  </>
);

export const MessageCircleOffSolidIcon = createFunctionalIcon(
  'message_circle_off_icon_a11y',
  MessageCircleOffSolidIcon16x16,
  MessageCircleOffSolidIcon24x24,
);

/**
 * MessageIcon
 */
const MessageIcon16x16 = () => (
  <path d="M7.875 2.25C4.7684 2.25 2.25 4.7684 2.25 7.875C2.25 10.9816 4.7684 13.5 7.875 13.5C8.87884 13.5 9.81773 13.2382 10.631 12.7799L11.0754 12.5296L11.5681 12.6616L13.0615 13.0617L12.6614 11.5685L12.5294 11.0758L12.7797 10.6314C13.2381 9.818 13.5 8.87897 13.5 7.875C13.5 4.7684 10.9816 2.25 7.875 2.25ZM1 7.875C1 4.07805 4.07805 1 7.875 1C11.672 1 14.75 4.07805 14.75 7.875C14.75 9.09947 14.4299 10.2492 13.8688 11.245M13.8688 11.245L14.8293 14.8295L11.2446 13.869C10.2489 14.43 9.09931 14.75 7.875 14.75C4.07805 14.75 1 11.672 1 7.875" />
);

const MessageIcon24x24 = () => (
  <path d="M11.5705 3.875C7.3204 3.875 3.875 7.3204 3.875 11.5705C3.875 15.8207 7.3204 19.2661 11.5705 19.2661C12.9347 19.2661 14.211 18.9126 15.3184 18.2937C15.3184 18.2937 15.3184 18.2937 15.3184 18.2937L15.9388 17.9469L18.6818 18.6819L17.9468 15.9389L18.2936 15.3186C18.9126 14.2111 19.2661 12.9348 19.2661 11.5705C19.2661 7.3204 15.8207 3.875 11.5705 3.875ZM2.125 11.5705C2.125 6.3539 6.3539 2.125 11.5705 2.125C16.7871 2.125 21.0161 6.3539 21.0161 11.5705C21.0161 13.2413 20.5823 14.8108 19.8212 16.1724L21.1567 21.1568L16.1723 19.8212C14.8106 20.5823 13.2412 21.0161 11.5705 21.0161C6.3539 21.0161 2.125 16.7871 2.125 11.5705Z" />
);

export const MessageIcon = createFunctionalIcon(
  'chat_message_icon_label_a11y',
  MessageIcon16x16,
  MessageIcon24x24,
);

/**
 * MessageSolidIcon
 */
const MessageSolidIcon16x16 = () => (
  <path d="M13.8689 11.2448C14.4299 10.249 14.75 9.09937 14.75 7.875C14.75 4.07804 11.672 1 7.875 1C4.07804 1 1 4.07804 1 7.875C1 11.672 4.07804 14.75 7.875 14.75C9.09978 14.75 10.2498 14.4297 11.2458 13.8684L14.8292 14.8285L13.8689 11.2448Z" />
);

const MessageSolidIcon24x24 = () => (
  <path d="M19.6963 16.0471C20.4573 14.6855 20.8911 13.1162 20.8911 11.4455C20.8911 6.22891 16.6622 2 11.4455 2C6.22891 2 2 6.22891 2 11.4455C2 16.6621 6.22891 20.8911 11.4455 20.8911C13.1167 20.8911 14.6866 20.457 16.0484 19.6956L21.0317 21.0308L19.6963 16.0471Z" />
);

export const MessageSolidIcon = createFunctionalIcon(
  'chat_message_icon_label_a11y',
  MessageSolidIcon16x16,
  MessageSolidIcon24x24,
);

/**
 * MinimizeIcon
 */

const MinimizeIcon16x16 = () => (
  <path d="M10.25 4.86665L14.0581 1.05859L14.942 1.94248L11.1339 5.75054L14 5.75054V7.00054L9.00004 7.00054V2.00054H10.25V4.86665ZM4.86613 10.2505H2.00004V9.00054H7.00004L7.00004 14.0005H5.75004L5.75004 11.1344L1.94198 14.9424L1.05811 14.0585L4.86613 10.2505Z" />
);

const MinimizeIcon24x24 = () => (
  <path d="M14.7501 8.01214L20.3813 2.38086L21.6188 3.6183L15.9875 9.24958L20.0001 9.24958V10.9996H13.0001L13.0001 3.99958L14.7501 3.99958L14.7501 8.01214ZM8.01263 14.7496H4.00007V12.9996H11.0001V19.9996H9.25007V15.987L3.61878 21.6183L2.38135 20.3809L8.01263 14.7496Z" />
);

export const MinimizeIcon = createFunctionalIcon(
  'collapse_icon_label_a11y',
  MinimizeIcon16x16,
  MinimizeIcon24x24,
);

/**
 * MinusIcon
 */

const MinusIcon16x16 = () => (
  <path d="M13 8.625L3 8.625L3 7.375L13 7.375L13 8.625Z" />
);

const MinusIcon24x24 = () => <path d="M19 12.875H5V11.125H19V12.875Z" />;

export const MinusIcon = createFunctionalIcon(
  'remove_icon_label_a11y',
  MinusIcon16x16,
  MinusIcon24x24,
);

/**
 * ModeDarkIcon
 */

const ModeDarkIcon16x16 = () => (
  <path d="M7.77511 2.25313C6.86796 2.28338 5.95492 2.53184 5.11277 3.02012C2.37724 4.6062 1.43601 8.1237 3.01854 10.8764C4.59931 13.626 8.0956 14.564 10.828 12.9798C11.6232 12.5187 12.2662 11.8954 12.74 11.1749C10.8221 10.9771 9.02308 9.8894 7.98649 8.08634L8.52833 7.77483L7.98649 8.08633C6.91824 6.22819 6.91123 4.04848 7.77511 2.25313ZM4.48578 1.93874C5.88095 1.12982 7.44038 0.860259 8.922 1.06632L9.94742 1.20894L9.34359 2.0499C8.23444 3.5946 8.06178 5.7093 9.07017 7.46332C10.0531 9.17301 11.8924 10.0781 13.7321 9.94469L14.7647 9.86982L14.35 10.8184C13.7742 12.1355 12.7898 13.2872 11.455 14.0612L11.1415 13.5205L11.455 14.0612C8.1217 15.9938 3.85939 14.8469 1.93486 11.4994L2.4767 11.1879L1.93486 11.4994C0.0103467 8.15186 1.15248 3.87141 4.48578 1.93874Z" />
);

const ModeDarkIcon24x24 = () => (
  <path d="M11.7853 3.75059C10.4534 3.78395 9.11106 4.14129 7.87509 4.85487C3.92917 7.13305 2.5772 12.1787 4.85537 16.1246C7.13354 20.0705 12.1792 21.4225 16.1251 19.1443C17.2895 18.472 18.2271 17.5601 18.9127 16.5064C16.1529 16.243 13.5556 14.6938 12.0626 12.1079C10.5248 9.44435 10.5252 6.31633 11.7853 3.75059ZM7.0001 3.33932C9.01285 2.17727 11.2638 1.79426 13.4 2.09764L14.8392 2.30204L13.9849 3.47818C12.3805 5.68691 12.1268 8.7191 13.5781 11.2328L12.8204 11.6703L13.5781 11.2328C14.9939 13.685 17.6479 14.9787 20.2977 14.776L21.7468 14.6651L21.1667 15.9976C20.3423 17.8916 18.9253 19.5483 17.0001 20.6598C12.2172 23.4213 6.10124 21.7825 3.33982 16.9996C0.578406 12.2167 2.21716 6.10075 7.00009 3.33932L7.43759 4.0971L7.0001 3.33932Z" />
);

export const ModeDarkIcon = createFunctionalIcon(
  'dark_mode_icon_label_a11y',
  ModeDarkIcon16x16,
  ModeDarkIcon24x24,
);

/**
 * ModeDarkSolidIcon
 */

const ModeDarkSolidIcon16x16 = () => (
  <path d="M4.48577 1.93874C5.93772 1.09689 7.56733 0.839305 9.10199 1.09375L9.9625 1.23643L9.43149 1.93125C8.24436 3.48462 8.03557 5.66373 9.07016 7.46331C10.08 9.21983 11.9938 10.1269 13.8827 9.93143L14.75 9.84168L14.4201 10.6522C13.856 12.0383 12.8458 13.2547 11.455 14.0612C8.12166 15.9938 3.85936 14.8469 1.93486 11.4994C0.0103654 8.15188 1.15245 3.87143 4.48577 1.93874Z" />
);

const ModeDarkSolidIcon24x24 = () => (
  <path d="M6.99972 3.33932C9.08308 2.13649 11.4214 1.76845 13.6234 2.132L14.8582 2.33585L14.0962 3.32862C12.3928 5.54807 12.0933 8.66158 13.5778 11.2328C15.0267 13.7425 17.7729 15.0386 20.4832 14.7593L21.7277 14.631L21.2544 15.7891C20.4449 17.7696 18.9954 19.5076 16.9997 20.6598C12.2168 23.4213 6.10089 21.7825 3.33946 16.9996C0.578039 12.2166 2.21679 6.10074 6.99972 3.33932Z" />
);

export const ModeDarkSolidIcon = createFunctionalIcon(
  'dark_mode_icon_label_a11y',
  ModeDarkSolidIcon16x16,
  ModeDarkSolidIcon24x24,
);

/**
 * ModeIcon
 */

const ModeIcon16x16 = () => (
  <path d="M3.039 3.06153C1.77886 4.32738 1 6.07273 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C6.07273 1 4.32738 1.77886 3.06153 3.039C3.05777 3.04274 3.05401 3.0465 3.05025 3.05025C3.0465 3.05401 3.04274 3.05777 3.039 3.06153ZM8 13.75C11.1756 13.75 13.75 11.1756 13.75 8C13.75 4.82436 11.1756 2.25 8 2.25V13.75Z" />
);

const ModeIcon24x24 = () => (
  <path d="M5.02844 19.1692C6.82968 20.9211 9.28884 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C9.24766 2 6.75502 3.11194 4.94681 4.9111C4.94084 4.91704 4.93488 4.92298 4.92893 4.92893C4.92298 4.93488 4.91704 4.94084 4.9111 4.94681C3.11194 6.75502 2 9.24766 2 12C2 14.7614 3.11929 17.2614 4.92893 19.0711C4.9619 19.104 4.99507 19.1367 5.02844 19.1692ZM12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75V20.25Z" />
);

export const ModeIcon = createFunctionalIcon(
  'visual_mode_icon_label_a11y',
  ModeIcon16x16,
  ModeIcon24x24,
);

/**
 * ModeLightIcon
 */

const ModeLightIcon16x16 = () => (
  <path d="M7.375 2.66797V0.667969H8.625V2.66797H7.375ZM3.10893 2.22603L4.44227 3.55936L3.55838 4.44324L2.22505 3.10991L3.10893 2.22603ZM11.5581 3.55936L12.8914 2.22603L13.7753 3.10991L12.4419 4.44324L11.5581 3.55936ZM5.20136 5.20234C5.94369 4.46001 6.95051 4.04297 8.00033 4.04297C9.05014 4.04297 10.057 4.46001 10.7993 5.20234C11.5416 5.94467 11.9587 6.95149 11.9587 8.0013C11.9587 9.05112 11.5416 10.0579 10.7993 10.8003C10.057 11.5426 9.05014 11.9596 8.00033 11.9596C6.95051 11.9596 5.94369 11.5426 5.20136 10.8003C4.45903 10.0579 4.04199 9.05112 4.04199 8.0013C4.04199 6.95149 4.45903 5.94467 5.20136 5.20234ZM8.00033 5.29297C7.28203 5.29297 6.59316 5.57831 6.08524 6.08622C5.57733 6.59413 5.29199 7.28301 5.29199 8.0013C5.29199 8.7196 5.57733 9.40847 6.08524 9.91638C6.59316 10.4243 7.28203 10.7096 8.00033 10.7096C8.71862 10.7096 9.4075 10.4243 9.91541 9.91638C10.4233 9.40847 10.7087 8.7196 10.7087 8.0013C10.7087 7.28301 10.4233 6.59413 9.91541 6.08622C9.4075 5.57831 8.71862 5.29297 8.00033 5.29297ZM0.666992 7.375H2.66699V8.625H0.666992V7.375ZM13.3334 7.375H15.3334V8.625H13.3334V7.375ZM2.22505 12.8914L3.55838 11.5581L4.44227 12.4419L3.10893 13.7753L2.22505 12.8914ZM12.4419 11.5581L13.7753 12.8914L12.8914 13.7753L11.5581 12.4419L12.4419 11.5581ZM7.375 15.334V13.334H8.625V15.334H7.375Z" />
);

const ModeLightIcon24x24 = () => (
  <path d="M11.125 4V1H12.875V4H11.125ZM4.61872 3.38128L6.61872 5.38128L5.38128 6.61872L3.38128 4.61872L4.61872 3.38128ZM17.3813 5.38128L19.3813 3.38128L20.6187 4.61872L18.6187 6.61872L17.3813 5.38128ZM7.84575 7.84575C8.94752 6.74397 10.4419 6.125 12 6.125C13.5581 6.125 15.0525 6.74397 16.1543 7.84575C17.256 8.94752 17.875 10.4419 17.875 12C17.875 13.5581 17.256 15.0525 16.1543 16.1543C15.0525 17.256 13.5581 17.875 12 17.875C10.4419 17.875 8.94752 17.256 7.84575 16.1543C6.74397 15.0525 6.125 13.5581 6.125 12C6.125 10.4419 6.74397 8.94752 7.84575 7.84575ZM12 7.875C10.906 7.875 9.85677 8.3096 9.08318 9.08318C8.3096 9.85677 7.875 10.906 7.875 12C7.875 13.094 8.3096 14.1432 9.08318 14.9168C9.85677 15.6904 10.906 16.125 12 16.125C13.094 16.125 14.1432 15.6904 14.9168 14.9168C15.6904 14.1432 16.125 13.094 16.125 12C16.125 10.906 15.6904 9.85677 14.9168 9.08318C14.1432 8.3096 13.094 7.875 12 7.875ZM1 11.125H4V12.875H1V11.125ZM20 11.125H23V12.875H20V11.125ZM3.38128 19.3813L5.38128 17.3813L6.61872 18.6187L4.61872 20.6187L3.38128 19.3813ZM18.6187 17.3813L20.6187 19.3813L19.3813 20.6187L17.3813 18.6187L18.6187 17.3813ZM11.125 23V20H12.875V23H11.125Z" />
);

export const ModeLightIcon = createFunctionalIcon(
  'light_mode_icon_label_a11y',
  ModeLightIcon16x16,
  ModeLightIcon24x24,
);

/**
 * ModeLightSolidIcon
 */

const ModeLightSolidIcon16x16 = () => (
  <>
    <path d="M7.375 2.66797V0.667969H8.625V2.66797H7.375Z" />
    <path d="M5.20136 5.20234C5.94369 4.46001 6.95051 4.04297 8.00033 4.04297C9.05014 4.04297 10.057 4.46001 10.7993 5.20234C11.5416 5.94467 11.9587 6.95149 11.9587 8.0013C11.9587 9.05112 11.5416 10.0579 10.7993 10.8003C10.057 11.5426 9.05014 11.9596 8.00033 11.9596C6.95051 11.9596 5.94369 11.5426 5.20136 10.8003C4.45903 10.0579 4.04199 9.05112 4.04199 8.0013C4.04199 6.95149 4.45903 5.94467 5.20136 5.20234Z" />
    <path d="M7.375 13.334V15.334H8.625V13.334H7.375Z" />
    <path d="M11.5581 3.5599L12.8914 2.22656L13.7753 3.11045L12.442 4.44378L11.5581 3.5599Z" />
    <path d="M3.55843 11.5586L2.2251 12.8919L3.10898 13.7758L4.44231 12.4425L3.55843 11.5586Z" />
    <path d="M13.3335 7.375H15.3335V8.625H13.3335V7.375Z" />
    <path d="M2.66699 7.375H0.666992V8.625H2.66699V7.375Z" />
    <path d="M12.442 11.5586L13.7753 12.8919L12.8914 13.7758L11.5581 12.4425L12.442 11.5586Z" />
    <path d="M4.44231 3.5599L3.10898 2.22656L2.2251 3.11045L3.55843 4.44378L4.44231 3.5599Z" />
  </>
);

const ModeLightSolidIcon24x24 = () => (
  <>
    <path d="M11.125 4V1H12.875V4H11.125Z" />
    <path d="M7.84575 7.84575C8.94752 6.74397 10.4419 6.125 12 6.125C13.5581 6.125 15.0525 6.74397 16.1543 7.84575C17.256 8.94752 17.875 10.4419 17.875 12C17.875 13.5581 17.256 15.0525 16.1543 16.1543C15.0525 17.256 13.5581 17.875 12 17.875C10.4419 17.875 8.94752 17.256 7.84575 16.1543C6.74397 15.0525 6.125 13.5581 6.125 12C6.125 10.4419 6.74397 8.94752 7.84575 7.84575Z" />
    <path d="M11.125 20V23H12.875V20H11.125Z" />
    <path d="M17.3813 5.38135L19.3813 3.38135L20.6187 4.61878L18.6187 6.61878L17.3813 5.38135Z" />
    <path d="M5.38129 17.3813L3.38129 19.3813L4.61872 20.6188L6.61872 18.6188L5.38129 17.3813Z" />
    <path d="M20 11.125H23V12.875H20V11.125Z" />
    <path d="M4 11.125H1V12.875H4V11.125Z" />
    <path d="M18.6187 17.3813L20.6187 19.3813L19.3813 20.6188L17.3813 18.6188L18.6187 17.3813Z" />
    <path d="M6.61872 5.38135L4.61872 3.38135L3.38129 4.61878L5.38129 6.61878L6.61872 5.38135Z" />
  </>
);

export const ModeLightSolidIcon = createFunctionalIcon(
  'light_mode_icon_label_a11y',
  ModeLightSolidIcon16x16,
  ModeLightSolidIcon24x24,
);

/**
 * MoreIcon
 */

const MoreIcon16x16 = () => (
  <>
    <path d="M4.00203 8.00187C4.00203 7.17241 3.32962 6.5 2.50016 6.5C1.6707 6.5 0.998291 7.17241 0.998291 8.00187C0.998291 8.83133 1.6707 9.50374 2.50016 9.50374C3.32962 9.50374 4.00203 8.83133 4.00203 8.00187Z" />
    <path d="M8.00016 6.5C8.82962 6.5 9.50203 7.17241 9.50203 8.00187C9.50203 8.83133 8.82962 9.50374 8.00016 9.50374C7.1707 9.50374 6.49829 8.83133 6.49829 8.00187C6.49829 7.17241 7.1707 6.5 8.00016 6.5Z" />
    <path d="M13.5002 6.5C14.3296 6.5 15.002 7.17241 15.002 8.00187C15.002 8.83133 14.3296 9.50374 13.5002 9.50374C12.6707 9.50374 11.9983 8.83133 11.9983 8.00187C11.9983 7.17241 12.6707 6.5 13.5002 6.5Z" />
  </>
);

const MoreIcon24x24 = () => (
  <>
    <path d="M7 12C7 10.8954 6.10457 10 5 10C3.89543 10 3 10.8954 3 12C3 13.1046 3.89543 14 5 14C6.10457 14 7 13.1046 7 12Z" />
    <path d="M12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10Z" />
    <path d="M19 10C20.1046 10 21 10.8954 21 12C21 13.1046 20.1046 14 19 14C17.8954 14 17 13.1046 17 12C17 10.8954 17.8954 10 19 10Z" />
  </>
);

export const MoreIcon = createFunctionalIcon(
  'more_icon_label_a11y',
  MoreIcon16x16,
  MoreIcon24x24,
);

/**
 * MoreVerticalIcon
 */

const MoreVerticalIcon16x16 = () => (
  <>
    <path d="M8.00016 4.00374C8.82962 4.00374 9.50203 3.33133 9.50203 2.50187C9.50203 1.67241 8.82962 1 8.00016 1C7.1707 1 6.49829 1.67241 6.49829 2.50187C6.49829 3.33133 7.1707 4.00374 8.00016 4.00374Z" />
    <path d="M9.50203 8.00187C9.50203 8.83133 8.82962 9.50374 8.00016 9.50374C7.1707 9.50374 6.49829 8.83133 6.49829 8.00187C6.49829 7.17241 7.1707 6.5 8.00016 6.5C8.82962 6.5 9.50203 7.17241 9.50203 8.00187Z" />
    <path d="M9.50203 13.5019C9.50203 14.3313 8.82962 15.0037 8.00016 15.0037C7.1707 15.0037 6.49829 14.3313 6.49829 13.5019C6.49829 12.6724 7.1707 12 8.00016 12C8.82962 12 9.50203 12.6724 9.50203 13.5019Z" />
  </>
);

const MoreVerticalIcon24x24 = () => (
  <>
    <path d="M12 7C13.1046 7 14 6.10457 14 5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5C10 6.10457 10.8954 7 12 7Z" />
    <path d="M14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12Z" />
    <path d="M14 19C14 20.1046 13.1046 21 12 21C10.8954 21 10 20.1046 10 19C10 17.8954 10.8954 17 12 17C13.1046 17 14 17.8954 14 19Z" />
  </>
);

export const MoreVerticalIcon = createFunctionalIcon(
  'more_icon_label_a11y',
  MoreVerticalIcon16x16,
  MoreVerticalIcon24x24,
);

/**
 * MoveDotsIcon
 */

const MoveDotsIcon16x16 = () => (
  <>
    <path d="M10 2.5C10 1.67157 10.6716 1 11.5 1C12.3284 1 13 1.67157 13 2.5C13 3.32843 12.3284 4 11.5 4C10.6716 4 10 3.32843 10 2.5Z" />
    <path d="M10 8C10 7.17157 10.6716 6.5 11.5 6.5C12.3284 6.5 13 7.17157 13 8C13 8.82843 12.3284 9.5 11.5 9.5C10.6716 9.5 10 8.82843 10 8Z" />
    <path d="M4.5 12C3.67157 12 3 12.6716 3 13.5C3 14.3284 3.67157 15 4.5 15C5.32843 15 6 14.3284 6 13.5C6 12.6716 5.32843 12 4.5 12Z" />
    <path d="M11.5 12C10.6716 12 10 12.6716 10 13.5C10 14.3284 10.6716 15 11.5 15C12.3284 15 13 14.3284 13 13.5C13 12.6716 12.3284 12 11.5 12Z" />
    <path d="M4.5 6.5C3.67157 6.5 3 7.17157 3 8C3 8.82843 3.67157 9.5 4.5 9.5C5.32843 9.5 6 8.82843 6 8C6 7.17157 5.32843 6.5 4.5 6.5Z" />
    <path d="M4.5 1C3.67157 1 3 1.67157 3 2.5C3 3.32843 3.67157 4 4.5 4C5.32843 4 6 3.32843 6 2.5C6 1.67157 5.32843 1 4.5 1Z" />
  </>
);

const MoveDotsIcon24x24 = () => (
  <>
    <path d="M8 3.25C7.0335 3.25 6.25 4.0335 6.25 5C6.25 5.9665 7.0335 6.75 8 6.75C8.9665 6.75 9.75 5.9665 9.75 5C9.75 4.0335 8.9665 3.25 8 3.25Z" />
    <path d="M16 3.25C15.0335 3.25 14.25 4.0335 14.25 5C14.25 5.9665 15.0335 6.75 16 6.75C16.9665 6.75 17.75 5.9665 17.75 5C17.75 4.0335 16.9665 3.25 16 3.25Z" />
    <path d="M6.25 12C6.25 11.0335 7.0335 10.25 8 10.25C8.9665 10.25 9.75 11.0335 9.75 12C9.75 12.9665 8.9665 13.75 8 13.75C7.0335 13.75 6.25 12.9665 6.25 12Z" />
    <path d="M16 10.25C15.0335 10.25 14.25 11.0335 14.25 12C14.25 12.9665 15.0335 13.75 16 13.75C16.9665 13.75 17.75 12.9665 17.75 12C17.75 11.0335 16.9665 10.25 16 10.25Z" />
    <path d="M6.25 19C6.25 18.0335 7.0335 17.25 8 17.25C8.9665 17.25 9.75 18.0335 9.75 19C9.75 19.9665 8.9665 20.75 8 20.75C7.0335 20.75 6.25 19.9665 6.25 19Z" />
    <path d="M16 17.25C15.0335 17.25 14.25 18.0335 14.25 19C14.25 19.9665 15.0335 20.75 16 20.75C16.9665 20.75 17.75 19.9665 17.75 19C17.75 18.0335 16.9665 17.25 16 17.25Z" />
  </>
);

export const MoveDotsIcon = createFunctionalIcon(
  'drag_object_icon_label_a11y',
  MoveDotsIcon16x16,
  MoveDotsIcon24x24,
);

/**
 * MoveIcon
 */

const MoveIcon16x16 = () => (
  <path d="M8.00001 0.447266L10.5506 2.99782L9.66668 3.8817L8.62509 2.84011V7.37412H13.1592L12.1162 6.33115L13.0001 5.44727L15.5506 7.99782L13.0001 10.5484L12.1162 9.66448L13.1566 8.62412H8.62509V13.1562L9.66668 12.1146L10.5506 12.9985L8.00001 15.549L5.44946 12.9985L6.33335 12.1146L7.37509 13.1563V8.62412H2.84353L3.8839 9.66448L3.00001 10.5484L0.449463 7.99782L3.00001 5.44727L3.8839 6.33115L2.84093 7.37412H7.37509V2.83995L6.33335 3.8817L5.44946 2.99782L8.00001 0.447266Z" />
);

const MoveIcon24x24 = () => (
  <path d="M12 1.76172L15.7374 5.49916L14.5 6.73659L12.875 5.11159V11.1242H18.8876L17.2626 9.49916L18.5 8.26172L22.2374 11.9992L18.5 15.7366L17.2626 14.4992L18.8876 12.8742H12.875V18.8867L14.5 17.2617L15.7374 18.4992L12 22.2366L8.26257 18.4992L9.50001 17.2617L11.125 18.8867V12.8742H5.11245L6.73745 14.4992L5.50001 15.7366L1.76257 11.9992L5.50001 8.26172L6.73745 9.49916L5.11245 11.1242H11.125V5.11159L9.50001 6.73659L8.26257 5.49916L12 1.76172Z" />
);

export const MoveIcon = createFunctionalIcon(
  'move_icon_label_a11y',
  MoveIcon16x16,
  MoveIcon24x24,
);

/**
 * MoveUpDownIcon
 */

const MoveUpDownIcon16x16 = () => (
  <path d="M8.00001 0.447266L11.5506 3.99782L10.6667 4.8817L8.62503 2.84005V13.1562L10.6667 11.1146L11.5506 11.9985L8.00001 15.549L4.44946 11.9985L5.33335 11.1146L7.37503 13.1563V2.84001L5.33335 4.8817L4.44946 3.99782L8.00001 0.447266Z" />
);

const MoveUpDownIcon24x24 = () => (
  <path d="M12 1.76172L17.2374 6.99916L16 8.23659L12.875 5.11159V18.8867L16 15.7617L17.2374 16.9992L12 22.2366L6.76257 16.9992L8.00001 15.7617L11.125 18.8867V5.11159L8.00001 8.23659L6.76257 6.99916L12 1.76172Z" />
);

export const MoveUpDownIcon = createFunctionalIcon(
  'move_up_or_down_icon_label_a11y',
  MoveUpDownIcon16x16,
  MoveUpDownIcon24x24,
);

/**
 * NetworkIcon
 */

const NetworkIcon16x16 = () => (
  <path d="M12.125 2.25C11.2275 2.25 10.5 2.97754 10.5 3.875C10.5 4.77246 11.2275 5.5 12.125 5.5C13.0225 5.5 13.75 4.77246 13.75 3.875C13.75 2.97754 13.0225 2.25 12.125 2.25ZM9.25 3.875C9.25 2.28718 10.5372 1 12.125 1C13.7128 1 15 2.28718 15 3.875C15 5.46282 13.7128 6.75 12.125 6.75C11.2456 6.75 10.4585 6.35521 9.93113 5.7332L6.67841 7.35957C6.72526 7.56551 6.75 7.77987 6.75 8C6.75 8.22013 6.72526 8.43449 6.67841 8.64043L9.93113 10.2668C10.4585 9.64479 11.2456 9.25 12.125 9.25C13.7128 9.25 15 10.5372 15 12.125C15 13.7128 13.7128 15 12.125 15C10.5372 15 9.25 13.7128 9.25 12.125C9.25 11.865 9.28452 11.613 9.34924 11.3734L6.1412 9.76937C5.615 10.4424 4.79552 10.875 3.875 10.875C2.28718 10.875 1 9.58782 1 8C1 6.41218 2.28718 5.125 3.875 5.125C4.79552 5.125 5.615 5.55762 6.1412 6.23063L9.34924 4.62661C9.28452 4.38702 9.25 4.13504 9.25 3.875ZM3.875 6.375C2.97754 6.375 2.25 7.10254 2.25 8C2.25 8.89746 2.97754 9.625 3.875 9.625C4.77246 9.625 5.5 8.89746 5.5 8C5.5 7.10254 4.77246 6.375 3.875 6.375ZM12.125 10.5C11.2275 10.5 10.5 11.2275 10.5 12.125C10.5 13.0225 11.2275 13.75 12.125 13.75C13.0225 13.75 13.75 13.0225 13.75 12.125C13.75 11.2275 13.0225 10.5 12.125 10.5Z" />
);

const NetworkIcon24x24 = () => (
  <path d="M17.919 3.75C16.6316 3.75 15.5879 4.79366 15.5879 6.08108C15.5879 7.3685 16.6316 8.41216 17.919 8.41216C19.2064 8.41216 20.2501 7.3685 20.2501 6.08108C20.2501 4.79366 19.2064 3.75 17.919 3.75ZM13.8379 6.08108C13.8379 3.82716 15.6651 2 17.919 2C20.1729 2 22.0001 3.82716 22.0001 6.08108C22.0001 8.335 20.1729 10.1622 17.919 10.1622C16.6824 10.1622 15.5743 9.61222 14.8259 8.74358L10.1374 11.301C10.1538 11.4494 10.1622 11.6002 10.1622 11.753C10.1622 12.064 10.1274 12.367 10.0614 12.6581L14.8257 15.2568C15.5741 14.388 16.6823 13.8379 17.919 13.8379C20.1729 13.8379 22.0001 15.6651 22.0001 17.919C22.0001 20.1729 20.1729 22.0001 17.919 22.0001C15.6651 22.0001 13.8379 20.1729 13.8379 17.919C13.8379 17.5297 13.8924 17.1532 13.9942 16.7966L9.31409 14.2438C8.56781 15.211 7.3972 15.834 6.08108 15.834C3.82716 15.834 2 14.0069 2 11.753C2 9.49904 3.82716 7.67188 6.08108 7.67188C7.55379 7.67188 8.8443 8.45194 9.56197 9.62143L13.9943 7.20381C13.8924 6.84712 13.8379 6.47047 13.8379 6.08108ZM6.08108 9.42188C4.79366 9.42188 3.75 10.4655 3.75 11.753C3.75 13.0404 4.79366 14.084 6.08108 14.084C7.3685 14.084 8.41216 13.0404 8.41216 11.753C8.41216 10.4655 7.3685 9.42188 6.08108 9.42188ZM17.919 15.5879C16.6316 15.5879 15.5879 16.6316 15.5879 17.919C15.5879 19.2064 16.6316 20.2501 17.919 20.2501C19.2064 20.2501 20.2501 19.2064 20.2501 17.919C20.2501 16.6316 19.2064 15.5879 17.919 15.5879Z" />
);

export const NetworkIcon = createFunctionalIcon(
  'share_icon_label_a11y',
  NetworkIcon16x16,
  NetworkIcon24x24,
);

/**
 * NumbersIcon
 */

const NumbersIcon16x16 = () => (
  <path d="M7.79201 1.45898V1H9.04201V8H7.79201V3.5162C7.27545 3.82825 6.64847 4.00065 6.00002 4.00065V2.75065C6.51722 2.75065 6.98449 2.5852 7.30975 2.33419C7.63163 2.08578 7.79201 1.77689 7.79201 1.45898ZM3.56217 8.75387C2.92331 8.75387 2.52051 9.20593 2.52051 9.62887H1.27051C1.27051 8.39495 2.36009 7.50387 3.56217 7.50387C4.76426 7.50387 5.85384 8.39495 5.85384 9.62887C5.85384 10.3237 5.49664 10.9229 4.97777 11.3008L4.96634 11.3091L3.23122 12.5134C2.81481 12.8025 2.49027 13.2682 2.34843 13.7539H6.06217V15.0039H1.02051V14.2739C1.02051 13.1773 1.64762 12.0911 2.51839 11.4866L4.24694 10.2866C4.47999 10.1148 4.60384 9.87471 4.60384 9.62887C4.60384 9.20593 4.20104 8.75387 3.56217 8.75387ZM12.3749 8.83398C11.8325 8.83398 11.4456 9.17114 11.3556 9.52845L10.1435 9.22286C10.3902 8.24417 11.331 7.58398 12.3749 7.58398C13.577 7.58398 14.6666 8.47507 14.6666 9.70898C14.6666 10.2361 14.4677 10.7007 14.1462 11.0599C14.2259 11.1171 14.3011 11.1795 14.3714 11.2474C14.8437 11.7032 14.9999 12.3066 14.9999 12.8757C14.9999 14.196 13.8419 15.1673 12.5416 15.1673C11.4056 15.1673 10.3939 14.4365 10.1414 13.3735L11.3576 13.0846C11.4617 13.5231 11.9193 13.9173 12.5416 13.9173C13.2663 13.9173 13.7499 13.3962 13.7499 12.8757C13.7499 12.5243 13.6561 12.2943 13.5034 12.1469C13.3474 11.9964 13.0288 11.834 12.3749 11.834H12.0416V10.584H12.3749C13.0138 10.584 13.4166 10.1319 13.4166 9.70898C13.4166 9.28605 13.0138 8.83398 12.3749 8.83398Z" />
);

const NumbersIcon24x24 = () => (
  <path d="M11.75 1.875V1H13.5V11H11.75V4.84876C10.9682 5.34748 10.002 5.625 9 5.625V3.875C9.78851 3.875 10.5031 3.6229 11.0028 3.23728C11.4977 2.85532 11.75 2.37496 11.75 1.875ZM6.75 13.625C5.76354 13.625 5.125 14.325 5.125 15H3.375C3.375 13.1897 4.97504 11.875 6.75 11.875C8.52496 11.875 10.125 13.1897 10.125 15C10.125 16.0199 9.60078 16.9008 8.8366 17.4573L8.82859 17.4631L6.21793 19.2755C5.55948 19.7326 5.05097 20.4783 4.84713 21.25H10.5V23H3V21.9675C3 20.3449 3.9291 18.734 5.21996 17.8379L7.8135 16.0375C8.17754 15.7693 8.375 15.3912 8.375 15C8.375 14.325 7.73646 13.625 6.75 13.625ZM17.5001 13.625C16.6614 13.625 16.0534 14.1469 15.9105 14.7139L14.2136 14.2861C14.5759 12.8491 15.9592 11.875 17.5001 11.875C19.275 11.875 20.8751 13.1897 20.8751 15C20.8751 15.7989 20.5634 16.5013 20.0627 17.0368C20.2017 17.1315 20.3314 17.2367 20.4515 17.3526C21.1439 18.0209 21.3751 18.9073 21.3751 19.75C21.3751 21.6906 19.6717 23.125 17.7501 23.125C16.0717 23.125 14.582 22.0456 14.2107 20.4823L15.9133 20.0779C16.0769 20.7668 16.791 21.375 17.7501 21.375C18.8659 21.375 19.6251 20.5709 19.6251 19.75C19.6251 19.212 19.4812 18.8484 19.2362 18.6118C18.9866 18.371 18.4906 18.125 17.5001 18.125H17.0001V16.375H17.5001C18.4865 16.375 19.1251 15.675 19.1251 15C19.1251 14.325 18.4865 13.625 17.5001 13.625Z" />
);

export const NumbersIcon = createFunctionalIcon(
  'numbers_icon_label_a11y',
  NumbersIcon16x16,
  NumbersIcon24x24,
);

/**
 * OpenInNewTabIcon
 */

const OpenInNewTabIcon16x16 = () => (
  <path d="M1 1H6V2.25H2.25V13.75H13.75V9H15V15H1V1ZM12.8661 2.25H10V1H15V6H13.75V3.13388L8.44194 8.44194L7.55806 7.55806L12.8661 2.25Z" />
);

const OpenInNewTabIcon24x24 = () => (
  <path d="M4 4H9V5.75H5.75V18.25H18.25V14H20V20H4V4ZM17.0126 5.75H12.875V4H20L20 11.125H18.25L18.25 6.98744L11.6187 13.6187L10.3813 12.3813L17.0126 5.75Z" />
);

export const OpenInNewTabIcon = createFunctionalIcon(
  'open_in_new_page_icon_label_a11y',
  OpenInNewTabIcon16x16,
  OpenInNewTabIcon24x24,
);

/**
 * OrderedListIcon
 */

const OrderedListIcon16x16 = () => (
  <>
    <path d="M14.6667 4.00004L7.33334 4.00004V2.66671L14.6667 2.66671V4.00004Z" />
    <path d="M14.6667 13.3334H7.33334V12H14.6667V13.3334Z" />
    <path d="M1.63052 5.46671V4.83309H2.74932V1.89593H2.69688L1.81116 2.96184L1.33334 2.55324L2.34143 1.33337H3.51267V4.83309H4.43334V5.46671H1.63052Z" />
    <path d="M4.43334 14.1334H1.40715V13.4231L2.78493 12.3112C3.01456 12.1249 3.18473 11.9561 3.29545 11.8047C3.40616 11.6495 3.46152 11.4845 3.46152 11.3099V11.2517C3.46152 11.0576 3.40411 10.9101 3.2893 10.8092C3.17448 10.7083 3.01456 10.6579 2.80953 10.6579C2.584 10.6579 2.41178 10.7161 2.29287 10.8325C2.17395 10.949 2.08784 11.0926 2.03453 11.2633L1.33334 11.013C1.37845 10.881 1.44201 10.7549 1.52402 10.6346C1.61013 10.5104 1.71469 10.4017 1.83771 10.3086C1.96482 10.2154 2.11449 10.1417 2.28672 10.0874C2.45894 10.0291 2.65576 10 2.87719 10C3.10682 10 3.3098 10.0311 3.48612 10.0932C3.66654 10.1514 3.81826 10.2348 3.94128 10.3435C4.0643 10.4483 4.15656 10.5744 4.21807 10.7219C4.28367 10.8694 4.31648 11.0305 4.31648 11.2051C4.31648 11.372 4.28777 11.5253 4.23037 11.665C4.17706 11.8047 4.1012 11.9386 4.00279 12.0667C3.90438 12.1909 3.78751 12.3112 3.65219 12.4276C3.52098 12.5441 3.37746 12.6605 3.22164 12.7769L2.28672 13.493H4.43334V14.1334Z" />
  </>
);

const OrderedListIcon24x24 = () => (
  <>
    <path d="M22 6L11 6V4L22 4V6Z" />
    <path d="M22 20H11V18H22V20Z" />
    <path d="M2.44577 8.2V7.24957H4.12397V2.84384H4.0453L2.71673 4.44269L2 3.8298L3.51212 2H5.26899V7.24957H6.65V8.2H2.44577Z" />
    <path d="M6.65 21.2H2.11071V20.1346L4.17738 18.4668C4.52183 18.1873 4.77708 17.9341 4.94316 17.707C5.10923 17.4742 5.19226 17.2268 5.19226 16.9648V16.8775C5.19226 16.5864 5.10615 16.3652 4.93393 16.2138C4.76171 16.0624 4.52183 15.9868 4.21429 15.9868C3.87599 15.9868 3.61766 16.0741 3.43929 16.2487C3.26091 16.4234 3.13175 16.6388 3.05179 16.8949L2 16.5194C2.06766 16.3215 2.163 16.1323 2.28601 15.9518C2.41518 15.7655 2.57202 15.6025 2.75655 15.4628C2.94722 15.3231 3.17173 15.2125 3.43006 15.131C3.68839 15.0437 3.98363 15 4.31577 15C4.66022 15 4.96468 15.0466 5.22917 15.1397C5.4998 15.227 5.72738 15.3522 5.91191 15.5152C6.09643 15.6724 6.23482 15.8616 6.32708 16.0828C6.4255 16.304 6.4747 16.5456 6.4747 16.8076C6.4747 17.0579 6.43165 17.2879 6.34554 17.4975C6.26558 17.707 6.15179 17.9079 6.00417 18.1C5.85655 18.2863 5.68125 18.4668 5.47827 18.6414C5.28145 18.8161 5.06617 18.9907 4.83244 19.1654L3.43006 20.2394H6.65V21.2Z" />
  </>
);

export const OrderedListIcon = createFunctionalIcon(
  'ordered_list_icon_label_a11y',
  OrderedListIcon16x16,
  OrderedListIcon24x24,
);

/**
 * PauseSolidIcon
 */

const PauseSolidIcon16x16 = () => (
  <>
    <path d="M7 2H2V14H7V2Z" />
    <path d="M14 2H9V14H14V2Z" />
  </>
);

const PauseSolidIcon24x24 = () => (
  <>
    <path d="M11 4H5V20H11V4Z" />
    <path d="M19 4H13V20H19V4Z" />
  </>
);

export const PauseSolidIcon = createFunctionalIcon(
  'pause_icon_label_a11y',
  PauseSolidIcon16x16,
  PauseSolidIcon24x24,
);

/**
 * PencilIcon
 */

const PencilIcon16x16 = () => (
  <path d="M11 -0.00976562L16.0089 4.99912L6.00888 14.999H1V9.99023L11 -0.00976562ZM2.25 10.508V13.749H5.49112L14.2411 4.99911L11 1.758L2.25 10.508Z" />
);

const PencilIcon24x24 = () => (
  <path d="M14.625 3.0625L20.9357 9.37316L9.31066 20.9982H3V14.6875L14.625 3.0625ZM4.75 15.4124V19.2482H8.58579L18.4608 9.37316L14.625 5.53737L4.75 15.4124Z" />
);

export const PencilIcon = createFunctionalIcon(
  'edit_icon_label_a11y',
  PencilIcon16x16,
  PencilIcon24x24,
);

/**
 * PencilSolidIcon
 */

const PencilSolidIcon16x16 = () => (
  <path d="M11 -0.0078125L16.0089 5.00107L6.00888 15.001H1V9.99219L11 -0.0078125Z" />
);

const PencilSolidIcon24x24 = () => (
  <path d="M14.625 3.06445L20.9357 9.37511L9.31066 21.0001H3V14.6895L14.625 3.06445Z" />
);

export const PencilSolidIcon = createFunctionalIcon(
  'edit_icon_label_a11y',
  PencilSolidIcon16x16,
  PencilSolidIcon24x24,
);

/**
 * PencilLineIcon
 */

const PencilLineIcon16x16 = () => (
  <path d="M11 -0.00976562L16.0089 4.99912L6.00888 14.999H1V9.99023L11 -0.00976562ZM2.25 10.508V13.749H5.49112L14.2411 4.99911L11 1.758L2.25 10.508ZM9 13.7491H15V14.9991H9V13.7491Z" />
);

const PencilLineIcon24x24 = () => (
  <path d="M14.625 2.0625L20.9357 8.37316L8.31066 20.9982H2V14.6875L14.625 2.0625ZM3.75 15.4124V19.2482H7.58579L18.4608 8.37316L14.625 4.53737L3.75 15.4124ZM13 19.2492H22V20.9992H13V19.2492Z" />
);

export const PencilLineIcon = createFunctionalIcon(
  'edit_icon_label_a11y',
  PencilLineIcon16x16,
  PencilLineIcon24x24,
);

/**
 * PencilLineSolidIcon
 */

const PencilLineSolidIcon16x16 = () => (
  <>
    <path d="M16.0089 5.00107L11 -0.0078125L1 9.99219V15.001H6.00888L16.0089 5.00107Z" />
    <path d="M15 13.75H9V15H15V13.75Z" />
  </>
);

const PencilLineSolidIcon24x24 = () => (
  <>
    <path d="M20.9357 8.37511L14.625 2.06445L2 14.6895V21.0001H8.31066L20.9357 8.37511Z" />
    <path d="M22 19.5H13V21H22V19.5Z" />
  </>
);

export const PencilLineSolidIcon = createFunctionalIcon(
  'edit_icon_label_a11y',
  PencilLineSolidIcon16x16,
  PencilLineSolidIcon24x24,
);

/**
 * PencilRulerIcon
 */

const PencilRulerIcon16x16 = () => (
  <>
    <path d="M7 12.062L4 15.25L1 12.062V1H7V12.062ZM5.75 2.25H2.25V11.5664L4 13.426L5.75 11.5664V2.25Z" />
    <path d="M9 1V15H15V1H9ZM13.75 2.25H10.25V4.30005H12.4375V5.55005H10.25V7.375H11.6875V8.625H10.25V10.45H12.4375V11.7H10.25V13.75H13.75V2.25Z" />
  </>
);

const PencilRulerIcon24x24 = () => (
  <>
    <path d="M11 17.6144L6.5 21.5625L2 17.6144V3H11V17.6144ZM9.25 4.75H3.75V16.8218L6.5 19.2345L9.25 16.8218V4.75Z" />
    <path d="M13 3V21H22V3H13ZM20.25 4.75H14.75V7H18V8.75H14.75V11H17V12.75H14.75V15H18V16.75H14.75V19.25H20.25V4.75Z" />
  </>
);

export const PencilRulerIcon = createFunctionalIcon(
  'edit_icon_label_a11y',
  PencilRulerIcon16x16,
  PencilRulerIcon24x24,
);

/**
 * PencilRulerSolidIcon
 */

const PencilRulerSolidIcon16x16 = () => (
  <>
    <path d="M7 1H1V12.062L4 15.25L7 12.062V1Z" />
    <path d="M9 1H15V15H9V12H12V11H9V8.5H11V7.5H9V5H12V4H9V1Z" />
  </>
);

const PencilRulerSolidIcon24x24 = () => (
  <>
    <path d="M11 3H3V17.6144L7 21.5625L11 17.6144V3Z" />
    <path d="M14 3H22V21H14V17.5H18V16H14V13H17V11.5H14V8.5H18V7H14V3Z" />
  </>
);

export const PencilRulerSolidIcon = createFunctionalIcon(
  'edit_icon_label_a11y',
  PencilRulerSolidIcon16x16,
  PencilRulerSolidIcon24x24,
);

/**
 * PercentIcon
 */

const PercentIcon16x16 = () => (
  <path d="M3.5 3.125C3.29289 3.125 3.125 3.29289 3.125 3.5C3.125 3.70711 3.29289 3.875 3.5 3.875C3.70711 3.875 3.875 3.70711 3.875 3.5C3.875 3.29289 3.70711 3.125 3.5 3.125ZM1.875 3.5C1.875 2.60254 2.60254 1.875 3.5 1.875C4.39746 1.875 5.125 2.60254 5.125 3.5C5.125 4.39746 4.39746 5.125 3.5 5.125C2.60254 5.125 1.875 4.39746 1.875 3.5ZM2.05806 13.0581L13.0581 2.05806L13.9419 2.94194L2.94194 13.9419L2.05806 13.0581ZM12.5 12.125C12.2929 12.125 12.125 12.2929 12.125 12.5C12.125 12.7071 12.2929 12.875 12.5 12.875C12.7071 12.875 12.875 12.7071 12.875 12.5C12.875 12.2929 12.7071 12.125 12.5 12.125ZM10.875 12.5C10.875 11.6025 11.6025 10.875 12.5 10.875C13.3975 10.875 14.125 11.6025 14.125 12.5C14.125 13.3975 13.3975 14.125 12.5 14.125C11.6025 14.125 10.875 13.3975 10.875 12.5Z" />
);

const PercentIcon24x24 = () => (
  <path d="M6.5 5.875C6.15482 5.875 5.875 6.15482 5.875 6.5C5.875 6.84518 6.15482 7.125 6.5 7.125C6.84518 7.125 7.125 6.84518 7.125 6.5C7.125 6.15482 6.84518 5.875 6.5 5.875ZM4.125 6.5C4.125 5.18832 5.18832 4.125 6.5 4.125C7.81168 4.125 8.875 5.18832 8.875 6.5C8.875 7.81168 7.81168 8.875 6.5 8.875C5.18832 8.875 4.125 7.81168 4.125 6.5ZM4.38128 18.3813L18.3813 4.38128L19.6187 5.61872L5.61872 19.6187L4.38128 18.3813ZM17.5 16.875C17.1548 16.875 16.875 17.1548 16.875 17.5C16.875 17.8452 17.1548 18.125 17.5 18.125C17.8452 18.125 18.125 17.8452 18.125 17.5C18.125 17.1548 17.8452 16.875 17.5 16.875ZM15.125 17.5C15.125 16.1883 16.1883 15.125 17.5 15.125C18.8117 15.125 19.875 16.1883 19.875 17.5C19.875 18.8117 18.8117 19.875 17.5 19.875C16.1883 19.875 15.125 18.8117 15.125 17.5Z" />
);

export const PercentIcon = createFunctionalIcon(
  'percent_icon_label_a11y',
  PercentIcon16x16,
  PercentIcon24x24,
);

/**
 * PieIcon
 */

const PieIcon16x16 = () => (
  <path d="M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1ZM2.25 8C2.25 4.82436 4.82436 2.25 8 2.25V8.0125H13.75C13.7432 11.1824 11.1715 13.75 8 13.75C4.82436 13.75 2.25 11.1756 2.25 8ZM13.6165 6.7625C13.1388 4.5844 11.4263 2.8687 9.25 2.38625V6.7625H13.6165Z" />
);

const PieIcon24x24 = () => (
  <path d="M11.3739 3.7734C7.11004 4.09335 3.75 7.65429 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.3464 20.25 19.9078 16.8889 20.2267 12.6241H11.3739V3.7734ZM13.1239 3.82591V10.8741H20.1738C19.6749 7.21785 16.7804 4.32398 13.1239 3.82591ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z" />
);

export const PieIcon = createFunctionalIcon(
  'pie_chart_icon_label_a11y',
  PieIcon16x16,
  PieIcon24x24,
);

/**
 * PieSolidIcon
 */

const PieSolidIcon16x16 = () => (
  <path d="M1 8.00442C1.00238 11.8529 4.11048 14.9749 7.95368 14.9999C7.98346 15 8.01324 15.0001 8.04302 14.9999C11.875 14.9768 14.977 11.8747 14.9999 8.04258C15 8.02839 15 8.0142 15 8C15 4.13401 11.866 1 8 1C4.13415 1 1.00023 4.13378 1 7.99958L1 8.00442ZM13.75 8C13.75 4.82436 11.1756 2.25 8 2.25L8 8H13.75Z" />
);

const PieSolidIcon24x24 = () => (
  <path d="M22 12C22 17.502 17.5566 21.9662 12.0625 21.9998C12.0181 22.0001 11.9737 22.0001 11.9293 21.9998C9.2031 21.9809 6.73609 20.8711 4.94305 19.0852C4.93363 19.0758 4.92423 19.0664 4.91485 19.057C3.61527 17.7522 2.67371 16.0906 2.25 14.2319C2.08681 13.516 2.00043 12.7709 2 12.0057L2 12C2 8.5404 3.75682 5.49114 6.42699 3.69566C7.91632 2.6942 9.68979 2.0828 11.6011 2.00781C11.7334 2.00262 11.8664 2 12 2C17.5229 2 22 6.47715 22 12ZM12 3.75L12 12H20.25C20.25 7.44365 16.5563 3.75 12 3.75Z" />
);

export const PieSolidIcon = createFunctionalIcon(
  'pie_chart_icon_label_a11y',
  PieSolidIcon16x16,
  PieSolidIcon24x24,
);

/**
 * PlaceholderIcon
 */

const PlaceholderIcon16x16 = () => (
  <path d="M1 1H4.8125V2.25H2.25V4.8125H1V1ZM13.75 2.25H11.1875V1H15V4.8125H13.75V2.25ZM2.25 13.75V11.1875H1V15H4.8125V13.75H2.25ZM13.75 13.75V11.1875H15V15H11.1875V13.75H13.75Z" />
);

const PlaceholderIcon24x24 = () => (
  <path d="M3 3H7.9375V4.75H4.75V7.9375H3V3ZM19.25 4.75H16.0625V3H21V7.9375H19.25V4.75ZM4.75 19.25V16.0625H3V21H7.9375V19.25H4.75ZM19.25 19.25V16.0625H21V21H16.0625V19.25H19.25Z" />
);

export const PlaceholderIcon = createFunctionalIcon(
  'placeholder_icon_label_a11y',
  PlaceholderIcon16x16,
  PlaceholderIcon24x24,
);

/**
 * PlaySolidIcon
 */

const PlaySolidIcon16x16 = () => <path d="M4 1.75V14.25L14.5 8L4 1.75Z" />;

const PlaySolidIcon24x24 = () => <path d="M5 3V21L21 12L5 3Z" />;

export const PlaySolidIcon = createFunctionalIcon(
  'play_icon_label_a11y',
  PlaySolidIcon16x16,
  PlaySolidIcon24x24,
);

/**
 * PlusIcon
 */

const PlusIcon16x16 = () => (
  <path d="M7.375 7.375V3H8.625V7.375L13 7.375V8.625L8.625 8.625V13H7.375V8.625L3 8.625V7.375L7.375 7.375Z" />
);

const PlusIcon24x24 = () => (
  <path d="M11.125 11.125V5H12.875V11.125H19V12.875H12.875V19H11.125V12.875H5V11.125H11.125Z" />
);

export const PlusIcon = createFunctionalIcon(
  'add_icon_label_a11y',
  PlusIcon16x16,
  PlusIcon24x24,
);

/**
 * PresentationIcon
 */

const PresentationIcon16x16 = () => (
  <path d="M0 1H16V12H0V1ZM1.25 2.25V10.75H14.75V2.25H1.25ZM6.5 4.43654L10.0069 6.49942L6.5 8.5623V4.43654ZM12 15H4V13.75H12V15Z" />
);

const PresentationIcon24x24 = () => (
  <>
    <path d="M15.75 10.5L9.75 7.00003V14L15.75 10.5Z" />
    <path d="M23 3H1V18H23V3ZM2.75 16.25V4.75H21.25V16.25H2.75Z" />
    <path d="M7 21H17V19.25H7V21Z" />
  </>
);

export const PresentationIcon = createFunctionalIcon(
  'presentation_icon_label_a11y',
  PresentationIcon16x16,
  PresentationIcon24x24,
);

/**
 * ProjectorIcon
 */

const ProjectorIcon16x16 = () => (
  <path d="M0 1H16V12H0V1ZM1.25 2.25V10.75H14.75V2.25H1.25ZM12 15H4V13.75H12V15Z" />
);

const ProjectorIcon24x24 = () => (
  <path d="M1 3H23V18H1V3ZM2.75 4.75V16.25H21.25V4.75H2.75ZM17 21H7V19.25H17V21Z" />
);

export const ProjectorIcon = createFunctionalIcon(
  'projector_icon_label_a11y',
  ProjectorIcon16x16,
  ProjectorIcon24x24,
);

/**
 * QAIcon
 */
const QAIcon16x16 = () => (
  <path d="M6.5 2.25C4.1528 2.25 2.25 4.1528 2.25 6.5V10.75H6.00683C6.0023 10.6672 6 10.5839 6 10.5C6 8.01472 8.01472 6 10.5 6C10.5743 6 10.6481 6.0018 10.7215 6.00536C10.4765 3.8912 8.67988 2.25 6.5 2.25ZM11.9946 6.25416C11.866 3.33069 9.45517 1 6.5 1C3.46245 1 1 3.46245 1 6.5V12H6.25606C6.87382 13.7478 8.54067 15 10.5 15H15V10.5C15 8.53866 13.7452 6.87039 11.9946 6.25416ZM10.5 7.25C8.70508 7.25 7.25 8.70508 7.25 10.5C7.25 12.2949 8.70508 13.75 10.5 13.75H13.75V10.5C13.75 8.70508 12.2949 7.25 10.5 7.25Z" />
);

const QAIcon24x24 = () => (
  <path d="M10 3.75C6.54825 3.75 3.75 6.54823 3.75 10V16.25H10C10.0016 16.25 10.0031 16.25 10.0047 16.25C10.133 12.859 12.859 10.133 16.25 10.0047C16.25 10.0031 16.25 10.0016 16.25 10C16.25 6.54823 13.4518 3.75 10 3.75ZM17.9982 10.1735C17.9994 10.1158 18 10.058 18 10C18 5.58172 14.4182 2 10 2C5.58175 2 2 5.58172 2 10V18H10C10.058 18 10.1158 17.9994 10.1735 17.9982C10.85 20.8658 13.4258 23 16.5 23H23V16.5C23 13.4258 20.8658 10.85 17.9982 10.1735ZM16.5 11.75C13.8767 11.75 11.75 13.8767 11.75 16.5C11.75 19.1233 13.8767 21.25 16.5 21.25H21.25V16.5C21.25 13.8767 19.1233 11.75 16.5 11.75Z" />
);

export const QAIcon = createFunctionalIcon(
  'questions_and_answers_icon_label_a11y',
  QAIcon16x16,
  QAIcon24x24,
);

/**
 * QASolidIcon
 */
const QASolidIcon16x16 = () => (
  <>
    <path d="M10.5 4.75C10.9329 4.75 11.3546 4.79784 11.7601 4.88851C11.0713 2.63724 8.97684 1 6.5 1C3.46245 1 1 3.46243 1 6.5V12H4.94762C4.81874 11.5218 4.75 11.0189 4.75 10.5C4.75 7.32436 7.32438 4.75 10.5 4.75Z" />
    <path d="M10.5 15C8.01473 15 6 12.9853 6 10.5C6 8.01472 8.01473 6 10.5 6C12.9853 6 15 8.01472 15 10.5V15H10.5Z" />
  </>
);

const QASolidIcon24x24 = () => (
  <>
    <path d="M16.5 8.25C16.9532 8.25 17.3978 8.28654 17.8311 8.35682C17.0733 4.72677 13.855 2 10 2C5.58174 2 2 5.58172 2 10V18H8.38603C8.29668 17.5136 8.25 17.0122 8.25 16.5C8.25 11.9436 11.9437 8.25 16.5 8.25Z" />
    <path d="M16.5 23C12.9102 23 10 20.0899 10 16.5C10 12.9101 12.9102 10 16.5 10C20.0898 10 23 12.9101 23 16.5V23H16.5Z" />
  </>
);

export const QASolidIcon = createFunctionalIcon(
  'questions_and_answers_icon_label_a11y',
  QASolidIcon16x16,
  QASolidIcon24x24,
);

/**
 * QrCodeIcon
 */
const QrCodeIcon16x16 = () => (
  <path d="M1 1H7V7H1V1ZM2.25 2.25V5.75H5.75V2.25H2.25ZM9 1H15V7H9V1ZM10.25 2.25V5.75H13.75V2.25H10.25ZM3.25 3.25H4.75V4.75H3.25V3.25ZM11.25 3.25H12.75V4.75H11.25V3.25ZM1 9H7V15H1V9ZM2.25 10.25V13.75H5.75V10.25H2.25ZM9 9H10.5V10.5H9V9ZM13.5 9H15V10.5H13.5V9ZM3.25 11.25H4.75V12.75H3.25V11.25ZM11.25 11.375H12.75V12.875H11.25V11.375ZM9 13.5H10.5V15H9V13.5ZM13.5 13.5H15V15H13.5V13.5Z" />
);

const QrCodeIcon24x24 = () => (
  <path d="M3 3H11V11H3V3ZM4.75 4.75V9.25H9.25V4.75H4.75ZM13 3H21V11H13V3ZM14.75 4.75V9.25H19.25V4.75H14.75ZM6 6H8V8H6V6ZM16 6H18V8H16V6ZM3 13H11V21H3V13ZM4.75 14.75V19.25H9.25V14.75H4.75ZM13 13H15V15H13V13ZM19 13H21V15H19V13ZM6 16H8V18H6V16ZM16 16H18V18H16V16ZM13 19H15V21H13V19ZM19 19H21V21H19V19Z" />
);

export const QrCodeIcon = createFunctionalIcon(
  'qr_code_icon_label_a11y',
  QrCodeIcon16x16,
  QrCodeIcon24x24,
);

/**
 * QuestionMarkCircleIcon
 */
const QuestionMarkCircleIcon16x16 = () => (
  <path d="M8 2.25C4.82436 2.25 2.25 4.82436 2.25 8C2.25 11.1756 4.82436 13.75 8 13.75C11.1756 13.75 13.75 11.1756 13.75 8C13.75 4.82436 11.1756 2.25 8 2.25ZM1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8ZM8.09865 5.375C7.4083 5.375 6.84865 5.93464 6.84865 6.625V6.76923H5.59865V6.625C5.59865 5.24429 6.71794 4.125 8.09865 4.125H8.23965C9.54249 4.125 10.5987 5.18116 10.5987 6.484C10.5987 7.16677 10.3028 7.81605 9.78763 8.26408L9.17194 8.79949C8.82447 9.10165 8.62497 9.53953 8.62497 10H7.37497C7.37497 9.17775 7.73122 8.39582 8.35169 7.85626L8.96738 7.32084C9.20959 7.11022 9.34865 6.80498 9.34865 6.484C9.34865 5.87152 8.85214 5.375 8.23965 5.375H8.09865ZM7.375 12V11.125H8.625V12H7.375Z" />
);

const QuestionMarkCircleIcon24x24 = () => (
  <path d="M12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM12 8.375C11.2406 8.375 10.625 8.99061 10.625 9.75V10H8.875V9.75C8.875 8.02411 10.2741 6.625 12 6.625H12.3632C14.0266 6.625 15.375 7.97345 15.375 9.63684C15.375 10.5204 14.987 11.3594 14.3138 11.9317L13.5191 12.6071C13.1105 12.9545 12.875 13.4637 12.875 14H11.125C11.125 12.9503 11.5859 11.9536 12.3857 11.2737L13.1804 10.5983C13.4624 10.3585 13.625 10.007 13.625 9.63684C13.625 8.93994 13.0601 8.375 12.3632 8.375H12ZM11.125 17V15.25H12.875V17H11.125Z" />
);

export const QuestionMarkCircleIcon = createFunctionalIcon(
  'description_icon_label_a11y',
  QuestionMarkCircleIcon16x16,
  QuestionMarkCircleIcon24x24,
);

/**
 * QuestionMarkIcon
 */
const QuestionMarkIcon16x16 = () => (
  <path d="M7.875 3.625C6.77043 3.625 5.875 4.52043 5.875 5.625V6H4.625V5.625C4.625 3.83007 6.08007 2.375 7.875 2.375H8.03396C9.74109 2.375 11.125 3.75891 11.125 5.46604V5.55588C11.125 6.50822 10.6603 7.40067 9.88015 7.9468C9.0935 8.49746 8.625 9.39729 8.625 10.3575V10.625H7.375V10.3575C7.375 8.9894 8.04252 7.70733 9.16333 6.92276C9.60936 6.61054 9.875 6.10033 9.875 5.55588V5.46604C9.875 4.44926 9.05074 3.625 8.03396 3.625H7.875ZM7.375 14V12.75H8.625V14H7.375Z" />
);

const QuestionMarkIcon24x24 = () => (
  <path d="M11.9342 5.875C10.5208 5.875 9.375 7.0208 9.375 8.43421V9H7.625V8.43421C7.625 6.0543 9.5543 4.125 11.9342 4.125H12.3158C14.6957 4.125 16.625 6.0543 16.625 8.43421V8.55555C16.625 9.89587 16.0013 11.1599 14.9376 11.9754L14.0204 12.6786C13.2983 13.2322 12.875 14.0902 12.875 15H11.125C11.125 13.5459 11.8016 12.1745 12.9556 11.2898L13.8729 10.5866C14.5046 10.1022 14.875 9.35155 14.875 8.55555V8.43421C14.875 7.0208 13.7292 5.875 12.3158 5.875H11.9342ZM11.125 19.25V17.5H12.875V19.25H11.125Z" />
);

export const QuestionMarkIcon = createFunctionalIcon(
  'help_icon_label_a11y',
  QuestionMarkIcon16x16,
  QuestionMarkIcon24x24,
);

/**
 * RefreshIcon
 */
const RefreshIcon16x16 = () => (
  <path d="M4.58354 1.89042C6.01032 1.09255 7.67136 0.819187 9.27859 1.11776C10.8858 1.41632 12.3379 2.26799 13.3829 3.52505C14.428 4.78211 15.0001 6.36529 15.0001 8.00001H13.7501C13.7501 6.6572 13.2801 5.35674 12.4217 4.32415C11.5633 3.29156 10.3705 2.59198 9.05029 2.34673C7.73007 2.10148 6.36564 2.32602 5.19364 2.98142C4.32562 3.46683 3.6037 4.16589 3.09169 5.005H6.255V6.255H1V1H2.25V4.00797C2.85271 3.13986 3.6485 2.41331 4.58354 1.89042ZM2.54506 12.3866C1.5452 11.1432 1.00012 9.59554 1.00012 8H2.25012C2.25012 9.31062 2.69786 10.5819 3.51918 11.6033C4.34049 12.6246 5.48607 13.3348 6.76616 13.616C8.04625 13.8973 9.38401 13.7328 10.5578 13.1498C11.5311 12.6664 12.3432 11.9194 12.9055 11H9.62512V9.75H15.0001V15.125H13.7501V11.9922C13.081 12.9559 12.1763 13.7417 11.1139 14.2693C9.68486 14.9791 8.05628 15.1793 6.49791 14.8369C4.93954 14.4945 3.54491 13.63 2.54506 12.3866Z" />
);

const RefreshIcon24x24 = () => (
  <path d="M2 2H3.75V6.3473C5.55159 3.72289 8.57393 2 12 2C17.5228 2 22 6.47715 22 12H20.25C20.25 7.44365 16.5563 3.75 12 3.75C9.21311 3.75 6.74767 5.1319 5.25359 7.25H9V9H2V2ZM3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C14.7869 20.25 17.2523 18.8681 18.7464 16.75H15V15H22V22H20.25V17.6527C18.4484 20.2771 15.4261 22 12 22C6.47715 22 2 17.5228 2 12H3.75Z" />
);

export const RefreshIcon = createFunctionalIcon(
  'reload_page_icon_label_a11y',
  RefreshIcon16x16,
  RefreshIcon24x24,
);

/**
 * ResendIcon
 */
const ResendIcon16x16 = () => (
  <path d="M5.5 2.125C3.70507 2.125 2.25 3.58008 2.25 5.375V7.95833H1V5.375C1 2.88972 3.01472 0.875 5.5 0.875C7.98528 0.875 10 2.88972 10 5.375V7.85802L11.2419 6.61612L12.1258 7.50002L9.37092 10.2548L6.61612 7.5L7.5 6.61612L8.75 7.86612V5.375C8.75 3.58007 7.29493 2.125 5.5 2.125Z" />
);

const ResendIcon24x24 = () => (
  <path d="M9 3.125C6.1005 3.125 3.75 5.4755 3.75 8.375V11H2V8.375C2 4.50901 5.13401 1.375 9 1.375C12.866 1.375 16 4.50901 16 8.375V12.2624L17.9315 10.3309L19.1689 11.5684L15.125 15.6122L11.0812 11.5684L12.3186 10.3309L14.25 12.2623V8.375C14.25 5.4755 11.8995 3.125 9 3.125Z" />
);

export const ResendIcon = createFunctionalIcon(
  'resend_icon_label_a11y',
  ResendIcon16x16,
  ResendIcon24x24,
);

/**
 * ResetResultsIcon
 */
const ResetResultsIcon16x16 = () => (
  <path d="M3.7387 2.44653C4.96121 1.50846 6.45909 1 8.00003 1C9.54097 1 11.0388 1.50846 12.2614 2.44653C13.4839 3.38459 14.3627 4.69983 14.7615 6.18827C15.1603 7.6767 15.0569 9.25514 14.4672 10.6788C13.8775 12.1024 12.8345 13.2917 11.5 14.0622C10.1655 14.8326 8.6141 15.1412 7.08634 14.9401C5.55859 14.739 4.13989 14.0394 3.05028 12.9497L3.93416 12.0659C4.8292 12.9609 5.99456 13.5356 7.2495 13.7008C8.50444 13.866 9.77884 13.6125 10.875 12.9796C11.9712 12.3468 12.8279 11.3699 13.3123 10.2004C13.7967 9.03101 13.8817 7.73443 13.5541 6.51179C13.2265 5.28915 12.5046 4.20877 11.5004 3.43822C10.4962 2.66767 9.2658 2.25 8.00003 2.25C6.73426 2.25 5.50385 2.66767 4.49965 3.43822C3.9341 3.87218 3.45808 4.40442 3.09162 5.005H5V6.255H1V2H2.25V4.00786C2.65986 3.41752 3.16089 2.88989 3.7387 2.44653ZM9.75 11L9.75 5H11L11 11H9.75ZM7.75 11L7.75 7H9V11H7.75ZM5.75 11V9H7V11H5.75Z" />
);

const ResetResultsIcon24x24 = () => (
  <path d="M11.0198 2.04817C13.3222 1.8214 15.6321 2.39998 17.5557 3.68532C19.4794 4.97066 20.8978 6.88324 21.5694 9.09717C22.241 11.3111 22.1242 13.6894 21.2388 15.8269C20.3534 17.9643 18.7543 19.7286 16.714 20.8192C14.6736 21.9098 12.3182 22.2592 10.0491 21.8079C7.78 21.3565 5.7376 20.1323 4.26989 18.3439C2.80219 16.5555 2 14.3136 2 12H3.75C3.75 13.9087 4.41181 15.7583 5.62266 17.2338C6.83352 18.7092 8.5185 19.7191 10.3905 20.0915C12.2625 20.4639 14.2057 20.1756 15.889 19.2759C17.5723 18.3761 18.8916 16.9205 19.622 15.1572C20.3524 13.3938 20.4488 11.4317 19.8948 9.60517C19.3407 7.77867 18.1705 6.2008 16.5835 5.14039C14.9964 4.07999 13.0909 3.60266 11.1914 3.78974C9.29417 3.9766 7.52032 4.81471 6.17128 6.16148L5.11765 7.25002H8V9.00002H2V3.00002H3.75V6.14677L4.92389 4.93399L4.92893 4.92895C6.56486 3.29302 8.71742 2.27494 11.0198 2.04817ZM14.25 15L14.25 8.00002H16L16 15H14.25ZM11.125 15V10.5H12.875V15H11.125ZM8 15V13H9.75V15H8Z" />
);

export const ResetResultsIcon = createFunctionalIcon(
  'reset_input_results_for_slide_icon_label_a11y',
  ResetResultsIcon16x16,
  ResetResultsIcon24x24,
);

/**
 * RestoreInputsIcon
 */
const RestoreInputsIcon16x16 = () => (
  <path d="M3.7387 2.44653C4.96121 1.50846 6.45909 1 8.00003 1C9.54097 1 11.0388 1.50846 12.2614 2.44653C13.4839 3.38459 14.3627 4.69983 14.7615 6.18827C15.1603 7.6767 15.0569 9.25514 14.4672 10.6788C13.8775 12.1024 12.8345 13.2917 11.5 14.0622C10.1655 14.8326 8.6141 15.1412 7.08634 14.9401C5.55859 14.739 4.13989 14.0394 3.05028 12.9497L3.93416 12.0659C4.8292 12.9609 5.99456 13.5356 7.2495 13.7008C8.50444 13.866 9.77884 13.6125 10.875 12.9796C11.9712 12.3468 12.8279 11.3699 13.3123 10.2004C13.7967 9.03101 13.8817 7.73443 13.5541 6.51179C13.2265 5.28915 12.5046 4.20877 11.5004 3.43822C10.4962 2.66767 9.2658 2.25 8.00003 2.25C6.73426 2.25 5.50385 2.66767 4.49965 3.43822C3.93409 3.87218 3.45808 4.40442 3.09162 5.005H5V6.255H1V2H2.25V4.00786C2.65986 3.41752 3.16089 2.88989 3.7387 2.44653ZM7.20708 4.375H8.79292L11.3309 10.7694L10.1691 11.2306L9.4326 9.375H6.5674L5.83092 11.2306L4.66908 10.7694L7.20708 4.375ZM7.06354 8.125L8.93646 8.125L8 5.7656L7.06354 8.125Z" />
);

const RestoreInputsIcon24x24 = () => (
  <path d="M11.0198 2.04817C13.3222 1.8214 15.632 2.39998 17.5557 3.68532C19.4793 4.97066 20.8978 6.88323 21.5694 9.09716C22.241 11.3111 22.1241 13.6894 21.2388 15.8268C20.3534 17.9643 18.7543 19.7286 16.7139 20.8192C14.6736 21.9098 12.3182 22.2592 10.0491 21.8078C7.77996 21.3565 5.73756 20.1323 4.26986 18.3439L5.62262 17.2337C6.83348 18.7092 8.51846 19.7191 10.3905 20.0915C12.2625 20.4638 14.2057 20.1756 15.889 19.2758C17.5723 18.3761 18.8915 16.9205 19.622 15.1571C20.3524 13.3937 20.4488 11.4317 19.8947 9.60516C19.3407 7.77867 18.1704 6.2008 16.5834 5.14039C14.9964 4.07999 13.0908 3.60266 11.1913 3.78974C9.29413 3.9766 7.52028 4.81471 6.17124 6.16148L5.11762 7.25H8V9H2V3H3.75V6.14672L4.92385 4.93398L4.92889 4.92894C6.56482 3.29302 8.71738 2.27494 11.0198 2.04817ZM10.9267 7.125H13.0733L16.3182 15.69L14.6818 16.31L13.7592 13.875H10.2408L9.31824 16.31L7.68176 15.69L10.9267 7.125ZM10.9038 12.125H13.0962L12 9.23163L10.9038 12.125Z" />
);

export const RestoreInputsIcon = createFunctionalIcon(
  'restore_inputs_from_participants_icon_label_a11y',
  RestoreInputsIcon16x16,
  RestoreInputsIcon24x24,
);

/**
 * ResultsChartIcon
 */
const ResultsChartIcon16x16 = () => (
  <path d="M0.375 0.375H15.625V15.625H0.375V0.375ZM1.625 1.625V14.375H14.375V1.625H1.625ZM4.375 12L4.375 5H5.625L5.625 12H4.375ZM10.375 12V7H11.625V12H10.375ZM7.375 12V8H8.625V12H7.375Z" />
);

const ResultsChartIcon24x24 = () => (
  <path d="M3 3H21V21H3V3ZM4.75 4.75V19.25H19.25V4.75H4.75ZM7.125 17V7H8.875V17H7.125ZM15.125 17V9H16.875V17H15.125ZM11.125 17V12H12.875V17H11.125Z" />
);

export const ResultsChartIcon = createFunctionalIcon(
  'results_icon_label_a11y',
  ResultsChartIcon16x16,
  ResultsChartIcon24x24,
);

/**
 * ResultsChartSolidIcon
 */
const ResultsChartSolidIcon16x16 = () => (
  <path d="M15 1H1V15H15V1ZM4.375 5L4.375 12H5.625L5.625 5H4.375ZM7.375 12V8H8.625V12H7.375ZM10.375 7V12H11.625V7H10.375Z" />
);

const ResultsChartSolidIcon24x24 = () => (
  <path d="M21 3H3V21H21V3ZM7.125 7V17H8.875V7H7.125ZM11.125 17V12H12.875V17H11.125ZM15.125 9V17H16.875V9H15.125Z" />
);

export const ResultsChartSolidIcon = createFunctionalIcon(
  'results_icon_label_a11y',
  ResultsChartSolidIcon16x16,
  ResultsChartSolidIcon24x24,
);

/**
 * RotateCcwIcon
 */
const RotateCcwIcon16x16 = () => (
  <path d="M6.92455 1.08312C8.54384 0.831351 10.2001 1.15568 11.6049 1.99961C13.0096 2.84353 14.0737 4.15355 14.6118 5.70143C15.15 7.24931 15.128 8.93692 14.5497 10.4702C13.9714 12.0036 12.8734 13.2854 11.4472 14.0924C10.0209 14.8994 8.3567 15.1804 6.74453 14.8865C5.13235 14.5926 3.6744 13.7424 2.62467 12.484C1.57495 11.2256 1 9.63876 1 8.00001H2.25C2.25 9.34613 2.72228 10.6496 3.58455 11.6833C4.44682 12.7169 5.64443 13.4153 6.96872 13.6568C8.29301 13.8982 9.66002 13.6674 10.8316 13.0045C12.0032 12.3416 12.905 11.2886 13.3801 10.0291C13.8551 8.76962 13.8732 7.38337 13.4312 6.11189C12.9891 4.84042 12.115 3.76433 10.9611 3.07111C9.80726 2.37788 8.44672 2.11147 7.11659 2.31828C5.79115 2.52436 4.57962 3.18732 3.69152 4.19219L3.0415 5.00501H5V6.25501H1V2.00001H2.25V3.99333L2.73379 3.38838L2.74344 3.3774C3.82562 2.14681 5.30525 1.33489 6.92455 1.08312Z" />
);

const RotateCcwIcon24x24 = () => (
  <path d="M11.0198 2.04817C13.3222 1.8214 15.6321 2.39998 17.5557 3.68532C19.4794 4.97066 20.8978 6.88324 21.5694 9.09717C22.241 11.3111 22.1242 13.6894 21.2388 15.8269C20.3534 17.9643 18.7543 19.7286 16.714 20.8192C14.6736 21.9098 12.3182 22.2592 10.0491 21.8079C7.78 21.3565 5.7376 20.1323 4.26989 18.3439C2.80219 16.5555 2 14.3136 2 12H3.75C3.75 13.9087 4.41181 15.7583 5.62266 17.2338C6.83352 18.7092 8.5185 19.7191 10.3905 20.0915C12.2625 20.4639 14.2057 20.1756 15.889 19.2759C17.5723 18.3761 18.8916 16.9205 19.622 15.1572C20.3524 13.3938 20.4488 11.4317 19.8948 9.60517C19.3407 7.77867 18.1705 6.2008 16.5835 5.14039C14.9964 4.07999 13.0909 3.60266 11.1914 3.78974C9.29417 3.9766 7.52032 4.81471 6.17128 6.16148L5.11765 7.25002H8V9.00002H2V3.00002H3.75V6.14677L4.92389 4.93399L4.92893 4.92895C6.56486 3.29302 8.71742 2.27494 11.0198 2.04817Z" />
);

export const RotateCcwIcon = createFunctionalIcon(
  'rotate_counterclockwise_icon_label_a11y',
  RotateCcwIcon16x16,
  RotateCcwIcon24x24,
);

/**
 * RotateCwIcon
 */
const RotateCwIcon16x16 = () => (
  <path d="M8.88341 2.31828C7.55328 2.11147 6.19274 2.37788 5.03885 3.07111C3.88496 3.76433 3.01086 4.84042 2.56884 6.11189C2.12682 7.38337 2.1449 8.76962 2.61993 10.0291C3.09496 11.2886 3.99683 12.3416 5.1684 13.0045C6.33998 13.6674 7.70699 13.8982 9.03128 13.6568C10.3556 13.4153 11.5532 12.7169 12.4154 11.6833C13.2777 10.6496 13.75 9.34613 13.75 8.00001H15C15 9.63876 14.4251 11.2256 13.3753 12.484C12.3256 13.7424 10.8676 14.5926 9.25547 14.8865C7.6433 15.1804 5.9791 14.8994 4.55284 14.0924C3.12657 13.2854 2.02864 12.0036 1.45035 10.4702C0.872048 8.93692 0.85004 7.24931 1.38815 5.70143C1.92627 4.15355 2.99039 2.84353 4.39513 1.99961C5.79986 1.15568 7.45616 0.831351 9.07545 1.08312C10.6947 1.33489 12.1744 2.14681 13.2566 3.3774L13.2662 3.38838L13.75 3.99333V2H15V6.255H11V5.005H12.9585L12.3085 4.19219C11.4204 3.18733 10.2089 2.52436 8.88341 2.31828Z" />
);

const RotateCwIcon24x24 = () => (
  <path d="M12.8086 3.78974C10.9092 3.60266 9.00356 4.07999 7.41655 5.14039C5.82954 6.2008 4.65931 7.77867 4.10525 9.60517C3.55118 11.4317 3.64758 13.3938 4.378 15.1572C5.10842 16.9205 6.42767 18.3761 8.11098 19.2759C9.79429 20.1756 11.7375 20.4639 13.6095 20.0915C15.4815 19.7191 17.1665 18.7092 18.3773 17.2338C19.5882 15.7583 20.25 13.9087 20.25 12H22C22 14.3136 21.1978 16.5555 19.7301 18.3439C18.2624 20.1324 16.22 21.3565 13.9509 21.8079C11.6818 22.2592 9.32641 21.9098 7.28604 20.8192C5.24567 19.7286 3.64657 17.9643 2.76121 15.8269C1.87585 13.6894 1.75901 11.3111 2.4306 9.09717C3.10219 6.88324 4.52065 4.97066 6.4443 3.68532C8.36795 2.39998 10.6778 1.8214 12.9802 2.04817C15.2826 2.27494 17.4351 3.29302 19.0711 4.92895L19.0761 4.93399L20.25 6.14677V3H22V9H16V7.25H18.8823L17.8287 6.16146C16.4797 4.8147 14.7058 3.9766 12.8086 3.78974Z" />
);

export const RotateCwIcon = createFunctionalIcon(
  'rotate_clockwise_icon_label_a11y',
  RotateCwIcon16x16,
  RotateCwIcon24x24,
);

/**
 * RowsIcon
 */
const RowsIcon16x16 = () => (
  <path d="M1 1L15 1V15H1L1 1ZM2.25 2.25L2.25 5.125H13.75V2.25L2.25 2.25ZM13.75 6.375H2.25V9.625H13.75V6.375ZM13.75 10.875H2.25V13.75H13.75V10.875Z" />
);

const RowsIcon24x24 = () => (
  <path d="M3 3L21 3V21H3L3 3ZM4.75 4.75L4.75 8.25H19.25V4.75L4.75 4.75ZM19.25 10H4.75L4.75 14H19.25V10ZM19.25 15.75H4.75V19.25H19.25V15.75Z" />
);

export const RowsIcon = createFunctionalIcon(
  'rows_icon_label_a11y',
  RowsIcon16x16,
  RowsIcon24x24,
);

/**
 * SearchIcon
 */
const SearchIcon16x16 = () => (
  <path d="M7 2.75C4.65279 2.75 2.75 4.65279 2.75 7C2.75 9.34721 4.65279 11.25 7 11.25C9.34721 11.25 11.25 9.34721 11.25 7C11.25 4.65279 9.34721 2.75 7 2.75ZM1.5 7C1.5 3.96243 3.96243 1.5 7 1.5C10.0376 1.5 12.5 3.96243 12.5 7C12.5 8.2936 12.0534 9.48289 11.306 10.4221L13.9419 13.0581L13.0581 13.9419L10.4221 11.306C9.48289 12.0534 8.2936 12.5 7 12.5C3.96243 12.5 1.5 10.0376 1.5 7Z" />
);

const SearchIcon24x24 = () => (
  <path d="M11 4.625C7.47918 4.625 4.625 7.47918 4.625 11C4.625 14.5208 7.47918 17.375 11 17.375C14.5208 17.375 17.375 14.5208 17.375 11C17.375 7.47918 14.5208 4.625 11 4.625ZM2.875 11C2.875 6.51269 6.51269 2.875 11 2.875C15.4873 2.875 19.125 6.51269 19.125 11C19.125 12.9285 18.4532 14.7 17.3307 16.0933L21.1187 19.8813L19.8813 21.1187L16.0933 17.3307C14.7 18.4532 12.9285 19.125 11 19.125C6.51269 19.125 2.875 15.4873 2.875 11Z" />
);

export const SearchIcon = createFunctionalIcon(
  'search_icon_label_a11y',
  SearchIcon16x16,
  SearchIcon24x24,
);

/**
 * SegmentedBarsIcon
 */
const SegmentedBarsIcon16x16 = () => (
  <path d="M14 2V8.50016H12.75V2H14ZM8.625 5V8.49984H7.375V5H8.625ZM3.25 10V14H2V10H3.25ZM8.625 10V14H7.375V10H8.625ZM14 10V14H12.75V10H14Z" />
);

const SegmentedBarsIcon24x24 = () => (
  <path d="M18.25 2H20V12H18.25V2ZM12.875 7V12H11.125V7H12.875ZM5.75 14V20H4V14H5.75ZM12.875 14V20H11.125L11.125 14H12.875ZM20 14L20 20H18.25L18.25 14H20Z" />
);

export const SegmentedBarsIcon = createFunctionalIcon(
  'segmented_bar_chart_icon_label_a11y',
  SegmentedBarsIcon16x16,
  SegmentedBarsIcon24x24,
);

/**
 * SelectIcon
 */

const SelectIcon16x16 = () => (
  <>
    <path d="M11.5 6.25H4.5L8 9.75L11.5 6.25Z" />
    <path d="M13.75 2.25H2.25V13.75H13.75V2.25ZM1 1V15H15V1H1Z" />
  </>
);

const SelectIcon24x24 = () => (
  <>
    <path d="M16.5674 9.72461H7.44238L12.0049 14.2871L16.5674 9.72461Z" />
    <path d="M19.37 4.63H4.63V19.37H19.37V4.63ZM2.87988 2.88086V21.1309H21.1299V2.88086H2.87988Z" />
  </>
);

export const SelectIcon = createFunctionalIcon(
  'select_icon_label_a11y',
  SelectIcon16x16,
  SelectIcon24x24,
);

/**
 * SendIcon
 */
const SendIcon16x16 = () => (
  <path d="M0.116211 4H12.0001V15.8839L0.116211 4ZM3.13398 5.25L6.50009 8.61612L9.86621 5.25H3.13398ZM10.7501 6.13388L7.38398 9.5L10.7501 12.8661V6.13388Z" />
);

const SendIcon24x24 = () => (
  <path d="M0.0644531 4H20.0001V23.9357L0.0644531 4ZM4.28933 5.75L10.651 12.1117L17.0127 5.75H4.28933ZM18.2501 6.98745V19.7108L11.8885 13.3491L18.2501 6.98745Z" />
);

export const SendIcon = createFunctionalIcon(
  'send_icon_label_a11y',
  SendIcon16x16,
  SendIcon24x24,
);

/**
 * SendSolidIcon
 */
const SendSolidIcon16x16 = () => (
  <>
    <path d="M6.41176 10.2955L12.0001 15.8839V4.70721L6.41176 10.2955Z" />
    <path d="M11.2931 4H0.116211L5.70465 9.58844L11.2931 4Z" />
  </>
);

const SendSolidIcon24x24 = () => (
  <>
    <path d="M10.3858 14.3214L20.0001 23.9357V4.70713L10.3858 14.3214Z" />
    <path d="M19.293 4H0.0644531L9.67874 13.6143L19.293 4Z" />
  </>
);

export const SendSolidIcon = createFunctionalIcon(
  'send_icon_label_a11y',
  SendSolidIcon16x16,
  SendSolidIcon24x24,
);

/**
 * SettingsIcon
 */
const SettingsIcon16x16 = () => (
  <path d="M7.94969 0.0117188L15 4.0106V11.9882L8 15.9686L1 11.9882V4.01331L7.94969 0.0117188ZM7.95436 1.45144L2.25 4.73597V11.2611L8 14.5307L13.75 11.2611V4.73868L7.95436 1.45144ZM8 6.62416C7.24061 6.62416 6.625 7.23976 6.625 7.99916C6.625 8.75855 7.24061 9.37416 8 9.37416C8.75939 9.37416 9.375 8.75855 9.375 7.99916C9.375 7.23976 8.75939 6.62416 8 6.62416ZM5.375 7.99916C5.375 6.54941 6.55025 5.37416 8 5.37416C9.44975 5.37416 10.625 6.54941 10.625 7.99916C10.625 9.4489 9.44975 10.6242 8 10.6242C6.55025 10.6242 5.375 9.4489 5.375 7.99916Z" />
);

const SettingsIcon24x24 = () => (
  <path d="M12.0001 2.00781L21 6.98999V17.0081L12.0001 21.9903L3 17.0081V6.98999L12.0001 2.00781ZM4.75 8.02148V15.9766L12.0001 19.99L19.25 15.9766V8.02148L12.0001 4.00806L4.75 8.02148ZM12.0001 9.87512C10.8265 9.87512 9.87512 10.8265 9.87512 12.0001C9.87512 13.1737 10.8265 14.1251 12.0001 14.1251C13.1737 14.1251 14.1251 13.1737 14.1251 12.0001C14.1251 10.8265 13.1737 9.87512 12.0001 9.87512ZM8.12512 12.0001C8.12512 9.86002 9.86002 8.12512 12.0001 8.12512C14.1402 8.12512 15.8751 9.86002 15.8751 12.0001C15.8751 14.1402 14.1402 15.8751 12.0001 15.8751C9.86002 15.8751 8.12512 14.1402 8.12512 12.0001Z" />
);

export const SettingsIcon = createFunctionalIcon(
  'settings_icon_label_a11y',
  SettingsIcon16x16,
  SettingsIcon24x24,
);

/**
 * SettingsSolidIcon
 */
const SettingsSolidIcon16x16 = () => (
  <path d="M15 4.0106L7.94969 0.0117188L1 4.01331V11.9882L8 15.9686L15 11.9882V4.0106ZM8.01057 10.2863C9.27293 10.2863 10.2963 9.26292 10.2963 8.00056C10.2963 6.73819 9.27293 5.71484 8.01057 5.71484C6.7482 5.71484 5.72485 6.73819 5.72485 8.00056C5.72485 9.26292 6.7482 10.2863 8.01057 10.2863Z" />
);

const SettingsSolidIcon24x24 = () => (
  <path d="M21 6.98998L12.0001 2.00781L3 6.98997V17.0081L12.0001 21.9903L21 17.0081V6.98998ZM12.0001 15C13.6569 15 15.0001 13.6569 15.0001 12C15.0001 10.3431 13.6569 9 12.0001 9C10.3432 9 9.00009 10.3431 9.00009 12C9.00009 13.6569 10.3432 15 12.0001 15Z" />
);

export const SettingsSolidIcon = createFunctionalIcon(
  'settings_icon_label_a11y',
  SettingsSolidIcon16x16,
  SettingsSolidIcon24x24,
);

/**
 * ShapeArcSolidIcon
 */
const ShapeArcSolidIcon16x16 = () => (
  <path d="M2 7.93389C5.35026 7.93389 8.06611 10.6497 8.06611 14H14C13.9994 7.37174 8.62702 2 2 2V7.93389Z" />
);

const ShapeArcSolidIcon24x24 = () => (
  <path d="M3 11.9008C8.02538 11.9008 12.0992 15.9746 12.0992 21H21C20.9991 11.0576 12.9405 3 3 3V11.9008Z" />
);

export const ShapeArcSolidIcon = createFunctionalIcon(
  'arc_icon_label_a11y',
  ShapeArcSolidIcon16x16,
  ShapeArcSolidIcon24x24,
);

/**
 * ShapeCircleSolidIcon
 */
const ShapeCircleSolidIcon16x16 = () => (
  <path d="M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" />
);

const ShapeCircleSolidIcon24x24 = () => (
  <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" />
);

export const ShapeCircleSolidIcon = createFunctionalIcon(
  'circle_icon_label_a11y',
  ShapeCircleSolidIcon16x16,
  ShapeCircleSolidIcon24x24,
);

/**
 * ShapeDiamondSolidIcon
 */
const ShapeDiamondSolidIcon16x16 = () => (
  <path d="M8.00005 0L16.0001 8.00005L8.00005 16.0001L0 8.00005L8.00005 0Z" />
);

const ShapeDiamondSolidIcon24x24 = () => (
  <path d="M12 1L23.0001 12L12 23.0001L1 12L12 1Z" />
);

export const ShapeDiamondSolidIcon = createFunctionalIcon(
  'diamond_icon_label_a11y',
  ShapeDiamondSolidIcon16x16,
  ShapeDiamondSolidIcon24x24,
);

/**
 * ShapeHexagonSolidIcon
 */
const ShapeHexagonSolidIcon16x16 = () => (
  <path d="M8 0L15 4V12L8 16L1 12V4L8 0Z" />
);

const ShapeHexagonSolidIcon24x24 = () => (
  <path d="M12 0L22 6V18L12 24L2 18V6L12 0Z" />
);

export const ShapeHexagonSolidIcon = createFunctionalIcon(
  'hexagon_icon_label_a11y',
  ShapeHexagonSolidIcon16x16,
  ShapeHexagonSolidIcon24x24,
);

/**
 * ShapeRightTriangleSolidIcon
 */
const ShapeRightTriangleSolidIcon16x16 = () => <path d="M15 15V1L1 15H15Z" />;

const ShapeRightTriangleSolidIcon24x24 = () => <path d="M21 21V3L3 21H21Z" />;

export const ShapeRightTriangleSolidIcon = createFunctionalIcon(
  'right_triangle_icon_ally',
  ShapeRightTriangleSolidIcon16x16,
  ShapeRightTriangleSolidIcon24x24,
);

/**
 * ShapeSharkSolidIcon
 */
const ShapeSharkSolidIcon16x16 = () => (
  <path d="M10.7879 0.75C4.13909 2.77703 0.75 9.55254 0.75 15H15.5L10.7879 0.75Z" />
);

const ShapeSharkSolidIcon24x24 = () => (
  <path d="M15.5696 1.75C6.65782 4.48826 2.11523 13.6411 2.11523 21H21.8855L15.5696 1.75Z" />
);

export const ShapeSharkSolidIcon = createFunctionalIcon(
  'shark_icon_label_a11y',
  ShapeSharkSolidIcon16x16,
  ShapeSharkSolidIcon24x24,
);

/**
 * ShapeTrapezoidSolidIcon
 */
const ShapeTrapezoidSolidIcon16x16 = () => (
  <path d="M5.99386 1H14V15H2L5.99386 1Z" />
);

const ShapeTrapezoidSolidIcon24x24 = () => (
  <path d="M9.32514 3H20V21H4L9.32514 3Z" />
);

export const ShapeTrapezoidSolidIcon = createFunctionalIcon(
  'trapezoid_icon_label_a11y',
  ShapeTrapezoidSolidIcon16x16,
  ShapeTrapezoidSolidIcon24x24,
);

/**
 * ShareIcon
 */
const ShareIcon16x16 = () => (
  <path d="M8 1.11523L11.3839 4.49912L10.5 5.383L8.625 3.508V10.9991H7.375V3.508L5.5 5.383L4.61612 4.49912L8 1.11523ZM2 5.99912H5V7.24912H3.25V13.7491H12.75V7.24912H11V5.99912H14V14.9991H2V5.99912Z" />
);

const ShareIcon24x24 = () => (
  <path d="M12 1.76172L17.2374 6.99916L16 8.23659L12.875 5.11159V16.2492H11.125L11.125 5.11159L8 8.23659L6.76256 6.99916L12 1.76172ZM4 9.99916H8V11.7492H5.75V20.2492H18.25V11.7492H16V9.99916H20V21.9992H4V9.99916Z" />
);

export const ShareIcon = createFunctionalIcon(
  'share_icon_label_a11y',
  ShareIcon16x16,
  ShareIcon24x24,
);

/**
 * SidebarIcon
 */
const SidebarIcon16x16 = () => (
  <path d="M1 2H15V14H1V2ZM2.25 3.25V12.75H9V3.25H2.25ZM10.25 3.25V12.75H13.75V3.25H10.25Z" />
);

const SidebarIcon24x24 = () => (
  <path d="M3 4H21V20H3V4ZM4.75 5.75V18.25H13.375V5.75H4.75ZM15.125 5.75V18.25H19.25V5.75H15.125Z" />
);

export const SidebarIcon = createFunctionalIcon(
  'sidebar_icon_label_a11y',
  SidebarIcon16x16,
  SidebarIcon24x24,
);

/**
 * SidebarSolidIcon
 */
const SidebarSolidIcon16x16 = () => (
  <>
    <path d="M10.25 2H15V14H10.25V2Z" />
    <path d="M1 2H9V14H1V2Z" />
  </>
);

const SidebarSolidIcon24x24 = () => (
  <>
    <path d="M3 4H13.375V20H3V4Z" />
    <path d="M15 4H21V20H15V4Z" />
  </>
);

export const SidebarSolidIcon = createFunctionalIcon(
  'sidebar_icon_label_a11y',
  SidebarSolidIcon16x16,
  SidebarSolidIcon24x24,
);

/**
 * SidebarModalIcon
 */
const SidebarModalIcon16x16 = () => (
  <path d="M1 2H15V14H1V2ZM2.25 3.25V12.75H5.75V3.25H2.25ZM7 3.25V12.75H13.75V3.25H7ZM5 5.625H3V4.375H5V5.625ZM5 8H3V6.75H5V8Z" />
);

const SidebarModalIcon24x24 = () => (
  <path d="M3 4H21V20H3V4ZM4.75 5.75V18.25H8.875V5.75H4.75ZM10.625 5.75V18.25H19.25V5.75H10.625ZM7.75 8.5H5.75V6.75H7.75V8.5ZM7.75 11.25H5.75V9.5H7.75V11.25Z" />
);

export const SidebarModalIcon = createFunctionalIcon(
  'templates_icon_label_a11y',
  SidebarModalIcon16x16,
  SidebarModalIcon24x24,
);

/**
 * SidebarModalSolidIcon
 */
const SidebarModalSolidIcon16x16 = () => (
  <>
    <path d="M1 2H5.75V14H1V2ZM2.375 4.875H4.5V3.625H2.375V4.875ZM4.5 7.25H2.375V6H4.5V7.25Z" />
    <path d="M7 2H15V14H7V2Z" />
  </>
);

const SidebarModalSolidIcon24x24 = () => (
  <>
    <path d="M3 4H8.875V20H3V4ZM5 8H7V6.25H5V8ZM7 10.75H5V9H7V10.75Z" />
    <path d="M10.5 4H21V20H10.5V4Z" />
  </>
);

export const SidebarModalSolidIcon = createFunctionalIcon(
  'templates_icon_label_a11y',
  SidebarModalSolidIcon16x16,
  SidebarModalSolidIcon24x24,
);

/**
 * SlidersIcon
 */
const SlidersIcon16x16 = () => (
  <path d="M4.375 8.23544C3.36654 8.5098 2.625 9.43197 2.625 10.5273C2.625 11.6227 3.36654 12.5449 4.375 12.8192L4.375 15H5.625V12.8192C6.63346 12.5449 7.37499 11.6227 7.37499 10.5273C7.37499 9.43197 6.63346 8.5098 5.625 8.23544L5.625 1H4.375L4.375 8.23544ZM10.375 3.18075C9.36654 3.45511 8.625 4.37728 8.625 5.47265C8.625 6.56802 9.36654 7.4902 10.375 7.76456L10.375 15H11.625V7.76455C12.6335 7.49019 13.375 6.56802 13.375 5.47265C13.375 4.37729 12.6335 3.45511 11.625 3.18075V1H10.375V3.18075ZM11 4.34766C10.3787 4.34766 9.875 4.85133 9.875 5.47265C9.875 6.09397 10.3787 6.59765 11 6.59765C11.6213 6.59765 12.125 6.09397 12.125 5.47265C12.125 4.85133 11.6213 4.34766 11 4.34766ZM5 9.40234C4.37868 9.40234 3.875 9.90602 3.875 10.5273C3.875 11.1487 4.37868 11.6523 5 11.6523C5.62132 11.6523 6.12499 11.1487 6.12499 10.5273C6.12499 9.90602 5.62132 9.40234 5 9.40234Z" />
);

const SlidersIcon24x24 = () => (
  <path d="M7.375 12.2492C6.07499 12.6276 5.125 13.8278 5.125 15.25C5.125 16.6722 6.07499 17.8724 7.375 18.2508L7.375 21H9.125V18.2508C10.425 17.8724 11.375 16.6722 11.375 15.25C11.375 13.8278 10.425 12.6276 9.125 12.2492V3H7.375V12.2492ZM14.875 5.74915C13.575 6.12757 12.625 7.32785 12.625 8.75C12.625 10.1722 13.575 11.3724 14.875 11.7508V21H16.625V11.7508C17.925 11.3724 18.875 10.1722 18.875 8.75C18.875 7.32785 17.925 6.12757 16.625 5.74915V3H14.875V5.74915ZM15.75 7.375C14.9906 7.375 14.375 7.99061 14.375 8.75C14.375 9.50939 14.9906 10.125 15.75 10.125C16.5094 10.125 17.125 9.50939 17.125 8.75C17.125 7.99061 16.5094 7.375 15.75 7.375ZM8.25 13.875C7.49061 13.875 6.875 14.4906 6.875 15.25C6.875 16.0094 7.49061 16.625 8.25 16.625C9.00939 16.625 9.625 16.0094 9.625 15.25C9.625 14.4906 9.00939 13.875 8.25 13.875Z" />
);

export const SlidersIcon = createFunctionalIcon(
  'sliders_icon_label_a11y',
  SlidersIcon16x16,
  SlidersIcon24x24,
);

/**
 * SmartphoneIcon
 */
const SmartphoneIcon16x16 = () => (
  <path d="M5.45833 2.25C5.34327 2.25 5.25 2.34327 5.25 2.45833V13.5417C5.25 13.6567 5.34327 13.75 5.45833 13.75H10.5417C10.6567 13.75 10.75 13.6567 10.75 13.5417V2.45833C10.75 2.34327 10.6567 2.25 10.5417 2.25H5.45833ZM4 2.45833C4 1.65292 4.65292 1 5.45833 1H10.5417C11.3471 1 12 1.65292 12 2.45833V13.5417C12 14.3471 11.3471 15 10.5417 15H5.45833C4.65292 15 4 14.3471 4 13.5417V2.45833ZM9 5H7V3.75H9V5Z" />
);

const SmartphoneIcon24x24 = () => (
  <path d="M8.125 3.75C7.91789 3.75 7.75 3.91789 7.75 4.125V19.875C7.75 20.0821 7.91789 20.25 8.125 20.25H15.875C16.0821 20.25 16.25 20.0821 16.25 19.875V4.125C16.25 3.91789 16.0821 3.75 15.875 3.75H8.125ZM6 4.125C6 2.95139 6.95139 2 8.125 2H15.875C17.0486 2 18 2.9514 18 4.125V19.875C18 21.0486 17.0486 22 15.875 22H8.125C6.95139 22 6 21.0486 6 19.875V4.125ZM14 6.75H10V5H14V6.75Z" />
);

export const SmartphoneIcon = createFunctionalIcon(
  'smartphone_icon_label_a11y',
  SmartphoneIcon16x16,
  SmartphoneIcon24x24,
);

/**
 * SparkleSolidIcon
 */
const SparkleSolidIcon16x16 = () => (
  <>
    <path d="M5.5 1L7.09099 3.90901L10 5.5L7.09099 7.09099L5.5 10L3.90901 7.09099L1 5.5L3.90901 3.90901L5.5 1Z" />
    <path d="M11.5 8L12.7374 10.2626L15 11.5L12.7374 12.7374L11.5 15L10.2626 12.7374L8 11.5L10.2626 10.2626L11.5 8Z" />
    <path d="M12.7071 2.29289L12 1L11.2929 2.29289L10 3L11.2929 3.70711L12 5L12.7071 3.70711L14 3L12.7071 2.29289Z" />
  </>
);

const SparkleSolidIcon24x24 = () => (
  <>
    <path d="M8.5 2L10.7981 6.2019L15 8.5L10.7981 10.7981L8.5 15L6.2019 10.7981L2 8.5L6.2019 6.2019L8.5 2Z" />
    <path d="M17 12L18.7678 15.2322L22 17L18.7678 18.7678L17 22L15.2322 18.7678L12 17L15.2322 15.2322L17 12Z" />
    <path d="M18.8839 3.61612L18 2L17.1161 3.61612L15.5 4.5L17.1161 5.38388L18 7L18.8839 5.38388L20.5 4.5L18.8839 3.61612Z" />
  </>
);

export const SparkleSolidIcon = createFunctionalIcon(
  'sparkle_icon_label_a11y',
  SparkleSolidIcon16x16,
  SparkleSolidIcon24x24,
);

/**
 * StarIcon
 */
const StarIcon16x16 = () => (
  <path d="M8.00015 1.5L10.0574 5.66843L14.6576 6.33686L11.3289 9.58154L12.1147 14.1631L8.00015 12L3.88567 14.1631L4.67148 9.58154L1.34277 6.33686L5.94291 5.66844L8.00015 1.5ZM8.00016 4.32443L6.77299 6.81095L4.02898 7.20967L6.01458 9.14514L5.54584 11.8781L8.00015 10.5878L10.4545 11.8781L9.98575 9.14514L11.9713 7.20967L9.22734 6.81095L8.00016 4.32443Z" />
);

const StarIcon24x24 = () => (
  <path d="M12.0001 2.5L14.939 8.45493L21.5106 9.40985L16.7554 14.0451L17.8779 20.5902L12.0001 17.5L6.12223 20.5902L7.2448 14.0451L2.4895 9.40985L9.06114 8.45493L12.0001 2.5ZM12.0001 6.45421L10.2233 10.0544L6.2502 10.6318L9.12514 13.4341L8.44646 17.3911L12.0001 15.5229L15.5537 17.3911L14.875 13.4341L17.75 10.6318L13.7769 10.0544L12.0001 6.45421Z" />
);

export const StarIcon = createFunctionalIcon(
  'star_icon_label_a11y',
  StarIcon16x16,
  StarIcon24x24,
);

/**
 * StarSolidIcon
 */
const StarSolidIcon16x16 = () => (
  <path d="M7.99993 1.5L10.0572 5.66844L14.6573 6.33688L11.3286 9.58156L12.1144 14.1631L7.99993 12L3.88543 14.1631L4.67123 9.58156L1.34253 6.33688L5.94268 5.66844L7.99993 1.5Z" />
);

const StarSolidIcon24x24 = () => (
  <path d="M12.0001 2.5L14.939 8.45492L21.5106 9.40983L16.7553 14.0451L17.8779 20.5902L12.0001 17.5L6.12221 20.5902L7.24478 14.0451L2.4895 9.40983L9.06114 8.45492L12.0001 2.5Z" />
);

export const StarSolidIcon = createFunctionalIcon(
  'star_icon_label_a11y',
  StarSolidIcon16x16,
  StarSolidIcon24x24,
);

/**
 * StopSolidIcon
 */
const StopSolidIcon16x16 = () => <path d="M14 2H2V14H14V2Z" />;

const StopSolidIcon24x24 = () => <path d="M19 5H5V19H19V5Z" />;

export const StopSolidIcon = createFunctionalIcon(
  'stop_icon_label_a11y',
  StopSolidIcon16x16,
  StopSolidIcon24x24,
);

/**
 * StrikethroughIcon
 */
const StrikethroughIcon16x16 = () => (
  <path d="M5.85434 1.626C6.68387 1.21271 7.64032 1.05024 8.57824 1.1568C9.51611 1.26335 10.3995 1.635 11.0875 2.22873C11.7768 2.82361 12.2353 3.61287 12.3679 4.48153L11.1321 4.67005C11.0482 4.12008 10.7539 3.59197 10.2708 3.17507C9.78638 2.75702 9.14318 2.47902 8.43713 2.39881C7.73114 2.3186 7.0182 2.44269 6.41177 2.74483C5.80577 3.04676 5.35151 3.50302 5.10013 4.02673C4.85015 4.54753 4.80812 5.11767 4.97522 5.65582C5.14312 6.19656 5.51933 6.69363 6.06851 7.05728C6.61856 7.42151 7.3032 7.62499 8.01611 7.62499L15 7.625V8.875L1 8.87499V7.62499L4.784 7.62499C4.31766 7.17777 3.96961 6.63252 3.78144 6.02649C3.51939 5.18253 3.58868 4.28697 3.97323 3.48582C4.35638 2.68758 5.02439 2.03951 5.85434 1.626ZM10.9202 11.5113C11.095 11.0093 11.0852 10.4749 10.8912 9.97693L12.0559 9.52306C12.3576 10.2974 12.3737 11.1385 12.1007 11.9223C11.8285 12.7041 11.2877 13.3759 10.5726 13.8539C9.85825 14.3314 8.99941 14.5965 8.11696 14.6217C7.23451 14.6468 6.35937 14.4312 5.61336 13.997C4.86683 13.5625 4.27933 12.9257 3.95019 12.1639L5.09766 11.6681C5.31185 12.1638 5.7062 12.6047 6.24215 12.9166C6.77863 13.2289 7.42218 13.391 8.08133 13.3722C8.74049 13.3534 9.36872 13.1551 9.87802 12.8146C10.3866 12.4747 10.7447 12.0152 10.9202 11.5113Z" />
);

const StrikethroughIcon24x24 = () => (
  <path d="M9.24437 3.76978C10.3101 3.23771 11.5382 3.02885 12.742 3.1659C13.9458 3.30294 15.0809 3.78104 15.9659 4.54631C16.8528 5.31318 17.4441 6.33196 17.615 7.45487L15.885 7.71828C15.7818 7.04061 15.4195 6.38736 14.8213 5.87005C14.2212 5.35114 13.4226 5.00468 12.5441 4.90467C11.6657 4.80467 10.7791 4.95952 10.026 5.3355C9.27354 5.71118 8.71215 6.27752 8.40226 6.92444C8.09428 7.56738 8.04275 8.27017 8.24834 8.93363C8.45502 9.60062 8.91904 10.2166 9.60014 10.6685C10.001 10.9345 10.4607 11.1323 10.9533 11.25L21 11.25V13L10.8399 13L10.8372 13.0142C10.8129 13.0096 10.7885 13.0049 10.7643 13L3 13V11.25H7.61098C7.13933 10.7277 6.78339 10.1185 6.57676 9.45161C6.23876 8.36085 6.32824 7.20334 6.82399 6.16841C7.31784 5.13747 8.17799 4.30216 9.24437 3.76978ZM15.7179 16.5083C15.939 15.8734 15.9267 15.1978 15.6811 14.5677L17.3117 13.9323C17.708 14.9492 17.7291 16.0543 17.3706 17.0838C17.013 18.1105 16.3034 18.9913 15.3669 19.6174C14.4312 20.2427 13.3073 20.5895 12.1532 20.6224C10.999 20.6553 9.85392 20.3733 8.87714 19.8048C7.89963 19.2358 7.12891 18.4012 6.69677 17.401L8.30323 16.7069C8.57444 17.3346 9.07475 17.895 9.75745 18.2923C10.4409 18.6901 11.2618 18.8971 12.1033 18.8731C12.9448 18.8491 13.7459 18.5959 14.3944 18.1624C15.0419 17.7296 15.4959 17.146 15.7179 16.5083Z" />
);

export const StrikethroughIcon = createFunctionalIcon(
  'strikethrough_icon_label_a11y',
  StrikethroughIcon16x16,
  StrikethroughIcon24x24,
);

/**
 * StudentIcon
 */
const StudentIcon16x16 = () => (
  <path d="M7.99999 1.45312L15.8237 5.72061L13.0001 7.26075L13 11.6709L8.00001 14.3852L3.00002 11.6709L3.00013 7.2609L0.17627 5.72061L7.99999 1.45312ZM4.25011 7.94271L4.25004 10.9272L8.00002 12.9629L11.75 10.9272L11.7501 7.94258L7.99999 9.9881L4.25011 7.94271ZM2.78668 5.72061L7.99999 8.56424L13.2133 5.72061L7.99999 2.87698L2.78668 5.72061Z" />
);

const StudentIcon24x24 = () => (
  <path d="M12 3.00391L23.605 9.33394L18.9202 11.8893L19.0001 11.8893L18.9999 17.4019L11.9999 21.1162L5 17.4019L5.00014 11.8893L5.07965 11.8893L0.394897 9.33394L12 3.00391ZM6.75012 12.8004L6.75003 16.3494L12 19.1351L17.25 16.3493L17.2501 12.8003L12 15.664L6.75012 12.8004ZM4.04947 9.33394L12 13.6706L19.9505 9.33394L12 4.99731L4.04947 9.33394Z" />
);

export const StudentIcon = createFunctionalIcon(
  'student_icon_label_a11y',
  StudentIcon16x16,
  StudentIcon24x24,
);

/**
 * SurfaceBackgroundIcon
 */
const SurfaceBackgroundIcon16x16 = () => (
  <path d="M10 2H2V10H6V14H14V6H10V2ZM7.25 12.75V7.25H12.75V12.75H7.25Z" />
);

const SurfaceBackgroundIcon24x24 = () => (
  <path d="M15 3H3V15H9V21H21V9H15V3ZM10.75 19.25V10.75H19.25V19.25H10.75Z" />
);

export const SurfaceBackgroundIcon = createFunctionalIcon(
  'surface_background_icon_label_a11y',
  SurfaceBackgroundIcon16x16,
  SurfaceBackgroundIcon24x24,
);

/**
 * SurfaceForegroundIcon
 */
const SurfaceForegroundIcon16x16 = () => (
  <path d="M6 10H2V2H10V6H14V14H6V10ZM3.75 3.75H8.25V6H6V8.25H3.75V3.75Z" />
);

const SurfaceForegroundIcon24x24 = () => (
  <path d="M15 3H3V15H9V21H21V9H15V3ZM13.25 9V4.75H4.75V13.25H9V9H13.25Z" />
);

export const SurfaceForegroundIcon = createFunctionalIcon(
  'surface_foreground_icon_label_a11y',
  SurfaceForegroundIcon16x16,
  SurfaceForegroundIcon24x24,
);

/**
 * TabletIcon
 */
const TabletIcon16x16 = () => (
  <path d="M2 1.75C2 1.33579 2.33579 1 2.75 1H13.25C13.6642 1 14 1.33579 14 1.75V14.25C14 14.6642 13.6642 15 13.25 15H2.75C2.33579 15 2 14.6642 2 14.25V1.75ZM3.25 2.25V13.75H12.75V2.25H3.25ZM6.75 11.125C6.75 10.4346 7.30964 9.875 8 9.875C8.69036 9.875 9.25 10.4346 9.25 11.125C9.25 11.8154 8.69036 12.375 8 12.375C7.30964 12.375 6.75 11.8154 6.75 11.125Z" />
);

const TabletIcon24x24 = () => (
  <path d="M5.75 3.75V20.25H18.25V3.75H5.75ZM4 3.375C4 2.61561 4.61561 2 5.375 2H18.625C19.3844 2 20 2.61561 20 3.375V20.625C20 21.3844 19.3844 22 18.625 22H5.375C4.61561 22 4 21.3844 4 20.625V3.375ZM10.25 16.875C10.25 15.9085 11.0335 15.125 12 15.125C12.9665 15.125 13.75 15.9085 13.75 16.875C13.75 17.8415 12.9665 18.625 12 18.625C11.0335 18.625 10.25 17.8415 10.25 16.875Z" />
);

export const TabletIcon = createFunctionalIcon(
  'tablet_icon_label_a11y',
  TabletIcon16x16,
  TabletIcon24x24,
);

/**
 * TemplateMinusIcon
 */
const TemplateMinusIcon16x16 = () => (
  <path d="M2 1H9.88388L14 5.11612V15H2V1ZM3.25 2.25V13.75H12.75V6H9V2.25H3.25ZM10.25 3.13388L11.8661 4.75H10.25V3.13388ZM10.5 10.125H5.5V8.875H10.5V10.125Z" />
);

const TemplateMinusIcon24x24 = () => (
  <path d="M4 3H14.3624L20 8.63756V21H4V3ZM5.75 4.75V19.25H18.25V9.875H13.125V4.75H5.75ZM14.875 5.98744L17.0126 8.125H14.875V5.98744ZM15 14.875H9V13.125H15V14.875Z" />
);

export const TemplateMinusIcon = createFunctionalIcon(
  'remove_template_icon_label_a11y',
  TemplateMinusIcon16x16,
  TemplateMinusIcon24x24,
);

/**
 * TemplatePlusIcon
 */
const TemplatePlusIcon16x16 = () => (
  <path d="M2 1H9.88388L14 5.11612V15H2V1ZM3.25 2.25V13.75H12.75V6H9V2.25H3.25ZM10.25 3.13388L11.8661 4.75H10.25V3.13388ZM7.375 8.875V7H8.625V8.875H10.5V10.125H8.625V12H7.375V10.125H5.5V8.875H7.375Z" />
);

const TemplatePlusIcon24x24 = () => (
  <path d="M4 3H14.3624L20 8.63756V21H4V3ZM5.75 4.75V19.25H18.25V9.875H13.125V4.75H5.75ZM14.875 5.98744L17.0126 8.125H14.875V5.98744ZM11.125 13.125V11H12.875V13.125H15V14.875H12.875V17H11.125V14.875H9V13.125H11.125Z" />
);

export const TemplatePlusIcon = createFunctionalIcon(
  'add_template_icon_label_a11y',
  TemplatePlusIcon16x16,
  TemplatePlusIcon24x24,
);

/**
 * TextFormatIcon
 */
const TextFormatIcon16x16 = () => (
  <path d="M3.6995 2H5.69152L9.33169 13.2237L8.14266 13.6093L7.05313 10.25H2.33788L1.24835 13.6093L0.0593262 13.2237L3.6995 2ZM2.74329 9L6.64772 9L4.78283 3.25H4.60818L2.74329 9ZM14.7502 7.64579V7.08203H16.0002V13.332H14.7502V12.8542C14.3032 13.1807 13.7525 13.375 13.1252 13.375C11.4385 13.375 10.1252 11.9453 10.1252 10.25C10.1252 8.55471 11.4385 7.125 13.1252 7.125C13.7525 7.125 14.3032 7.31934 14.7502 7.64579ZM14.7502 10.25C14.7502 9.13765 14.0195 8.375 13.1252 8.375C12.1885 8.375 11.3752 9.18387 11.3752 10.25C11.3752 11.3161 12.1885 12.125 13.1252 12.125C14.0195 12.125 14.7502 11.3623 14.7502 10.25Z" />
);

const TextFormatIcon24x24 = () => (
  <path d="M5.49923 3.875H8.37588L13.8281 19.8422L12.172 20.4078L10.3681 15.125L3.50698 15.125L1.70311 20.4078L0.0469971 19.8422L5.49923 3.875ZM4.10454 13.375L9.77057 13.375L7.12423 5.625H6.75088L4.10454 13.375ZM19.1251 12.125C17.4243 12.125 16.0001 13.5545 16.0001 15.375C16.0001 17.1955 17.4243 18.625 19.1251 18.625C20.8258 18.625 22.2501 17.1955 22.2501 15.375C22.2501 13.5545 20.8258 12.125 19.1251 12.125ZM22.2501 11.5366C21.4074 10.8131 20.3204 10.375 19.1251 10.375C16.4075 10.375 14.2501 12.6392 14.2501 15.375C14.2501 18.1108 16.4075 20.375 19.1251 20.375C20.3204 20.375 21.4074 19.9369 22.2501 19.2134V20H24.0001V10.625H22.2501V11.5366Z" />
);

export const TextFormatIcon = createFunctionalIcon(
  'text_format_icon_label_a11y',
  TextFormatIcon16x16,
  TextFormatIcon24x24,
);

/**
 * ThumbsDownIcon
 */
const ThumbsDownIcon16x16 = () => (
  <path d="M3.23553 5.43493C3.23553 5.43493 3.23554 5.43493 3.23553 5.43493L2.53951 8.75L8.2498 8.75V11.8C8.2498 12.2156 8.47896 12.5951 8.82163 12.8117L10.7527 9.25H11.3749V3.25002L5.92687 3.25C4.62588 3.25 3.50288 4.1617 3.23553 5.43493ZM2.01221 5.17807C2.40107 3.3261 4.03452 2 5.92687 2L12.6249 2.00003V10.5H11.4969L9.44028 14.2932L8.98802 14.2027C7.87517 13.98 6.9998 12.9916 6.9998 11.8V10H2.23157C1.59659 10 1.12244 9.41592 1.25291 8.79453L2.01221 5.17807ZM13.7499 10.5V2H14.9999V10.5H13.7499Z" />
);

const ThumbsDownIcon24x24 = () => (
  <path d="M8.80497 5.75C6.73147 5.75 4.94276 7.20552 4.5213 9.23575L3.48038 14.25L11.75 14.25L11.75 18.125C11.75 18.8597 12.169 19.4927 12.7755 19.8006L15.75 14.881V5.75H8.80497ZM2.80783 8.88005C3.39787 6.03773 5.90206 4 8.80497 4H17.5V15.369L13.6127 21.7981L13.0289 21.7028C11.3108 21.4221 10 19.9281 10 18.125L10 16L3.34406 16C2.77016 16 2.31878 15.6863 2.05347 15.3197C1.79034 14.9561 1.64896 14.4625 1.75298 13.9614L2.80783 8.88005ZM19.25 15.375V4H21V15.375H19.25Z" />
);

export const ThumbsDownIcon = createFunctionalIcon(
  'thumbs_down_icon_label_a11y',
  ThumbsDownIcon16x16,
  ThumbsDownIcon24x24,
);

/**
 * ThumbsDownSolidIcon
 */
const ThumbsDownSolidIcon16x16 = () => (
  <>
    <path d="M2.01245 5.17807C2.40131 3.3261 4.03476 2 5.92712 2L12.6251 2.00003V10.5H11.4971L9.44052 14.2932L8.98826 14.2027C7.87541 13.98 7.00004 12.9916 7.00004 11.8V10H2.23182C1.59684 10 1.12269 9.41592 1.25315 8.79453L2.01245 5.17807Z" />
    <path d="M13.7501 10.5V2H15.0001V10.5H13.7501Z" />
  </>
);

const ThumbsDownSolidIcon24x24 = () => (
  <>
    <path d="M17.5002 4V15.369L13.6128 21.7981L13.029 21.7028C11.3109 21.4221 10.0002 19.9281 10.0002 18.125L10.0002 16L3.34418 16C2.77028 16 2.3189 15.6863 2.0536 15.3197C1.79046 14.9561 1.64909 14.4625 1.7531 13.9614L2.80795 8.88005C3.39799 6.03773 5.90219 4 8.80509 4L17.5002 4Z" />
    <path d="M19.2501 15.375L19.2501 3.375L21.0001 3.375L21.0001 15.375H19.2501Z" />
  </>
);

export const ThumbsDownSolidIcon = createFunctionalIcon(
  'thumbs_down_icon_label_a11y',
  ThumbsDownSolidIcon16x16,
  ThumbsDownSolidIcon24x24,
);

/**
 * ThumbsUpIcon
 */
const ThumbsUpIcon16x16 = () => (
  <path d="M6.54103 1.70703L6.99329 1.79755C8.10614 2.02027 8.98151 3.00867 8.98151 4.20024L8.98151 6.00026L13.7497 6.00026C14.3847 6.00026 14.8589 6.58435 14.7284 7.20574L13.9691 10.8222C13.5802 12.6742 11.9468 14.0003 10.0544 14.0003L3.35645 14.0002L3.35645 5.50026H4.48442L6.54103 1.70703ZM7.15968 3.18857L5.2286 6.75026H4.60645L4.60645 12.7502L10.0544 12.7503C11.3554 12.7503 12.4784 11.8386 12.7458 10.5653C12.7458 10.5653 12.7458 10.5653 12.7458 10.5653L13.4418 7.25026L7.73151 7.25026L7.73151 4.20024C7.73151 3.78468 7.50235 3.40521 7.15968 3.18857ZM2.23145 5.50026L2.23145 14.0003H0.981445L0.981446 5.50026H2.23145Z" />
);

const ThumbsUpIcon24x24 = () => (
  <path d="M10.3561 2.20117L10.9399 2.29654C12.658 2.57722 13.9688 4.07119 13.9688 5.87429L13.9688 7.99932L20.6247 7.99932C21.1986 7.99932 21.65 8.313 21.9153 8.6796C22.1784 9.0432 22.3198 9.53684 22.2158 10.0379L21.161 15.1193C20.5709 17.9616 18.0667 19.9993 15.1638 19.9993H6.46875V8.63035L10.3561 2.20117ZM11.1933 4.19876L8.21875 9.11828V18.2493H15.1638C17.2373 18.2493 19.026 16.7938 19.4475 14.7636L20.4884 9.74932L12.2188 9.74932L12.2188 5.87429C12.2188 5.13959 11.7998 4.50659 11.1933 4.19876ZM20.4975 9.70546C20.4973 9.70511 20.4973 9.70521 20.4975 9.70546C20.4978 9.70589 20.4988 9.70729 20.4996 9.70911C20.5 9.71005 20.5003 9.7108 20.5005 9.71131C20.5005 9.71133 20.5006 9.71179 20.5005 9.71131C20.5005 9.71128 20.5006 9.71143 20.5004 9.71028C20.5003 9.70976 20.4997 9.70988 20.4996 9.70911M20.4975 9.70546L20.5023 9.68229C20.5003 9.69227 20.4999 9.69998 20.5 9.70485C20.5001 9.70622 20.4995 9.70814 20.4996 9.70911M4.71875 8.62494V19.9999H2.96875V8.62494H4.71875Z" />
);

export const ThumbsUpIcon = createFunctionalIcon(
  'thumbs_up_icon_label_a11y',
  ThumbsUpIcon16x16,
  ThumbsUpIcon24x24,
);

/**
 * ThumbsUpSolidIcon
 */
const ThumbsUpSolidIcon16x16 = () => (
  <>
    <path d="M13.9691 10.8652C13.5802 12.7171 11.9468 14.0432 10.0544 14.0432L3.35645 14.0432L3.35645 5.54323H4.48442L6.54103 1.75L6.99329 1.84051C8.10614 2.06324 8.98151 3.05164 8.98151 4.24321V6.04323L13.7497 6.04323C14.3847 6.04323 14.8589 6.62732 14.7284 7.24871L13.9691 10.8652Z" />
    <path d="M2.23145 5.54323L2.23145 14.0432L0.981445 14.0432L0.981446 5.54323H2.23145Z" />
  </>
);

const ThumbsUpSolidIcon24x24 = () => (
  <>
    <path d="M10.3561 2.20117L10.9399 2.29654C12.658 2.57722 13.9688 4.07119 13.9688 5.87429L13.9688 7.99932L20.6247 7.99932C21.1986 7.99932 21.65 8.313 21.9153 8.6796C22.1784 9.0432 22.3198 9.53684 22.2158 10.0379L21.161 15.1193C20.5709 17.9616 18.0667 19.9993 15.1638 19.9993H6.46875V8.63035L10.3561 2.20117Z" />
    <path d="M4.71875 8.625V20H2.96875V8.625H4.71875Z" />
  </>
);
export const ThumbsUpSolidIcon = createFunctionalIcon(
  'thumbs_up_icon_label_a11y',
  ThumbsUpSolidIcon16x16,
  ThumbsUpSolidIcon24x24,
);

/**
 * TransferIcon
 */
const TransferIcon16x16 = () => (
  <path d="M12.1198 1.55859L14.8842 4.32298L12.1198 7.08738L11.2359 6.20349L12.4386 5.00076L5.24987 5.00076C4.14542 5.00076 3.25009 5.89609 3.25009 7.00053H2.00009C2.00009 5.20573 3.45507 3.75076 5.24987 3.75076L12.5442 3.75076L11.2359 2.44248L12.1198 1.55859ZM3.45606 12.2505L4.76449 13.559L3.8806 14.4428L1.11621 11.6785L3.8806 8.91406L4.76449 9.79795L3.5619 11.0005H11.7484C12.853 11.0005 13.7484 10.1051 13.7484 9.00054H14.9984C14.9984 10.7955 13.5433 12.2505 11.7484 12.2505H3.45606Z" />
);

const TransferIcon24x24 = () => (
  <path d="M18.3686 3.25586L22.4873 7.37458L18.3686 11.4933L17.1312 10.2559L19.1375 8.24958L7.37489 8.24957C5.85611 8.24957 4.62489 9.48079 4.62489 10.9996H2.87489C2.87489 8.5143 4.88961 6.49957 7.37489 6.49957L19.1374 6.49958L17.1312 4.4933L18.3686 3.25586ZM4.86232 17.7496L6.86861 19.7559L5.63117 20.9933L1.51245 16.8746L5.63117 12.7559L6.86861 13.9933L4.86232 15.9996H16.3749C18.0317 15.9996 19.3749 14.6564 19.3749 12.9996H21.1249C21.1249 15.6229 18.9982 17.7496 16.3749 17.7496H4.86232Z" />
);

export const TransferIcon = createFunctionalIcon(
  'transfer_icon_label_a11y',
  TransferIcon16x16,
  TransferIcon24x24,
);

/**
 * TrashIcon
 */
const TrashIcon16x16 = () => (
  <path d="M4 0.375H12V3H15V4.25H13.7869L12.125 14H3.78677L2.20304 4.25H1V3H4V0.375ZM5.25 3H10.75V1.625H5.25V3ZM3.46942 4.25L4.85011 12.75H11.0701L12.5189 4.25H3.46942Z" />
);

const TrashIcon24x24 = () => (
  <path d="M7 2H17.125V5H21V6.75H19.7266L17.5 21H6.38236L4.26056 6.75H3V5H7V2ZM8.75 5H15.375V3.75H8.75V5ZM6.02986 6.75L7.89109 19.25H16.0022L17.9553 6.75H6.02986Z" />
);

export const TrashIcon = createFunctionalIcon(
  'trash_can_icon_label_a11y',
  TrashIcon16x16,
  TrashIcon24x24,
);

/**
 * TrendIcon
 */
const TrendIcon16x16 = () => (
  <path d="M11.625 3L16 3V7.375H14.75V5.18228L9.64008 10.6458L6.01433 7.02024L1.19199 11.8426L0.308105 10.9587L6.01431 5.2525L9.61001 8.84798L13.9105 4.25L11.625 4.25V3Z" />
);

const TrendIcon24x24 = () => (
  <path d="M16.9999 5.125L22.9999 5.125V11.125H21.2499V8.02158L14.0144 15.5395L9.12763 10.7008L1.59282 17.6435L0.406982 16.3565L9.17112 8.28107L13.9844 13.0471L19.9246 6.875L16.9999 6.875V5.125Z" />
);

export const TrendIcon = createFunctionalIcon(
  'trend_icon_label_a11y',
  TrendIcon16x16,
  TrendIcon24x24,
);

/**
 * TriangleSolidIcon
 */
const TriangleSolidIcon16x16 = () => <path d="M1 14H15L8 2L1 14Z" />;

const TriangleSolidIcon24x24 = () => <path d="M4 19H20L12 5L4 19Z" />;

export const TriangleSolidIcon = createFunctionalIcon(
  'triangle_icon_label_a11y',
  TriangleSolidIcon16x16,
  TriangleSolidIcon24x24,
);

/**
 * TvIcon
 */
const TvIcon16x16 = () => (
  <path d="M0 2.625C0 1.72754 0.727537 1 1.625 1H14.375C15.2725 1 16 1.72754 16 2.625V12.375C16 13.2725 15.2725 14 14.375 14H12V15H4V14H1.625C0.727537 14 0 13.2725 0 12.375V2.625ZM1.625 2.25C1.41789 2.25 1.25 2.41789 1.25 2.625V12.375C1.25 12.5821 1.41789 12.75 1.625 12.75H14.375C14.5821 12.75 14.75 12.5821 14.75 12.375V2.625C14.75 2.41789 14.5821 2.25 14.375 2.25H1.625Z" />
);

const TvIcon24x24 = () => (
  <path d="M1 4.875C1 3.83947 1.83947 3 2.875 3H21.125C22.1605 3 23 3.83947 23 4.875V18.125C23 19.1605 22.1605 20 21.125 20H17V21H7V20H2.875C1.83946 20 1 19.1605 1 18.125V4.875ZM2.875 4.75C2.80596 4.75 2.75 4.80596 2.75 4.875V18.125C2.75 18.194 2.80596 18.25 2.875 18.25H21.125C21.194 18.25 21.25 18.194 21.25 18.125V4.875C21.25 4.80596 21.194 4.75 21.125 4.75H2.875Z" />
);

export const TvIcon = createFunctionalIcon(
  'tv_icon_label_a11y',
  TvIcon16x16,
  TvIcon24x24,
);

/**
 * TypeIcon
 */
const TypeIcon16x16 = () => (
  <path d="M3 2H13V5.25H11.75V3.25H8.625L8.625 12.75H10.375V14H5.625V12.75H7.375L7.375 3.25H4.25V5.25H3V2Z" />
);

const TypeIcon24x24 = () => (
  <path d="M5 4H19V7.125H17.25V5.75H12.875L12.875 18.25H14.125V20H9.875V18.25H11.125L11.125 5.75H6.75V7.125H5V4Z" />
);

export const TypeIcon = createFunctionalIcon(
  'text_icon_label_a11y',
  TypeIcon16x16,
  TypeIcon24x24,
);

/**
 * UnderlineIcon
 */
const UnderlineIcon16x16 = () => (
  <path d="M4.25 2V8.125C4.25 10.1961 5.92893 11.875 8 11.875C10.0711 11.875 11.75 10.1961 11.75 8.125V2H13V8.125C13 10.8864 10.7614 13.125 8 13.125C5.23858 13.125 3 10.8864 3 8.125V2H4.25ZM14.375 14.75V16H1.625V14.75H14.375Z" />
);

const UnderlineIcon24x24 = () => (
  <>
    <path d="M6.875 11.75V3H5.125V11.75C5.125 15.547 8.20304 18.625 12 18.625C15.797 18.625 18.875 15.547 18.875 11.75V3H17.125V11.75C17.125 14.5805 14.8305 16.875 12 16.875C9.16954 16.875 6.875 14.5805 6.875 11.75Z" />
    <path d="M22.125 22V20.25H1.875V22H22.125Z" />
  </>
);

export const UnderlineIcon = createFunctionalIcon(
  'underline_icon_label_a11y',
  UnderlineIcon16x16,
  UnderlineIcon24x24,
);

/**
 * UnlockIcon
 */
const UnlockIcon16x16 = () => (
  <path d="M8 2.375C6.52086 2.375 5.34375 3.54646 5.34375 4.96469V7H14V15H2V7H4.09375V4.96469C4.09375 2.83208 5.85477 1.125 8 1.125C10.1452 1.125 11.9062 2.83208 11.9062 4.96469H10.6562C10.6562 3.54646 9.47914 2.375 8 2.375ZM3.25 8.25V13.75H12.75V8.25H3.25Z" />
);

const UnlockIcon24x24 = () => (
  <path d="M12 3.625C10.1191 3.625 8.625 5.11427 8.625 6.9138V10H21V22H3V10H6.875V6.9138C6.875 4.11413 9.18654 1.875 12 1.875C14.8135 1.875 17.125 4.11413 17.125 6.9138H15.375C15.375 5.11427 13.8809 3.625 12 3.625ZM4.75 11.75V20.25H19.25V11.75H4.75Z" />
);

export const UnlockIcon = createFunctionalIcon(
  'unlock_icon_label_a11y',
  UnlockIcon16x16,
  UnlockIcon24x24,
);

/**
 * UnlockSolidIcon
 */
const UnlockSolidIcon16x16 = () => (
  <path d="M5.44255 4.96389C5.44255 3.60212 6.57351 2.473 8 2.473C9.42649 2.473 10.5574 3.60212 10.5574 4.96389H12.0051C12.0051 2.77481 10.1979 1.02539 8 1.02539C5.80212 1.02539 3.99495 2.77481 3.99495 4.96389V6.9992H2V14.9992H14V6.9992H5.44255V4.96389Z" />
);

const UnlockSolidIcon24x24 = () => (
  <path d="M8.625 6.9138C8.625 5.11427 10.1191 3.625 12 3.625C13.8809 3.625 15.375 5.11427 15.375 6.9138H17.125C17.125 4.11413 14.8135 1.875 12 1.875C9.18654 1.875 6.875 4.11413 6.875 6.9138V10H3V22H21V10H8.625V6.9138Z" />
);

export const UnlockSolidIcon = createFunctionalIcon(
  'unlock_icon_label_a11y',
  UnlockSolidIcon16x16,
  UnlockSolidIcon24x24,
);

/**
 * UnorderedListIcon
 */

const UnorderedListIcon16x16 = () => (
  <>
    <path d="M14 13H7V12H14V13Z" />
    <path d="M4 12.9667C4.53388 12.9667 4.96667 12.5339 4.96667 12C4.96667 11.4661 4.53388 11.0333 4 11.0333C3.46612 11.0333 3.03333 11.4661 3.03333 12C3.03333 12.5339 3.46612 12.9667 4 12.9667ZM4 14C5.10457 14 6 13.1046 6 12C6 10.8954 5.10457 10 4 10C2.89543 10 2 10.8954 2 12C2 13.1046 2.89543 14 4 14Z" />
    <path d="M14 4H7V3H14V4Z" />
    <path d="M4 4.96667C4.53388 4.96667 4.96667 4.53388 4.96667 4C4.96667 3.46612 4.53388 3.03333 4 3.03333C3.46612 3.03333 3.03333 3.46612 3.03333 4C3.03333 4.53388 3.46612 4.96667 4 4.96667ZM4 6C5.10457 6 6 5.10457 6 4C6 2.89543 5.10457 2 4 2C2.89543 2 2 2.89543 2 4C2 5.10457 2.89543 6 4 6Z" />
  </>
);

const UnorderedListIcon24x24 = () => (
  <>
    <path d="M22 20H11V18H22V20Z" />
    <path d="M5 20.45C5.80081 20.45 6.45 19.8008 6.45 19C6.45 18.1992 5.80081 17.55 5 17.55C4.19919 17.55 3.55 18.1992 3.55 19C3.55 19.8008 4.19919 20.45 5 20.45ZM5 22C6.65685 22 8 20.6569 8 19C8 17.3431 6.65685 16 5 16C3.34315 16 2 17.3431 2 19C2 20.6569 3.34315 22 5 22Z" />
    <path d="M22 6H11V4H22V6Z" />
    <path d="M5 6.45C5.80081 6.45 6.45 5.80081 6.45 5C6.45 4.19919 5.80081 3.55 5 3.55C4.19919 3.55 3.55 4.19919 3.55 5C3.55 5.80081 4.19919 6.45 5 6.45ZM5 8C6.65685 8 8 6.65685 8 5C8 3.34315 6.65685 2 5 2C3.34315 2 2 3.34315 2 5C2 6.65685 3.34315 8 5 8Z" />
  </>
);

export const UnorderedListIcon = createFunctionalIcon(
  'unordered_list_icon_label_a11y',
  UnorderedListIcon16x16,
  UnorderedListIcon24x24,
);

/**
 * UploadIcon
 */
const UploadIcon16x16 = () => (
  <path d="M8 0.865234L11.6919 4.55718L10.8081 5.44106L8.625 3.258L8.625 11.9991H7.375L7.375 3.258L5.19194 5.44106L4.30806 4.55718L8 0.865234ZM14.9964 9.99326L15 10.3716C15.011 12.9337 12.9371 15.0165 10.375 15.0165L5.625 15.0165C3.07068 15.0165 1 12.9458 1 10.3915V9.99912H2.25V10.3915C2.25 12.2555 3.76104 13.7665 5.625 13.7665L10.375 13.7665C12.2441 13.7665 13.7572 12.2474 13.75 10.3784C13.75 10.378 13.75 10.3775 13.75 10.377L13.7465 10.005L14.9964 9.99326Z" />
);

const UploadIcon24x24 = () => (
  <path d="M12 2.51172L17.1187 7.63044L15.8813 8.86787L12.875 5.86159L12.875 16.9992H11.125L11.125 5.86159L8.11872 8.86787L6.88128 7.63044L12 2.51172ZM3.75 13.9992V15.15C3.75 17.9805 6.04454 20.275 8.875 20.275L15.1251 20.275C17.9641 20.275 20.2622 17.967 20.25 15.1279L20.25 15.1242V13.9992H22V15.1222C22.0154 18.9299 18.933 22.025 15.1251 22.025L8.87499 22.025C5.07804 22.025 2 18.947 2 15.15V13.9992H3.75Z" />
);

export const UploadIcon = createFunctionalIcon(
  'upload_icon_label_a11y',
  UploadIcon16x16,
  UploadIcon24x24,
);

/**
 * UserAddedIcon
 */
const UserAddedIcon16x16 = () => (
  <path d="M6 1.79102C4.96447 1.79102 4.125 2.63048 4.125 3.66602C4.125 4.70155 4.96447 5.54102 6 5.54102C7.03553 5.54102 7.875 4.70155 7.875 3.66602C7.875 2.63048 7.03553 1.79102 6 1.79102ZM2.875 3.66602C2.875 1.94013 4.27411 0.541016 6 0.541016C7.72589 0.541016 9.125 1.94013 9.125 3.66602C9.125 5.39191 7.72589 6.79102 6 6.79102C4.27411 6.79102 2.875 5.39191 2.875 3.66602ZM12.375 5.375V3H13.625V5.375H16V6.625H13.625V9H12.375V6.625H10V5.375H12.375ZM0 14C0 10.6863 2.68629 8 6 8C9.31371 8 12 10.6863 12 14V15H0V14ZM1.25647 13.75H10.7435C10.6135 11.2429 8.53949 9.25 6 9.25C3.46051 9.25 1.38647 11.2429 1.25647 13.75Z" />
);

const UserAddedIcon24x24 = () => (
  <path d="M9 2.75C7.48122 2.75 6.25 3.98122 6.25 5.5C6.25 7.01878 7.48122 8.25 9 8.25C10.5188 8.25 11.75 7.01878 11.75 5.5C11.75 3.98122 10.5188 2.75 9 2.75ZM4.5 5.5C4.5 3.01473 6.51473 1 9 1C11.4853 1 13.5 3.01473 13.5 5.5C13.5 7.98527 11.4853 10 9 10C6.51473 10 4.5 7.98527 4.5 5.5ZM19.125 7.125V4H20.875V7.125H24V8.875H20.875V12H19.125V8.875H16V7.125H19.125ZM9.00005 13.75C5.54825 13.75 2.75 16.5482 2.75 20V20.25H15.25V20C15.25 16.5482 12.4518 13.75 9.00005 13.75ZM1 20C1 15.5818 4.58175 12 9.00005 12C13.4183 12 17 15.5817 17 20V22H1V20Z" />
);

export const UserAddedIcon = createFunctionalIcon(
  'add_team_member_icon_label_a11y',
  UserAddedIcon16x16,
  UserAddedIcon24x24,
);

/**
 * UserAddedSolidIcon
 */
const UserAddedSolidIcon16x16 = () => (
  <>
    <path d="M6 0.541016C4.27411 0.541016 2.875 1.94013 2.875 3.66602C2.875 5.39191 4.27411 6.79102 6 6.79102C7.72589 6.79102 9.125 5.39191 9.125 3.66602C9.125 1.94013 7.72589 0.541016 6 0.541016Z" />
    <path d="M12.375 3V5.375H10V6.625H12.375V9H13.625V6.625H16V5.375H13.625V3H12.375Z" />
    <path d="M6 8C2.68629 8 0 10.6863 0 14V15H12V14C12 10.6863 9.31371 8 6 8Z" />
  </>
);

const UserAddedSolidIcon24x24 = () => (
  <>
    <path d="M9 1C6.51472 1 4.5 3.01472 4.5 5.5C4.5 7.98528 6.51472 10 9 10C11.4853 10 13.5 7.98528 13.5 5.5C13.5 3.01472 11.4853 1 9 1Z" />
    <path d="M9.00004 12C4.58175 12 1 15.5817 1 20V22H17V20C17 15.5817 13.4183 12 9.00004 12Z" />
    <path d="M19.125 7.125V4H20.875V7.125H24V8.875H20.875V12H19.125V8.875H16V7.125H19.125Z" />
  </>
);

export const UserAddedSolidIcon = createFunctionalIcon(
  'add_team_member_icon_label_a11y',
  UserAddedSolidIcon16x16,
  UserAddedSolidIcon24x24,
);

/**
 * UserCheckIcon
 */
const UserCheckIcon16x16 = () => (
  <path d="M6 1.79102C4.96447 1.79102 4.125 2.63048 4.125 3.66602C4.125 4.70155 4.96447 5.54102 6 5.54102C7.03553 5.54102 7.875 4.70155 7.875 3.66602C7.875 2.63048 7.03553 1.79102 6 1.79102ZM2.875 3.66602C2.875 1.94013 4.27411 0.541016 6 0.541016C7.72589 0.541016 9.125 1.94013 9.125 3.66602C9.125 5.39191 7.72589 6.79102 6 6.79102C4.27411 6.79102 2.875 5.39191 2.875 3.66602ZM15.9054 4.53183L12.0786 8.35792L9.55343 5.77901L10.4466 4.90448L12.0881 6.58088L15.0216 3.64786L15.9054 4.53183ZM0 14C0 10.6863 2.68629 8 6 8C9.31371 8 12 10.6863 12 14V15H0V14ZM1.25647 13.75H10.7435C10.6135 11.2429 8.53949 9.25 6 9.25C3.46051 9.25 1.38647 11.2429 1.25647 13.75Z" />
);

const UserCheckIcon24x24 = () => (
  <path d="M9 2.75C7.48122 2.75 6.25 3.98122 6.25 5.5C6.25 7.01878 7.48122 8.25 9 8.25C10.5188 8.25 11.75 7.01878 11.75 5.5C11.75 3.98122 10.5188 2.75 9 2.75ZM4.5 5.5C4.5 3.01473 6.51473 1 9 1C11.4853 1 13.5 3.01473 13.5 5.5C13.5 7.98527 11.4853 10 9 10C6.51473 10 4.5 7.98527 4.5 5.5ZM23.6459 5.59028L18.4306 11.297L15.3541 7.93071L16.6459 6.75014L18.4306 8.70295L22.3541 4.40972L23.6459 5.59028ZM9.00005 13.75C5.54825 13.75 2.75 16.5482 2.75 20V20.25H15.25V20C15.25 16.5482 12.4518 13.75 9.00005 13.75ZM1 20C1 15.5818 4.58175 12 9.00005 12C13.4183 12 17 15.5817 17 20V22H1V20Z" />
);

export const UserCheckIcon = createFunctionalIcon(
  'team_member_added_icon_label_a11y',
  UserCheckIcon16x16,
  UserCheckIcon24x24,
);

/**
 * UserCheckSolidIcon
 */
const UserCheckSolidIcon16x16 = () => (
  <>
    <path d="M6 0.541016C4.27411 0.541016 2.875 1.94013 2.875 3.66602C2.875 5.39191 4.27411 6.79102 6 6.79102C7.72589 6.79102 9.125 5.39191 9.125 3.66602C9.125 1.94013 7.72589 0.541016 6 0.541016Z" />
    <path d="M15.9052 4.53241L12.0784 8.35849L9.55322 5.77958L10.4464 4.90505L12.0878 6.58146L15.0214 3.64844L15.9052 4.53241Z" />
    <path d="M0 14C0 10.6863 2.68629 8 6 8C9.31371 8 12 10.6863 12 14V15H0V14Z" />
  </>
);

const UserCheckSolidIcon24x24 = () => (
  <>
    <path d="M9 1C6.51472 1 4.5 3.01472 4.5 5.5C4.5 7.98528 6.51472 10 9 10C11.4853 10 13.5 7.98528 13.5 5.5C13.5 3.01472 11.4853 1 9 1Z" />
    <path d="M23.6458 5.59072L18.4305 11.2975L15.354 7.93115L16.6458 6.75058L18.4305 8.70339L22.354 4.41016L23.6458 5.59072Z" />
    <path d="M1 20C1 15.5817 4.58175 12 9.00004 12C13.4183 12 17 15.5817 17 20V22H1V20Z" />
  </>
);

export const UserCheckSolidIcon = createFunctionalIcon(
  'team_member_added_icon_label_a11y',
  UserCheckSolidIcon16x16,
  UserCheckSolidIcon24x24,
);

/**
 * UserDowngradeIcon
 */
const UserDowngradeIcon16x16 = () => (
  <path d="M6 1.79102C4.96447 1.79102 4.125 2.63048 4.125 3.66602C4.125 4.70155 4.96447 5.54102 6 5.54102C7.03553 5.54102 7.875 4.70155 7.875 3.66602C7.875 2.63048 7.03553 1.79102 6 1.79102ZM2.875 3.66602C2.875 1.94013 4.27411 0.541016 6 0.541016C7.72589 0.541016 9.125 1.94013 9.125 3.66602C9.125 5.39191 7.72589 6.79102 6 6.79102C4.27411 6.79102 2.875 5.39191 2.875 3.66602ZM12.375 6.32323V3H13.625V6.32307L14.6666 5.28148L15.5505 6.16536L12.9999 8.71591L10.4494 6.16536L11.3333 5.28148L12.375 6.32323ZM0 14C0 10.6863 2.68629 8 6 8C9.31371 8 12 10.6863 12 14V15H0V14ZM1.25647 13.75H10.7435C10.6135 11.2429 8.53949 9.25 6 9.25C3.46051 9.25 1.38647 11.2429 1.25647 13.75Z" />
);

const UserDowngradeIcon24x24 = () => (
  <path d="M9 2.75C7.48122 2.75 6.25 3.98122 6.25 5.5C6.25 7.01878 7.48122 8.25 9 8.25C10.5188 8.25 11.75 7.01878 11.75 5.5C11.75 3.98122 10.5188 2.75 9 2.75ZM4.5 5.5C4.5 3.01473 6.51473 1 9 1C11.4853 1 13.5 3.01473 13.5 5.5C13.5 7.98527 11.4853 10 9 10C6.51473 10 4.5 7.98527 4.5 5.5ZM18.125 8.63756V4H19.875V8.63756L21.5 7.01256L22.7374 8.25L19 11.9874L15.2626 8.25L16.5 7.01256L18.125 8.63756ZM9.00005 13.75C5.54825 13.75 2.75 16.5482 2.75 20V20.25H15.25V20C15.25 16.5482 12.4518 13.75 9.00005 13.75ZM1 20C1 15.5818 4.58175 12 9.00005 12C13.4183 12 17 15.5817 17 20V22H1V20Z" />
);

export const UserDowngradeIcon = createFunctionalIcon(
  'downgrade_team_member_icon_label_a11y',
  UserDowngradeIcon16x16,
  UserDowngradeIcon24x24,
);

/**
 * UserDowngradeSolidIcon
 */
const UserDowngradeSolidIcon16x16 = () => (
  <>
    <path d="M6 0.541016C4.27411 0.541016 2.875 1.94013 2.875 3.66602C2.875 5.39191 4.27411 6.79102 6 6.79102C7.72589 6.79102 9.125 5.39191 9.125 3.66602C9.125 1.94013 7.72589 0.541016 6 0.541016Z" />
    <path d="M13.625 6.3238L14.6664 5.28236L15.5503 6.16625L12.9998 8.7168L10.4492 6.16625L11.3331 5.28236L12.375 6.32426V3H13.625V6.3238Z" />
    <path d="M0 14C0 10.6863 2.68629 8 6 8C9.31371 8 12 10.6863 12 14V15H0V14Z" />
  </>
);

const UserDowngradeSolidIcon24x24 = () => (
  <>
    <path d="M9 1C6.51472 1 4.5 3.01472 4.5 5.5C4.5 7.98528 6.51472 10 9 10C11.4853 10 13.5 7.98528 13.5 5.5C13.5 3.01472 11.4853 1 9 1Z" />
    <path d="M19.875 8.63854L21.5001 7.01341L22.7376 8.25084L19.0001 11.9883L15.2627 8.25084L16.5001 7.01341L18.125 8.63828V4H19.875V8.63854Z" />
    <path d="M1 20C1 15.5817 4.58175 12 9.00004 12C13.4183 12 17 15.5817 17 20V22H1V20Z" />
  </>
);

export const UserDowngradeSolidIcon = createFunctionalIcon(
  'downgrade_team_member_icon_label_a11y',
  UserDowngradeSolidIcon16x16,
  UserDowngradeSolidIcon24x24,
);

/**
 * UserIcon
 */
const UserIcon16x16 = () => (
  <path d="M8 1.79102C6.96447 1.79102 6.125 2.63048 6.125 3.66602C6.125 4.70155 6.96447 5.54102 8 5.54102C9.03553 5.54102 9.875 4.70155 9.875 3.66602C9.875 2.63048 9.03553 1.79102 8 1.79102ZM4.875 3.66602C4.875 1.94013 6.27411 0.541016 8 0.541016C9.72589 0.541016 11.125 1.94013 11.125 3.66602C11.125 5.39191 9.72589 6.79102 8 6.79102C6.27411 6.79102 4.875 5.39191 4.875 3.66602ZM2 14C2 10.6863 4.68629 8 8 8C11.3137 8 14 10.6863 14 14V15H2V14ZM3.25647 13.75H12.7435C12.6135 11.2429 10.5395 9.25 8 9.25C5.46051 9.25 3.38647 11.2429 3.25647 13.75Z" />
);

const UserIcon24x24 = () => (
  <path d="M12.0001 2.75C10.4813 2.75 9.25006 3.98122 9.25006 5.5C9.25006 7.01878 10.4813 8.25 12.0001 8.25C13.5188 8.25 14.7501 7.01878 14.7501 5.5C14.7501 3.98122 13.5188 2.75 12.0001 2.75ZM7.50006 5.5C7.50006 3.01472 9.51478 1 12.0001 1C14.4853 1 16.5001 3.01472 16.5001 5.5C16.5001 7.98528 14.4853 10 12.0001 10C9.51478 10 7.50006 7.98528 7.50006 5.5ZM12 13.75C8.54827 13.75 5.75002 16.5482 5.75 20V20.25H18.25V20C18.25 16.5482 15.4518 13.75 12 13.75ZM4 20C4.00003 15.5817 7.58178 12 12 12C16.4183 12 20 15.5817 20 20V22H4V20Z" />
);

export const UserIcon = createFunctionalIcon(
  'member_icon_label_a11y',
  UserIcon16x16,
  UserIcon24x24,
);

/**
 * UserSolidIcon
 */
const UserSolidIcon16x16 = () => (
  <>
    <path d="M2 14C2 10.6863 4.68629 8 8 8C11.3137 8 14 10.6863 14 14V15H2V14Z" />
    <path d="M4.875 3.66602C4.875 1.94013 6.27411 0.541016 8 0.541016C9.72589 0.541016 11.125 1.94013 11.125 3.66602C11.125 5.39191 9.72589 6.79102 8 6.79102C6.27411 6.79102 4.875 5.39191 4.875 3.66602Z" />
  </>
);

const UserSolidIcon24x24 = () => (
  <>
    <path d="M4.00001 20C4.00004 15.5817 7.58178 12 12.0001 12C16.4183 12 20 15.5817 20 20L20 22H4L4.00001 20Z" />
    <path d="M16.5 5.5C16.5 7.98528 14.4853 10 12 10C9.51472 10 7.5 7.98528 7.5 5.5C7.5 3.01472 9.51472 1 12 1C14.4853 1 16.5 3.01472 16.5 5.5Z" />
  </>
);

export const UserSolidIcon = createFunctionalIcon(
  'member_icon_label_a11y',
  UserSolidIcon16x16,
  UserSolidIcon24x24,
);

/**
 * UsersAddedIcon
 */
const UsersAddedIcon16x16 = () => (
  <path d="M7.30985 1.7787C7.86944 1.51006 8.50345 1.43863 9.10878 1.57604C9.71411 1.71345 10.2551 2.05161 10.6439 2.53553C11.0327 3.01945 11.2463 3.62066 11.25 4.24137C11.2537 4.86209 11.0473 5.46581 10.6643 5.95434C10.2814 6.44287 9.74445 6.78748 9.14081 6.93212C8.53716 7.07676 7.90234 7.01292 7.33958 6.75098C7.0217 6.60302 6.73737 6.39677 6.50001 6.14527C5.99863 6.67636 5.28802 7.00781 4.5 7.00781C2.98122 7.00781 1.75 5.7766 1.75 4.25781C1.75 2.73903 2.98122 1.50781 4.5 1.50781C5.28802 1.50781 5.99863 1.83926 6.50002 2.37036C6.72982 2.12685 7.00378 1.92564 7.30985 1.7787ZM7.16453 3.57485C7.22033 3.79322 7.25 4.02205 7.25 4.25781C7.25 4.49358 7.22033 4.72241 7.16452 4.94079C7.31667 5.2383 7.56388 5.47662 7.86705 5.61772C8.17401 5.7606 8.52028 5.79542 8.84954 5.71653C9.1788 5.63763 9.47168 5.44966 9.68056 5.18319C9.88944 4.91672 10.002 4.58742 9.99999 4.24885C9.99797 3.91027 9.88146 3.58234 9.66941 3.31839C9.45736 3.05443 9.16225 2.86998 8.83207 2.79503C8.50189 2.72008 8.15607 2.75904 7.85084 2.90557C7.55477 3.04771 7.3138 3.28294 7.16453 3.57485ZM4.5 2.75781C3.67157 2.75781 3 3.42939 3 4.25781C3 5.08624 3.67157 5.75781 4.5 5.75781C5.32843 5.75781 6 5.08624 6 4.25781C6 3.42939 5.32843 2.75781 4.5 2.75781ZM13.375 6.875V5.5H14.625V6.875H16V8.125H14.625V9.5H13.375V8.125H12V6.875H13.375ZM0 13.0332C0 10.5479 2.01473 8.5332 4.50002 8.5332C5.2185 8.5332 5.89766 8.70159 6.50018 9.00104C7.10311 8.70152 7.78261 8.5332 8.50002 8.5332C10.9853 8.5332 13 10.5479 13 13.0332V15.0002H0V13.0332ZM7.75 13.7502V13.0332C7.75 11.2383 6.29494 9.7832 4.50002 9.7832C2.70509 9.7832 1.25 11.2383 1.25 13.0332V13.7502H7.75ZM9 13.7502H11.75V13.0332C11.75 11.2383 10.2949 9.7832 8.50002 9.7832C8.22732 9.7832 7.96282 9.81667 7.71021 9.87969C8.50795 10.6917 9 11.805 9 13.0332V13.7502Z" />
);

const UsersAddedIcon24x24 = () => (
  <path d="M6.50003 5.03516C5.40973 5.03516 4.52588 5.91901 4.52588 7.00931C4.52588 8.09958 5.40972 8.98343 6.50003 8.98343C7.5903 8.98343 8.47415 8.09958 8.47415 7.00931C8.47415 5.919 7.5903 5.03516 6.50003 5.03516ZM2.77588 7.00931C2.77588 4.95251 4.44323 3.28516 6.50003 3.28516C8.10878 3.28516 9.47928 4.30523 10 5.73386C10.5208 4.30523 11.8913 3.28516 13.5 3.28516C15.5568 3.28516 17.2242 4.95251 17.2242 7.00931C17.2242 9.06608 15.5568 10.7334 13.5 10.7334C11.8913 10.7334 10.5208 9.71336 10 8.28474C9.47928 9.71336 8.10878 10.7334 6.50003 10.7334C4.44323 10.7334 2.77588 9.06609 2.77588 7.00931ZM13.5 5.03516C12.4097 5.03516 11.5259 5.91901 11.5259 7.00931C11.5259 8.09958 12.4097 8.98343 13.5 8.98343C14.5903 8.98343 15.4742 8.09958 15.4742 7.00931C15.4742 5.919 14.5903 5.03516 13.5 5.03516ZM20.125 10.125V8H21.875V10.125H24V11.875H21.875V14H20.125V11.875H18V10.125H20.125ZM6.50002 14.5078C3.87667 14.5078 1.75 16.6345 1.75 19.2578V20.2492H11.25V19.2578C11.25 16.6344 9.12338 14.5078 6.50002 14.5078ZM13 20.2492H18.25V19.2578C18.25 16.6344 16.1234 14.5078 13.5 14.5078C12.7476 14.5078 12.037 14.6823 11.4055 14.9931C12.3987 16.1345 13 17.6259 13 19.2578V20.2492ZM10.0001 13.7797C8.98982 13.1328 7.78872 12.7578 6.50002 12.7578C2.91018 12.7578 0 15.668 0 19.2578V21.9992H20V19.2578C20 15.6679 17.0899 12.7578 13.5 12.7578C12.2123 12.7578 11.0106 13.133 10.0001 13.7797Z" />
);

export const UsersAddedIcon = createFunctionalIcon(
  'add_team_member_icon_label_a11y',
  UsersAddedIcon16x16,
  UsersAddedIcon24x24,
);

/**
 * UsersAddedSolidIcon
 */
const UsersAddedSolidIcon16x16 = () => (
  <>
    <path d="M4.5 1.50781C2.98122 1.50781 1.75 2.73903 1.75 4.25781C1.75 5.7766 2.98122 7.00781 4.5 7.00781C6.01878 7.00781 7.25 5.7766 7.25 4.25781C7.25 2.73903 6.01878 1.50781 4.5 1.50781Z" />
    <path d="M4.50002 8.5332C2.01473 8.5332 0 10.5479 0 13.0332V15.0002H9V13.0332C9 10.5479 6.98529 8.5332 4.50002 8.5332Z" />
    <path d="M13.375 6.875V5.5H14.625V6.875H16V8.125H14.625V9.5H13.375V8.125H12V6.875H13.375Z" />
    <path d="M10.25 15.0002H13V13.0332C13 10.5479 10.9853 8.5332 8.49998 8.5332C8.36576 8.5332 8.23287 8.53909 8.10156 8.55063C9.4116 9.60455 10.25 11.221 10.25 13.0332V15.0002Z" />
    <path d="M8.49994 4.25781C8.49994 5.24243 8.14419 6.14396 7.5542 6.84087C7.84909 6.94887 8.16763 7.00781 8.49994 7.00781C10.0187 7.00781 11.2499 5.7766 11.2499 4.25781C11.2499 2.73903 10.0187 1.50781 8.49994 1.50781C8.16763 1.50781 7.84909 1.56675 7.5542 1.67475C8.14419 2.37166 8.49994 3.27319 8.49994 4.25781Z" />
  </>
);

const UsersAddedSolidIcon24x24 = () => (
  <>
    <path d="M11.9742 7.00929C11.9742 8.06994 11.6726 9.06013 11.1504 9.89876C11.7913 10.4206 12.6092 10.7334 13.5001 10.7334C15.5569 10.7334 17.2242 9.06608 17.2242 7.00929C17.2242 4.95251 15.5569 3.28516 13.5001 3.28516C12.6092 3.28516 11.7913 3.598 11.1504 4.11983C11.6726 4.95846 11.9742 5.94865 11.9742 7.00929Z" />
    <path d="M14.75 19.2578V21.9992H20V19.2578C20 15.6679 17.0899 12.7578 13.5001 12.7578C12.9272 12.7578 12.3717 12.8319 11.8425 12.9711C13.6215 14.4843 14.75 16.7393 14.75 19.2578Z" />
    <path d="M0 19.2578C0 15.668 2.91017 12.7578 6.50003 12.7578C10.0899 12.7578 13 15.6679 13 19.2578V21.9992H0V19.2578Z" />
    <path d="M6.50002 3.28516C4.44323 3.28516 2.77588 4.95251 2.77588 7.00929C2.77588 9.06608 4.44323 10.7334 6.50002 10.7334C8.5568 10.7334 10.2242 9.06608 10.2242 7.00929C10.2242 4.95251 8.5568 3.28516 6.50002 3.28516Z" />
    <path d="M20.125 10.125V8H21.875V10.125H24V11.875H21.875V14H20.125V11.875H18V10.125H20.125Z" />
  </>
);

export const UsersAddedSolidIcon = createFunctionalIcon(
  'add_team_member_icon_label_a11y',
  UsersAddedSolidIcon16x16,
  UsersAddedSolidIcon24x24,
);

/**
 * UsersIcon
 */
const UsersIcon16x16 = () => (
  <path d="M4.5 2.75781C3.67157 2.75781 3 3.42939 3 4.25781C3 5.08624 3.67157 5.75781 4.5 5.75781C5.32843 5.75781 6 5.08624 6 4.25781C6 3.42939 5.32843 2.75781 4.5 2.75781ZM1.75 4.25781C1.75 2.73903 2.98122 1.50781 4.5 1.50781C6.01878 1.50781 7.25 2.73903 7.25 4.25781C7.25 5.7766 6.01878 7.00781 4.5 7.00781C2.98122 7.00781 1.75 5.7766 1.75 4.25781ZM11.5 2.75781C10.6716 2.75781 10 3.42939 10 4.25781C10 5.08624 10.6716 5.75781 11.5 5.75781C12.3284 5.75781 13 5.08624 13 4.25781C13 3.42939 12.3284 2.75781 11.5 2.75781ZM8.75 4.25781C8.75 2.73903 9.98122 1.50781 11.5 1.50781C13.0188 1.50781 14.25 2.73903 14.25 4.25781C14.25 5.7766 13.0188 7.00781 11.5 7.00781C9.98122 7.00781 8.75 5.7766 8.75 4.25781ZM0 13.0332C0 10.5479 2.01473 8.5332 4.50002 8.5332C5.91369 8.5332 7.17511 9.18507 8.00008 10.2046C8.82462 9.18565 10.0857 8.5332 11.5 8.5332C13.9853 8.5332 16 10.5479 16 13.0332V15.0002H0V13.0332ZM7.75 13.7502V13.0332C7.75 11.2383 6.29494 9.7832 4.50002 9.7832C2.70509 9.7832 1.25 11.2383 1.25 13.0332V13.7502H7.75ZM9 13.7502H14.75V13.0332C14.75 11.2383 13.2949 9.7832 11.5 9.7832C10.3035 9.7832 9.25689 10.4298 8.69245 11.3948C8.89101 11.9025 9 12.4551 9 13.0332V13.7502Z" />
);

const UsersIcon24x24 = () => (
  <path d="M7.50003 5.03516C6.40973 5.03516 5.52588 5.91901 5.52588 7.00931C5.52588 8.09958 6.40972 8.98343 7.50003 8.98343C8.5903 8.98343 9.47415 8.09958 9.47415 7.00931C9.47415 5.919 8.5903 5.03516 7.50003 5.03516ZM3.77588 7.00931C3.77588 4.95251 5.44323 3.28516 7.50003 3.28516C9.55681 3.28516 11.2242 4.95251 11.2242 7.00931C11.2242 9.06608 9.5568 10.7334 7.50003 10.7334C5.44323 10.7334 3.77588 9.06609 3.77588 7.00931ZM16.5 5.03516C15.4097 5.03516 14.5259 5.91901 14.5259 7.00931C14.5259 8.09958 15.4097 8.98343 16.5 8.98343C17.5903 8.98343 18.4742 8.09958 18.4742 7.00931C18.4742 5.919 17.5903 5.03516 16.5 5.03516ZM12.7759 7.00931C12.7759 4.95251 14.4432 3.28516 16.5 3.28516C18.5568 3.28516 20.2242 4.95251 20.2242 7.00931C20.2242 9.06608 18.5568 10.7334 16.5 10.7334C14.4432 10.7334 12.7759 9.06609 12.7759 7.00931ZM7.50002 14.5078C4.87667 14.5078 2.75 16.6345 2.75 19.2578V20.2492H12.25V19.2578C12.25 16.6344 10.1234 14.5078 7.50002 14.5078ZM14 20.2492H21.25V19.2578C21.25 16.6344 19.1234 14.5078 16.5 14.5078C15.1648 14.5078 13.9583 15.058 13.0945 15.9464C13.6697 16.9162 14 18.0484 14 19.2578V20.2492ZM12.0001 14.5674C10.8322 13.4466 9.24654 12.7578 7.50002 12.7578C3.91018 12.7578 1 15.668 1 19.2578V21.9992H23V19.2578C23 15.6679 20.0899 12.7578 16.5 12.7578C14.7532 12.7578 13.1673 13.4475 12.0001 14.5674Z" />
);

export const UsersIcon = createFunctionalIcon(
  'members_icon_label_a11y',
  UsersIcon16x16,
  UsersIcon24x24,
);

/**
 * UsersSolidIcon
 */
const UsersSolidIcon16x16 = () => (
  <>
    <path d="M0 13.0332C0 10.5479 2.01473 8.5332 4.50002 8.5332C6.98529 8.5332 9 10.5479 9 13.0332V15.0002H0V13.0332Z" />
    <path d="M1.75 4.25781C1.75 2.73903 2.98122 1.50781 4.5 1.50781C6.01878 1.50781 7.25 2.73903 7.25 4.25781C7.25 5.7766 6.01878 7.00781 4.5 7.00781C2.98122 7.00781 1.75 5.7766 1.75 4.25781Z" />
    <path d="M8.75 4.25781C8.75 2.73903 9.98122 1.50781 11.5 1.50781C13.0188 1.50781 14.25 2.73903 14.25 4.25781C14.25 5.7766 13.0188 7.00781 11.5 7.00781C9.98122 7.00781 8.75 5.7766 8.75 4.25781Z" />
    <path d="M10.25 15.0002V13.0332C10.25 11.6315 9.74847 10.347 8.9151 9.34932C9.6463 8.83527 10.5377 8.5332 11.5 8.5332C13.9853 8.5332 16 10.5479 16 13.0332V15.0002H10.25Z" />
  </>
);

const UsersSolidIcon24x24 = () => (
  <>
    <path d="M1 19.2578C1 15.668 3.91017 12.7578 7.50003 12.7578C11.0899 12.7578 14 15.6679 14 19.2578V21.9992H1V19.2578Z" />
    <path d="M3.77588 7.00929C3.77588 4.95251 5.44323 3.28516 7.50002 3.28516C9.5568 3.28516 11.2242 4.95251 11.2242 7.00929C11.2242 9.06608 9.5568 10.7334 7.50002 10.7334C5.44323 10.7334 3.77588 9.06608 3.77588 7.00929Z" />
    <path d="M12.7759 7.00929C12.7759 4.95251 14.4432 3.28516 16.5 3.28516C18.5568 3.28516 20.2242 4.95251 20.2242 7.00929C20.2242 9.06608 18.5568 10.7334 16.5 10.7334C14.4432 10.7334 12.7759 9.06608 12.7759 7.00929Z" />
    <path d="M15.75 21.9992V19.2578C15.75 17.0307 14.8675 15.0097 13.4331 13.5254C14.3467 13.0356 15.3909 12.7578 16.5 12.7578C20.0898 12.7578 23 15.6679 23 19.2578V21.9992H15.75Z" />
  </>
);

export const UsersSolidIcon = createFunctionalIcon(
  'members_icon_label_a11y',
  UsersSolidIcon16x16,
  UsersSolidIcon24x24,
);

/**
 * UserSuspendIcon
 */
const UserSuspendIcon16x16 = () => (
  <>
    <path d="M6 0.541016C4.27411 0.541016 2.875 1.94013 2.875 3.66602C2.875 5.39191 4.27411 6.79102 6 6.79102C7.72589 6.79102 9.125 5.39191 9.125 3.66602C9.125 1.94013 7.72589 0.541016 6 0.541016ZM4.125 3.66602C4.125 2.63048 4.96447 1.79102 6 1.79102C7.03553 1.79102 7.875 2.63048 7.875 3.66602C7.875 4.70155 7.03553 5.54102 6 5.54102C4.96447 5.54102 4.125 4.70155 4.125 3.66602Z" />
    <path d="M6 8C2.68629 8 0 10.6863 0 14V15H12V14C12 10.6863 9.31371 8 6 8ZM6 9.25C8.53949 9.25 10.6135 11.2429 10.7435 13.75H1.25647C1.38647 11.2429 3.46051 9.25 6 9.25Z" />
    <path d="M12.1161 5.50076L10.4368 3.82138L11.3207 2.9375L13 4.61688L14.6794 2.9375L15.5633 3.82138L13.8839 5.50076L15.5633 7.18014L14.6794 8.06402L13 6.38465L11.3207 8.06402L10.4368 7.18014L12.1161 5.50076Z" />
  </>
);

const UserSuspendIcon24x24 = () => (
  <path d="M9 2.75C7.48122 2.75 6.25 3.98122 6.25 5.5C6.25 7.01878 7.48122 8.25 9 8.25C10.5188 8.25 11.75 7.01878 11.75 5.5C11.75 3.98122 10.5188 2.75 9 2.75ZM4.5 5.5C4.5 3.01473 6.51473 1 9 1C11.4853 1 13.5 3.01473 13.5 5.5C13.5 7.98527 11.4853 10 9 10C6.51473 10 4.5 7.98527 4.5 5.5ZM19.9995 9.23744L17.7901 11.4468L16.5527 10.2094L18.7621 8L16.5527 5.79059L17.7901 4.55316L19.9995 6.76256L22.2095 4.55255L23.447 5.78999L21.2369 8L23.447 10.21L22.2095 11.4474L19.9995 9.23744ZM9.00005 13.75C5.54825 13.75 2.75 16.5482 2.75 20V20.25H15.25V20C15.25 16.5482 12.4518 13.75 9.00005 13.75ZM1 20C1 15.5818 4.58175 12 9.00005 12C13.4183 12 17 15.5817 17 20V22H1V20Z" />
);

export const UserSuspendIcon = createFunctionalIcon(
  'deactivate_team_member_icon_label_a11y',
  UserSuspendIcon16x16,
  UserSuspendIcon24x24,
);

/**
 * UserSuspendSolidIcon
 */
const UserSuspendSolidIcon16x16 = () => (
  <>
    <path d="M6 0.541016C4.27411 0.541016 2.875 1.94013 2.875 3.66602C2.875 5.39191 4.27411 6.79102 6 6.79102C7.72589 6.79102 9.125 5.39191 9.125 3.66602C9.125 1.94013 7.72589 0.541016 6 0.541016Z" />
    <path d="M13.0003 6.38465L14.6797 8.06402L15.5635 7.18014L13.8842 5.50076L15.5635 3.82138L14.6797 2.9375L13.0003 4.61688L11.3209 2.9375L10.437 3.82138L12.1164 5.50076L10.437 7.18014L11.3209 8.06402L13.0003 6.38465Z" />
    <path d="M0 14C0 10.6863 2.68629 8 6 8C9.31371 8 12 10.6863 12 14V15H0V14Z" />
  </>
);

const UserSuspendSolidIcon24x24 = () => (
  <>
    <path d="M9 1C6.51472 1 4.5 3.01472 4.5 5.5C4.5 7.98528 6.51472 10 9 10C11.4853 10 13.5 7.98528 13.5 5.5C13.5 3.01472 11.4853 1 9 1Z" />
    <path d="M21.2365 8L23.4469 5.78957L22.2095 4.55213L19.999 6.76256L17.79 4.55358L16.5526 5.79102L18.7616 8L16.5526 10.209L17.79 11.4464L19.999 9.23744L22.2095 11.4479L23.4469 10.2104L21.2365 8Z" />
    <path d="M1 20C1 15.5817 4.58175 12 9.00004 12C13.4183 12 17 15.5817 17 20V22H1V20Z" />
  </>
);

export const UserSuspendSolidIcon = createFunctionalIcon(
  'deactivate_team_member_icon_label_a11y',
  UserSuspendSolidIcon16x16,
  UserSuspendSolidIcon24x24,
);

/**
 * UserUpgradeIcon
 */
const UserUpgradeIcon16x16 = () => (
  <path d="M6 1.79102C4.96447 1.79102 4.125 2.63048 4.125 3.66602C4.125 4.70155 4.96447 5.54102 6 5.54102C7.03553 5.54102 7.875 4.70155 7.875 3.66602C7.875 2.63048 7.03553 1.79102 6 1.79102ZM2.875 3.66602C2.875 1.94013 4.27411 0.541016 6 0.541016C7.72589 0.541016 9.125 1.94013 9.125 3.66602C9.125 5.39191 7.72589 6.79102 6 6.79102C4.27411 6.79102 2.875 5.39191 2.875 3.66602ZM12.9998 2.28409L15.5504 4.83464L14.6665 5.71852L13.6248 4.67677L13.6248 8H12.3748L12.3748 4.67693L11.3332 5.71852L10.4493 4.83464L12.9998 2.28409ZM0 14C0 10.6863 2.68629 8 6 8C9.31371 8 12 10.6863 12 14V15H0V14ZM1.25647 13.75H10.7435C10.6135 11.2429 8.53949 9.25 6 9.25C3.46051 9.25 1.38647 11.2429 1.25647 13.75Z" />
);

const UserUpgradeIcon24x24 = () => (
  <path d="M9 2.75C7.48122 2.75 6.25 3.98122 6.25 5.5C6.25 7.01878 7.48122 8.25 9 8.25C10.5188 8.25 11.75 7.01878 11.75 5.5C11.75 3.98122 10.5188 2.75 9 2.75ZM4.5 5.5C4.5 3.01473 6.51473 1 9 1C11.4853 1 13.5 3.01473 13.5 5.5C13.5 7.98527 11.4853 10 9 10C6.51473 10 4.5 7.98527 4.5 5.5ZM19 3.01256L22.7374 6.75L21.5 7.98744L19.875 6.36244V11H18.125V6.36244L16.5 7.98744L15.2626 6.75L19 3.01256ZM9.00005 13.75C5.54825 13.75 2.75 16.5482 2.75 20V20.25H15.25V20C15.25 16.5482 12.4518 13.75 9.00005 13.75ZM1 20C1 15.5818 4.58175 12 9.00005 12C13.4183 12 17 15.5817 17 20V22H1V20Z" />
);

export const UserUpgradeIcon = createFunctionalIcon(
  'upgrade_team_member_icon_label_a11y',
  UserUpgradeIcon16x16,
  UserUpgradeIcon24x24,
);

/**
 * UserUpgradeSolidIcon
 */
const UserUpgradeSolidIcon16x16 = () => (
  <>
    <path d="M6 0.541016C4.27411 0.541016 2.875 1.94013 2.875 3.66602C2.875 5.39191 4.27411 6.79102 6 6.79102C7.72589 6.79102 9.125 5.39191 9.125 3.66602C9.125 1.94013 7.72589 0.541016 6 0.541016Z" />
    <path d="M6 8C2.68629 8 0 10.6863 0 14V15H12V14C12 10.6863 9.31371 8 6 8Z" />
    <path d="M13.6248 4.67598L13.6248 8H12.3748L12.3748 4.67596L11.3331 5.71764L10.4492 4.83375L12.9997 2.2832L15.5503 4.83375L14.6664 5.71764L13.6248 4.67598Z" />
  </>
);

const UserUpgradeSolidIcon24x24 = () => (
  <>
    <path d="M9 1C6.51472 1 4.5 3.01472 4.5 5.5C4.5 7.98528 6.51472 10 9 10C11.4853 10 13.5 7.98528 13.5 5.5C13.5 3.01472 11.4853 1 9 1Z" />
    <path d="M9.00004 12C4.58175 12 1 15.5817 1 20V22H17V20C17 15.5817 13.4183 12 9.00004 12Z" />
    <path d="M19.875 6.3617L21.4999 7.98659L22.7373 6.74916L18.9999 3.01172L15.2625 6.74916L16.4999 7.98659L18.125 6.36148V11H19.875V6.3617Z" />
  </>
);

export const UserUpgradeSolidIcon = createFunctionalIcon(
  'upgrade_team_member_icon_label_a11y',
  UserUpgradeSolidIcon16x16,
  UserUpgradeSolidIcon24x24,
);

/**
 * VolumeIcon
 */
const VolumeIcon16x16 = () => (
  <path d="M13.0559 14.0345C13.9158 10.0574 13.9158 5.94244 13.0559 1.96534L14.2776 1.70117C15.1752 5.85238 15.1752 10.1475 14.2776 14.2987L13.0559 14.0345ZM9 1.98831V14.1189L4.5527 11.0002H1V5.00025H4.55808L9 1.98831ZM7.75 4.34617L4.94192 6.25025H2.25V9.75025H4.9473L7.75 11.7156V4.34617ZM10.3851 11.5548C10.8125 9.20414 10.8125 6.7957 10.3851 4.44506L11.6149 4.22145C12.0692 6.71995 12.0692 9.27989 11.6149 11.7784L10.3851 11.5548Z" />
);

const VolumeIcon24x24 = () => (
  <path d="M19.3948 21.0645C20.6865 15.0901 20.6865 8.90865 19.3948 2.93429L21.1052 2.56445C22.4497 8.78255 22.4497 15.2162 21.1052 21.4343L19.3948 21.0645ZM13.125 2.83061V21.3314L7.16569 15.9994H2V7.99937H7.1731L13.125 2.83061ZM11.375 6.66813L7.8269 9.74937H3.75V14.2494H7.83431L11.375 17.4174V6.66813ZM15.3891 17.3428C16.0315 13.8095 16.0315 10.1892 15.3891 6.65589L17.1109 6.34284C17.791 10.0832 17.791 13.9155 17.1109 17.6559L15.3891 17.3428Z" />
);

export const VolumeIcon = createFunctionalIcon(
  'volume_on_icon_label_a11y',
  VolumeIcon16x16,
  VolumeIcon24x24,
);

/**
 * VolumeOffIcon
 */
const VolumeOffIcon16x16 = () => (
  <>
    <path d="M9 1.98828V14.1188L4.5527 11.0002H1V5.00022H4.55808L9 1.98828ZM7.75 4.34614L4.94192 6.25022H2.25V9.75022H4.9473L7.75 11.7156V4.34614Z" />
    <path d="M12.1164 8.12186L10.437 6.44248L11.3209 5.55859L13.0003 7.23797L14.6797 5.55859L15.5635 6.44248L13.8842 8.12186L15.5635 9.80123L14.6797 10.6851L13.0003 9.00574L11.3209 10.6851L10.437 9.80124L12.1164 8.12186Z" />
  </>
);

const VolumeOffIcon24x24 = () => (
  <path d="M13.125 2.83203V21.3328L7.16569 16.0008H2V8.00079H7.1731L13.125 2.83203ZM11.375 6.66955L7.8269 9.75079H3.75V14.2508H7.83431L11.375 17.4188V6.66955ZM18.5781 13.3164L16.3687 15.5258L15.1313 14.2883L17.3407 12.0789L15.1313 9.86951L16.3687 8.63207L18.5781 10.8415L20.7881 8.63147L22.0256 9.8689L19.8156 12.0789L22.0256 14.2889L20.7881 15.5264L18.5781 13.3164Z" />
);

export const VolumeOffIcon = createFunctionalIcon(
  'volume_off_icon_label_a11y',
  VolumeOffIcon16x16,
  VolumeOffIcon24x24,
);

/**
 * VotesIcon
 */
const VotesIcon16x16 = () => (
  <path d="M7.875 2.25C4.7684 2.25 2.25 4.7684 2.25 7.875C2.25 10.9816 4.7684 13.5 7.875 13.5C8.87884 13.5 9.81773 13.2382 10.631 12.7799L11.0755 12.5296L13.0615 13.0617L12.5294 11.0758L12.7797 10.6314C13.2381 9.818 13.5 8.87897 13.5 7.875C13.5 4.7684 10.9816 2.25 7.875 2.25ZM1 7.875C1 4.07805 4.07805 1 7.875 1C11.672 1 14.75 4.07805 14.75 7.875C14.75 9.09947 14.4299 10.2492 13.8688 11.245L14.8293 14.8295L11.2446 13.869C10.2489 14.43 9.09931 14.75 7.875 14.75C4.07805 14.75 1 11.672 1 7.875Z" />
);

const VotesIcon24x24 = () => (
  <path d="M11.4455 3.75C7.1954 3.75 3.75 7.1954 3.75 11.4455C3.75 15.6957 7.1954 19.1411 11.4455 19.1411C12.8097 19.1411 14.086 18.7876 15.1934 18.1687C15.1934 18.1687 15.1934 18.1687 15.1934 18.1687L15.8138 17.8219L18.5568 18.5569L17.8218 15.8139L18.1686 15.1936C18.7876 14.0861 19.1411 12.8098 19.1411 11.4455C19.1411 7.1954 15.6957 3.75 11.4455 3.75ZM2 11.4455C2 6.2289 6.2289 2 11.4455 2C16.6621 2 20.8911 6.2289 20.8911 11.4455C20.8911 13.1163 20.4573 14.6858 19.6962 16.0474L21.0317 21.0318L16.0473 19.6962C14.6856 20.4573 13.1162 20.8911 11.4455 20.8911C6.2289 20.8911 2 16.6621 2 11.4455Z" />
);

export const VotesIcon = createFunctionalIcon(
  'vote_icon_label_a11y',
  VotesIcon16x16,
  VotesIcon24x24,
);

/**
 * WifiIcon
 */
const WifiIcon16x16 = () => (
  <path d="M14.5505 6.68435C10.9302 2.93855 5.06966 2.93855 1.44935 6.68435L0.550537 5.81565C4.66222 1.56145 11.3377 1.56145 15.4493 5.81565L14.5505 6.68435ZM12.5401 8.67328C10.0263 5.94224 5.97365 5.94224 3.45979 8.67328L2.54009 7.82672C5.54909 4.55776 10.4508 4.55776 13.4598 7.82672L12.5401 8.67328ZM10.5567 10.6906C9.14426 9.26979 6.85568 9.26978 5.44319 10.6906L4.5567 9.80936C6.45793 7.89688 9.542 7.89687 11.4432 9.80937L10.5567 10.6906ZM6.99994 12H8.99994V14H6.99994V12Z" />
);

const WifiIcon24x24 = () => (
  <path d="M20.5227 9.92771C15.7857 5.19076 8.10561 5.19076 3.36866 9.92771L2.13123 8.69027C7.55159 3.26991 16.3397 3.26991 21.7601 8.69027L20.5227 9.92771ZM17.8949 12.5543C14.609 9.26836 9.28152 9.26836 5.99562 12.5543L4.75818 11.3169C8.72749 7.3475 15.163 7.3475 19.1323 11.3169L17.8949 12.5543ZM15.2681 15.1828C13.4333 13.3479 10.4584 13.3479 8.62355 15.1828L7.38611 13.9454C9.90438 11.4271 13.9874 11.4271 16.5056 13.9454L15.2681 15.1828ZM10.8749 16.875H13.1249V19.125H10.8749V16.875Z" />
);

export const WifiIcon = createFunctionalIcon(
  'wifi_icon_label_a11y',
  WifiIcon16x16,
  WifiIcon24x24,
);

/**
 * ZoomInIcon
 */
const ZoomInIcon16x16 = () => (
  <path d="M7 2.75C4.65279 2.75 2.75 4.65279 2.75 7C2.75 9.34721 4.65279 11.25 7 11.25C9.34721 11.25 11.25 9.34721 11.25 7C11.25 4.65279 9.34721 2.75 7 2.75ZM1.5 7C1.5 3.96243 3.96243 1.5 7 1.5C10.0376 1.5 12.5 3.96243 12.5 7C12.5 8.2936 12.0534 9.48289 11.306 10.4221L13.9419 13.0581L13.0581 13.9419L10.4221 11.306C9.48289 12.0534 8.2936 12.5 7 12.5C3.96243 12.5 1.5 10.0376 1.5 7ZM6.375 6.375V5H7.625V6.375H9V7.625H7.625V9H6.375V7.625H5V6.375H6.375Z" />
);

const ZoomInIcon24x24 = () => (
  <path d="M11 4.625C7.47918 4.625 4.625 7.47918 4.625 11C4.625 14.5208 7.47918 17.375 11 17.375C14.5208 17.375 17.375 14.5208 17.375 11C17.375 7.47918 14.5208 4.625 11 4.625ZM2.875 11C2.875 6.51269 6.51269 2.875 11 2.875C15.4873 2.875 19.125 6.51269 19.125 11C19.125 12.9285 18.4532 14.7 17.3307 16.0933L21.1187 19.8813L19.8813 21.1187L16.0933 17.3307C14.7 18.4532 12.9285 19.125 11 19.125C6.51269 19.125 2.875 15.4873 2.875 11ZM10.125 10.125V8H11.875V10.125H14V11.875H11.875V14H10.125V11.875H8V10.125H10.125Z" />
);

export const ZoomInIcon = createFunctionalIcon(
  'zoom_in_icon_label_a11y',
  ZoomInIcon16x16,
  ZoomInIcon24x24,
);

/**
 * ZoomOutIcon
 */
const ZoomOutIcon16x16 = () => (
  <path d="M7 2.75C4.65279 2.75 2.75 4.65279 2.75 7C2.75 9.34721 4.65279 11.25 7 11.25C9.34721 11.25 11.25 9.34721 11.25 7C11.25 4.65279 9.34721 2.75 7 2.75ZM1.5 7C1.5 3.96243 3.96243 1.5 7 1.5C10.0376 1.5 12.5 3.96243 12.5 7C12.5 8.2936 12.0534 9.48289 11.306 10.4221L13.9419 13.0581L13.0581 13.9419L10.4221 11.306C9.48289 12.0534 8.2936 12.5 7 12.5C3.96243 12.5 1.5 10.0376 1.5 7ZM9 6.375V7.625L5 7.625V6.375L9 6.375Z" />
);

const ZoomOutIcon24x24 = () => (
  <path d="M11 4.625C7.47918 4.625 4.625 7.47918 4.625 11C4.625 14.5208 7.47918 17.375 11 17.375C14.5208 17.375 17.375 14.5208 17.375 11C17.375 7.47918 14.5208 4.625 11 4.625ZM2.875 11C2.875 6.51269 6.51269 2.875 11 2.875C15.4873 2.875 19.125 6.51269 19.125 11C19.125 12.9285 18.4532 14.7 17.3307 16.0933L21.1187 19.8813L19.8813 21.1187L16.0933 17.3307C14.7 18.4532 12.9285 19.125 11 19.125C6.51269 19.125 2.875 15.4873 2.875 11ZM14 11.875H8V10.125H14V11.875Z" />
);

export const ZoomOutIcon = createFunctionalIcon(
  'zoom_out_icon_label_a11y',
  ZoomOutIcon16x16,
  ZoomOutIcon24x24,
);
